import React, { useState, useEffect, useRef } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { PortalLayout } from '../components/Layout'
import { supabase, supabaseAdmin, SIGNING_FEES, LCA_FLAT_FEE, getRank } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { sendNotaryAssignment, sendStatusUpdate, sendCredentialRejected, sendJobCompleted, sendPaymentSent, sendWelcomeEmail, sendClientWelcomeEmail, sendCredentialsVerifiedEmail } from '../lib/email'
import { smsNotaryAssignment, smsNotaryAssigned, smsClientConfirmation } from '../lib/sms'
import toast from 'react-hot-toast'
import Broadcast from './Broadcast'
import { ChecklistSummaryAdmin } from '../components/PreAppointmentChecklist'
import PlatformMaintenance from './PlatformMaintenance'
import AcademyCMS from './AcademyCMS'
import JobMessages from '../components/JobMessages'
import AdminJobDetail from './AdminJobDetail'
import '../styles/AdminColors.css'
import AdminSchedule from './AdminSchedule'
import ResourcesCMS from './ResourcesCMS'
import EducationDashboard from './EducationDashboard'
import TribeCMS from './TribeCMS'
import PodcastCMS from './PodcastCMS'

// ✅ STATE-SPECIFIC NOTARY COMMISSION URLS — For Resources section!!
const STATE_NOTARY_URLS = {
  'AL': 'https://www.sos.alabama.gov/alabama-votes/notary-public',
  'AK': 'https://www.commerce.alaska.gov/web/cbpl/Professionallicensing/NotaryPublic.aspx',
  'AZ': 'https://azsos.gov/business-services/notary',
  'AR': 'https://www.sos.arkansas.gov/business-commercial-services-bcs/notary',
  'CA': 'https://notary.sos.ca.gov/',
  'CO': 'https://www.sos.state.co.us/pubs/notary/home.html',
  'CT': 'https://portal.ct.gov/SOTS/Business-Services/Notary-Public/Notary-Public',
  'DE': 'https://sos.delaware.gov/notary-public/',
  'FL': 'https://dos.fl.gov/notaries/',
  'GA': 'https://sos.ga.gov/page/notaries',
  'HI': 'https://cca.hawaii.gov/ocp/notaries/',
  'ID': 'https://sos.idaho.gov/notary/',
  'IL': 'https://www.ilsos.gov/departments/index/notary/home.html',
  'IN': 'https://www.in.gov/sos/business/notary-division/',
  'IA': 'https://sos.iowa.gov/elections/notary/',
  'KS': 'https://sos.ks.gov/other/notary.html',
  'KY': 'https://www.sos.ky.gov/bus/notary/Pages/default.aspx',
  'LA': 'https://www.sos.la.gov/BusinessServices/FileBusinessDocuments/NotaryServices/Pages/default.aspx',
  'ME': 'https://www.maine.gov/sos/cec/notary/',
  'MD': 'https://sos.maryland.gov/Notary/Pages/default.aspx',
  'MA': 'https://www.mass.gov/how-to/become-a-notary-public',
  'MI': 'https://www.michigan.gov/sos/resources/notary',
  'MN': 'https://www.sos.state.mn.us/business-liens/notaries-authentications/',
  'MS': 'https://www.sos.ms.gov/business-services/notary-public',
  'MO': 'https://www.sos.mo.gov/business/notary',
  'MT': 'https://sosmt.gov/notary/',
  'NE': 'https://sos.nebraska.gov/business-services/notary-public',
  'NV': 'https://www.nvsos.gov/sos/businesses/notary',
  'NH': 'https://www.sos.nh.gov/legal/notary/',
  'NJ': 'https://www.njconsumeraffairs.gov/not/Pages/default.aspx',
  'NM': 'https://www.sos.nm.gov/business-services/notary-public/',
  'NY': 'https://www.dos.ny.gov/licensing-services/notary-public',
  'NC': 'https://www.sosnc.gov/online_services/notary',
  'ND': 'https://sos.nd.gov/notary-public',
  'OH': 'https://www.ohiosos.gov/businesses/notary-resources/',
  'OK': 'https://www.sos.ok.gov/business-filing/notary-public.aspx',
  'OR': 'https://sos.oregon.gov/notary/Pages/default.aspx',
  'PA': 'https://www.dos.pa.gov/BusinessCharities/Notaries/Pages/default.aspx',
  'RI': 'https://sos.ri.gov/divisions/notary-public',
  'SC': 'https://sos.sc.gov/Notary-Public',
  'SD': 'https://sdsos.gov/general-services/notary-public/default.aspx',
  'TN': 'https://sos.tn.gov/notary',
  'TX': 'https://www.sos.state.tx.us/statdoc/notaries.shtml',
  'UT': 'https://notary.utah.gov/',
  'VT': 'https://sos.vermont.gov/notaries/',
  'VA': 'https://www.commonwealth.virginia.gov/office-of-the-secretary-of-the-commonwealth/notary-services/',
  'WA': 'https://www.dol.wa.gov/business-services/notary-public',
  'WV': 'https://sos.wv.gov/business-licensing/notary/Pages/default.aspx',
  'WI': 'https://sos.wi.gov/Pages/Notaries/NotaryPublicHome.aspx',
  'WY': 'https://sos.wyo.gov/Business/Notary.aspx',
}

// ✅ Map of state abbreviations → full names. Used for reverse-lookup when normalizing display.
const STATE_CODE_TO_NAME = {
  AL:'Alabama',     AK:'Alaska',       AZ:'Arizona',        AR:'Arkansas',     CA:'California',
  CO:'Colorado',    CT:'Connecticut',  DE:'Delaware',       FL:'Florida',      GA:'Georgia',
  HI:'Hawaii',      ID:'Idaho',        IL:'Illinois',       IN:'Indiana',      IA:'Iowa',
  KS:'Kansas',      KY:'Kentucky',     LA:'Louisiana',      ME:'Maine',        MD:'Maryland',
  MA:'Massachusetts', MI:'Michigan',   MN:'Minnesota',      MS:'Mississippi',  MO:'Missouri',
  MT:'Montana',     NE:'Nebraska',     NV:'Nevada',         NH:'New Hampshire', NJ:'New Jersey',
  NM:'New Mexico',  NY:'New York',     NC:'North Carolina', ND:'North Dakota', OH:'Ohio',
  OK:'Oklahoma',    OR:'Oregon',       PA:'Pennsylvania',   RI:'Rhode Island', SC:'South Carolina',
  SD:'South Dakota', TN:'Tennessee',   TX:'Texas',          UT:'Utah',         VT:'Vermont',
  VA:'Virginia',    WA:'Washington',   WV:'West Virginia',  WI:'Wisconsin',    WY:'Wyoming',
  DC:'District of Columbia', PR:'Puerto Rico',
}

// Reverse map: full name (lowercase) → 2-letter code
const STATE_NAME_TO_CODE = Object.fromEntries(
  Object.entries(STATE_CODE_TO_NAME).map(([code, name]) => [name.toLowerCase(), code])
)

// Accepts EITHER a 2-letter code ("FL") OR a full name ("Florida") OR mixed-case junk ("florida ", "fl")
// and returns the standard 2-letter code ("FL"). Returns '—' if it can't figure it out.
function normalizeStateName(raw) {
  if (!raw) return '—'
  const trimmed = String(raw).trim()
  if (!trimmed) return '—'
  const upper = trimmed.toUpperCase()
  // If it's exactly 2 letters AND is a known code → return as-is uppercased
  if (upper.length === 2 && STATE_CODE_TO_NAME[upper]) return upper
  // Try matching against full name list (case-insensitive)
  const lower = trimmed.toLowerCase()
  if (STATE_NAME_TO_CODE[lower]) return STATE_NAME_TO_CODE[lower]
  // Fallback: return as-is uppercased (better than blanking out unknown input)
  return upper
}

// ✅ Safely parse jobs — ensure jsonb fields never break React!!
function safeParseJobs(jobs) {
  return (jobs || []).map(j => ({
    ...j,
    office_prefs: j.office_prefs && typeof j.office_prefs === 'object' ? j.office_prefs : {},
  }))
}

const NAV = [
  {
    group: 'EXECUTIVE OVERVIEW',
    icon: '◉',
    items: [
      { to: '/admin',          icon: '◉',  label: 'Dashboard',         end: true },
      { to: '/admin/schedule', icon: '📅', label: 'Signings Calendar' },
    ]
  },
  {
    group: 'OPERATIONS & WORKFLOW',
    icon: '◈',
    items: [
      { to: '/admin/jobs',             icon: '◈',  label: 'All Jobs'         },
      { to: '/admin/assign',           icon: '⊕',  label: 'Assign Notary'   },
      { to: '/admin/scanbacks',        icon: '📁', label: 'Scan-Backs QC'   },
      { to: '/admin/booking-services', icon: '🌟', label: 'Booking Services' },
    ]
  },
  {
    group: 'NETWORK & COMMUNICATIONS',
    icon: '◑',
    items: [
      { to: '/admin/notaries',  icon: '◑',  label: 'Notaries'       },
      { to: '/admin/performance', icon: '📊', label: 'Performance' },
      { to: '/admin/clients',   icon: '◎',  label: 'Clients'        },
      { to: '/admin/blacklist', icon: '🥜', label: 'Nutty Notaries' },
      { to: '/admin/broadcast', icon: '📢', label: 'Email Blast'    },
    ]
  },
  {
    group: 'FINANCIALS',
    icon: '🧾',
    items: [
      { to: '/admin/invoices', icon: '🧾', label: 'Invoices' },
      { to: '/admin/payouts',  icon: '◇',  label: 'Payouts'  },
      { to: '/admin/reports',  icon: '📈', label: 'Reports'  },
    ]
  },
  {
    group: 'STRATEGY & COMPLIANCE',
    icon: '📋',
    items: [
      { to: '/admin/services',    icon: '📋', label: 'Services & Pricing' },
      { to: '/admin/maps',        icon: '◈',  label: 'Coverage Map'       },
      { to: '/admin/credentials', icon: '🛡️', label: 'Compliance'         },
    ]
  },
  {
    group: 'GROWTH & RESOURCES',
    icon: '🎓',
    items: [
      { to: '/admin/academy-cms', icon: '🎓', label: 'Education CMS'    },
      { to: '/admin/resources-cms', icon: '📚', label: 'Resources CMS' },
    ]
  },
  {
    group: 'SYSTEM ADMINISTRATION',
    icon: '⚙',
    items: [
      { to: '/admin/settings',    icon: '⚙',  label: 'Settings'    },
      { to: '/admin/maintenance', icon: '🛠️', label: 'Maintenance'  },
    ]
  },
]

// ✅ ADMIN NAV — clean standard style matching Client/Notary portal
const NAV_FLAT = [
  { section: 'OVERVIEW' },
  { to: '/admin',               icon: '◉',  label: 'Dashboard',          end: true },
  { to: '/admin/schedule',      icon: '📅', label: 'Signings Calendar'   },
  { section: 'OPERATIONS' },
  { to: '/admin/jobs',          icon: '◈',  label: 'All Jobs'            },
  { to: '/admin/assign',        icon: '⊕',  label: 'Assign Notary'       },
  { to: '/admin/scanbacks',     icon: '📁', label: 'Scan-Backs QC'       },
  { section: 'NETWORK' },
  { to: '/admin/notaries',      icon: '◑',  label: 'Notaries'            },
  { to: '/admin/performance',   icon: '📊', label: 'Performance'         },
  { to: '/admin/clients',       icon: '◎',  label: 'Clients'             },
  { to: '/admin/blacklist',     icon: '🥜', label: 'Nutty Notaries'      },
  { to: '/admin/broadcast',     icon: '📢', label: 'Email Blast'         },
  { section: 'FINANCIALS' },
  { to: '/admin/invoices',      icon: '🧾', label: 'Invoices'            },
  { to: '/admin/payouts',       icon: '◇',  label: 'Payouts'             },
  { to: '/admin/reports',       icon: '📈', label: 'Reports'             },
  { section: 'STRATEGY' },
  { to: '/admin/services',      icon: '📋', label: 'Services & Pricing'  },
  { to: '/admin/maps',          icon: '◈',  label: 'Coverage Map'        },
  { to: '/admin/credentials',   icon: '🛡️', label: 'Compliance'          },
  { section: 'EDUCATION & GROWTH' },
  { to: '/admin/academy-cms',   icon: '🎓', label: 'Education CMS'         },
  { to: '/admin/resources-cms', icon: '📚', label: 'Resources CMS'       },
  { to: '/admin/education', icon: '📈', label: 'Tribe Progress' },
  { to: '/admin/tribe-cms', icon: '💜', label: 'The Lounge CMS' },
  { to: '/admin/podcast-cms', icon: '🎙️', label: 'Podcast CMS' },
  { section: 'SYSTEM' },
  { to: '/admin/settings',      icon: '⚙',  label: 'Settings'            },
]

// 🔔 Lounge nudge — show a red unread badge on "The Lounge CMS" sidebar item when
// notaries post or reply since Nancy last opened it. "Last seen" lives in the browser.
const LOUNGE_PATH = '/admin/tribe-cms'
const LOUNGE_SEEN_KEY = 'lca_lounge_last_seen'

function AdminPortalLayout({ children }) {
  const { user } = useAuth()
  const loc = useLocation()
  const [loungeCount, setLoungeCount] = useState(0)

  const onLounge =
    loc.pathname === LOUNGE_PATH || loc.pathname.startsWith(LOUNGE_PATH + '/')
  const enteredLounge = useRef(false)

  // Count new tribe posts + replies created since Nancy last opened the Lounge.
  async function refreshLoungeCount() {
    if (!user) { setLoungeCount(0); return }
    let lastSeen = null
    try { lastSeen = localStorage.getItem(LOUNGE_SEEN_KEY) } catch (e) { /* ignore */ }
    if (!lastSeen) {
      // First run on this browser: start fresh so we only badge genuinely new
      // activity going forward — never "everything ever posted since 1970".
      try { localStorage.setItem(LOUNGE_SEEN_KEY, new Date().toISOString()) } catch (e) { /* ignore */ }
      setLoungeCount(0)
      return
    }

    let newPosts = 0, newReplies = 0
    try {
      const { count } = await supabase.from('tribe_posts')
        .select('id', { count: 'exact', head: true })
        .eq('is_ceo_post', false)
        .gt('created_at', lastSeen)
      newPosts = count || 0
    } catch (e) { console.warn('[Lounge badge] posts count error:', e.message) }

    try {
      const { count } = await supabase.from('tribe_post_comments')
        .select('id', { count: 'exact', head: true })
        .gt('created_at', lastSeen)
        .neq('author_id', user.id)
      newReplies = count || 0
    } catch (e) { console.warn('[Lounge badge] replies count error:', e.message) }

    setLoungeCount(newPosts + newReplies)
  }

  // Opening the Lounge marks everything seen and clears the badge.
  useEffect(() => {
    if (onLounge) {
      // On ENTRY (once per visit): snapshot the previous "last seen" so the
      // Lounge CMS can tag NEW items — even across tab switches inside it.
      if (!enteredLounge.current) {
        enteredLounge.current = true
        try {
          const prev = localStorage.getItem(LOUNGE_SEEN_KEY)
          if (prev) localStorage.setItem('lca_lounge_prev_seen', prev)
        } catch (e) { /* ignore */ }
      }
      localStorage.setItem(LOUNGE_SEEN_KEY, new Date().toISOString())
      setLoungeCount(0)
    } else {
      enteredLounge.current = false
      refreshLoungeCount()
    }
  }, [loc.pathname, user])

  // Gentle 60s poll so fresh posts appear without a manual refresh.
  useEffect(() => {
    if (!user) return
    const id = setInterval(() => { if (!onLounge) refreshLoungeCount() }, 60000)
    return () => clearInterval(id)
  }, [user, onLounge])

  // Inject the live count onto the Lounge nav item.
  const items = NAV_FLAT.map(item =>
    item.to === LOUNGE_PATH ? { ...item, badge: loungeCount } : item
  )

  return (
    <PortalLayout sidebarItems={items}>
      {children}
    </PortalLayout>
  )
}


const REJECTION_REASONS = [
  'Wrong document type — please re-upload the correct document',
  'Image is too blurry or unreadable — please re-scan',
  'Document is expired — please upload a current version',
  'Missing signature or notary stamp',
  'Wrong category selected — please choose the correct document type',
  'Document is incomplete — missing pages',
  'Name on document does not match your profile',
  'Background check is older than 12 months — please get a new one',
  'Other — see admin notes',
]

/* ── REJECT MODAL ── */
function RejectModal({ credId, notaryId, onConfirm, onClose }) {
  const [reason,   setReason]   = useState(REJECTION_REASONS[0])
  const [custom,   setCustom]   = useState('')
  const [saving,   setSaving]   = useState(false)

  async function confirm() {
    setSaving(true)
    const finalReason = reason === 'Other — see admin notes' ? (custom || 'Does not meet requirements') : reason
    await onConfirm(credId, notaryId, finalReason)
    onClose()
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:480, width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:'#0D1B3E', marginBottom:4 }}>Reject Credential</h2>
        <p style={{ fontSize:13, color:'#7A7870', marginBottom:20 }}>Select a reason — the notary will be notified by email.</p>
        <div className="form-group" style={{ marginBottom:16 }}>
          <label className="form-label">Rejection reason</label>
          <select value={reason} onChange={e => setReason(e.target.value)} style={{ marginTop:4 }}>
            {REJECTION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {reason === 'Other — see admin notes' && (
          <div className="form-group" style={{ marginBottom:16 }}>
            <label className="form-label">Custom reason</label>
            <input placeholder="Describe the issue..." value={custom} onChange={e => setCustom(e.target.value)} style={{ marginTop:4 }} />
          </div>
        )}
        <div style={{ display:'flex', gap:10, marginTop:8 }}>
          <button className="btn btn-outline" onClick={onClose} disabled={saving} style={{ flex:1 }}>Cancel</button>
          <button className="btn btn-danger" onClick={confirm} disabled={saving} style={{ flex:2, background:'#E24B4A', color:'#fff', border:'none' }}>
            {saving ? 'Rejecting...' : 'Reject & Notify Notary →'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── VENDOR BADGE — shown everywhere a notary name appears ── */
function VendorBadge({ number, size = 'sm' }) {
  if (!number) return null
  return (
    <span style={{
      fontFamily:    'Cormorant Garamond, serif',
      fontSize:      size === 'lg' ? 13 : 10,
      fontWeight:    700,
      color:         '#9A7A30',
      background:    '#FDF6E3',
      border:        '1px solid rgba(201,168,76,0.3)',
      padding:       size === 'lg' ? '2px 10px' : '1px 7px',
      borderRadius:  20,
      letterSpacing: '0.05em',
      whiteSpace:    'nowrap',
      flexShrink:    0,
    }}>{number}</span>
  )
}

/* ── ASSIGN NOTARY MODAL ── */
function AssignNotaryModal({ job, onClose, onAssigned }) {
  const [notaries,    setNotaries]    = useState([])
  const [allNotaries, setAllNotaries] = useState([])
  const [selected,    setSelected]    = useState('')
  const [loading,     setLoading]     = useState(true)
  const [saving,      setSaving]      = useState(false)
  const [showAllStates, setShowAllStates] = useState(false) // ✅ Toggle for cross-state assignments (RON, etc.)
  const [vettedOnly,    setVettedOnly]    = useState(true)  // 🛡️ DEFAULT ON — Only show vetted notaries (Quality Gate!!)

  useEffect(() => {
    supabase.from('profiles')
      .select('*, notaries(*)')
      .eq('role', 'notary')
      .eq('is_approved', true)
      .then(({ data }) => {
        const sorted = (data || []).sort((a, b) =>
          (b.notaries?.priority_score || 0) - (a.notaries?.priority_score || 0)
        )
        setAllNotaries(sorted)
        setLoading(false)
      })
  }, [])

  // ✅ Filter by job state (unless "Show All States" is enabled) + Vetted-only quality gate
  useEffect(() => {
    let filtered = allNotaries
    // 🛡️ QUALITY GATE: Hide unvetted notaries by default!!
    if (vettedOnly) {
      filtered = filtered.filter(n => n.notaries?.is_vetted === true)
    }
    // State filter
    if (!showAllStates) {
      const jobState = job.signing_state?.toUpperCase().trim()
      filtered = filtered.filter(n => {
        const notaryState = n.notaries?.commission_state?.toUpperCase().trim()
        return notaryState === jobState
      })
    }
    setNotaries(filtered)
  }, [allNotaries, showAllStates, vettedOnly, job.signing_state])

  async function assign() {
    if (!selected) return toast.error('Please select a notary first')
    setSaving(true)
    try {
      const notary = notaries.find(n => n.id === selected)
      const { error } = await supabase.from('jobs')
        .update({ notary_id: selected, status: 'assigned' })
        .eq('id', job.id)
      if (error) throw error
      // 🔔 Notify the notary — fire BOTH channels in PARALLEL so the instant SMS
      // never waits behind the (slower) email, and a phone-only notary still gets a text.
      const addressStr = `${job.signing_address}, ${job.signing_city}, ${job.signing_state}`
      const apptStr    = job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : null
      const notifications = []
      if (notary?.email) {
        notifications.push(sendNotaryAssignment({
          notary_name:   notary.full_name,
          notary_email:  notary.email,
          job_number:    job.job_number,
          service_name:  job.signing_type,
          address:       addressStr,
          appointment:   apptStr,
          pay:           job.notary_pay,
          client_phone:  job.borrower_phone,
          special_notes: job.special_instructions,
        }))
      }
      if (notary?.phone) {
        notifications.push(smsNotaryAssignment({
          notary_name:  notary.full_name,
          notary_phone: notary.phone,
          job_number:   job.job_number,
          service_name: job.signing_type,
          address:      addressStr,
          appointment:  apptStr,
          pay:          job.notary_pay,
        }))
      }
      await Promise.allSettled(notifications)
      // 🔔 Client is intentionally NOT notified here. The "your notary is confirmed" email
      // fires when the notary ACCEPTS (see NotaryPortal accept handler) — so if a notary
      // declines and admin reassigns, the client never sees a name that then changes.
      toast.success(`✅ ${notary?.full_name} notified — awaiting acceptance for ${job.job_number}!!`)
      onAssigned()
      onClose()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  const modalStyles = `
    .lca-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.6); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
    .lca-modal { background:#fff; border-radius:16px; padding:28px; max-width:520px; width:100%; max-height:80vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,0.3); }
    .lca-modal-title { font-family:'Cormorant Garamond',serif; font-size:22px; color:#0D1B3E; margin-bottom:4px; }
    .lca-modal-sub { font-size:13px; color:#7A7870; margin-bottom:20px; }
    .lca-modal-job { background:#F8F7F4; border-radius:10px; padding:14px 16px; margin-bottom:20px; font-size:13px; }
    .lca-modal-job-row { display:flex; justify-content:space-between; padding:3px 0; color:#4A4840; }
    .lca-modal-job-row span:first-child { color:#7A7870; }
    .lca-notary-list { display:flex; flex-direction:column; gap:8px; margin-bottom:20px; max-height:280px; overflow-y:auto; }
    .lca-notary-item { display:flex; align-items:center; gap:12px; padding:12px 14px; border-radius:10px; cursor:pointer; border:1.5px solid #E8E6DF; transition:all 0.15s; background:#fff; }
    .lca-notary-item:hover { border-color:#C9A84C; background:#FDF6E3; }
    .lca-notary-item.selected { border-color:#C9A84C; background:#FDF6E3; }
    .lca-notary-avatar { width:36px; height:36px; border-radius:50%; background:#0D1B3E; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:500; color:#C9A84C; flex-shrink:0; }
    .lca-notary-name { font-size:13px; font-weight:500; color:#0D1B3E; }
    .lca-notary-meta { font-size:11px; color:#7A7870; margin-top:2px; }
    .lca-modal-actions { display:flex; gap:10px; }
  `

  return (
    <>
      <style>{modalStyles}</style>
      <div className="lca-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="lca-modal" role="dialog" aria-modal="true" aria-labelledby="assign-modal-title">
          <h2 id="assign-modal-title" className="lca-modal-title">Assign a Notary</h2>
          <p className="lca-modal-sub">Select a notary to assign to this job. They will receive an email notification automatically.</p>
          <div className="lca-modal-job">
            <div className="lca-modal-job-row"><span>Job #</span><strong>{job.job_number}</strong></div>
            <div className="lca-modal-job-row"><span>Service</span><span>{job.signing_type}</span></div>
            <div className="lca-modal-job-row"><span>Borrower</span><span>{job.borrower_name}</span></div>
            <div className="lca-modal-job-row"><span>Location</span><span>{job.signing_city}, {job.signing_state}</span></div>
            <div className="lca-modal-job-row"><span>Date</span><span>{job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : 'Flexible'}</span></div>
            <div className="lca-modal-job-row"><span>Notary Pay</span><strong style={{ color:'#2D7A4F' }}>${job.notary_pay}</strong></div>
            {job.order_notes && (
              <div style={{ background:'#FDF6E3', border:'1px solid rgba(201,168,76,0.3)', borderRadius:8, padding:'8px 12px', marginTop:8 }}>
                <div style={{ fontSize:10, fontWeight:600, color:'#8A5C00', marginBottom:4 }}>💬 CLIENT NOTES</div>
                <div style={{ fontSize:12, color:'#4A4840' }}>{job.order_notes}</div>
              </div>
            )}
            {job.special_instructions && (
              <div style={{ background:'#F0F4FF', border:'1px solid rgba(13,27,62,0.2)', borderRadius:8, padding:'8px 12px', marginTop:8 }}>
                <div style={{ fontSize:10, fontWeight:600, color:'#0D1B3E', marginBottom:4 }}>📋 SPECIAL INSTRUCTIONS</div>
                <div style={{ fontSize:12, color:'#4A4840' }}>{job.special_instructions}</div>
              </div>
            )}
            {job.docs_downloaded_at ? (
              <div style={{ background:'#EAF5EE', border:'1px solid rgba(45,122,79,0.3)', borderRadius:8, padding:'8px 12px', marginTop:8 }}>
                <div style={{ fontSize:11, color:'#2D7A4F', fontWeight:600 }}>✅ Docs downloaded by notary: {new Date(job.docs_downloaded_at).toLocaleString()}</div>
              </div>
            ) : (
              <div style={{ background:'#FDF6E3', border:'1px solid rgba(201,168,76,0.2)', borderRadius:8, padding:'8px 12px', marginTop:8 }}>
                <div style={{ fontSize:11, color:'#8A5C00' }}>⏳ Notary has not downloaded docs yet!!</div>
              </div>
            )}
          </div>
          {/* ✅ STATE FILTER WITH TOGGLE */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
            <div style={{ fontSize:12, fontWeight:500, color:'#7A7870', letterSpacing:'0.06em' }}>
              SELECT A NOTARY — {notaries.length} {showAllStates ? (vettedOnly ? 'VETTED (ALL STATES)' : 'APPROVED (ALL STATES)') : (vettedOnly ? `VETTED IN ${job.signing_state || 'THIS STATE'}` : `IN ${job.signing_state || 'THIS STATE'}`)}
            </div>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color: vettedOnly ? '#2D7A4F' : '#7A7870', cursor:'pointer', fontWeight: vettedOnly ? 600 : 400 }}>
                <input
                  type="checkbox"
                  checked={vettedOnly}
                  onChange={e => setVettedOnly(e.target.checked)}
                  style={{ width:'auto', cursor:'pointer' }}
                />
                <span>🛡️ Vetted only <span style={{ color:'#B8B5AB', fontSize:10 }}>(quality gate)</span></span>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#7A7870', cursor:'pointer' }}>
                <input
                  type="checkbox"
                  checked={showAllStates}
                  onChange={e => setShowAllStates(e.target.checked)}
                  style={{ width:'auto', cursor:'pointer' }}
                />
                <span>Show all states <span style={{ color:'#B8B5AB', fontSize:10 }}>(RON / cross-state)</span></span>
              </label>
            </div>
          </div>
          {loading && <div style={{ textAlign:'center', padding:24, color:'#B8B5AB' }}>Loading notaries...</div>}
          {!loading && notaries.length === 0 && (
            <div style={{ textAlign:'center', padding:24, color:'#B8B5AB', fontSize:13 }}>
              {vettedOnly && allNotaries.filter(n => n.notaries?.is_vetted).length === 0
                ? <>🛡️ <strong>No vetted notaries yet!</strong> Toggle "Vetted only" off to see all approved notaries, or vet existing ones in the Notaries section first.</>
                : showAllStates
                ? 'No matching notaries — try toggling filters above!'
                : <>No matching notaries in <strong>{job.signing_state}</strong> — toggle "Show all states" or "Vetted only" above to expand search.</>
              }
            </div>
          )}
          <div className="lca-notary-list">
            {notaries.map(n => (
              <div key={n.id} className={`lca-notary-item${selected === n.id ? ' selected' : ''}`} onClick={() => setSelected(n.id)} role="radio" aria-checked={selected === n.id} tabIndex={0} onKeyDown={e => e.key === 'Enter' && setSelected(n.id)}>
                <div className="lca-notary-avatar">{n.full_name?.charAt(0) || '?'}</div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    <span className="lca-notary-name">{n.full_name}</span>
                    {n.notaries?.is_elite  && <span style={{ fontSize:9, fontWeight:700, background:'#2D7A4F', color:'#fff', padding:'2px 6px', borderRadius:10 }}>ELITE</span>}
                    {n.notaries?.is_vetted
                      ? <span style={{ fontSize:9, fontWeight:700, background:'#0D1B3E', color:'#C9A84C', padding:'2px 6px', borderRadius:10 }}>VETTED</span>
                      : <span style={{ fontSize:9, fontWeight:700, background:'#FDF6E3', color:'#8A5C00', border:'1px solid #C9A84C', padding:'1px 5px', borderRadius:10 }}>⚠ NOT VETTED</span>
                    }
                    <VendorBadge number={n.notaries?.vendor_number} />
                    <span style={{ fontSize:9, color:'#B8B5AB', marginLeft:'auto' }}>{n.notaries?.priority_score || 0} pts</span>
                  </div>
                  <div className="lca-notary-meta">
                    {n.notaries?.commission_state || '—'} · {n.notaries?.total_signings || 0} signings ·{' '}
                    <span className={`badge badge-${(n.notaries?.rank || 'bronze').toLowerCase()}`} style={{ fontSize:9 }}>{n.notaries?.rank || 'Bronze'}</span>
                  </div>
                </div>
                {selected === n.id && <div style={{ color:'#C9A84C', fontSize:18, flexShrink:0 }} aria-hidden="true">✓</div>}
              </div>
            ))}
          </div>
          <div className="lca-modal-actions">
            <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={assign} disabled={!selected || saving} style={{ flex:1 }}>
              {saving ? 'Assigning...' : 'Assign & Notify Notary →'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

const STATUS_LABELS = {
  new:'🔴 New', awaiting_assignment:'🔍 Finding Notary', pending_acceptance:'⏳ Notary Notified',
  assigned:'📲 Notified — Awaiting Response', accepted:'✋ Notary Accepted', confirmed:'📅 Appt Confirmed',
  on_the_way:'🚗 On the Way', arrived:'📍 Arrived On-Site', in_progress:'✍️ Signing In Progress',
  completed:'🏆 Completed', scanbacks_uploaded:'📄 Docs Uploaded', awaiting_review:'🔎 Awaiting Review',
  client_approved:'✅ Client Approved', payment_received:'💳 Payment Received', notary_paid:'💰 Notary Paid',
  paid:'💰 Paid', closed:'🔒 Closed', cancelled:'❌ Cancelled', package_shipped:'📦 Package Shipped',
}
function statusLabel(s) { return STATUS_LABELS[s] || (s || '').replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()) }

function StatusBadge({ status }) {
  return <span className={`badge badge-${status}`}>{statusLabel(status)}</span>
}

// ✅ NOTARY AUDIT MODAL — full onboarding record + downloadable/printable PDF (audit production)
function NotaryAuditModal({ notary, getOnboardingSteps, onClose }) {
  const docRef = useRef(null)
  const [busy, setBusy] = useState(false)

  const fmtDateTime = (v) => {
    if (!v) return '—'
    const d = new Date(v)
    if (isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })
      + ' · ' + d.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' })
  }

  const generatedAt = fmtDateTime(new Date().toISOString())

  const CRED_LABELS = [
    { key:'government_id',    label:'Government-Issued ID' },
    { key:'commission',       label:'Notary Commission' },
    { key:'eo_insurance',     label:'E&O Insurance' },
    { key:'background_check', label:'Background Check' },
    { key:'w9_form',          label:'W-9 Tax Form' },
  ]
  const creds = notary.notary_docs || []
  const findCred = (key) => creds.find(c => c.doc_type === key)

  const steps = getOnboardingSteps(notary)
  const doneCount = steps.filter(s => s.done).length
  const pct = Math.round((doneCount / steps.length) * 100)

  const addr = [notary.address, notary.city, notary.state ? normalizeStateName(notary.state) : null, notary.zip].filter(Boolean).join(', ')
  const safeName = (notary.full_name || 'Notary').replace(/[^a-z0-9]+/gi, '-')

  async function downloadPDF() {
    setBusy(true)
    try {
      await ensurePdfLibs()
      const canvas = await window.html2canvas(docRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' })
      const imgData = canvas.toDataURL('image/png')
      const { jsPDF } = window.jspdf
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
      const pageW = pdf.internal.pageSize.getWidth()
      const pageH = pdf.internal.pageSize.getHeight()
      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height)
      const w = canvas.width * ratio
      const h = canvas.height * ratio
      pdf.addImage(imgData, 'PNG', (pageW - w) / 2, 20, w, h)
      pdf.save(`LCA-Notary-Audit-${safeName}.pdf`)
    } catch (err) {
      toast.error('PDF generation failed: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  async function printRecord() {
    setBusy(true)
    try {
      await ensurePdfLibs()
      const canvas = await window.html2canvas(docRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' })
      const imgData = canvas.toDataURL('image/png')
      const win = window.open('', '_blank')
      if (!win) { toast.error('Please allow pop-ups to print.'); return }
      win.document.write(`<html><head><title>LCA Notary Audit — ${notary.full_name || ''}</title></head><body style="margin:0;text-align:center;"><img src="${imgData}" style="width:100%;max-width:800px;" onload="window.focus();window.print();" /></body></html>`)
      win.document.close()
    } catch (err) {
      toast.error('Print failed: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  const Row = ({ label, value }) => (
    <div style={{ display:'flex', padding:'7px 0', borderBottom:'1px solid #F0EEE8', fontSize:13 }}>
      <div style={{ width:170, color:'#7A7870', fontWeight:600, flexShrink:0 }}>{label}</div>
      <div style={{ color:'#0D1B3E', wordBreak:'break-word' }}>{value || '—'}</div>
    </div>
  )

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:3000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:20, overflowY:'auto' }}>
      <div style={{ display:'flex', gap:10, marginBottom:16, position:'sticky', top:0, zIndex:5 }}>
        <button onClick={downloadPDF} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'linear-gradient(135deg,#C9A84C,#E8C46A)', color:'#0D1B3E', border:'none', cursor: busy?'wait':'pointer', fontWeight:700, fontSize:13, opacity: busy?0.7:1 }}>
          {busy ? '⏳ Working…' : '⬇️ Download PDF'}
        </button>
        <button onClick={printRecord} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'#fff', color:'#0D1B3E', border:'1px solid #C9A84C', cursor: busy?'wait':'pointer', fontWeight:700, fontSize:13, opacity: busy?0.7:1 }}>
          🖨️ Print
        </button>
        <button onClick={onClose} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'rgba(255,255,255,0.1)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', cursor:'pointer', fontSize:13 }}>
          ✕ Close
        </button>
      </div>

      {/* AUDIT DOCUMENT — matches LCA certificate branding */}
      <div ref={docRef} style={{ width:760, maxWidth:'100%', background:'#fff', padding:0, position:'relative', boxShadow:'0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ border:'12px solid #0D1B3E', position:'relative', padding:'40px 44px' }}>
          <div style={{ position:'absolute', inset:8, border:'3px solid #C9A84C', borderRadius:2, pointerEvents:'none' }} />

          {/* Header */}
          <div style={{ textAlign:'center', marginBottom:8 }}>
            <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:13, fontWeight:600, color:'#C9A84C', letterSpacing:'0.18em', textTransform:'uppercase' }}>
              Loan Closing Agent, LLC. DBA
            </div>
            <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:18, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.04em' }}>
              Nationwide Notary Professionals Network
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'14px 0 18px' }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,transparent,#C9A84C)' }} />
            <div style={{ color:'#C9A84C', fontSize:14 }}>✦</div>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,#C9A84C,transparent)' }} />
          </div>

          <div style={{ textAlign:'center', fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:28, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.02em', marginBottom:4 }}>
            Notary Onboarding Record
          </div>
          <div style={{ textAlign:'center', fontSize:11, color:'#7A7870', marginBottom:24 }}>
            Confidential — Generated for audit/compliance · {generatedAt}
          </div>

          {/* Notary identity */}
          <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:13, fontWeight:700, color:'#C9A84C', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Notary Information</div>
          <Row label="Full Name" value={notary.full_name} />
          <Row label="Email" value={notary.email} />
          <Row label="Phone" value={notary.phone} />
          <Row label="Address" value={addr} />
          <Row label="Vendor / Badge #" value={notary.notaries?.vendor_number} />
          <Row label="Onboarding Status" value={`${doneCount} of ${steps.length} complete (${pct}%)`} />

          {/* Credentials */}
          <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:13, fontWeight:700, color:'#C9A84C', letterSpacing:'0.08em', textTransform:'uppercase', margin:'22px 0 6px' }}>Credentials on File</div>
          <div style={{ display:'flex', padding:'7px 0', borderBottom:'2px solid #0D1B3E', fontSize:11, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.04em' }}>
            <div style={{ width:200, flexShrink:0 }}>CREDENTIAL</div>
            <div style={{ width:120, flexShrink:0 }}>STATUS</div>
            <div style={{ flex:1 }}>EXPIRES</div>
          </div>
          {CRED_LABELS.map(({ key, label }) => {
            const c = findCred(key)
            const status = c ? (c.status || 'pending') : 'missing'
            const statusColor = status === 'approved' ? '#2D7A4F' : status === 'rejected' ? '#C0392B' : status === 'missing' ? '#B8B5AB' : '#9A7A30'
            return (
              <div key={key} style={{ display:'flex', padding:'7px 0', borderBottom:'1px solid #F0EEE8', fontSize:13 }}>
                <div style={{ width:200, flexShrink:0, color:'#0D1B3E' }}>{label}{c?.custom_name ? ` (${c.custom_name})` : ''}</div>
                <div style={{ width:120, flexShrink:0, color:statusColor, fontWeight:600, textTransform:'capitalize' }}>{status}</div>
                <div style={{ flex:1, color:'#0D1B3E' }}>{fmtDateTime(c?.expires_at)}</div>
              </div>
            )
          })}

          {/* Divider */}
          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'26px 0 18px' }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,transparent,#C9A84C)' }} />
            <div style={{ color:'#C9A84C', fontSize:11 }}>✦ ✦ ✦</div>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,#C9A84C,transparent)' }} />
          </div>

          {/* Signature block */}
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:11, color:'#7A7870', marginBottom:6 }}>Record produced and certified by</div>
            <div style={{ width:220, borderBottom:'1.5px solid #0D1B3E', margin:'0 auto 4px' }} />
            <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:15, fontWeight:600, color:'#0D1B3E' }}>Nancy Phillips, Your Friendly CEO</div>
            <div style={{ fontSize:11, color:'#7A7870', marginTop:2 }}>Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network</div>
          </div>
        </div>
      </div>
      <div style={{ height:30 }} />
    </div>
  )
}

// ✅ NOTARY TRACKER FILTERS — All / Pending / Completed
function NotaryTrackerFilters({ notaryList, getOnboardingSteps }) {
  const [filter, setFilter] = useState('all') // 'all' | 'pending' | 'completed'
  const [selected, setSelected] = useState(null) // notary whose audit modal is open
  const [listOpen, setListOpen] = useState(false) // 🧹 whole-list tuck-away under the filter boxes
  const [expanded, setExpanded] = useState(() => new Set()) // 🪗 accordion: which notary cards are open

  function toggleExpanded(id) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const allCount = notaryList.length
  const completedCount = notaryList.filter(n => {
    const steps = getOnboardingSteps(n)
    return steps.every(s => s.done)
  }).length
  const pendingCount = allCount - completedCount

  const filtered = notaryList.filter(n => {
    if (filter === 'all') return true
    const steps = getOnboardingSteps(n)
    const allDone = steps.every(s => s.done)
    if (filter === 'completed') return allDone
    if (filter === 'pending')   return !allDone
    return true
  })

  const FilterBox = ({ value, label, count, color }) => (
    <button
      onClick={() => { setFilter(value); setListOpen(true) }}
      style={{
        flex: 1,
        padding: '12px 16px',
        borderRadius: 10,
        border: filter === value ? `2px solid ${color}` : '1.5px solid #E8E6DF',
        background: filter === value ? `${color}15` : '#fff',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: '#7A7870', letterSpacing: '0.05em', marginBottom: 4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 22, fontFamily: 'Cormorant Garamond,serif', fontWeight: 700, color: filter === value ? color : '#0D1B3E' }}>
        {count}
      </div>
    </button>
  )

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <FilterBox value="all"       label="All"       count={allCount}       color="#0D1B3E" />
        <FilterBox value="pending"   label="Pending"   count={pendingCount}   color="#C9A84C" />
        <FilterBox value="completed" label="Completed" count={completedCount} color="#2D7A4F" />
      </div>

      <button onClick={() => setListOpen(o => !o)}
        style={{ width:'100%', padding:'9px 14px', borderRadius:10, border:'1.5px dashed #C9A84C', background: listOpen ? '#fff' : '#FDF6E3', color:'#8A5C00', cursor:'pointer', fontSize:12, fontWeight:700, marginBottom:14, transition:'all 0.15s' }}>
        {listOpen ? '⏫ Tuck the list away' : `⏬ Show ${filtered.length} notar${filtered.length === 1 ? 'y' : 'ies'} (${filter})`}
      </button>

      {listOpen && (
      <>

      <div style={{ display:'flex', gap:16, marginBottom:12, fontSize:11, color:'#7A7870', flexWrap:'wrap' }}>
        <span style={{ display:'flex', alignItems:'center', gap:4 }}><span style={{ width:10, height:10, borderRadius:2, background:'#EAF5EE', border:'1px solid #2D7A4F', display:'inline-block' }} />Completed</span>
        <span style={{ display:'flex', alignItems:'center', gap:4 }}><span style={{ width:10, height:10, borderRadius:2, background:'#F8F7F4', border:'1px solid #E8E6DF', display:'inline-block' }} />Pending</span>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <button onClick={() => setExpanded(new Set(filtered.map(n => n.id)))}
          style={{ fontSize:11, padding:'5px 12px', borderRadius:8, border:'1.5px solid #C9A84C', background:'#FDF6E3', color:'#8A5C00', cursor:'pointer', fontWeight:600 }}>
          ⏬ Expand All
        </button>
        <button onClick={() => setExpanded(new Set())}
          style={{ fontSize:11, padding:'5px 12px', borderRadius:8, border:'1.5px solid #E8E6DF', background:'#fff', color:'#7A7870', cursor:'pointer', fontWeight:600 }}>
          ⏫ Collapse All
        </button>
      </div>

      {filtered.length === 0 && (
        <div style={{ color:'#B8B5AB', fontSize:13, textAlign:'center', padding:'24px 0', fontStyle:'italic' }}>
          {filter === 'pending'   && '🎉 No notaries pending — everyone is fully onboarded!!'}
          {filter === 'completed' && 'No notaries fully completed yet — keep helping them onboard!!'}
        </div>
      )}

      {filtered.map(n => {
        const steps   = getOnboardingSteps(n)
        const done    = steps.filter(s => s.done).length
        const pct     = Math.round((done / steps.length) * 100)
        const allDone = done === steps.length
        const isOpen  = expanded.has(n.id)
        return (
          <div key={n.id} style={{ background:'#fff', border:`1.5px solid ${isOpen ? '#C9A84C' : '#E8E6DF'}`, borderRadius:10, marginBottom:8, overflow:'hidden', transition:'border-color 0.15s' }}>

            {/* 🪗 COLLAPSED HEADER — one clean line, click to toggle */}
            <div onClick={() => toggleExpanded(n.id)}
              style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', cursor:'pointer', background: isOpen ? '#FDFCF7' : '#fff' }}>
              <span style={{ fontSize:11, color:'#C9A84C', transform: isOpen ? 'rotate(90deg)' : 'none', transition:'transform 0.15s', flexShrink:0 }}>▶</span>
              <div className="onboard-name"
                onClick={e => { e.stopPropagation(); setSelected(n) }}
                title="View full onboarding record / download audit PDF"
                style={{ cursor:'pointer', textDecoration:'underline', textDecorationColor:'#C9A84C', textUnderlineOffset:'3px', flexShrink:0 }}>
                {n.full_name || '—'}
              </div>
              <span style={{ fontSize:10.5, color:'#7A7870', flexShrink:0 }}>{done} of {steps.length} steps</span>
              <div style={{ flex:1 }} />
              <div className="onboard-bar-wrap" style={{ maxWidth:140 }}>
                <div className="onboard-bar-fill" style={{ width:`${pct}%`, background:allDone?'#2D7A4F':'#C9A84C' }} />
              </div>
              <span className="onboard-pct" style={{ color:allDone?'#2D7A4F':'#9A7A30', flexShrink:0 }}>{pct}%</span>
            </div>

            {/* 🪗 EXPANDED BODY — full record */}
            {isOpen && (
              <div style={{ padding:'0 16px 14px 39px', borderTop:'1px solid #F0EEE8' }}>
                <div style={{ fontSize:10, color:'#7A7870', display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginTop:10 }}>
                  <a href={`mailto:${n.email}`} style={{ color:'inherit', textDecoration:'none' }}>{n.email}</a>
                  {n.phone && <a href={`tel:${n.phone}`} style={{ color:'#9A7A30', textDecoration:'none' }}>📞 {n.phone}</a>}
                  {n.notaries?.vendor_number && <VendorBadge number={n.notaries.vendor_number} />}
                </div>
                {(n.address || n.city || n.state) && (
                  <div style={{ fontSize:10, color:'#B8B5AB', marginTop:4 }}>
                    📍 {[n.address, n.city, n.state ? normalizeStateName(n.state) : null, n.zip].filter(Boolean).join(', ')}
                  </div>
                )}
                <div className="onboard-steps" style={{ marginTop:10 }}>
                  {steps.map((s, i) => (
                    <span key={i} className={`onboard-step ${s.done?'done':'todo'}`}>{s.done ? '✓ ' : ''}{s.label}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      })}

      </>
      )}

      {selected && (
        <NotaryAuditModal
          notary={selected}
          getOnboardingSteps={getOnboardingSteps}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  )
}

// DASHBOARD
function Dashboard() {
  const [stats, setStats] = useState({ total_jobs:0, completed:0, active:0, new_jobs:0, total_revenue:0, total_lca:0, total_notary_pay:0, total_notaries:0, vetted_notaries:0, elite_notaries:0, pending_creds:0, pending_clients:0, pending_notaries:0, avg_profit:0 })
  const [topNotaries,    setTopNotaries]    = useState([])
  const [monthlyData,    setMonthlyData]    = useState([])
  const [notaryList,     setNotaryList]     = useState([])
  const [exceptionJobs,  setExceptionJobs]  = useState([])
  const [loading,        setLoading]        = useState(true)

  useEffect(() => { loadAll() }, [])

  async function loadAll() {
    const { data: jobs } = await supabase.from('jobs').select('*').order('created_at', { ascending: false })
    const all      = jobs || []
    const completed = all.filter(j => ['completed','scanbacks_uploaded','client_approved','closed'].includes(j.status))
    const active    = all.filter(j => ['new','awaiting_assignment','assigned','accepted','on_the_way','arrived'].includes(j.status))
    const newJobs   = all.filter(j => j.status === 'new')
    const totalLCA  = all.reduce((s,j) => s + (j.lca_cut || 0), 0)
    const totalRev  = all.reduce((s,j) => s + (j.total_fee || 0), 0)

    setStats({
      total_jobs:       all.length,
      completed:        completed.length,
      active:           active.length,
      new_jobs:         newJobs.length,
      total_revenue:    totalRev,
      total_lca:        totalLCA,
      total_notary_pay: all.reduce((s,j) => s + (j.notary_pay || 0), 0),
      avg_profit:       all.length > 0 ? Math.round(totalLCA / all.length) : 0,
    })

    // ✅ EXCEPTION REPORTING — jobs within 2hrs with no docs downloaded!!
    const now2 = new Date()
    const exceptions = active.filter(j => {
      if (!j.scheduled_at || !j.notary_id) return false
      const appt = new Date(j.scheduled_at)
      const hoursUntil = (appt - now2) / (1000 * 60 * 60)
      return hoursUntil >= 0 && hoursUntil <= 2 && !j.docs_downloaded_at
    })
    setExceptionJobs(exceptions)

    const monthly = {}
    const now = new Date()
    for (let i = 5; i >= 0; i--) {
      const d   = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = d.toLocaleString('en-US', { month:'short', year:'2-digit' })
      monthly[key] = { month:key, revenue:0, lca:0 }
    }
    all.forEach(j => {
      const key = new Date(j.created_at).toLocaleString('en-US', { month:'short', year:'2-digit' })
      if (monthly[key]) { monthly[key].revenue += j.total_fee || 0; monthly[key].lca += j.lca_cut || 0 }
    })
    setMonthlyData(Object.values(monthly))

    const { data: notaries } = await supabaseAdmin
      .from('profiles')
      .select('*, notaries(*), notary_docs(*)')
      .eq('role', 'notary')
      .order('created_at', { ascending: false })
    const nots = notaries || []
    setNotaryList(nots)
    setTopNotaries(
      [...nots].filter(n => n.notaries)
        .sort((a,b) => (b.notaries?.priority_score||0) - (a.notaries?.priority_score||0))
        .slice(0,5)
    )
    setStats(s => ({
      ...s,
      total_notaries:  nots.length,
      vetted_notaries: nots.filter(n => n.notaries?.is_vetted).length,
      elite_notaries:  nots.filter(n => n.notaries?.is_elite).length,
    }))

    const { count } = await supabaseAdmin.from('notary_docs').select('*', { count:'exact', head:true }).eq('status','pending')
    setStats(s => ({ ...s, pending_creds: count || 0 }))

    // ✅ Pending clients awaiting approval
    const { count: pendingClients } = await supabaseAdmin.from('profiles').select('*', { count:'exact', head:true }).eq('role','client').eq('is_approved', false)
    setStats(s => ({ ...s, pending_clients: pendingClients || 0 }))

    // ✅ Pending notaries awaiting approval
    const { count: pendingNotaries } = await supabaseAdmin.from('profiles').select('*', { count:'exact', head:true }).eq('role','notary').eq('is_approved', false)
    setStats(s => ({ ...s, pending_notaries: pendingNotaries || 0 }))

    setLoading(false)
  }

  function getOnboardingSteps(notary) {
    const creds = notary.notary_docs || []
    return [
      { label:'Personal Info',    done: !!notary.full_name && !!notary.email },
      { label:'Gov ID',           done: creds.some(c => c.doc_type === 'government_id' && c.status === 'approved') },
      { label:'Commission',       done: creds.some(c => c.doc_type === 'commission' && c.status === 'approved') },
      { label:'E&O Insurance',    done: creds.some(c => c.doc_type === 'eo_insurance' && c.status === 'approved') },
      { label:'Background Check', done: creds.some(c => c.doc_type === 'background_check' && c.status === 'approved') },
      { label:'W-9 Uploaded',     done: creds.some(c => c.doc_type === 'w9_form' && c.status === 'approved') },
    ]
  }

  const DASH_STYLES = `
    .kpi-strip { display:grid; grid-template-columns:repeat(6,1fr); gap:0; border:1px solid #E8E6DF; border-radius:12px; overflow:hidden; margin-bottom:20px; }
    @media(max-width:900px){ .kpi-strip { grid-template-columns:repeat(3,1fr); } }
    @media(max-width:500px){ .kpi-strip { grid-template-columns:repeat(2,1fr); } }
    .kpi-cell { padding:16px 18px; border-right:1px solid rgba(201,168,76,0.2); background:#0D1B3E; }
    .kpi-cell:last-child { border-right:none; }
    .kpi-cell.navy  { background:#0D1B3E; }
    .kpi-cell.gold  { background:#C9A84C; }
    .kpi-cell.green { background:#1A4A2E; }
    .kpi-cell.red   { background:#7A1010; }
    .kpi-lbl { font-size:9px; color:rgba(201,168,76,0.6); letter-spacing:0.09em; font-weight:700; margin-bottom:6px; }
    .kpi-cell.gold  .kpi-lbl { color:rgba(13,27,62,0.6); }
    .kpi-val { font-size:24px; font-family:'Cormorant Garamond',serif; font-weight:500; color:#C9A84C; line-height:1; }
    .kpi-cell.gold  .kpi-val { color:#0D1B3E; }
    .kpi-cell.green .kpi-val { color:#5BA882; }
    .kpi-cell.red   .kpi-val { color:#FCA5A5; }
    .kpi-sub { font-size:10px; color:rgba(255,255,255,0.35); margin-top:4px; }
    .kpi-cell.gold .kpi-sub { color:rgba(13,27,62,0.5); }
    .dash-two { display:grid; grid-template-columns:1fr 280px; gap:16px; margin-bottom:16px; }
    @media(max-width:860px){ .dash-two { grid-template-columns:1fr; } }
    .dash-chart { display:flex; align-items:flex-end; gap:8px; height:90px; padding:0 4px; }
    .dash-bar-col { display:flex; flex-direction:column; align-items:center; gap:3px; flex:1; }
    .dash-bar { width:40%; max-width:28px; border-radius:3px 3px 0 0; min-height:3px; }
    .dash-bar-lbl { font-size:9px; color:#B8B5AB; white-space:nowrap; }
    .dash-not-row { display:flex; align-items:center; gap:8px; padding:7px 0; border-bottom:1px solid #F0EEE8; }
    .dash-not-row:last-child { border-bottom:none; }
    .dash-not-av { width:28px; height:28px; border-radius:50%; background:#0D1B3E; color:#C9A84C; display:flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; }
    .dash-alert { background:#FDF6E3; border:1px solid rgba(201,168,76,0.3); border-radius:12px; padding:11px 16px; margin-bottom:16px; display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .onboard-row { display:flex; align-items:center; justify-content:space-between; padding:10px 0; border-bottom:1px solid #F0EEE8; gap:12px; flex-wrap:wrap; }
    .onboard-row:last-child { border-bottom:none; }
    .onboard-name { font-size:13px; font-weight:500; color:#0D1B3E; min-width:140px; }
    .onboard-steps { display:flex; gap:4px; flex:1; flex-wrap:wrap; }
    .onboard-step { font-size:9px; padding:2px 7px; border-radius:10px; font-weight:500; white-space:nowrap; }
    .onboard-step.done { background:#EAF5EE; color:#2D7A4F; }
    .onboard-step.todo { background:#F8F7F4; color:#B8B5AB; border:1px solid #E8E6DF; }
    .onboard-pct { font-size:12px; font-weight:600; color:#0D1B3E; min-width:36px; text-align:right; }
    .onboard-bar-wrap { width:60px; height:4px; background:#E8E6DF; border-radius:2px; flex-shrink:0; }
    .onboard-bar-fill { height:4px; border-radius:2px; background:#C9A84C; }
  `

  if (loading) return (
    <>
      <style>{DASH_STYLES}</style>
      <div style={{ textAlign:'center', padding:60 }}><div className="spinner" style={{ margin:'auto' }} /></div>
    </>
  )

  const maxRev = Math.max(...monthlyData.map(m => m.revenue), 1)

  return (
    <>
      <style>{DASH_STYLES}</style>
<h1 className="page-title" tabIndex={-1}>LCA Command Center</h1>
      <p className="page-sub" style={{ marginBottom:16 }}>Real-time overview of your operation</p>

      {/* ✅ EXCEPTION REPORTING — RED FLAG!! */}
      {exceptionJobs.length > 0 && (
        <div style={{ background:'#FDF2F2', border:'2px solid #E24B4A', borderRadius:12, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }} role="alert">
          <span style={{ fontSize:20 }}>🚨</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#C0392B' }}>⚠️ URGENT — {exceptionJobs.length} job{exceptionJobs.length > 1 ? 's' : ''} within 2 hours with NO docs downloaded!!</div>
            {exceptionJobs.map(j => (
              <div key={j.id} style={{ fontSize:12, color:'#E24B4A', marginTop:4 }}>
                🔴 {j.job_number} · {j.borrower_name} · {j.signing_city}, {j.signing_state} · {new Date(j.scheduled_at).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}
              </div>
            ))}
          </div>
          <a href="/admin/jobs" className="btn btn-sm" style={{ background:'#E24B4A', color:'#fff', borderColor:'#E24B4A', fontSize:11, whiteSpace:'nowrap' }}>Take Action →</a>
        </div>
      )}

      {(stats.new_jobs > 0 || stats.pending_creds > 0 || stats.pending_clients > 0 || stats.pending_notaries > 0) && (
        <div className="dash-alert" role="alert">
          <span style={{ fontSize:16 }}>🔔</span>
          <div style={{ flex:1 }}>
            {stats.new_jobs > 0         && <div style={{ fontSize:13, fontWeight:500, color:'#8A5C00' }}>{stats.new_jobs} job{stats.new_jobs>1?'s':''} waiting to be assigned!!</div>}
            {stats.pending_notaries > 0 && <div style={{ fontSize:12, color:'#B8A060' }}>{stats.pending_notaries} notary{stats.pending_notaries>1?'s':''} awaiting approval!!</div>}
            {stats.pending_creds > 0    && <div style={{ fontSize:12, color:'#B8A060' }}>{stats.pending_creds} credential{stats.pending_creds>1?'s':''} pending review</div>}
            {stats.pending_clients > 0  && <div style={{ fontSize:12, color:'#B8A060' }}>{stats.pending_clients} client{stats.pending_clients>1?'s':''} awaiting approval!!</div>}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {stats.new_jobs > 0         && <a href="/admin/jobs"        className="btn btn-primary btn-sm">Assign →</a>}
            {stats.pending_notaries > 0 && <a href="/admin/notaries?filter=pending_approval" className="btn btn-outline btn-sm">Review Notaries</a>}
            {stats.pending_creds > 0    && <a href="/admin/notaries?filter=pending_creds" className="btn btn-outline btn-sm">Verify Credentials</a>}
            {stats.pending_clients > 0  && <a href="/admin/clients"     className="btn btn-outline btn-sm">Review Clients</a>}
          </div>
        </div>
      )}

      {/* 🎂 TODAY'S BIRTHDAYS — celebrate the tribe!! */}
      {(() => {
        const today = new Date()
        const todayMMDD = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
        const birthdayNotaries = notaryList.filter(n => n.notaries?.birthday === todayMMDD)
        if (birthdayNotaries.length === 0) return null
        return (
          <div style={{ background:'linear-gradient(135deg,#FDF6E3,#FCE9A8)', border:'1px solid #C9A84C', borderRadius:12, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <span style={{ fontSize:28, lineHeight:1 }}>🎂</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#8A5C00', marginBottom:4 }}>
                🎉 Today's Birthday{birthdayNotaries.length > 1 ? 's' : ''}!! Send a love note to celebrate!!
              </div>
              <div style={{ fontSize:12, color:'#9A6800' }}>
                {birthdayNotaries.map(n => n.full_name).join(' · ')}
              </div>
            </div>
            <a href="/admin/broadcast?tab=birthdays" className="btn btn-sm" style={{ background:'#8A5C00', color:'#FDF6E3', borderColor:'#8A5C00', fontSize:11, whiteSpace:'nowrap' }}>📢 Send Birthday Note</a>
          </div>
        )
      })()}

      <div style={{ fontSize:10, fontWeight:600, color:'#7A7870', letterSpacing:'0.08em', marginBottom:8 }}>KPIS AT A GLANCE</div>
      <div className="kpi-strip" role="region" aria-label="Key performance indicators">
        <div className="kpi-cell navy"><div className="kpi-lbl">TOTAL ORDERS</div><div className="kpi-val">{stats.total_jobs}</div><div className="kpi-sub">{stats.completed} completed</div></div>
        <div className="kpi-cell green"><div className="kpi-lbl">TOTAL REVENUE</div><div className="kpi-val">${stats.total_revenue.toLocaleString()}</div><div className="kpi-sub">all time</div></div>
        <div className="kpi-cell navy"><div className="kpi-lbl">LCA NET PROFIT</div><div className="kpi-val">${stats.total_lca.toLocaleString()}</div><div className="kpi-sub">after notary pay</div></div>
        <div className="kpi-cell gold"><div className="kpi-lbl">AVG PROFIT/ORDER</div><div className="kpi-val">${stats.avg_profit}</div><div className="kpi-sub">per job</div></div>
        <div className="kpi-cell"><div className="kpi-lbl">ACTIVE JOBS</div><div className="kpi-val">{stats.active}</div><div className="kpi-sub">in progress</div></div>
        {stats.new_jobs > 0 ? (
          <div className="kpi-cell red"><div className="kpi-lbl">NEEDS ASSIGNMENT</div><div className="kpi-val">{stats.new_jobs}</div><div className="kpi-sub">action required!!</div></div>
        ) : (
          <div className="kpi-cell green"><div className="kpi-lbl">NOTARIES</div><div className="kpi-val">{stats.total_notaries}</div><div className="kpi-sub">{stats.vetted_notaries} vetted</div></div>
        )}
      </div>

      <div className="dash-two">
        <section className="section-card" aria-labelledby="chart-heading">
          <div className="section-header">
            <h2 id="chart-heading" className="section-title">Revenue by Month</h2>
            <a href="/admin/jobs" className="btn btn-outline btn-sm">All Jobs →</a>
          </div>
          <div className="section-body">
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#7A7870', marginBottom:8 }}>
              <span>Client Revenue</span><span style={{ color:'#2D7A4F' }}>LCA Profit</span>
            </div>
            <div className="dash-chart" role="img" aria-label="Monthly revenue chart">
              {monthlyData.map((m, i) => (
                <div key={i} className="dash-bar-col">
                  <div className="dash-bar" style={{ height:`${Math.round((m.revenue/maxRev)*80)}px`, background:i===monthlyData.length-1?'#C9A84C':'#0D1B3E', opacity:0.6+(i/monthlyData.length)*0.4 }} />
                  <div className="dash-bar-lbl">{m.month}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:16, marginTop:12, paddingTop:10, borderTop:'1px solid #F0EEE8', flexWrap:'wrap' }}>
              <div style={{ fontSize:11, color:'#7A7870' }}>Paid to notaries: <strong style={{ color:'#0D1B3E' }}>${stats.total_notary_pay.toLocaleString()}</strong></div>
              <div style={{ fontSize:11, color:'#7A7870' }}>Avg profit/order: <strong style={{ color:'#C9A84C' }}>${stats.avg_profit}</strong></div>
            </div>
          </div>
        </section>

        <section className="section-card" aria-labelledby="top-heading">
          <div className="section-header">
            <h2 id="top-heading" className="section-title">Top Notaries</h2>
            <a href="/admin/notaries" className="btn btn-outline btn-sm">All →</a>
          </div>
          <div className="section-body">
            {topNotaries.length === 0 ? (
              <div style={{ color:'#B8B5AB', fontSize:12, textAlign:'center', padding:'16px 0' }}>No notaries yet!!</div>
            ) : (
              topNotaries.map((n, i) => (
                <div key={n.id} className="dash-not-row">
                  <div style={{ fontSize:10, fontWeight:700, color:'#B8B5AB', width:14 }}>#{i+1}</div>
                  <div className="dash-not-av">{n.full_name?.charAt(0)||'?'}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
                      <span style={{ fontSize:12, fontWeight:500, color:'#0D1B3E' }}>{n.full_name||'—'}</span>
                      {n.notaries?.is_elite  && <span style={{ fontSize:7, fontWeight:700, background:'#2D7A4F', color:'#fff', padding:'1px 4px', borderRadius:6 }}>ELITE</span>}
                      {n.notaries?.is_vetted
                        ? <span style={{ fontSize:7, fontWeight:700, background:'#0D1B3E', color:'#C9A84C', padding:'1px 4px', borderRadius:6 }}>VETTED</span>
                        : <span style={{ fontSize:7, fontWeight:700, background:'#FDF6E3', color:'#8A5C00', border:'1px solid #C9A84C', padding:'0px 3px', borderRadius:6 }}>⚠ NOT VETTED</span>
                      }
                    </div>
                    <div style={{ fontSize:9, color:'#B8B5AB' }}><VendorBadge number={n.notaries?.vendor_number} /> · {n.notaries?.total_signings||0} signings</div>
                  </div>
                  <div style={{ fontSize:11, fontWeight:600, color:'#C9A84C' }}>{n.notaries?.priority_score||0}pts</div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <section className="section-card" aria-labelledby="onboard-heading">
        <div className="section-header">
          <h2 id="onboard-heading" className="section-title">Notary Onboarding Tracker</h2>
          <a href="/admin/notaries" className="btn btn-outline btn-sm">Manage All →</a>
        </div>
        <div className="section-body">
          {notaryList.length === 0 ? (
            <div style={{ color:'#B8B5AB', fontSize:12, textAlign:'center', padding:'16px 0' }}>No notaries registered yet!!</div>
          ) : (
            <>
              {/* ✅ FILTER BOXES — All / Pending / Completed */}
              <NotaryTrackerFilters notaryList={notaryList} getOnboardingSteps={getOnboardingSteps} />
            </>
          )}
        </div>
      </section>
    </>
  )
}

// 📄 PDF libraries loader — reliable UMD loading via <script> tags (window globals)
function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-pdfsrc="${src}"]`)
    if (existing) {
      if (existing.getAttribute('data-loaded') === '1') return resolve()
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Failed to load ' + src)))
      return
    }
    const s = document.createElement('script')
    s.src = src; s.async = true; s.setAttribute('data-pdfsrc', src)
    s.addEventListener('load', () => { s.setAttribute('data-loaded', '1'); resolve() })
    s.addEventListener('error', () => reject(new Error('Failed to load ' + src)))
    document.head.appendChild(s)
  })
}
async function ensurePdfLibs() {
  if (!window.html2canvas) await loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
  if (!window.jspdf)       await loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')
}

// 🧾 INVOICE MODAL — branded client invoice + Download PDF + Print (single)
function InvoiceModal({ job, onClose }) {
  const docRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [client, setClient] = useState(null)

  const isIndividual = job.job_type === 'individual' || job.scan_backs_required === false

  useEffect(() => {
    let active = true
    async function loadClient() {
      if (isIndividual || !job.client_id) return
      const { data } = await supabase.from('profiles')
        .select('full_name, company_name, email, phone, address, city, state, zip')
        .eq('id', job.client_id).single()
      if (active && data) setClient(data)
    }
    loadClient()
    return () => { active = false }
  }, [job.client_id, isIndividual])

  const fmtDateTime = (v) => {
    if (!v) return '—'
    const d = new Date(v)
    if (isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })
      + ' · ' + d.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' })
  }
  const money = (n) => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD' })

  // Bill To
  const billCompany = isIndividual ? (job.borrower_name || 'Individual Client')
    : (job.company_name || client?.company_name || job.client_name || 'Client')
  const billAttn   = isIndividual ? null : (job.client_name || client?.full_name || null)
  const billStreet = isIndividual ? job.signing_address : client?.address
  const billCityLine = isIndividual
    ? [job.signing_city, job.signing_state ? normalizeStateName(job.signing_state) : null].filter(Boolean).join(', ')
    : [client?.city, client?.state ? normalizeStateName(client.state) : null, client?.zip].filter(Boolean).join(', ')
  const billPhone = isIndividual ? job.borrower_phone : client?.phone
  const billEmail = isIndividual ? job.borrower_email : (job.client_email || client?.email)

  const paid = !!job.client_paid_at
  const invoiceDate = job.invoice_sent_at || job.created_at || new Date().toISOString()
  const signerLoc = [job.signing_city, job.signing_state ? normalizeStateName(job.signing_state) : null].filter(Boolean).join(', ')
  const safeNo = (job.job_number || 'invoice').replace(/[^a-z0-9]+/gi, '-')

  async function downloadPDF() {
    setBusy(true)
    try {
      await ensurePdfLibs()
      const canvas = await window.html2canvas(docRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' })
      const imgData = canvas.toDataURL('image/png')
      const { jsPDF } = window.jspdf
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
      const pageW = pdf.internal.pageSize.getWidth()
      const pageH = pdf.internal.pageSize.getHeight()
      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height)
      const w = canvas.width * ratio, h = canvas.height * ratio
      pdf.addImage(imgData, 'PNG', (pageW - w) / 2, 20, w, h)
      pdf.save(`LCA-Invoice-${safeNo}.pdf`)
    } catch (err) {
      toast.error('PDF generation failed: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  async function printRecord() {
    setBusy(true)
    try {
      await ensurePdfLibs()
      const canvas = await window.html2canvas(docRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' })
      const imgData = canvas.toDataURL('image/png')
      const win = window.open('', '_blank')
      if (!win) { toast.error('Please allow pop-ups to print.'); return }
      win.document.write(`<html><head><title>LCA Invoice ${job.job_number || ''}</title></head><body style="margin:0;text-align:center;"><img src="${imgData}" style="width:100%;max-width:760px;" onload="window.focus();window.print();" /></body></html>`)
      win.document.close()
    } catch (err) {
      toast.error('Print failed: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:3000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:20, overflowY:'auto' }}>
      <div style={{ display:'flex', gap:10, marginBottom:16, position:'sticky', top:0, zIndex:5 }}>
        <button onClick={downloadPDF} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'linear-gradient(135deg,#C9A84C,#E8C46A)', color:'#0D1B3E', border:'none', cursor: busy?'wait':'pointer', fontWeight:700, fontSize:13, opacity: busy?0.7:1 }}>
          {busy ? '⏳ Working…' : '⬇️ Download PDF'}
        </button>
        <button onClick={printRecord} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'#fff', color:'#0D1B3E', border:'1px solid #C9A84C', cursor: busy?'wait':'pointer', fontWeight:700, fontSize:13, opacity: busy?0.7:1 }}>
          🖨️ Print
        </button>
        <button onClick={onClose} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'rgba(255,255,255,0.1)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', cursor:'pointer', fontSize:13 }}>
          ✕ Close
        </button>
      </div>

      {/* INVOICE DOCUMENT */}
      <div ref={docRef} style={{ width:720, maxWidth:'100%', background:'#fff', boxShadow:'0 18px 50px rgba(13,27,62,0.25)' }}>
        <div style={{ border:'12px solid #0D1B3E', position:'relative', padding:'38px 42px' }}>
          <div style={{ position:'absolute', inset:8, border:'3px solid #C9A84C', pointerEvents:'none' }} />

          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:20 }}>
            <div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:13, fontWeight:600, color:'#C9A84C', letterSpacing:'0.18em', textTransform:'uppercase' }}>Loan Closing Agent, LLC. DBA</div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:21, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.03em' }}>Nationwide Notary Professionals Network</div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:34, fontWeight:700, color:'#0D1B3E', lineHeight:1 }}>INVOICE</div>
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0 20px' }}>
            <div style={{ flex:1, height:2, background:'linear-gradient(90deg,#0D1B3E,#C9A84C)' }} />
            <div style={{ color:'#C9A84C', fontSize:13 }}>✦</div>
            <div style={{ flex:1, height:2, background:'linear-gradient(90deg,#C9A84C,#0D1B3E)' }} />
          </div>

          {/* Meta + Bill To */}
          <div style={{ display:'flex', justifyContent:'space-between', gap:24, marginBottom:24 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#C9A84C', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Bill To</div>
              <div style={{ fontSize:15, fontWeight:700, color:'#0D1B3E' }}>{billCompany}</div>
              {billAttn   && <div style={{ fontSize:13, color:'#2C2C2C' }}>Attn: {billAttn}</div>}
              {billStreet && <div style={{ fontSize:13, color:'#2C2C2C' }}>{billStreet}</div>}
              {billCityLine && <div style={{ fontSize:13, color:'#2C2C2C' }}>{billCityLine}</div>}
              {billPhone  && <div style={{ fontSize:13, color:'#2C2C2C' }}>{billPhone}</div>}
              {billEmail  && <div style={{ fontSize:13, color:'#1F5C99' }}>{billEmail}</div>}
            </div>
            <div style={{ flex:1, textAlign:'right' }}>
              <table style={{ marginLeft:'auto', fontSize:12, color:'#2C2C2C', borderCollapse:'collapse' }}>
                <tbody>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Invoice #</td><td style={{ padding:'2px 0', fontWeight:700, color:'#0D1B3E', fontFamily:'Courier New, monospace' }}>{job.job_number || '—'}</td></tr>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Invoice Date</td><td style={{ padding:'2px 0', color:'#0D1B3E' }}>{fmtDateTime(invoiceDate)}</td></tr>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Service Date</td><td style={{ padding:'2px 0', color:'#0D1B3E' }}>{fmtDateTime(job.scheduled_at)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Line items */}
          <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:6 }}>
            <thead>
              <tr style={{ background:'#0D1B3E' }}>
                <th style={{ textAlign:'left', color:'#fff', fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', padding:'9px 12px' }}>Description</th>
                <th style={{ textAlign:'right', color:'#fff', fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', padding:'9px 12px', width:120 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom:'1px solid #EDEAE2' }}>
                <td style={{ padding:'13px 12px', fontSize:13, color:'#2C2C2C', verticalAlign:'top' }}>
                  <div style={{ fontWeight:600, color:'#0D1B3E' }}>Notary Signing Service{job.signing_type ? ` — ${job.signing_type}` : ''}</div>
                  <div style={{ fontSize:11.5, color:'#7A7870', marginTop:3 }}>{job.borrower_name ? `Signer: ${job.borrower_name}` : ''}{job.borrower_name && signerLoc ? '  ·  ' : ''}{signerLoc}</div>
                  {(job.file_number || job.client_po) && <div style={{ fontSize:11.5, color:'#7A7870', marginTop:2 }}>Client File #: <span style={{ fontWeight:600, color:'#0D1B3E' }}>{job.file_number || job.client_po}</span></div>}
                </td>
                <td style={{ padding:'13px 12px', fontSize:14, color:'#0D1B3E', textAlign:'right', fontWeight:600, verticalAlign:'top' }}>{money(job.total_fee)}</td>
              </tr>
            </tbody>
          </table>

          {/* Flat fee note + total */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:20, marginTop:14 }}>
            <div style={{ flex:1, fontSize:11, color:'#7A7870', fontStyle:'italic', lineHeight:1.5, maxWidth:320 }}>
              One flat, all-inclusive fee — no per-file fees, no technology fees, no hidden charges. Thank you for trusting us with your signing.
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:11, color:'#7A7870', letterSpacing:'0.08em', textTransform:'uppercase' }}>Total Due</div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:30, fontWeight:700, color:'#0D1B3E', lineHeight:1.1 }}>{money(job.total_fee)}</div>
              {paid
                ? <div style={{ display:'inline-block', marginTop:6, padding:'4px 14px', borderRadius:20, background:'#D7F0DE', color:'#2D7A4F', fontSize:11, fontWeight:700, letterSpacing:'0.05em' }}>PAID · {fmtDateTime(job.client_paid_at)}</div>
                : <div style={{ display:'inline-block', marginTop:6, padding:'4px 14px', borderRadius:20, background:'#FCE9C8', color:'#9A7A30', fontSize:11, fontWeight:700, letterSpacing:'0.05em' }}>DUE UPON FILE CLOSING</div>}
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'24px 0 16px' }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,transparent,#C9A84C)' }} />
            <div style={{ color:'#C9A84C', fontSize:11 }}>✦ ✦ ✦</div>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,#C9A84C,transparent)' }} />
          </div>

          {/* Footer */}
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:12, color:'#2C2C2C', marginBottom:4 }}>Please remit payment to <span style={{ fontWeight:600, color:'#0D1B3E' }}>Loan Closing Agent, LLC.</span></div>
            <div style={{ fontSize:11, color:'#7A7870' }}>7946 Meadow Walk Ln.  ·  Jacksonville, FL 32256</div>
            <div style={{ fontSize:11, color:'#7A7870' }}>Questions? bookings@loanclosingagent.com  ·  904-865-0133</div>
          </div>
        </div>
      </div>
      <div style={{ height:30 }} />
    </div>
  )
}

function AllJobs() {
  const [jobs,       setJobs]       = useState([])
  const [filter,     setFilter]     = useState('active')
  const [typeFilter, setTypeFilter] = useState('all') // ✅ NEW: all, client, individual
  const [search,     setSearch]     = useState('')
  const [loading,    setLoading]    = useState(true)
  const [assignJob,  setAssignJob]  = useState(null)
  const [invoiceJob, setInvoiceJob] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    loadJobs()
    const interval = setInterval(loadJobs, 30000)
    return () => clearInterval(interval)
  }, [])

  async function loadJobs() {
    const { data } = await supabase.from('jobs')
      .select('*, notary:profiles!jobs_notary_id_fkey(full_name, notaries(vendor_number))')
      .order('created_at', { ascending: false })
    setJobs(safeParseJobs(data))
    setLoading(false)
  }

  async function updateStatus(job, status) {
    // 🛡️ DATA INTEGRITY GATE — Can't mark notary paid without a notary assigned!!
    if (status === 'notary_paid' && !job.notary_id) {
      toast.error('🛡️ Cannot mark as paid — no notary is assigned to this job!!', { duration: 6000 })
      return
    }
    await supabase.from('jobs').update({ status }).eq('id', job.id)
    if (job.borrower_email) {
      // Fetch notary details for assigned/accepted status
      let notaryProfile = null
      if (['assigned','accepted'].includes(status) && job.notary_id) {
        const { data } = await supabase.from('profiles').select('full_name, email, phone').eq('id', job.notary_id).single()
        notaryProfile = data
      }
      await sendStatusUpdate({
        client_name:   job.borrower_name,
        client_email:  job.borrower_email,
        job_number:    job.job_number,
        service_name:  job.signing_type,
        new_status:    status,
        notary_name:   notaryProfile?.full_name || null,
        notary_phone:  notaryProfile?.phone     || null,
        notary_email:  notaryProfile?.email     || null,
        appointment:   job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : null,
      })
    }
    toast.success(`Job marked as ${status}`)
    loadJobs()
  }

  // 📧 SEND INVOICE — stamps invoice_sent_at (the gate before closing) + notifies client
  async function sendInvoice(job) {
    await supabase.from('jobs').update({ invoice_sent_at: new Date().toISOString() }).eq('id', job.id)
    if (job.borrower_email) {
      try {
        await sendStatusUpdate({
          client_name:  job.borrower_name,
          client_email: job.borrower_email,
          job_number:   job.job_number,
          service_name: job.signing_type,
          new_status:   'invoice_sent',
          message:      `Your invoice for job ${job.job_number} has been issued. Thank you for trusting Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network!!`,
        })
      } catch (e) { console.error('Invoice email error:', e) }
    }
    toast.success(`📧 Invoice sent to ${job.borrower_name || 'client'} — you can now close ${job.job_number} when ready!!`)
    loadJobs()
  }

  // 🔒 CLOSE ORDER
  async function closeOrder(job) {
    const isIndividual = job.job_type === 'individual' || job.scan_backs_required === false
    if (isIndividual) {
      // 💳 Individuals pay UPFRONT via Stripe — no invoice step. Just confirm payment landed.
      if (!job.client_paid_at) {
        if (!window.confirm(`💵 Has the individual paid for ${job.job_number}?\n\nThis order isn't marked paid yet. Click OK only if payment has been collected — then it will close.`)) return
      }
      await updateStatus(job, 'closed')
      return
    }
    // 🏢 Company orders: invoice must be sent first
    if (!job.invoice_sent_at) {
      toast.error('🛡️ Send the client invoice before closing this order!!', { duration: 6000 })
      return
    }
    await updateStatus(job, 'closed')
  }

  const filtered = jobs.filter(j => {
    const matchStatus = filter === 'all' ? true
      : filter === 'active' ? ['new','awaiting_assignment','assigned','accepted','on_the_way','arrived','completed','scanbacks_uploaded','client_approved'].includes(j.status)
      : j.status === filter
    const matchType   = typeFilter === 'all' ? true : (j.job_type || 'client') === typeFilter
    const matchSearch = !search || j.borrower_name?.toLowerCase().includes(search.toLowerCase()) || j.job_number?.includes(search)
    return matchStatus && matchType && matchSearch
  })

  // 🚨 COLD FILE DETECTION — Jobs unassigned for 2+ days = REVENUE LEAK!!
  const now = new Date()
  const coldFiles = jobs.filter(j => {
    if (j.notary_id) return false
    if (!['new','awaiting_assignment'].includes(j.status)) return false
    const created = new Date(j.created_at)
    const hoursOld = (now - created) / (1000 * 60 * 60)
    return hoursOld >= 48  // 2 days = cold!!
  })

  return (
    <>
      {assignJob && <AssignNotaryModal job={assignJob} onClose={() => setAssignJob(null)} onAssigned={loadJobs} />}

      {/* 🚨 COLD FILE ALERT BANNER — Don't lose revenue to cold leads!! */}
      {coldFiles.length > 0 && (
        <div style={{
          background:'linear-gradient(135deg,#FDF2F2,#FFE8E8)',
          border:'2px solid #E24B4A',
          borderRadius:12,
          padding:'14px 18px',
          marginBottom:16,
          display:'flex',
          alignItems:'center',
          justifyContent:'space-between',
          gap:12,
          flexWrap:'wrap',
          boxShadow:'0 4px 12px rgba(226,75,74,0.15)',
        }}>
          <div style={{ flex:1, minWidth:200 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#B23332', marginBottom:4 }}>
              🚨 {coldFiles.length} COLD FILE{coldFiles.length > 1 ? 'S' : ''} — Unassigned for 2+ days!!
            </div>
            <div style={{ fontSize:11, color:'#6B2020' }}>
              {coldFiles.slice(0, 3).map(j => j.job_number).join(', ')}{coldFiles.length > 3 ? ` + ${coldFiles.length - 3} more` : ''} — assign or cancel ASAP to protect client relationships!
            </div>
          </div>
          <button onClick={() => { setFilter('awaiting_assignment'); setTypeFilter('all'); setSearch(''); }}
            style={{ background:'#E24B4A', color:'#fff', border:'none', borderRadius:8, padding:'10px 16px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
            👀 View Cold Files
          </button>
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <h1 className="page-title" style={{ margin:0 }}>All Jobs</h1>
        <button onClick={loadJobs} className="btn btn-outline btn-sm" style={{ fontSize:11 }}>🔄 Refresh</button>
      </div>
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        {/* ✅ JOB TYPE FILTER */}
        <div style={{ display:'flex', gap:6, marginBottom:8 }}>
          {[
            { value:'all',        label:'All Types'       },
            { value:'client',     label:'🏢 Client'       },
            { value:'individual', label:'👤 Individual'   },
          ].map(f => (
            <button key={f.value} onClick={() => setTypeFilter(f.value)}
              style={{ fontSize:12, padding:'6px 12px', borderRadius:8, border:'1.5px solid', cursor:'pointer', fontWeight: typeFilter===f.value ? 700 : 400,
                background: typeFilter===f.value ? '#C9A84C' : '#fff',
                color:      typeFilter===f.value ? '#0D1B3E' : '#7A7870',
                borderColor:typeFilter===f.value ? '#C9A84C' : '#E8E6DF',
              }}>
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          {[
            { value:'all',       label:'All Jobs'     },
            { value:'active',    label:'🟢 Active'    },
            { value:'new',                 label:'🔴 New'                  },
            { value:'awaiting_assignment', label:'🔍 Finding Notary'        },
            { value:'assigned',            label:'📨 Notary Notified'       },
            { value:'accepted',            label:'✅ Notary Accepted'       },
            { value:'on_the_way',          label:'🚗 On The Way'           },
            { value:'arrived',             label:'📍 Arrived'              },
            { value:'completed',           label:'🏆 Completed by Notary'   },
            { value:'scanbacks_uploaded',  label:'📄 Scanbacks Uploaded'    },
            { value:'client_approved',     label:'🔎 QC Approved'           },
            { value:'closed',              label:'🔒 Closed'                },
            { value:'cancelled',           label:'❌ Cancelled'             },
            { value:'declined',            label:'🔄 Declined'              },
          ].map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              style={{ fontSize:12, padding:'6px 12px', borderRadius:8, border:'1.5px solid', cursor:'pointer', fontWeight: filter===f.value ? 700 : 400,
                background: filter===f.value ? '#0D1B3E' : '#fff',
                color: filter===f.value ? '#C9A84C' : '#7A7870',
                borderColor: filter===f.value ? '#0D1B3E' : '#E8E6DF' }}>
              {f.label}
            </button>
          ))}
        </div>
        <input placeholder="Search by borrower or job #..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex:1, minWidth:200 }} />
      </div>
      <div className="section-card" style={{ padding:0 }}>
        <table className="data-table">
          <thead><tr><th>Job #</th><th>Type</th><th>Borrower</th><th>Notary</th><th>Location</th><th>Date</th><th>Total Fee</th><th>Notary Pay</th><th>LCA Cut</th><th>Status</th><th>Checklist</th><th>Messages</th><th>Actions</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={13} style={{ textAlign:'center', padding:24 }}><div className="spinner" style={{ margin:'auto' }} /></td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={13} style={{ textAlign:'center', color:'#B8B5AB', padding:24 }}>No jobs found</td></tr>}
            {filtered.map(j => (
              <tr key={j.id}>
                <td style={{ fontWeight:500 }}>
                  <button onClick={() => navigate(`/admin/jobs/${j.id}`)} style={{ background:'none', border:'none', cursor:'pointer', color:'#C9A84C', fontWeight:700, fontSize:13, fontFamily:'inherit', padding:0, textDecoration:'underline' }}>{j.job_number}</button>
                </td>
                <td>{j.signing_type}</td>
                <td>
                  <div style={{ fontSize:12, fontWeight:500 }}>{j.borrower_name}</div>
                  {j.borrower_phone && <a href={`tel:${j.borrower_phone}`} style={{ fontSize:11, color:'#9A7A30', textDecoration:'none' }}>📞 {j.borrower_phone}</a>}
                </td>
                <td>
                  {j.notary ? (
                    <div>
                      <div style={{ fontWeight:500, fontSize:12 }}>{j.notary.full_name}</div>
                      <VendorBadge number={j.notary.notaries?.vendor_number} />
                    </div>
                  ) : <span style={{ color:'#B8B5AB', fontSize:12 }}>Unassigned</span>}
                </td>
                <td>{j.signing_city}, {j.signing_state}</td>
                <td>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</td>
                <td>${j.total_fee}</td>
                <td>{j.notary_pay ? `$${j.notary_pay}` : '—'}</td>
                <td style={{ color:'#9A7A30', fontWeight:500 }}>${j.lca_cut || 25}</td>
                <td>
                  <StatusBadge status={j.status} />
                  {j.job_type === 'individual' && (
                    <div style={{ marginTop:4 }}>
                      <span style={{ background:'#F0E8FF', color:'#6A3A9A', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>👤 Individual</span>
                    </div>
                  )}
                </td>
                <td style={{ minWidth:160 }}>
                  {j.checklist
                    ? <ChecklistSummaryAdmin checklist={j.checklist} />
                    : <span style={{ fontSize:11, color:'#B8B5AB' }}>Not started</span>}
                </td>
                <td style={{ minWidth:220 }}>
                  <JobMessages
                    jobId={j.id}
                    jobNumber={j.job_number}
                    role="admin"
                    recipientRole="notary"
                  />
                </td>
                <td style={{ minWidth:150 }}>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                  <button onClick={() => setInvoiceJob(j)} style={{ background:'#fff', color:'#9A7A30', border:'1px solid #C9A84C', borderRadius:6, padding:'5px 12px', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>🧾 Invoice</button>
                  {(j.status === 'new' || j.status === 'awaiting_assignment') && (
                    (j.job_type === 'individual' && !j.client_paid_at)
                      ? <span style={{ fontSize:11, color:'#E24B4A', fontWeight:700 }}>⚠️ Awaiting payment — can't assign</span>
                      : <button className="btn btn-primary btn-sm" onClick={() => setAssignJob(j)}>Assign</button>
                  )}
                  {j.status === 'assigned' && (
                    <>
                      <button className="btn btn-outline btn-sm" style={{ fontSize:10, color:'#7A7870' }} onClick={() => setAssignJob(j)}>Reassign</button>
                      <button className="btn btn-primary btn-sm" style={{ background:'#2D7A4F', borderColor:'#2D7A4F', fontWeight:700 }}
                        onClick={() => { if(window.confirm(`Mark ${j.job_number} as accepted? Use this if the notary confirmed by phone!!`)) updateStatus(j, 'accepted') }}>
                        ✓ Confirm Accepted
                      </button>
                    </>
                  )}
                  {j.status === 'accepted'          && <button className="btn btn-outline btn-sm" onClick={() => updateStatus(j, 'on_the_way')}>Mark On The Way</button>}
                  {j.status === 'on_the_way'        && <button className="btn btn-outline btn-sm" onClick={() => updateStatus(j, 'arrived')}>Mark Arrived</button>}
                  {j.status === 'arrived'           && <button className="btn btn-outline btn-sm" onClick={() => updateStatus(j, 'completed')}>Mark Completed</button>}
                  {j.status === 'completed' && (
                    (j.job_type === 'individual' || j.scan_backs_required === false)
                      ? <button className="btn btn-primary btn-sm" style={{ background:'#2D7A4F', borderColor:'#2D7A4F' }} onClick={() => closeOrder(j)}>✓ Complete &amp; Close</button>
                      : <button className="btn btn-outline btn-sm" onClick={() => updateStatus(j, 'scanbacks_uploaded')}>Scanbacks Uploaded</button>
                  )}
                  {j.status === 'scanbacks_uploaded'&& <button className="btn btn-primary btn-sm" style={{ background:'#2D7A4F', borderColor:'#2D7A4F' }} onClick={() => updateStatus(j, 'client_approved')}>✓ QC Approve</button>}
                  {j.status === 'client_approved' && (j.job_type === 'individual' || j.scan_backs_required === false) && <button className="btn btn-outline btn-sm" onClick={() => closeOrder(j)}>🔒 Close Order</button>}
                  {j.status === 'client_approved' && j.job_type !== 'individual' && j.scan_backs_required !== false && !j.invoice_sent_at && <button className="btn btn-primary btn-sm" style={{ background:'#6A3A9A', borderColor:'#6A3A9A' }} onClick={() => sendInvoice(j)}>📧 Send Invoice</button>}
                  {j.status === 'client_approved' && j.job_type !== 'individual' && j.scan_backs_required !== false &&  j.invoice_sent_at && <button className="btn btn-outline btn-sm" onClick={() => closeOrder(j)}>🔒 Close Order</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {invoiceJob && <InvoiceModal job={invoiceJob} onClose={() => setInvoiceJob(null)} />}
    </>
  )
}

function AssignNotaryPage() {
  const [jobs,      setJobs]      = useState([])
  const [loading,   setLoading]   = useState(true)
  const [assignJob, setAssignJob] = useState(null)

  useEffect(() => {
    loadJobs()
    // Real-time — all job changes
    const ch = supabase.channel('admin-jobs-realtime')
      .on('postgres_changes', { event:'*', schema:'public', table:'jobs' }, payload => {
        if (payload.eventType === 'INSERT') {
          setJobs(prev => [payload.new, ...prev])
          toast.success('🔔 New job received!!', { duration:5000 })
        } else if (payload.eventType === 'UPDATE') {
          setJobs(prev => prev.map(j => j.id === payload.new.id ? { ...j, ...payload.new } : j))
        } else if (payload.eventType === 'DELETE') {
          setJobs(prev => prev.filter(j => j.id !== payload.old.id))
        }
      })
      .subscribe()

    // ── 💬 REAL-TIME MESSAGE NOTIFICATIONS — Admin never misses a message!! ──
    const msgCh = supabase.channel('admin-messages-realtime')
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'job_messages' }, payload => {
        const msg = payload.new
        // Only notify for messages addressed TO admin (not from admin)
        if (msg.sender_role !== 'admin' && msg.recipient_role === 'admin') {
          const senderIcon = msg.sender_role === 'notary' ? '✍️'
                          : msg.sender_role === 'client' ? '🏢'
                          : msg.sender_role === 'individual' ? '👤' : '💬'
          toast.success(`${senderIcon} New message from ${msg.sender_name || msg.sender_role}!!`, { duration:6000 })
        }
      })
      .subscribe()

    return () => {
      supabase.removeChannel(ch)
      supabase.removeChannel(msgCh)
    }
  }, [])

  async function loadJobs() {
    const { data } = await supabase.from('jobs').select('*').eq('status','new').order('created_at', { ascending:false })
    setJobs(safeParseJobs(data))
    setLoading(false)
  }

  return (
    <>
      {assignJob && <AssignNotaryModal job={assignJob} onClose={() => setAssignJob(null)} onAssigned={loadJobs} />}
      <h1 className="page-title">Assign Notary</h1>
      <p className="page-sub">All unassigned jobs — assign a notary to each one</p>
      {!loading && jobs.length === 0 && (
        <div className="section-card" style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🎉</div>
          <div style={{ color:'#7A7870' }}>All jobs are assigned! Great work!</div>
        </div>
      )}
      <div className="section-card" style={{ padding:0 }}>
        <table className="data-table">
          <thead><tr><th>Job #</th><th>Service</th><th>Borrower</th><th>Location</th><th>Date</th><th>Fee</th><th>Action</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign:'center', padding:24 }}><div className="spinner" style={{ margin:'auto' }} /></td></tr>}
            {jobs.map(j => (
              <tr key={j.id}>
                <td style={{ fontWeight:500 }}>{j.job_number}</td>
                <td>{j.signing_type}</td>
                <td>
                  <div style={{ fontSize:12, fontWeight:500 }}>{j.borrower_name}</div>
                  {j.borrower_phone && <a href={`tel:${j.borrower_phone}`} style={{ fontSize:11, color:'#9A7A30', textDecoration:'none' }}>📞 {j.borrower_phone}</a>}
                </td>
                <td>{j.signing_city}, {j.signing_state}</td>
                <td>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : 'Flexible'}</td>
                <td style={{ color:'#2D7A4F', fontWeight:500 }}>${j.total_fee}</td>
                <td><button className="btn btn-primary btn-sm" onClick={() => setAssignJob(j)}>Assign Notary →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// TODAY'S SCHEDULE
// 📅 Month-grid calendar with the client-style navy borrower card (admin schedule)
function AdminScheduleCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState(null)
  const [calJobs,     setCalJobs]     = useState([])
  const year  = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const monthName = currentDate.toLocaleString('en-US', { month:'long', year:'numeric' })
  const today = new Date()

  useEffect(() => {
    const from = new Date(year, month, 1)
    const to   = new Date(year, month + 1, 1)
    supabase.from('jobs')
      .select('*, profiles!jobs_notary_id_fkey(full_name, phone)')
      .gte('scheduled_at', from.toISOString())
      .lt('scheduled_at',  to.toISOString())
      .order('scheduled_at', { ascending: true })
      .then(({ data }) => setCalJobs(safeParseJobs(data || [])))
  }, [year, month])

  const firstDay    = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const jobsByDay = {}
  calJobs.forEach(j => {
    if (!j.scheduled_at) return
    const d = new Date(j.scheduled_at)
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate()
      ;(jobsByDay[day] = jobsByDay[day] || []).push(j)
    }
  })

  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const selectedJobs = selectedDay ? (jobsByDay[selectedDay] || []) : []
  const gridStyle = { display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:6 }

  return (
    <div style={{ background:'#fff', border:'1px solid #ECEAE3', borderRadius:16, padding:'18px 18px 20px', marginBottom:24 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <button onClick={() => { setCurrentDate(new Date(year, month - 1, 1)); setSelectedDay(null) }} style={{ background:'none', border:'1px solid #E8E6DF', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:15, color:'#0D1B3E' }} aria-label="Previous month">←</button>
        <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:700, color:'#0D1B3E' }}>{monthName}</span>
        <button onClick={() => { setCurrentDate(new Date(year, month + 1, 1)); setSelectedDay(null) }} style={{ background:'none', border:'1px solid #E8E6DF', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:15, color:'#0D1B3E' }} aria-label="Next month">→</button>
      </div>
      <div style={{ ...gridStyle, marginBottom:6 }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} style={{ textAlign:'center', fontSize:10, fontWeight:700, color:'#B8B5AB', letterSpacing:'0.06em' }}>{d}</div>
        ))}
      </div>
      <div style={gridStyle}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />
          const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear()
          const dayJobs = jobsByDay[day] || []
          const has = dayJobs.length > 0
          const isSel = selectedDay === day
          return (
            <button key={day} onClick={() => has && setSelectedDay(isSel ? null : day)} disabled={!has}
              style={{
                minHeight:54, borderRadius:10, padding:'5px 4px', textAlign:'left', cursor: has ? 'pointer' : 'default',
                background: isSel ? '#0D1B3E' : has ? '#FFF8E8' : '#FAFAF8',
                border: isToday ? '2px solid #C9A84C' : '1px solid #EFEDE7',
                color: isSel ? '#fff' : '#0D1B3E'
              }}>
              <div style={{ fontWeight:isToday?700:500, fontSize:12 }}>{day}</div>
              {dayJobs.slice(0,2).map((j, idx) => (
                <div key={idx} title={j.borrower_name} style={{ fontSize:8, marginTop:2, padding:'1px 4px', borderRadius:4, background:'#C9A84C', color:'#0D1B3E', fontWeight:600, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
                  {j.borrower_name || 'Signing'}
                </div>
              ))}
              {dayJobs.length > 2 && <div style={{ fontSize:8, marginTop:1, color: isSel ? '#C9A84C' : '#B8B5AB' }}>+{dayJobs.length - 2} more</div>}
            </button>
          )
        })}
      </div>
      {selectedJobs.length > 0 && (
        <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:10 }}>
          {selectedJobs.map(j => (
            <div key={j.id} style={{ padding:14, background:'#0D1B3E', borderRadius:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:6 }}>
                <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:16, color:'#C9A84C', fontWeight:700 }}>{j.signing_type}</span>
                <span style={{ fontSize:10, fontWeight:700, padding:'3px 9px', borderRadius:20, background:'rgba(201,168,76,0.2)', color:'#C9A84C' }}>{statusLabel(j.status)}</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:12, color:'rgba(255,255,255,0.75)' }}>
                <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:9 }}>JOB #</span><br/><span style={{ color:'#C9A84C', fontWeight:600 }}>{j.job_number}</span></div>
                <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:9 }}>BORROWER</span><br/>{j.borrower_name || '—'}</div>
                <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:9 }}>WHEN</span><br/>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : 'TBD'}</div>
                <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:9 }}>WHERE</span><br/>{j.signing_city}, {j.signing_state}</div>
              </div>
              {j.profiles?.full_name && <div style={{ marginTop:8, fontSize:11, color:'#7FD4A3' }}>👤 {j.profiles.full_name}{j.profiles.phone ? ` · ${j.profiles.phone}` : ''}</div>}
              <a href={`/admin/jobs/${j.id}`} style={{ display:'inline-block', marginTop:12, background:'#C9A84C', color:'#0D1B3E', fontWeight:700, fontSize:12, padding:'8px 16px', borderRadius:8, textDecoration:'none' }}>View Details →</a>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TodaySchedule() {
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [view,    setView]    = useState('today') // today | tomorrow | week

  useEffect(() => { loadSchedule() }, [view])

  async function loadSchedule() {
    setLoading(true)
    const now    = new Date()
    const today  = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1)
    const dayAfter = new Date(today); dayAfter.setDate(dayAfter.getDate() + 2)
    const weekEnd  = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7)

    let from, to
    if (view === 'today')    { from = today; to = tomorrow }
    if (view === 'tomorrow') { from = tomorrow; to = dayAfter }
    if (view === 'week')     { from = today; to = weekEnd }

    const { data } = await supabase
      .from('jobs')
      .select('*, profiles!jobs_notary_id_fkey(full_name, phone)')
      .gte('scheduled_at', from.toISOString())
      .lt('scheduled_at',  to.toISOString())
      .in('status', ['new', 'awaiting_assignment', 'assigned', 'accepted', 'on_the_way', 'arrived', 'completed', 'scanbacks_uploaded', 'client_approved', 'closed'])
      .order('scheduled_at', { ascending: true })
    setJobs(safeParseJobs(data))
    setLoading(false)
  }

  const grouped = jobs.reduce((acc, j) => {
    const date = new Date(j.scheduled_at).toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })
    if (!acc[date]) acc[date] = []
    acc[date].push(j)
    return acc
  }, {})

  return (
    <>
      <h1 className="page-title">Signings Calendar</h1>
      <p className="page-sub">All scheduled orders — including unassigned and pending!!</p>

      <AdminScheduleCalendar />
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {['today','tomorrow','week'].map(v => (
          <button key={v} onClick={() => setView(v)} className={`btn ${view===v ? 'btn-primary' : 'btn-outline'} btn-sm`} style={{ textTransform:'capitalize' }}>
            {v === 'today' ? '📅 Today' : v === 'tomorrow' ? '📆 Tomorrow' : '🗓️ This Week'}
          </button>
        ))}
        <span style={{ marginLeft:'auto', fontSize:12, color:'#7A7870', alignSelf:'center' }}>{jobs.length} signing{jobs.length !== 1 ? 's' : ''}</span>
      </div>

      {loading && <div style={{ textAlign:'center', padding:48 }}><div className="spinner" style={{ margin:'auto' }} /></div>}

      {!loading && jobs.length === 0 && (
        <div style={{ background:'#F8F7F4', borderRadius:14, padding:40, textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📅</div>
          <div style={{ fontSize:16, fontWeight:500, color:'#0D1B3E', marginBottom:4 }}>No signings scheduled</div>
          <div style={{ fontSize:13, color:'#B8B5AB' }}>Enjoy the quiet — or go assign some jobs!!</div>
        </div>
      )}

      {Object.entries(grouped).map(([date, dayJobs]) => (
        <div key={date} style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#7A7870', letterSpacing:'0.08em', marginBottom:12, paddingBottom:8, borderBottom:'2px solid #C9A84C', display:'inline-block' }}>
            {date.toUpperCase()} — {dayJobs.length} SIGNING{dayJobs.length !== 1 ? 'S' : ''}
          </div>
          {dayJobs.map(j => (
            <div key={j.id} className="section-card" style={{ marginBottom:10, padding:0 }}>
              <div style={{ padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ background:'#0D1B3E', color:'#C9A84C', borderRadius:10, padding:'8px 14px', textAlign:'center', flexShrink:0 }}>
                    <div style={{ fontSize:16, fontWeight:700 }}>{new Date(j.scheduled_at).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:16, fontWeight:600, color:'#0D1B3E' }}>{j.borrower_name}</div>
                    <div style={{ fontSize:12, color:'#7A7870' }}>{j.signing_type} · {j.signing_city}, {j.signing_state}</div>
                    {j.signing_address && <div style={{ fontSize:11, color:'#B8B5AB' }}>📍 {j.signing_address}</div>}
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                  <span className={`badge badge-${j.status}`}>{statusLabel(j.status)}</span>
                  {j.profiles?.full_name && (
                    <div style={{ fontSize:11, color:'#2D7A4F', fontWeight:500 }}>
                      👤 {j.profiles.full_name}
                      {j.profiles.phone && <span> · <a href={`tel:${j.profiles.phone}`} style={{ color:'#9A7A30', textDecoration:'none' }}>{j.profiles.phone}</a></span>}
                    </div>
                  )}
                  <div style={{ fontSize:11, color:'#C9A84C', fontWeight:600 }}>${j.notary_pay} notary pay</div>
                </div>
              </div>
              {(j.order_notes || j.special_instructions) && (
                <div style={{ padding:'8px 18px', borderTop:'1px solid #F0EEE8', background:'#FFFBF0', fontSize:11, color:'#8A5C00' }}>
                  💬 {j.order_notes || j.special_instructions}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </>
  )
}

// FEE CALCULATOR
function FeeCalculator() {
  const [activeCat,      setActiveCat]      = useState('affidavits')
  const [selectedSvc,    setSelectedSvc]    = useState(null)
  const [clientFee,      setClientFee]      = useState(0)
  const [notaryPay,      setNotaryPay]      = useState(0)
  const [overrideMode,   setOverrideMode]   = useState(false)
  const [overrideReason, setOverrideReason] = useState('')
  const [addons,         setAddons]         = useState([])
  const [isVet,          setIsVet]          = useState(false)

  const SERVICES = {
    affidavits: { label:'Affidavits', services: [
      { name:'Affidavit (General)',           client:35,  notary:20  },
      { name:'Acknowledgement',               client:35,  notary:20  },
      { name:'Jurat / Oath',                  client:35,  notary:20  },
      { name:'Name Change Affidavit',         client:35,  notary:20  },
      { name:'Residency Affidavit',           client:45,  notary:25  },
      { name:'Identity Affidavit',            client:45,  notary:25  },
      { name:'Lost Documents Affidavit',      client:55,  notary:30  },
      { name:'Financial Affidavit',           client:75,  notary:49  },
      { name:'Heirship Affidavit',            client:175, notary:114 },
      { name:'Estate Affidavit',              client:175, notary:114 },
      { name:'Certified Identity Statement',  client:35,  notary:20  },
      { name:'Certified Residency Statement', client:35,  notary:20  },
    ]},
    family: { label:'Family', services: [
      { name:'Parental Consent Form',              client:35,  notary:20  },
      { name:'Tattoo Parental Consent (Minor)',     client:35,  notary:20  },
      { name:'Travel Consent Letter for Minors',   client:100, notary:65  },
      { name:'Adoption Documents',                 client:175, notary:114 },
      { name:'Guardianship / Custodian Affidavit', client:150, notary:98  },
      { name:'School Documents',                   client:75,  notary:49  },
      { name:'Proof of Life / Life Certificate',   client:75,  notary:49  },
    ]},
    legal: { label:'Legal', services: [
      { name:'Power of Attorney',                      client:150, notary:98 },
      { name:'Advance Health Directive / Living Will', client:150, notary:98 },
      { name:'Living Will',                            client:150, notary:98 },
      { name:'Court Documents',                        client:125, notary:81 },
      { name:'Hold Harmless Agreement',                client:100, notary:65 },
      { name:'Consent & Release Form',                 client:75,  notary:49 },
      { name:'Private Party Agreement',                client:100, notary:65 },
    ]},
    medical: { label:'Medical', services: [
      { name:'Medical Authorization Form',      client:75,  notary:49  },
      { name:'Insurance Claims Form',           client:75,  notary:49  },
      { name:'Hospital / Nursing Home Signing', client:175, notary:123 },
    ]},
    loans: { label:'Loan Signings', services: [
      { name:'Refinance Package',        client:175, notary:123 },
      { name:'Buyer Closing Package',    client:225, notary:169 },
      { name:'Seller Closing Package',   client:150, notary:105 },
      { name:'HELOC Package',            client:150, notary:105 },
      { name:'Reverse Mortgage (Final)', client:275, notary:215 },
      { name:'Loan Modification',        client:125, notary:88  },
      { name:'Scanbacks',                client:35,  notary:20  },
    ]},
    financial: { label:'Financial', services: [
      { name:'Banking Forms',                     client:75,  notary:49  },
      { name:'Pension / Retirement Forms',        client:125, notary:81  },
      { name:'Promissory Note (Non-Real Estate)', client:125, notary:81  },
      { name:'Bank Safe Deposit Box Inventory',   client:250, notary:163 },
    ]},
    dealership: { label:'Auto', services: [
      { name:'Vehicle Purchase Contract',     client:100, notary:55 },
      { name:'Title Transfer',                client:100, notary:55 },
      { name:'Lease Agreement (Auto)',        client:100, notary:55 },
      { name:'Finance Documents Package',     client:150, notary:83 },
      { name:'Power of Attorney (Vehicle)',   client:125, notary:69 },
      { name:'Odometer Disclosure Statement', client:75,  notary:41 },
      { name:'Bill of Sale (Vehicle)',        client:75,  notary:41 },
      { name:'Dealer Reassignment Form',      client:100, notary:55 },
      { name:'Out-of-State Title Transfer',   client:75,  notary:41 },
      { name:'Fleet / Commercial Packet',     client:125, notary:69 },
      { name:'Dealership Bulk Visit',         client:100, notary:55 },
    ]},
    international: { label:'International', services: [
      { name:'Apostille',                        client:200, notary:130 },
      { name:'International Document Statement', client:150, notary:98  },
      { name:'Certified Copies',                 client:100, notary:65  },
    ]},
    signature: { label:'Signature', services: [
      { name:'Signature Witnessing',              client:75,  notary:49  },
      { name:'Prenuptial / Postnuptial Agreement',client:250, notary:163 },
    ]},
    wedding: { label:'Wedding', services: [
      { name:'Wedding Ceremony Officiation', client:200, notary:120 },
    ]},
  }

  const ADDONS_LIST = [
    { key:'after_hours',  label:'After hours / Weekend', client:35, notary:20 },
    { key:'rush',         label:'Same-day rush',          client:50, notary:30 },
    { key:'extra_signer', label:'Additional signer',      client:25, notary:15 },
    { key:'witness',      label:'Witness provided by LCA',client:35, notary:20 },
  ]

  function selectService(svc) { setSelectedSvc(svc); setClientFee(svc.client); setNotaryPay(svc.notary); setAddons([]) }
  function toggleAddon(key) { setAddons(a => a.includes(key) ? a.filter(x => x !== key) : [...a, key]) }

  const addonClientTotal = ADDONS_LIST.filter(a => addons.includes(a.key)).reduce((s,a) => s + a.client, 0)
  const addonNotaryTotal = ADDONS_LIST.filter(a => addons.includes(a.key)).reduce((s,a) => s + a.notary, 0)
  const baseClient       = overrideMode ? clientFee : (selectedSvc?.client || 0)
  const baseNotary       = overrideMode ? notaryPay : (selectedSvc?.notary || 0)
  const totalClient      = isVet ? Math.round((baseClient + addonClientTotal) * 0.9) : (baseClient + addonClientTotal)
  const totalNotary      = isVet ? Math.round((baseNotary + addonNotaryTotal) * 0.9) : (baseNotary + addonNotaryTotal)
  const totalLCA         = totalClient - totalNotary
  const margin           = totalClient > 0 ? Math.round((totalLCA / totalClient) * 100) : 0
  const currentCat       = SERVICES[activeCat]

  const CALC_STYLES = `
    .calc-cat-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:16px; }
    .calc-cat-tab { padding:4px 12px; border-radius:20px; font-size:11px; font-weight:500; cursor:pointer; border:1px solid #E8E6DF; background:#F8F7F4; color:#7A7870; transition:all 0.15s; }
    .calc-cat-tab.active { background:#0D1B3E; color:#C9A84C; border-color:#0D1B3E; }
    .calc-svc-list { max-height:220px; overflow-y:auto; border:1px solid #E8E6DF; border-radius:10px; margin-bottom:16px; }
    .calc-svc-item { display:flex; justify-content:space-between; align-items:center; padding:9px 14px; border-bottom:1px solid #F0EEE8; cursor:pointer; transition:background 0.12s; gap:10px; }
    .calc-svc-item:last-child { border-bottom:none; }
    .calc-svc-item:hover { background:#FDF6E3; }
    .calc-svc-item.selected { background:#FDF6E3; border-left:3px solid #C9A84C; }
    .calc-svc-name { font-size:12px; color:#0D1B3E; }
    .calc-svc-prices { display:flex; gap:12px; align-items:center; flex-shrink:0; font-size:11px; }
    .calc-client-price { color:#0D1B3E; font-weight:500; }
    .calc-notary-price { color:#2D7A4F; }
    .calc-summary { background:#0D1B3E; border-radius:14px; padding:20px; }
    .calc-sum-row { display:flex; justify-content:space-between; font-size:13px; color:rgba(255,255,255,0.7); padding:5px 0; }
    .calc-sum-divider { height:1px; background:rgba(201,168,76,0.3); margin:8px 0; }
    .calc-sum-total { display:flex; justify-content:space-between; font-size:16px; font-weight:500; color:#C9A84C; padding:5px 0; }
    .calc-margin-bar { height:6px; border-radius:3px; background:rgba(255,255,255,0.1); margin-top:12px; }
    .calc-margin-fill { height:6px; border-radius:3px; transition:width 0.3s; }
    .calc-addon-row { display:flex; align-items:center; justify-content:space-between; padding:8px 0; border-bottom:1px solid #F0EEE8; }
    .calc-addon-row:last-child { border-bottom:none; }
  `

  return (
    <>
      <style>{CALC_STYLES}</style>
      <h1 className="page-title">Fee Calculator</h1>
      <p className="page-sub">Select any service to see client price, notary pay and LCA margin instantly!!</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:20, alignItems:'start' }}>
        <div>
          <div style={{ background:'#0D1B3E', borderRadius:12, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
            <button type="button" onClick={() => setIsVet(v => !v)} style={{ width:36, height:20, borderRadius:10, border:'none', cursor:'pointer', position:'relative', background:isVet?'#C9A84C':'rgba(255,255,255,0.2)', flexShrink:0, transition:'background 0.2s' }}>
              <div style={{ width:16, height:16, borderRadius:'50%', background:'#fff', position:'absolute', top:2, left:isVet?18:2, transition:'left 0.2s' }} />
            </button>
            <div style={{ fontSize:12, color:isVet?'#C9A84C':'rgba(255,255,255,0.6)' }}>
              {isVet ? 'Veteran discount active — 10% off!!' : 'Toggle for veteran discount (10% off)'}
            </div>
          </div>
          <div className="calc-cat-tabs">
            {Object.entries(SERVICES).map(([id, cat]) => (
              <button key={id} className={`calc-cat-tab${activeCat===id?' active':''}`} onClick={() => setActiveCat(id)} type="button">{cat.label}</button>
            ))}
          </div>
          <div style={{ fontSize:11, color:'#7A7870', marginBottom:6, display:'flex', justifyContent:'space-between' }}>
            <span>Service</span>
            <span style={{ display:'flex', gap:40, paddingRight:4 }}><span>Client pays</span><span style={{ color:'#2D7A4F' }}>Notary gets</span></span>
          </div>
          <div className="calc-svc-list">
            {currentCat.services.map((svc, i) => (
              <div key={i} className={`calc-svc-item${selectedSvc?.name===svc.name?' selected':''}`} onClick={() => selectService(svc)}>
                <span className="calc-svc-name">{svc.name}</span>
                <div className="calc-svc-prices">
                  <span className="calc-client-price">${isVet ? Math.round(svc.client*0.9) : svc.client}</span>
                  <span className="calc-notary-price">${isVet ? Math.round(svc.notary*0.9) : svc.notary}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize:11, fontWeight:600, color:'#7A7870', letterSpacing:'0.06em', marginBottom:10 }}>ADD-ONS</div>
          <div style={{ border:'1px solid #E8E6DF', borderRadius:10, padding:'4px 14px', marginBottom:16 }}>
            {ADDONS_LIST.map(a => (
              <div key={a.key} className="calc-addon-row">
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color:'#0D1B3E', flex:1 }}>
                  <input type="checkbox" style={{ width:'auto' }} checked={addons.includes(a.key)} onChange={() => toggleAddon(a.key)} />
                  {a.label}
                </label>
                <div style={{ display:'flex', gap:20, fontSize:11 }}>
                  <span style={{ color:'#0D1B3E', fontWeight:500 }}>+${isVet ? Math.round(a.client*0.9) : a.client}</span>
                  <span style={{ color:'#2D7A4F' }}>+${isVet ? Math.round(a.notary*0.9) : a.notary}</span>
                </div>
              </div>
            ))}
          </div>
          {selectedSvc && (
            <div style={{ border:'1px solid #E8E6DF', borderRadius:10, padding:14, marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:overrideMode?12:0 }}>
                <span style={{ fontSize:12, fontWeight:500, color:'#0D1B3E' }}>Manual Override</span>
                <button className="btn btn-sm btn-outline" onClick={() => setOverrideMode(o => !o)} style={{ fontSize:11, color:overrideMode?'#E24B4A':'#7A7870', borderColor:overrideMode?'#E24B4A':'#E8E6DF' }}>
                  {overrideMode ? 'Cancel Override' : 'Override Prices'}
                </button>
              </div>
              {overrideMode && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:10 }}>
                  <div className="form-group"><label className="form-label">Client fee ($)</label><input type="number" value={clientFee} onChange={e => setClientFee(Number(e.target.value))} style={{ borderColor:'#E24B4A' }} /></div>
                  <div className="form-group"><label className="form-label">Notary pay ($)</label><input type="number" value={notaryPay} onChange={e => setNotaryPay(Number(e.target.value))} style={{ borderColor:'#E24B4A' }} /></div>
                  <div className="form-group" style={{ gridColumn:'1/-1' }}><label className="form-label">Reason for override</label><input placeholder="e.g. Bulk deal" value={overrideReason} onChange={e => setOverrideReason(e.target.value)} /></div>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ position:'sticky', top:20 }}>
          <div className="calc-summary">
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', letterSpacing:'0.08em', marginBottom:14 }}>
              {selectedSvc ? selectedSvc.name.toUpperCase() : 'SELECT A SERVICE'}
            </div>
            {!selectedSvc ? (
              <div style={{ textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:13, padding:'20px 0' }}>Click any service to see the breakdown!!</div>
            ) : (
              <>
                <div className="calc-sum-row"><span>Client pays</span><span>${baseClient + addonClientTotal}{isVet ? ' → $' + totalClient + ' (vet)' : ''}</span></div>
                {addons.map(key => { const a = ADDONS_LIST.find(x => x.key === key); return a ? (<div key={key} className="calc-sum-row" style={{ fontSize:11, color:'rgba(255,255,255,0.5)' }}><span>  + {a.label}</span><span>+${isVet ? Math.round(a.client*0.9) : a.client}</span></div>) : null })}
                <div className="calc-sum-divider" />
                <div className="calc-sum-row"><span>Notary receives</span><span style={{ color:'#2D7A4F' }}>${totalNotary}</span></div>
                <div className="calc-sum-divider" />
                <div className="calc-sum-total"><span>LCA profit</span><span>${totalLCA}</span></div>
                <div style={{ marginTop:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:6 }}>
                    <span>Margin</span>
                    <span style={{ color: margin >= 30 ? '#2D7A4F' : margin >= 20 ? '#C9A84C' : '#E24B4A' }}>{margin}%</span>
                  </div>
                  <div className="calc-margin-bar">
                    <div className="calc-margin-fill" style={{ width:`${Math.min(margin,100)}%`, background: margin >= 30 ? '#2D7A4F' : margin >= 20 ? '#C9A84C' : '#E24B4A' }} />
                  </div>
                  <div style={{ fontSize:10, color:'rgba(255,255,255,0.3)', marginTop:6 }}>
                    {margin >= 40 ? 'Excellent margin!!' : margin >= 30 ? 'Good margin!!' : margin >= 20 ? 'Acceptable margin' : 'Low margin — consider adjusting'}
                  </div>
                </div>
                {overrideMode && overrideReason && <div style={{ marginTop:12, padding:'8px 10px', background:'rgba(226,75,74,0.1)', borderRadius:8, fontSize:11, color:'rgba(226,75,74,0.8)' }}>Override reason: {overrideReason}</div>}
                {isVet && <div style={{ marginTop:10, padding:'6px 10px', background:'rgba(201,168,76,0.1)', borderRadius:8, fontSize:11, color:'#C9A84C' }}>Veteran 10% discount applied!!</div>}
              </>
            )}
          </div>
          <div style={{ background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:12, padding:14, marginTop:14 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#0D1B3E', marginBottom:10 }}>LCA Margin Strategy</div>
            {[
              { label:'Standard signings',       target:'30–40%', color:'#2D7A4F' },
              { label:'Reverse mortgage/complex', target:'20–30%', color:'#C9A84C' },
              { label:'Dealerships & RON',        target:'40–50%', color:'#0D1B3E' },
            ].map(s => (
              <div key={s.label} style={{ display:'flex', justifyContent:'space-between', fontSize:11, padding:'4px 0', borderBottom:'1px solid #F0EEE8' }}>
                <span style={{ color:'#7A7870' }}>{s.label}</span>
                <span style={{ fontWeight:600, color:s.color }}>{s.target}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

// NOTARIES
function Notaries() {
  const { profile } = useAuth()
  const [notaries,     setNotaries]     = useState([])
  const [loading,      setLoading]      = useState(true)
  const [vendorInputs, setVendorInputs] = useState({})
  const [saving,       setSaving]       = useState(null)
  const [expanded,     setExpanded]     = useState(null)
  const [credentials,  setCredentials]  = useState({})
  const [rejectModal,  setRejectModal]  = useState(null) // { credId, notaryId }
  const [filter,       setFilter]       = useState('all') // ✅ NEW: 'all' | 'pending_creds' | 'pending_approval'
  const [adminNotes,   setAdminNotes]   = useState({}) // ✅ NEW: in-memory admin notes by notary id
  const [savingNotes,  setSavingNotes]  = useState(null) // ✅ NEW: track which notary's notes are saving
  const [birthdays,    setBirthdays]    = useState({}) // 🎂 NEW: in-memory birthdays by notary id (MM-DD format)
  const [savingBday,   setSavingBday]   = useState(null) // 🎂 NEW: track birthday saves
  const [searchQuery,  setSearchQuery]  = useState('') // 🔍 NEW: search across notary directory

  // ✅ Auto-detect ?filter=pending_creds from URL on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const f = params.get('filter')
    if (f === 'pending_creds' || f === 'pending_approval') {
      setFilter(f)
    }
  }, [])

  useEffect(() => { loadNotaries() }, [])

  // 🔧 #7: When you arrive via "Verify Credentials" (filter=pending_creds), the matching
  // rows auto-open — so preload their uploaded documents too. Otherwise the credential
  // list looks empty until you click the little triangle (which is what triggered the load).
  useEffect(() => {
    if (filter !== 'pending_creds') return
    notaries.forEach(n => {
      const hasPending = (n.notary_docs || []).some(d => d.status === 'pending')
      if (hasPending && !credentials[n.id]) loadCredentials(n.id)
    })
  }, [filter, notaries])

  async function loadNotaries() {
    const { data } = await supabase.from('profiles').select('*, notaries(*), notary_docs(*)').eq('role','notary').order('created_at', { ascending:false })

    // 🔢 Sort by vendor_number DESCENDING (newest 0021 at top → oldest 0001 at bottom).
    // Notaries without a vendor_number yet (pending approval) go to the bottom,
    // and within "no vendor" group we keep newest-first by created_at.
    const sorted = (data || []).slice().sort((a, b) => {
      const av = a?.notaries?.vendor_number
      const bv = b?.notaries?.vendor_number
      // Both have vendor numbers → numeric DESC by the trailing number
      if (av && bv) {
        // vendor numbers look like "LCA-2026-0021" — extract trailing digits for proper numeric sort
        const an = parseInt(String(av).replace(/\D/g, ''), 10) || 0
        const bn = parseInt(String(bv).replace(/\D/g, ''), 10) || 0
        return bn - an
      }
      // Only one has a vendor number → that one comes FIRST
      if (av && !bv) return -1
      if (!av && bv) return 1
      // Neither has one → keep newest-first
      const at = new Date(a.created_at || 0).getTime()
      const bt = new Date(b.created_at || 0).getTime()
      return bt - at
    })

    setNotaries(sorted)
    const inputs = {}
    const notes  = {}
    const bdays  = {}
    ;(sorted || []).forEach(n => {
      if (n.notaries?.vendor_number) inputs[n.id] = n.notaries.vendor_number
      if (n.notaries?.admin_notes)   notes[n.id]  = n.notaries.admin_notes
      if (n.notaries?.birthday)      bdays[n.id]  = n.notaries.birthday
    })
    setVendorInputs(inputs)
    setAdminNotes(notes) // ✅ Load existing admin notes
    setBirthdays(bdays)  // 🎂 Load existing birthdays
    setLoading(false)
  }

  // ✅ Save admin notes (debounced via blur)
  async function saveAdminNote(notaryId) {
    setSavingNotes(notaryId)
    try {
      await supabase.from('notaries').update({ admin_notes: adminNotes[notaryId] || null }).eq('id', notaryId)
      toast.success('Admin notes saved!! 🔒', { duration: 2000 })
    } catch (err) {
      toast.error('Error saving notes: ' + err.message)
    } finally {
      setSavingNotes(null)
    }
  }

  // 🎂 Save birthday (MM-DD format only — privacy first!!)
  async function saveBirthday(notaryId) {
    const bday = birthdays[notaryId] || null
    // Validate format if not empty
    if (bday && !/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(bday)) {
      toast.error('Birthday must be MM-DD format (e.g. 05-15)')
      return
    }
    setSavingBday(notaryId)
    try {
      await supabase.from('notaries').update({ birthday: bday }).eq('id', notaryId)
      toast.success('Birthday saved!! 🎂', { duration: 2000 })
    } catch (err) {
      toast.error('Error saving birthday: ' + err.message)
    } finally {
      setSavingBday(null)
    }
  }

  async function generate1099(profile, notary) {
    // 🛡️ TAX COMPLIANCE GATE — Cannot generate 1099 without an approved W-9!!
    const { data: w9docs } = await supabase
      .from('notary_docs')
      .select('id, status')
      .eq('notary_id', profile.id)
      .eq('doc_type', 'w9_form')

    const hasApprovedW9 = (w9docs || []).some(d => d.status === 'approved' || d.status === 'verified')

    if (!hasApprovedW9) {
      toast.error(
        '🛡️ Cannot generate 1099-NEC — ' + (profile.full_name || 'This notary') + ' has not uploaded an approved W-9!!\n\n' +
        'Tax compliance requires an approved W-9 on file before issuing 1099-NEC forms.',
        { duration: 8000 }
      )
      return
    }

    const year = new Date().getFullYear() - 1
    // Get total earnings for the tax year
    const { data: jobs } = await supabase
      .from('jobs')
      .select('notary_pay, created_at')
      .eq('notary_id', profile.id)
      .in('status', ['completed', 'scanbacks_uploaded', 'client_approved', 'closed'])
      .gte('created_at', `${year}-01-01`)
      .lte('created_at', `${year}-12-31`)

    const totalPay = (jobs || []).reduce((sum, j) => sum + (j.notary_pay || 0), 0)

    if (totalPay < 600) {
      toast.error(`Total pay is $${totalPay} — 1099-NEC only required for $600 or more!!`)
      return
    }

    const lcaEin   = process.env.REACT_APP_LCA_EIN || 'XX-XXXXXXX'
    const lcaName  = 'Loan Closing Agent LLC'
    const lcaAddr  = 'Jacksonville, FL'

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>1099-NEC ${year} — ${profile.full_name}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
          body { padding: 40px; color: #000; }
          .form-title { text-align: center; font-size: 22px; font-weight: bold; margin-bottom: 4px; }
          .form-subtitle { text-align: center; font-size: 13px; margin-bottom: 24px; color: #333; }
          .box { border: 1px solid #000; padding: 10px 14px; margin-bottom: 8px; }
          .box-label { font-size: 9px; text-transform: uppercase; color: #555; margin-bottom: 4px; }
          .box-value { font-size: 14px; font-weight: bold; }
          .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
          .amount { font-size: 22px; font-weight: bold; color: #000; }
          .header-bar { background: #0D1B3E; color: #C9A84C; padding: 12px 16px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; }
          .lca-logo { font-size: 18px; font-weight: bold; }
          .tax-year { font-size: 14px; }
          .divider { border-top: 2px solid #000; margin: 16px 0; }
          .note { font-size: 11px; color: #555; margin-top: 16px; padding: 10px; border: 1px dashed #ccc; }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header-bar">
          <div class="lca-logo">Loan Closing Agent LLC</div>
          <div class="tax-year">Tax Year ${year}</div>
        </div>

        <div class="form-title">Form 1099-NEC</div>
        <div class="form-subtitle">Nonemployee Compensation — Tax Year ${year}</div>

        <div class="divider"></div>

        <div class="grid-2" style="margin-bottom:8px">
          <div class="box">
            <div class="box-label">Payer's Name</div>
            <div class="box-value">${lcaName}</div>
          </div>
          <div class="box">
            <div class="box-label">Payer's EIN</div>
            <div class="box-value">${lcaEin}</div>
          </div>
        </div>

        <div class="box" style="margin-bottom:8px">
          <div class="box-label">Payer's Address</div>
          <div class="box-value">${lcaAddr}</div>
        </div>

        <div class="divider"></div>

        <div class="grid-2" style="margin-bottom:8px">
          <div class="box">
            <div class="box-label">Recipient's Name</div>
            <div class="box-value">${profile.full_name}</div>
          </div>
          <div class="box">
            <div class="box-label">Vendor Number</div>
            <div class="box-value">${notary?.vendor_number || '—'}</div>
          </div>
        </div>

        <div class="box" style="margin-bottom:8px">
          <div class="box-label">Recipient's Address</div>
          <div class="box-value">${[profile.address, profile.city, profile.state, profile.zip].filter(Boolean).join(', ') || 'On file'}</div>
        </div>

        <div class="box" style="margin-bottom:8px">
          <div class="box-label">Recipient's TIN (SSN/EIN) — See W-9 on file</div>
          <div class="box-value">See W-9 on file</div>
        </div>

        <div class="divider"></div>

        <div class="box" style="margin-bottom:8px; background:#FFFBF0;">
          <div class="box-label">Box 1 — Nonemployee Compensation</div>
          <div class="amount">$${totalPay.toFixed(2)}</div>
        </div>

        <div class="grid-3" style="margin-bottom:8px">
          <div class="box">
            <div class="box-label">Box 4 — Federal Tax Withheld</div>
            <div class="box-value">$0.00</div>
          </div>
          <div class="box">
            <div class="box-label">Total Signings ${year}</div>
            <div class="box-value">${(jobs || []).length} signings</div>
          </div>
          <div class="box">
            <div class="box-label">Form Generated</div>
            <div class="box-value">${new Date().toLocaleString()}</div>
          </div>
        </div>

        <div class="note">
          ⚠️ IMPORTANT: This is a summary for reference. The recipient's TIN must be verified against the W-9 on file before filing with the IRS. Please review all information for accuracy before submitting. LCA is not responsible for tax filing errors.
        </div>

        <div class="no-print" style="margin-top:24px; text-align:center;">
          <button onclick="window.print()" style="background:#0D1B3E; color:#C9A84C; border:none; padding:12px 32px; font-size:14px; font-weight:bold; border-radius:8px; cursor:pointer;">
            🖨️ Print / Save as PDF
          </button>
        </div>
      </body>
      </html>
    `

    const win = window.open('', '_blank')
    win.document.write(html)
    win.document.close()
    toast.success(`1099-NEC generated for ${profile.full_name} — $${totalPay.toFixed(2)}!!`)
  }

  async function loadCredentials(notaryId) {
    const { data } = await supabaseAdmin.from('notary_docs').select('*').eq('notary_id', notaryId)
    setCredentials(prev => ({ ...prev, [notaryId]: data || [] }))
  }

  function toggleExpand(id) {
    if (expanded === id) { setExpanded(null); return }
    setExpanded(id)
    loadCredentials(id)
  }

  function generateVendorNumber(existingNotaries) {
    const year     = new Date().getFullYear()
    const existing = existingNotaries.map(n => n.notaries?.vendor_number).filter(Boolean).map(v => parseInt(v?.split('-')[2] || '0')).filter(n => !isNaN(n))
    const next     = existing.length > 0 ? Math.max(...existing) + 1 : 1
    return `LCA-${year}-${String(next).padStart(4,'0')}`
  }

  async function approveNotary(notary) {
    setSaving(notary.id + '-approve')
    try {
      const vendorNumber = vendorInputs[notary.id] || generateVendorNumber(notaries)
      await supabase.from('profiles').update({ is_approved:true }).eq('id', notary.id)
      await supabase.from('notaries').update({ vendor_number:vendorNumber }).eq('id', notary.id)
      // ✅ Send beautiful welcome email with vendor number
      if (notary.email) {
        await sendWelcomeEmail({
          notary_name:   notary.full_name,
          notary_email:  notary.email,
          vendor_number: vendorNumber,
        })
      }
      toast.success(`${notary.full_name} approved! Vendor # ${vendorNumber} emailed!`)
      loadNotaries()
    } catch (err) {
      toast.error('Error: ' + err.message)
    } finally {
      setSaving(null)
    }
  }

  async function saveVendorNumber(notary) {
    const vendorNumber = vendorInputs[notary.id]
    if (!vendorNumber) return toast.error('Please enter a vendor number first')
    setSaving(notary.id + '-vendor')
    await supabase.from('notaries').update({ vendor_number:vendorNumber }).eq('id', notary.id)
    toast.success(`Vendor # ${vendorNumber} saved!`)
    setSaving(null)
  }

  async function grantVettedBadge(notary) {
    setSaving(notary.id + '-vet')
    try {
      await supabase.from('notaries').update({ is_vetted:true, vetted_at:new Date().toISOString(), vetted_by:profile?.full_name || 'Admin' }).eq('id', notary.id)
      toast.success(`LCA Vetted Badge granted to ${notary.full_name}!`)
      loadNotaries()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(null)
    }
  }

  async function grantRonBadge(notary) {
    setSaving(notary.id + '-ron')
    try {
      const isRon = !notary.is_ron
      await supabase.from('notaries').update({
        is_ron:         isRon,
        ron_granted_at: isRon ? new Date().toISOString() : null,
        ron_granted_by: isRon ? (profile?.full_name || 'Admin') : null,
      }).eq('id', notary.id)
      toast.success(isRon ? `💻 RON Notary badge granted to ${notary.full_name}!!` : 'RON badge removed')
      loadNotaries()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(null)
    }
  }

  async function grantPreferredBadge(notary) {
    setSaving(notary.id + '-preferred')
    try {
      const isPreferred = !notary.is_preferred
      await supabase.from('notaries').update({
        is_preferred:         isPreferred,
        preferred_granted_at: isPreferred ? new Date().toISOString() : null,
        preferred_granted_by: isPreferred ? (profile?.full_name || 'Admin') : null,
      }).eq('id', notary.id)
      toast.success(isPreferred ? `⭐ Preferred badge granted to ${notary.full_name}!!` : 'Preferred badge removed')
      loadNotaries()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(null)
    }
  }

  async function grantApostilleBadge(notary) {
    setSaving(notary.id + '-apostille')
    try {
      const isApostille = !notary.is_apostille
      await supabase.from('notaries').update({
        is_apostille:         isApostille,
        apostille_granted_at: isApostille ? new Date().toISOString() : null,
        apostille_granted_by: isApostille ? (profile?.full_name || 'Admin') : null,
      }).eq('id', notary.id)
      toast.success(isApostille ? `🌍 Apostille badge granted to ${notary.full_name}!!` : 'Apostille badge removed')
      loadNotaries()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(null)
    }
  }

  async function verifyCredential(credId, notaryId, docType) {
    setSaving(credId)
    try {
      const now = new Date().toISOString()
      await supabaseAdmin.from('notary_docs').update({ status:'approved', verified_at:now, verified_by:profile?.full_name || 'Admin' }).eq('id', credId)
      const colMap = { commission:'commission_verified_at', eo_insurance:'eo_verified_at', background_check:'background_verified_at' }
      if (colMap[docType]) {
        await supabase.from('notaries').update({ [colMap[docType]]:now }).eq('id', notaryId)
      }
      const { data: creds } = await supabaseAdmin.from('notary_docs').select('*').eq('notary_id', notaryId)
      const { data: notaryRecord } = await supabase.from('notaries').select('vendor_number, is_vetted').eq('id', notaryId).single()
      // ✅ ALL required credentials must be approved before sending verified email
      const requiredTypes = ['government_id', 'commission', 'eo_insurance', 'w9_form', 'background_check']
      const allVerified = requiredTypes.every(type =>
        creds?.some(c => c.doc_type === type && c.status === 'approved')
      )
      if (allVerified) {
        const alreadyVetted = notaryRecord?.is_vetted
        await supabase.from('notaries').update({ is_vetted:true, vetted_at:now, vetted_by:'Auto — All credentials verified' }).eq('id', notaryId)
        const { data: notaryProfile } = await supabase.from('profiles').select('full_name, email').eq('id', notaryId).single()
        // ✅ Only send email if NOT already vetted — prevents duplicate emails!!
        if (notaryProfile && !alreadyVetted) {
          await sendCredentialsVerifiedEmail({
            notary_name:   notaryProfile.full_name,
            notary_email:  notaryProfile.email,
            vendor_number: notaryRecord?.vendor_number || '—',
          })
          toast.success('All credentials verified — LCA Vetted Badge auto-granted — notary notified!!')
        } else {
          toast.success('Credential verified!! All credentials complete!!')
        }
      } else {
        const remaining = requiredTypes.filter(type => !creds?.some(c => c.doc_type === type && c.status === 'approved'))
        toast.success(`Credential verified!! Still pending: ${remaining.join(', ')}`)
      }
      loadCredentials(notaryId)
      loadNotaries()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(null)
    }
  }

  async function rejectCredential(credId, notaryId, reason) {
    setSaving(credId)
    try {
      await supabaseAdmin.from('notary_docs').update({
        status:        'rejected',
        rejected_at:   new Date().toISOString(),
        rejected_by:   profile?.full_name || 'Admin',
        reject_reason: reason,
      }).eq('id', credId)
      const notaryProfile = notaries.find(n => n.id === notaryId)
      if (notaryProfile?.email) {
        await sendCredentialRejected({
          notary_name:   notaryProfile.full_name,
          notary_email:  notaryProfile.email,
          doc_label:     credentials[notaryId]?.find(c => c.id === credId)?.doc_label || 'Document',
          reject_reason: reason,
        })
      }
      toast.success('Credential rejected — notary has been notified by email!')
      // ✅ Update local state immediately so UI reflects rejection without refetch
      setCredentials(prev => ({
        ...prev,
        [notaryId]: (prev[notaryId] || []).map(c =>
          c.id === credId ? { ...c, status:'rejected', reject_reason:reason } : c
        )
      }))
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(null)
    }
  }

  const VETTING_STYLES = `
    .vet-section { padding:20px 24px; border-top:1px solid #E8E6DF; background:#FAFAF8; }
    .vet-section-title { font-size:11px; font-weight:600; color:#7A7870; letter-spacing:0.08em; margin-bottom:14px; }
    .vet-badge { display:inline-flex; align-items:center; gap:5px; background:#0D1B3E; color:#C9A84C; font-size:10px; font-weight:600; padding:3px 10px; border-radius:20px; letter-spacing:0.05em; }
    .elite-badge { display:inline-flex; align-items:center; gap:5px; background:#2D7A4F; color:#fff; font-size:10px; font-weight:600; padding:3px 10px; border-radius:20px; }
    .cred-row { display:flex; align-items:center; justify-content:space-between; padding:10px 0; border-bottom:1px solid #F0EEE8; gap:12px; flex-wrap:wrap; }
    .cred-row:last-child { border-bottom:none; }
    .cred-info { flex:1; min-width:0; }
    .cred-name { font-size:13px; color:#0D1B3E; font-weight:500; }
    .cred-meta { font-size:11px; color:#7A7870; margin-top:2px; }
    .cred-actions { display:flex; gap:6px; flex-shrink:0; flex-wrap:wrap; }
    .audit-row { display:flex; justify-content:space-between; font-size:11px; padding:4px 0; }
    .audit-label { color:#7A7870; }
    .audit-value { color:#0D1B3E; font-weight:500; }
    .audit-verified { color:#2D7A4F; font-weight:500; }
    .notary-row-expanded { background:#FAFAF8 !important; }
    .expand-btn { background:#FDF6E3; border:1px solid #C9A84C; cursor:pointer; font-size:11px; font-weight:700; color:#0D1B3E; padding:4px 10px; border-radius:8px; transition:all 0.15s; box-shadow:0 1px 3px rgba(201,168,76,0.15); }
    .expand-btn:hover { background:linear-gradient(135deg,#0D1B3E,#1A3A5E); color:#C9A84C; border-color:#0D1B3E; box-shadow:0 2px 8px rgba(13,27,62,0.25); }
    .vendor-input { width:130px; padding:5px 10px; border-radius:6px; border:1px solid #E8E6DF; font-size:12px; font-family:'Cormorant Garamond',serif; font-weight:600; color:#9A7A30; letter-spacing:0.05em; }
    .vendor-badge { font-size:11px; font-weight:600; color:#9A7A30; background:#FDF6E3; border:1px solid rgba(201,168,76,0.3); padding:3px 10px; border-radius:20px; letter-spacing:0.04em; font-family:'Cormorant Garamond',serif; }
    .priority-bar { height:4px; border-radius:2px; background:#E8E6DF; margin-top:4px; }
    .priority-fill { height:4px; border-radius:2px; background:linear-gradient(to right,#C9A84C,#2D7A4F); }
    .cred-rejected-banner { background:#FFF0F0; border:1px solid rgba(226,75,74,0.2); border-radius:8px; padding:8px 12px; font-size:11px; color:#A32D2D; margin-top:4px; }
  `

  return (
    <>
      <style>{VETTING_STYLES}</style>

      {/* ✅ REJECT MODAL */}
      {rejectModal && (
        <RejectModal
          credId={rejectModal.credId}
          notaryId={rejectModal.notaryId}
          onConfirm={rejectCredential}
          onClose={() => setRejectModal(null)}
        />
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:8, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom:0 }}>Notary Roster</h1>
          <p className="page-sub" style={{ marginTop:4 }}>Approve notaries, assign vendor numbers, and manage vetting</p>
        </div>
        <button
          onClick={() => {
            if (notaries.length === 0) { toast.error('No notaries to export!!'); return }
            const rows = [
              ['Name','Email','Phone','State','City','Vendor #','Vetted','Ready to Work','Total Signings','Acceptance %','Completion %','Avg Rating','Approved','Created']
            ]
            notaries.forEach(n => {
              const m = n.notaries || {}
              rows.push([
                n.full_name || '',
                n.email || '',
                n.phone || '',
                n.state || m.commission_state || '',
                n.city || '',
                m.vendor_number || '',
                m.is_vetted ? 'YES' : 'NO',
                m.ready_to_work ? 'YES' : 'NO',
                m.total_signings || 0,
                m.acceptance_rate != null ? `${m.acceptance_rate}%` : '',
                m.completion_rate != null ? `${m.completion_rate}%` : '',
                m.avg_rating || '',
                n.is_approved ? 'YES' : 'NO',
                n.created_at ? new Date(n.created_at).toLocaleString() : ''
              ])
            })
            const csv = rows.map(row => row.map(cell => {
              const str = String(cell)
              return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
            }).join(',')).join('\n')
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `LCA_Notaries_${new Date().toISOString().split('T')[0]}.csv`
            link.click()
            URL.revokeObjectURL(url)
            toast.success(`📊 Exported ${notaries.length} notaries!!`)
          }}
          style={{
            background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)',
            color:'#C9A84C',
            border:'1px solid #C9A84C',
            borderRadius:8,
            padding:'9px 14px',
            fontSize:12,
            fontWeight:600,
            cursor:'pointer',
            fontFamily:'inherit',
            whiteSpace:'nowrap',
          }}
        >
          📊 Export CSV
        </button>
      </div>

      {/* 🔍 SEARCH BAR */}
      <div style={{ marginBottom:14, position:'relative' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="🔍 Search by name, email, phone, vendor #, city, or state..."
          style={{
            width:'100%',
            padding:'12px 40px 12px 16px',
            borderRadius:10,
            border:'1.5px solid #E8E6DF',
            background:'#fff',
            fontSize:13,
            fontFamily:'inherit',
            color:'#0D1B3E',
            outline:'none',
            transition:'border 0.15s',
          }}
          onFocus={e => e.target.style.borderColor = '#C9A84C'}
          onBlur={e => e.target.style.borderColor = '#E8E6DF'}
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            style={{
              position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
              background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#7A7870',
            }}
            aria-label="Clear search"
          >✕</button>
        )}
      </div>

      {/* ✅ FILTER BAR — auto-activates from banner clicks */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {[
          { key:'all',              label:'All Notaries',         count: notaries.length },
          { key:'pending_approval', label:'⏳ Pending Approval',   count: notaries.filter(n => !n.is_approved).length },
          { key:'pending_creds',    label:'📋 Pending Credentials', count: notaries.filter(n => n.is_approved && (n.notary_docs || []).some(d => d.status === 'pending')).length },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '8px 16px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              border: filter === f.key ? '2px solid #C9A84C' : '1.5px solid #E8E6DF',
              background: filter === f.key ? '#FDF6E3' : '#fff',
              color: filter === f.key ? '#9A7A30' : '#7A7870',
              transition: 'all 0.15s',
            }}
          >
            {f.label} <span style={{ marginLeft:4, fontWeight:700 }}>({f.count})</span>
          </button>
        ))}
      </div>

      <div className="section-card" style={{ padding:0 }}>
        <table className="data-table">
          <thead><tr><th>Notary</th><th>State</th><th>Priority</th><th>Vendor #</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ textAlign:'center', padding:24 }}><div className="spinner" style={{ margin:'auto' }} /></td></tr>}
            {notaries
              .filter(n => {
                if (filter === 'all') return true
                if (filter === 'pending_approval') return !n.is_approved
                if (filter === 'pending_creds') {
                  return n.is_approved && (n.notary_docs || []).some(d => d.status === 'pending')
                }
                return true
              })
              .filter(n => {
                // 🔍 Apply search query
                if (!searchQuery.trim()) return true
                const q = searchQuery.toLowerCase().trim()
                const m = n.notaries || {}
                return (
                  (n.full_name || '').toLowerCase().includes(q) ||
                  (n.email || '').toLowerCase().includes(q) ||
                  (n.phone || '').toLowerCase().includes(q) ||
                  (n.city || '').toLowerCase().includes(q) ||
                  (n.state || '').toLowerCase().includes(q) ||
                  (m.commission_state || '').toLowerCase().includes(q) ||
                  (m.vendor_number || '').toLowerCase().includes(q)
                )
              })
              .map(n => {
              const notary   = n.notaries || {}
              // ✅ Auto-expand notaries with pending credentials when filter is active
              const isOpen   = expanded === n.id || (filter === 'pending_creds' && (n.notary_docs || []).some(d => d.status === 'pending'))
              const isElite  = notary.is_elite
              const isVetted = notary.is_vetted
              const score    = notary.priority_score || 0
              const creds    = credentials[n.id] || []
              return (
                <React.Fragment key={n.id}>
                  <tr className={isOpen ? 'notary-row-expanded' : ''} style={{ border: !n.is_approved ? '2px solid #FCA5A5' : '1px solid #E8E6DF', background: !n.is_approved ? '#FFF5F5' : '#fff' }}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        {/* ✅ HEADSHOT */}
                        <div style={{ width:44, height:44, borderRadius:'50%', overflow:'hidden', background:'rgba(13,27,62,0.08)', border:'2px solid #E8E6DF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          {n.headshot_url
                            ? <img src={n.headshot_url} alt={n.full_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                            : <span style={{ fontSize:16, fontWeight:700, color:'#C9A84C' }}>{n.full_name?.charAt(0) || '?'}</span>}
                        </div>
                        <div>
                          <div style={{ fontWeight:500 }}>{n.full_name}</div>
                          <div style={{ fontSize:11, color:'#7A7870' }}><a href={`mailto:${n.email}`} style={{ color:'inherit', textDecoration:'none' }}>{n.email}</a></div>
                          {n.phone && <a href={`tel:${n.phone}`} style={{ fontSize:11, color:'#9A7A30', textDecoration:'none', display:'block' }}>📞 {n.phone}</a>}
                          {(n.address || n.city || n.state) && (
                            <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>
                              📍 {[n.address, n.city, n.state ? normalizeStateName(n.state) : null, n.zip].filter(Boolean).join(', ')}
                            </div>
                          )}
                          {!n.is_approved && (
                            <div style={{ background:'#FCA5A5', color:'#7A1010', fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:6, marginTop:4, marginBottom:4 }}>
                              ⚠️ PENDING APPROVAL
                            </div>
                          )}
                          <div style={{ display:'flex', gap:4, marginTop:4, flexWrap:'wrap' }}>
                            {isVetted
                              ? <span className="vet-badge">LCA Vetted</span>
                              : <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'#FDF6E3', color:'#8A5C00', border:'1px solid #C9A84C', fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>⚠ Not Yet Vetted</span>
                            }
                            {isElite  && <span className="elite-badge">Elite</span>}
                            {notary.is_ron && <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'linear-gradient(135deg,#1A4A6B,#2D7AB5)', color:'#fff', fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>💻 RON Notary</span>}
                            {notary.is_founder && <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'linear-gradient(135deg,#9A7A30,#C9A84C)', color:'#0D1B3E', fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>🏅 Founder</span>}
                            {notary.is_preferred && <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'linear-gradient(135deg,#2D7A4F,#3A9A60)', color:'#fff', fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>⭐ Preferred</span>}
                            {notary.is_apostille && <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'linear-gradient(135deg,#6A3A9A,#8B5AC2)', color:'#fff', fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>🌍 Apostille</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{normalizeStateName(notary.commission_state)}</td>
                    <td>
                      <div style={{ fontSize:12, fontWeight:500, color:'#0D1B3E' }}>{score} pts</div>
                      <div className="priority-bar"><div className="priority-fill" style={{ width:`${Math.min(score/2,100)}%` }} /></div>
                      <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>{notary.total_signings||0} signings · {notary.avg_rating||'—'} stars</div>
                    </td>
                    <td>
                      {n.is_approved ? (
                        <div style={{ display:'flex', gap:4, alignItems:'center', flexWrap:'wrap' }}>
                          {notary.vendor_number && <span className="vendor-badge">{notary.vendor_number}</span>}
                          <input className="vendor-input" placeholder={generateVendorNumber(notaries)} value={vendorInputs[n.id]||''} onChange={e => setVendorInputs(v => ({ ...v, [n.id]:e.target.value }))} />
                          <button className="btn btn-outline btn-sm" onClick={() => saveVendorNumber(n)} disabled={saving===n.id+'-vendor'} style={{ fontSize:10, padding:'4px 8px' }}>Save</button>
                        </div>
                      ) : (
                        <span style={{ fontSize:11, color:'#B8B5AB', fontStyle:'italic' }}>On approval</span>
                      )}
                    </td>
                    <td>{n.is_approved ? <span className="badge badge-approved">Approved</span> : <span className="badge badge-pending">Pending</span>}</td>
                    <td>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                        {!n.is_approved && <button onClick={() => approveNotary(n)} disabled={saving===n.id+'-approve'} style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#C9A84C', border:'none', borderRadius:10, padding:'7px 16px', fontWeight:700, fontSize:12, cursor:'pointer', boxShadow:'0 2px 8px rgba(13,27,62,0.25)', opacity: saving===n.id+'-approve' ? 0.6 : 1 }}>{saving===n.id+'-approve'?'...':'✓ Approve'}</button>}
                        {n.is_approved && !isVetted && <button className="btn btn-outline btn-sm" onClick={() => grantVettedBadge(n)} disabled={saving===n.id+'-vet'} style={{ fontSize:10 }}>Grant Vetted</button>}
                        {n.is_approved && (
                          <button className="btn btn-outline btn-sm" onClick={() => grantRonBadge(notary)} disabled={saving===n.id+'-ron'} style={{ fontSize:10, color: notary.is_ron ? '#E24B4A' : '#1A4A6B', borderColor: notary.is_ron ? '#E24B4A' : '#2D7AB5' }}>
                            {saving===n.id+'-ron' ? '...' : notary.is_ron ? 'Remove RON' : '💻 Grant RON'}
                          </button>
                        )}
                        {n.is_approved && (
                          <button className="btn btn-outline btn-sm" onClick={() => grantPreferredBadge(notary)} disabled={saving===n.id+'-preferred'} style={{ fontSize:10, color: notary.is_preferred ? '#E24B4A' : '#2D7A4F', borderColor: notary.is_preferred ? '#E24B4A' : '#2D7A4F' }}>
                            {saving===n.id+'-preferred' ? '...' : notary.is_preferred ? 'Remove Preferred' : '⭐ Grant Preferred'}
                          </button>
                        )}
                        {n.is_approved && (
                          <button className="btn btn-outline btn-sm" onClick={() => grantApostilleBadge(notary)} disabled={saving===n.id+'-apostille'} style={{ fontSize:10, color: notary.is_apostille ? '#E24B4A' : '#6A3A9A', borderColor: notary.is_apostille ? '#E24B4A' : '#6A3A9A' }}>
                            {saving===n.id+'-apostille' ? '...' : notary.is_apostille ? 'Remove Apostille' : '🌍 Grant Apostille'}
                          </button>
                        )}
                        <button className="btn btn-outline btn-sm" onClick={async () => {
                          await supabase.from('notaries').update({ is_founder: !notary.is_founder }).eq('id', n.id)
                          toast.success(notary.is_founder ? 'Founder badge removed' : '🏅 Founder badge granted!!')
                          loadNotaries()
                        }} style={{ fontSize:10, color: notary.is_founder ? '#E24B4A' : '#9A7A30', borderColor: notary.is_founder ? '#E24B4A' : '#C9A84C' }}>
                          {notary.is_founder ? 'Remove Founder' : '🏅 Grant Founder'}
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={() => generate1099(n, notary)} style={{ fontSize:10, color:'#2D7A4F', borderColor:'#2D7A4F' }}>
                          📄 Generate 1099-NEC
                        </button>
                        <button className="expand-btn" onClick={() => toggleExpand(n.id)}>{isOpen?'▲ Hide':'▼ Details'}</button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={6} style={{ padding:0 }}>
                        <div className="vet-section">
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
                            <div>
                              <div className="vet-section-title">AUDIT TRAIL</div>
                              {/* ✅ VENDOR NUMBER — shown prominently in details panel */}
                              <div className="audit-row" style={{ marginBottom:8, paddingBottom:8, borderBottom:'1px solid #E8E6DF' }}>
                                <span className="audit-label" style={{ fontWeight:600 }}>LCA Vendor #</span>
                                {notary.vendor_number
                                  ? <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:15, fontWeight:700, color:'#9A7A30', background:'#FDF6E3', border:'1px solid rgba(201,168,76,0.3)', padding:'2px 10px', borderRadius:10, letterSpacing:'0.05em' }}>{notary.vendor_number}</span>
                                  : <span style={{ fontSize:12, color:'#B8B5AB', fontStyle:'italic' }}>Not yet assigned</span>
                                }
                              </div>
                              <div className="audit-row"><span className="audit-label">Member since</span><span className="audit-value">{n.created_at ? new Date(n.created_at).toLocaleString('en-US',{year:'numeric',month:'long',day:'numeric',hour:'numeric',minute:'2-digit'}) : '—'}</span></div>
                              <div className="audit-row"><span className="audit-label">Commission verified</span><span className={notary.commission_verified_at?'audit-verified':'audit-label'}>{notary.commission_verified_at?new Date(notary.commission_verified_at).toLocaleString():'Not yet verified'}</span></div>
                              <div className="audit-row"><span className="audit-label">E&O verified</span><span className={notary.eo_verified_at?'audit-verified':'audit-label'}>{notary.eo_verified_at?new Date(notary.eo_verified_at).toLocaleString():'Not yet verified'}</span></div>
                              <div className="audit-row"><span className="audit-label">Background check verified</span><span className={notary.background_verified_at?'audit-verified':'audit-label'}>{notary.background_verified_at?new Date(notary.background_verified_at).toLocaleString():'Not yet verified'}</span></div>
                              {isVetted && <><div className="audit-row" style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #E8E6DF' }}><span className="audit-label">LCA Vetted badge granted</span><span className="audit-verified">{notary.vetted_at?new Date(notary.vetted_at).toLocaleString():'—'}</span></div><div className="audit-row"><span className="audit-label">Vetted by</span><span className="audit-value">{notary.vetted_by}</span></div></>}

                              {/* 🎂 BIRTHDAY — Privacy-friendly MM-DD only!! */}
                              <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #E8E6DF' }}>
                                <div className="vet-section-title" style={{ marginBottom:8, color:'#9A7A30' }}>🎂 BIRTHDAY (Optional)</div>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <input
                                    type="text"
                                    value={birthdays[n.id] || ''}
                                    onChange={e => setBirthdays(prev => ({ ...prev, [n.id]: e.target.value }))}
                                    onBlur={() => saveBirthday(n.id)}
                                    placeholder="MM-DD (e.g. 05-15)"
                                    maxLength={5}
                                    style={{
                                      width:120,
                                      padding:'8px 12px',
                                      border:'1.5px solid #E0D9CC',
                                      borderRadius:8,
                                      fontSize:13,
                                      fontFamily:'inherit',
                                      color:'#0D1B3E',
                                      background:'#FFFBF0',
                                      textAlign:'center',
                                      letterSpacing:'0.05em',
                                    }}
                                  />
                                  <span style={{ fontSize:11, color:'#7A7870' }}>
                                    {savingBday === n.id ? '💾 Saving...' : birthdays[n.id] ? '✓ Saved' : 'Format: MM-DD (year not stored — privacy!!)'}
                                  </span>
                                </div>
                                <div style={{ fontSize:10, color:'#B8B5AB', marginTop:4 }}>
                                  💡 Used to send personalized birthday wishes from the Email Blast Center!!
                                </div>
                              </div>

                              {/* ✅ ADMIN NOTES — Private, admin-only!! */}
                              <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #E8E6DF' }}>
                                <div className="vet-section-title" style={{ marginBottom:8, color:'#9A7A30' }}>🔒 ADMIN NOTES — Private, admin eyes only!!</div>
                                <textarea
                                  value={adminNotes[n.id] || ''}
                                  onChange={e => setAdminNotes(prev => ({ ...prev, [n.id]: e.target.value }))}
                                  onBlur={() => saveAdminNote(n.id)}
                                  placeholder="Notes about vetting, follow-ups, observations... auto-saves when you click outside!"
                                  rows={4}
                                  style={{
                                    width:'100%',
                                    padding:'10px 12px',
                                    border:'1.5px solid #E0D9CC',
                                    borderRadius:8,
                                    fontSize:12,
                                    fontFamily:'inherit',
                                    color:'#0D1B3E',
                                    background:'#FFFBF0',
                                    resize:'vertical',
                                    lineHeight:1.5,
                                  }}
                                />
                                <div style={{ fontSize:10, color:'#B8B5AB', marginTop:4, display:'flex', justifyContent:'space-between' }}>
                                  <span>{savingNotes === n.id ? '💾 Saving...' : '✓ Auto-saves on blur'}</span>
                                  <span>🔒 Never visible to notary or clients</span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="vet-section-title">PRIORITY SCORE</div>
                              <div className="audit-row"><span className="audit-label">Elite status</span><span className="audit-value">{isElite?'+100':'0'}</span></div>
                              <div className="audit-row"><span className="audit-label">Vetted badge bonus</span><span className="audit-value">{isVetted?'+50':'0'}</span></div>
                              <div className="audit-row"><span className="audit-label">Experience points</span><span className="audit-value">+{Math.min(notary.total_signings||0,50)}</span></div>
                              <div className="audit-row"><span className="audit-label">Rating points</span><span className="audit-value">+{Math.min(Math.round((notary.avg_rating||0)*10),50)}</span></div>
                              <div className="audit-row" style={{ borderTop:'1px solid #E8E6DF', marginTop:4, paddingTop:8 }}><span className="audit-label" style={{ fontWeight:600 }}>Total priority score</span><span className="audit-value" style={{ color:'#C9A84C', fontWeight:600 }}>{score} pts</span></div>
                              {/* ✅ RATINGS SUMMARY */}
                              <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #E8E6DF' }}>
                                <div className="vet-section-title" style={{ marginBottom:8 }}>RATINGS & REVIEWS</div>
                                <div className="audit-row">
                                  <span className="audit-label">Average Rating</span>
                                  <span style={{ color:'#C9A84C', fontWeight:600 }}>
                                    {notary.avg_rating ? <>{'★'.repeat(Math.round(notary.avg_rating))} {notary.avg_rating}/5</> : 'No ratings yet'}
                                  </span>
                                </div>
                                <NotaryRatingsPanel notaryId={n.id} />
                              </div>

                              {/* ✅ PRIVATE CLIENT RATINGS — admin eyes only!! */}
                              <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #E8E6DF' }}>
                                <div className="vet-section-title" style={{ marginBottom:8 }}>🔒 NOTARY FEEDBACK ON CLIENTS — Private</div>
                                <PrivateClientRatings notaryId={n.id} />
                              </div>
                            </div>
                          </div>
                          <div className="vet-section-title">CREDENTIALS — Click to verify or reject each document</div>
                          {creds.length === 0 ? (
                            <div style={{ fontSize:13, color:'#B8B5AB', padding:'12px 0' }}>No credentials uploaded yet</div>
                          ) : (
                            creds.map(c => (
                              <div key={c.id} className="cred-row">
                                <div className="cred-info">
                                  <div className="cred-name">{c.doc_label || c.doc_type}</div>
                                  <div className="cred-meta">
                                    Uploaded {new Date(c.created_at).toLocaleString()}
                                    {c.expires_at ? ` · Expires ${c.expires_at}` : ''}
                                    {c.verified_at && <span style={{ color:'#2D7A4F' }}> · Verified {new Date(c.verified_at).toLocaleString()} by {c.verified_by}</span>}
                                  </div>
                                  {/* ✅ REJECTION BANNER — shows reason, no verify button */}
                                  {c.status === 'rejected' && (
                                    <div className="cred-rejected-banner">
                                      ❌ Rejected: {c.reject_reason || 'Does not meet requirements'}
                                      <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>Notary must re-upload a corrected document</div>
                                    </div>
                                  )}
                                </div>
                                <div className="cred-actions">
                                  {c.file_url && <a href={c.file_url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">View</a>}
                                  {/* ✅ ONLY show Verify/Reject on PENDING docs — not on approved or rejected */}
                                  {c.status === 'pending' && (
                                    <>
                                      <button className="btn btn-primary btn-sm" onClick={() => verifyCredential(c.id, n.id, c.doc_type)} disabled={saving===c.id} style={{ fontSize:11 }}>
                                        {saving===c.id ? '...' : 'Verify'}
                                      </button>
                                      <button className="btn btn-outline btn-sm" onClick={() => setRejectModal({ credId:c.id, notaryId:n.id })} style={{ fontSize:11, color:'#E24B4A', borderColor:'#E24B4A' }}>
                                        Reject
                                      </button>
                                    </>
                                  )}
                                  {c.status === 'approved' && <span className="badge badge-approved" style={{ fontSize:10 }}>Verified</span>}
                                  {c.status === 'rejected' && <span className="badge badge-rejected" style={{ fontSize:10 }}>Rejected</span>}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {!loading && notaries.length === 0 && <tr><td colSpan={6} style={{ textAlign:'center', color:'#B8B5AB', padding:24 }}>No notaries yet</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ✅ COMPLIANCE CENTER — expiration alerts & audit export
function Credentials() {
  const [notaries,  setNotaries]  = useState([])
  const [loading,   setLoading]   = useState(true)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    async function fetchData() {
      try {
        const { data: profilesData } = await supabaseAdmin
          .from('profiles')
          .select('id, full_name, email, phone, address, city, state, zip, is_approved, created_at, notaries(*), notary_docs(*)')
          .eq('role', 'notary')
          .eq('is_approved', true)
        setNotaries(profilesData || [])
      } catch(err) {
        console.error('Compliance error:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  // Helper: days until expiration
  function daysUntil(dateStr) {
    if (!dateStr) return null
    const diff = new Date(dateStr) - new Date()
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  }

  function ExpiryBadge({ days }) {
    if (days === null) return null
    if (days < 0)  return <span style={{ fontSize:10, background:'#FDF2F2', color:'#C0392B', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>⛔ EXPIRED</span>
    if (days <= 30) return <span style={{ fontSize:10, background:'#FDF6E3', color:'#8A5C00', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>⚠️ {days}d left</span>
    if (days <= 60) return <span style={{ fontSize:10, background:'#EBF4FF', color:'#185FA5', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>🔔 {days}d left</span>
    return <span style={{ fontSize:10, background:'#EAF5EE', color:'#2D7A4F', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>✓ {days}d</span>
  }

  async function exportAuditPDF() {
    setExporting(true)
    try {
      if (!window.jspdf) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
          script.onload = resolve; script.onerror = reject
          document.head.appendChild(script)
        })
        await new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js'
          script.onload = resolve; script.onerror = reject
          document.head.appendChild(script)
        })
      }
      const { jsPDF } = window.jspdf
      const doc     = new jsPDF()
      const now     = new Date()
      const dateStr = now.toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })
      const timeStr = now.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' })
      doc.setFillColor(13,27,62); doc.rect(0,0,220,40,'F')
      doc.setTextColor(201,168,76); doc.setFontSize(18); doc.setFont('helvetica','bold')
      doc.text('Loan Closing Agent LLC',14,16)
      doc.setFontSize(10); doc.setFont('helvetica','normal')
      doc.text('NOTARY COMPLIANCE AUDIT REPORT',14,24)
      doc.setTextColor(255,255,255); doc.setFontSize(9)
      doc.text(`Generated: ${dateStr} at ${timeStr}`,14,32)
      doc.text('Prepared by: Nancy Phillips, Founder & CEO',14,38)
      doc.setTextColor(0,0,0); doc.setFontSize(10); doc.setFont('helvetica','normal')
      doc.text('This report certifies compliance status for all active LCA notaries.',14,52)
      const rows = notaries.map(n => {
        const docs      = n.notary_docs || []
        const commission = docs.find(d => d.doc_type === 'commission' && d.status === 'approved')
        const eo         = docs.find(d => d.doc_type === 'eo_insurance' && d.status === 'approved')
        const bg         = docs.find(d => d.doc_type === 'background_check' && d.status === 'approved')
        return [
          n.full_name,
          n.notaries?.vendor_number || '—',
          commission ? (commission.expires_at || 'No expiry') : 'MISSING',
          eo ? (eo.expires_at || 'No expiry') : 'MISSING',
          bg ? 'On file' : 'MISSING',
          n.notaries?.is_vetted ? 'YES' : 'No',
        ]
      })
      doc.autoTable({ startY:62, head:[['Notary Name','Vendor #','Commission Exp.','E&O Exp.','Background','LCA Vetted']], body:rows, headStyles:{ fillColor:[13,27,62], textColor:[201,168,76], fontSize:8, fontStyle:'bold' }, bodyStyles:{ fontSize:8 }, alternateRowStyles:{ fillColor:[248,247,244] }, margin:{ left:14, right:14 } })
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150,150,150)
        doc.text('Loan Closing Agent LLC — Confidential Compliance Report',14,doc.internal.pageSize.height-10)
        doc.text(`Page ${i} of ${pageCount}`,doc.internal.pageSize.width-30,doc.internal.pageSize.height-10)
        doc.setDrawColor(201,168,76); doc.line(14,doc.internal.pageSize.height-14,doc.internal.pageSize.width-14,doc.internal.pageSize.height-14)
      }
      doc.save(`LCA-Compliance-Audit-${now.toISOString().slice(0,10)}.pdf`)
      toast.success('Compliance audit exported!!')
    } catch (err) {
      toast.error('Export failed: ' + err.message)
    } finally {
      setExporting(false)
    }
  }

  // Build compliance alerts
  const alerts = []
  notaries.forEach(n => {
    const docs = n.notary_docs || []
    const commission = docs.find(d => d.doc_type === 'commission' && d.status === 'approved')
    const eo         = docs.find(d => d.doc_type === 'eo_insurance' && d.status === 'approved')
    const bg         = docs.find(d => d.doc_type === 'background_check' && d.status === 'approved')

    // Check for pending (uploaded but not yet verified)
    const commissionPending = !commission && docs.find(d => d.doc_type === 'commission' && d.status === 'pending')
    const eoPending         = !eo && docs.find(d => d.doc_type === 'eo_insurance' && d.status === 'pending')
    const bgPending         = !bg && docs.find(d => d.doc_type === 'background_check' && d.status === 'pending')

    if (commission?.expires_at) {
      const days = daysUntil(commission.expires_at)
      if (days !== null && days <= 60) alerts.push({ notary: n, type: 'Commission', expires: commission.expires_at, days })
    }
    if (eo?.expires_at) {
      const days = daysUntil(eo.expires_at)
      if (days !== null && days <= 60) alerts.push({ notary: n, type: 'E&O Insurance', expires: eo.expires_at, days })
    }
    if (!commission && !commissionPending) alerts.push({ notary: n, type: 'Commission',       expires: null, days: null, missing: true,  pending: false })
    if (!commission &&  commissionPending) alerts.push({ notary: n, type: 'Commission',       expires: null, days: null, missing: false, pending: true  })
    if (!eo         && !eoPending)         alerts.push({ notary: n, type: 'E&O Insurance',    expires: null, days: null, missing: true,  pending: false })
    if (!eo         &&  eoPending)         alerts.push({ notary: n, type: 'E&O Insurance',    expires: null, days: null, missing: false, pending: true  })
    if (!bg         && !bgPending)         alerts.push({ notary: n, type: 'Background Check', expires: null, days: null, missing: true,  pending: false })
    if (!bg         &&  bgPending)         alerts.push({ notary: n, type: 'Background Check', expires: null, days: null, missing: false, pending: true  })
  })

  const critical = alerts.filter(a => a.missing || (a.days !== null && a.days < 0))
  const pending  = alerts.filter(a => a.pending)
  const warning  = alerts.filter(a => !a.missing && !a.pending && a.days !== null && a.days >= 0 && a.days <= 30)
  const upcoming = alerts.filter(a => !a.missing && !a.pending && a.days !== null && a.days > 30 && a.days <= 60)

  const COMP_STYLES = `
    .comp-alert-row { display:flex; align-items:center; gap:12px; padding:12px 16px; border-bottom:1px solid #F0EEE8; flex-wrap:wrap; }
    .comp-alert-row:last-child { border-bottom:none; }
    .comp-notary-info { flex:1; min-width:140px; }
    .comp-notary-name { font-size:13px; font-weight:500; color:#0D1B3E; }
    .comp-alert-type { font-size:12px; color:#7A7870; margin-top:2px; }
    .comp-section-title { font-size:11px; font-weight:600; color:#7A7870; letter-spacing:0.08em; padding:12px 16px 8px; border-bottom:1px solid #E8E6DF; background:#F8F7F4; }
  `

  return (
    <>
      <style>{COMP_STYLES}</style>
      <h1 className="page-title">Compliance Center</h1>
      <p className="page-sub">Credential expiration alerts and compliance audit — manage credentials in the Notaries tab!!</p>

      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button className="btn btn-navy" onClick={exportAuditPDF} disabled={exporting} style={{ display:'flex', alignItems:'center', gap:8 }}>
          {exporting ? '⏳ Generating...' : '📄 Export Compliance Audit PDF'}
        </button>
      </div>

      {/* KPI STRIP */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:20 }}>
        <div className="stat-card"><div className="stat-label">Active Notaries</div><div className="stat-value">{notaries.length}</div></div>
        <div className="stat-card"><div className="stat-label">🔴 Critical Issues</div><div className="stat-value" style={{ color:'#E24B4A' }}>{critical.length}</div></div>
        <div className="stat-card"><div className="stat-label">⏳ Pending Review</div><div className="stat-value" style={{ color:'#C9A84C' }}>{pending.length}</div></div>
        <div className="stat-card"><div className="stat-label">⚠️ Expiring Soon</div><div className="stat-value" style={{ color:'#C9A84C' }}>{warning.length}</div></div>
        <div className="stat-card"><div className="stat-label">🔔 Upcoming</div><div className="stat-value" style={{ color:'#185FA5' }}>{upcoming.length}</div></div>
      </div>

      {loading && <div style={{ textAlign:'center', padding:48 }}><div className="spinner" style={{ margin:'auto' }} /></div>}

      {!loading && alerts.length === 0 && (
        <div style={{ background:'#EAF5EE', border:'1px solid rgba(45,122,79,0.3)', borderRadius:14, padding:32, textAlign:'center' }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🎉</div>
          <div style={{ fontSize:16, fontWeight:500, color:'#2D7A4F', marginBottom:4 }}>All Clear!!</div>
          <div style={{ fontSize:13, color:'#4A7A5C' }}>All notary credentials are current and up to date!!</div>
        </div>
      )}

      {/* CRITICAL */}
      {critical.length > 0 && (
        <div className="section-card" style={{ padding:0, marginBottom:16, border:'1.5px solid rgba(226,75,74,0.3)' }}>
          <div className="comp-section-title" style={{ background:'#FDF2F2', color:'#C0392B' }}>🔴 CRITICAL — Expired or Missing Documents ({critical.length})</div>
          {critical.map((a, i) => (
            <div key={i} className="comp-alert-row">
              <div className="comp-notary-info">
                <div className="comp-notary-name">{a.notary.full_name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                  <VendorBadge number={a.notary.notaries?.vendor_number} />
                  <span style={{ fontSize:11, color:'#7A7870' }}>{a.notary.email}</span>
                </div>
              </div>
              <div className="comp-alert-type">{a.type}</div>
              {a.missing
                ? <span style={{ fontSize:11, background:'#FDF2F2', color:'#C0392B', padding:'3px 10px', borderRadius:10, fontWeight:600 }}>⛔ NOT UPLOADED</span>
                : <span style={{ fontSize:11, background:'#FDF2F2', color:'#C0392B', padding:'3px 10px', borderRadius:10, fontWeight:600 }}>⛔ EXPIRED {new Date(a.expires).toLocaleDateString()}</span>
              }
              <a href="/admin/notaries" className="btn btn-outline btn-sm" style={{ fontSize:11 }}>Go to Notaries →</a>
            </div>
          ))}
        </div>
      )}

      {/* PENDING REVIEW */}
      {pending.length > 0 && (
        <div className="section-card" style={{ padding:0, marginBottom:16, border:'1.5px solid rgba(201,168,76,0.3)' }}>
          <div className="comp-section-title" style={{ background:'#FFFBF0', color:'#8A5C00' }}>⏳ PENDING REVIEW — Uploaded, awaiting verification ({pending.length})</div>
          {pending.map((a, i) => (
            <div key={i} className="comp-alert-row">
              <div className="comp-notary-info">
                <div className="comp-notary-name">{a.notary.full_name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                  <VendorBadge number={a.notary.notaries?.vendor_number} />
                  <span style={{ fontSize:11, color:'#7A7870' }}>{a.notary.email}</span>
                </div>
              </div>
              <div className="comp-alert-type">{a.type}</div>
              <span style={{ fontSize:11, background:'#FFFBF0', color:'#8A5C00', padding:'3px 10px', borderRadius:10, fontWeight:600 }}>⏳ PENDING REVIEW</span>
              <a href="/admin/notaries?filter=pending_creds" className="btn btn-primary btn-sm" style={{ fontSize:11 }}>Review Now →</a>
            </div>
          ))}
        </div>
      )}

      {/* WARNING */}
      {warning.length > 0 && (
        <div className="section-card" style={{ padding:0, marginBottom:16, border:'1.5px solid rgba(201,168,76,0.3)' }}>
          <div className="comp-section-title" style={{ background:'#FDF6E3', color:'#8A5C00' }}>⚠️ EXPIRING WITHIN 30 DAYS ({warning.length})</div>
          {warning.map((a, i) => (
            <div key={i} className="comp-alert-row">
              <div className="comp-notary-info">
                <div className="comp-notary-name">{a.notary.full_name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                  <VendorBadge number={a.notary.notaries?.vendor_number} />
                  <span style={{ fontSize:11, color:'#7A7870' }}>{a.notary.email}</span>
                </div>
              </div>
              <div className="comp-alert-type">{a.type}</div>
              <ExpiryBadge days={a.days} />
              <span style={{ fontSize:11, color:'#7A7870' }}>Expires {new Date(a.expires).toLocaleDateString()}</span>
              <a href="/admin/notaries" className="btn btn-outline btn-sm" style={{ fontSize:11 }}>Notify Notary →</a>
            </div>
          ))}
        </div>
      )}

      {/* UPCOMING */}
      {upcoming.length > 0 && (
        <div className="section-card" style={{ padding:0, border:'1.5px solid rgba(24,95,165,0.2)' }}>
          <div className="comp-section-title" style={{ background:'#EBF4FF', color:'#185FA5' }}>🔔 EXPIRING IN 31–60 DAYS ({upcoming.length})</div>
          {upcoming.map((a, i) => (
            <div key={i} className="comp-alert-row">
              <div className="comp-notary-info">
                <div className="comp-notary-name">{a.notary.full_name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                  <VendorBadge number={a.notary.notaries?.vendor_number} />
                  <span style={{ fontSize:11, color:'#7A7870' }}>{a.notary.email}</span>
                </div>
              </div>
              <div className="comp-alert-type">{a.type}</div>
              <ExpiryBadge days={a.days} />
              <span style={{ fontSize:11, color:'#7A7870' }}>Expires {new Date(a.expires).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ── 📊 NOTARY PERFORMANCE DASHBOARD — Admin command center ────
function PerformanceDashboard() {
  const [notaries, setNotaries] = useState([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState('total_signings') // default sort
  const [sortDir, setSortDir] = useState('desc')
  const [filter, setFilter] = useState('all') // 'all' | 'vetted' | 'auto_vetted' | 'underperforming' | 'top_performers'

  useEffect(() => { loadNotaries() }, [])

  async function loadNotaries() {
    setLoading(true)
    const { data } = await supabase.from('profiles')
      .select('*, notaries(*)')
      .eq('role', 'notary')
      .eq('is_approved', true)
    setNotaries(data || [])
    setLoading(false)
  }

  // Sort + filter
  const filtered = notaries.filter(n => {
    const m = n.notaries
    if (!m) return false
    switch(filter) {
      case 'ready':            return m.ready_to_work === true
      case 'vetted':           return m.is_vetted
      case 'auto_vetted':      return m.is_vetted && m.vetted_method === 'auto'
      case 'underperforming':  return (m.acceptance_rate != null && m.acceptance_rate < 70) || (m.completion_rate != null && m.completion_rate < 80)
      case 'top_performers':   return (m.acceptance_rate || 0) >= 90 && (m.completion_rate || 0) >= 95
      default: return true
    }
  })

  const sorted = [...filtered].sort((a, b) => {
    const aVal = a.notaries?.[sortBy] ?? -1
    const bVal = b.notaries?.[sortBy] ?? -1
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal
  })

  // Aggregate stats
  const stats = {
    total: notaries.length,
    vetted: notaries.filter(n => n.notaries?.is_vetted).length,
    autoVetted: notaries.filter(n => n.notaries?.is_vetted && n.notaries?.vetted_method === 'auto').length,
    ready: notaries.filter(n => n.notaries?.ready_to_work === true).length,
    notReady: notaries.filter(n => n.notaries?.ready_to_work !== true).length,
    avgAcceptance: Math.round(notaries.reduce((s, n) => s + (n.notaries?.acceptance_rate || 0), 0) / (notaries.length || 1)),
    avgCompletion: Math.round(notaries.reduce((s, n) => s + (n.notaries?.completion_rate || 0), 0) / (notaries.length || 1)),
    totalSignings: notaries.reduce((s, n) => s + (n.notaries?.total_signings || 0), 0),
    avgRating: Math.round(notaries.reduce((s, n) => s + (n.notaries?.avg_rating || 0), 0) / (notaries.length || 1) * 10) / 10,
  }

  function toggleSort(field) {
    if (sortBy === field) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(field)
      setSortDir('desc')
    }
  }

  function sortArrow(field) {
    if (sortBy !== field) return ''
    return sortDir === 'desc' ? ' ▼' : ' ▲'
  }

  if (loading) return <div style={{ textAlign:'center', padding:48 }}><div className="spinner" /></div>

  return (
    <>
      <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:28, color:'#0D1B3E', fontWeight:600, marginBottom:8 }}>
        📊 Notary Performance Dashboard
      </div>
      <div style={{ fontSize:13, color:'#7A7870', marginBottom:24 }}>
        Real-time scorecard for your entire tribe — sort, filter, and identify your champions!!
      </div>

      {/* ── AGGREGATE STATS ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12, marginBottom:24 }}>
        {[
          { label: 'TOTAL NOTARIES', value: stats.total, color: '#0D1B3E' },
          { label: '🟢 READY TO WORK', value: stats.ready, sub: `${stats.notReady} offline`, color: '#2D7A4F' },
          { label: 'VETTED', value: stats.vetted, sub: `${stats.autoVetted} auto-earned`, color: '#C9A84C' },
          { label: 'AVG ACCEPTANCE', value: `${stats.avgAcceptance}%`, color: '#2D7A4F' },
          { label: 'AVG COMPLETION', value: `${stats.avgCompletion}%`, color: '#2D7A4F' },
          { label: 'TOTAL SIGNINGS', value: stats.totalSignings.toLocaleString(), color: '#0D1B3E' },
          { label: 'AVG RATING', value: stats.avgRating > 0 ? `${stats.avgRating} ★` : '—', color: '#C9A84C' },
        ].map(s => (
          <div key={s.label} style={{ background:'#fff', border:'1px solid #E8E6DF', borderRadius:12, padding:'16px 14px', textAlign:'center' }}>
            <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, letterSpacing:'0.08em', marginBottom:6 }}>{s.label}</div>
            <div style={{ fontSize:24, fontFamily:'Cormorant Garamond,serif', fontWeight:700, color:s.color, lineHeight:1 }}>{s.value}</div>
            {s.sub && <div style={{ fontSize:10, color:'#B8B5AB', marginTop:4 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* ── FILTER TABS ── */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {[
          { key:'all', label: `All (${notaries.length})` },
          { key:'ready', label: `🟢 Ready (${stats.ready})` },
          { key:'top_performers', label: '🏆 Top Performers' },
          { key:'vetted', label: '✅ Vetted' },
          { key:'auto_vetted', label: '🤖 Auto-Vetted' },
          { key:'underperforming', label: '⚠️ Needs Attention' },
        ].map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            style={{
              background: filter === t.key ? '#0D1B3E' : '#fff',
              color:      filter === t.key ? '#C9A84C' : '#0D1B3E',
              border:     filter === t.key ? '1px solid #0D1B3E' : '1px solid #E8E6DF',
              borderRadius: 8, padding:'8px 14px', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── PERFORMANCE TABLE ── */}
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #E8E6DF', overflow:'hidden' }}>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:'#0D1B3E', color:'#C9A84C' }}>
                <th style={{ padding:'10px 12px', textAlign:'left', fontSize:11, letterSpacing:'0.04em' }}>NOTARY</th>
                <th style={{ padding:'10px 12px', textAlign:'left', fontSize:11, letterSpacing:'0.04em' }}>STATE</th>
                <th onClick={() => toggleSort('acceptance_rate')} style={{ padding:'10px 12px', textAlign:'center', fontSize:11, letterSpacing:'0.04em', cursor:'pointer' }}>
                  ACCEPT %{sortArrow('acceptance_rate')}
                </th>
                <th onClick={() => toggleSort('completion_rate')} style={{ padding:'10px 12px', textAlign:'center', fontSize:11, letterSpacing:'0.04em', cursor:'pointer' }}>
                  COMPLETE %{sortArrow('completion_rate')}
                </th>
                <th onClick={() => toggleSort('avg_response_minutes')} style={{ padding:'10px 12px', textAlign:'center', fontSize:11, letterSpacing:'0.04em', cursor:'pointer' }}>
                  AVG RESP{sortArrow('avg_response_minutes')}
                </th>
                <th onClick={() => toggleSort('avg_rating')} style={{ padding:'10px 12px', textAlign:'center', fontSize:11, letterSpacing:'0.04em', cursor:'pointer' }}>
                  RATING{sortArrow('avg_rating')}
                </th>
                <th onClick={() => toggleSort('total_signings')} style={{ padding:'10px 12px', textAlign:'center', fontSize:11, letterSpacing:'0.04em', cursor:'pointer' }}>
                  SIGNINGS{sortArrow('total_signings')}
                </th>
                <th style={{ padding:'10px 12px', textAlign:'center', fontSize:11, letterSpacing:'0.04em' }}>READY</th>
                <th style={{ padding:'10px 12px', textAlign:'center', fontSize:11, letterSpacing:'0.04em' }}>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign:'center', padding:32, color:'#B8B5AB' }}>No notaries match this filter</td></tr>
              )}
              {sorted.map(n => {
                const m = n.notaries || {}
                const accColor = (m.acceptance_rate || 0) >= 90 ? '#2D7A4F' : (m.acceptance_rate || 0) >= 70 ? '#9A7A30' : '#B8B5AB'
                const compColor = (m.completion_rate || 0) >= 95 ? '#2D7A4F' : (m.completion_rate || 0) >= 80 ? '#9A7A30' : '#B8B5AB'
                return (
                  <tr key={n.id} style={{ borderBottom:'1px solid #F0EEE8' }}>
                    <td style={{ padding:'10px 12px' }}>
                      <div style={{ fontWeight:600, color:'#0D1B3E' }}>{n.full_name}</div>
                      <div style={{ fontSize:10, color:'#7A7870' }}>{m.vendor_number || '—'}</div>
                    </td>
                    <td style={{ padding:'10px 12px', color:'#7A7870' }}>{m.commission_state || '—'}</td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:accColor, fontWeight:600 }}>
                      {m.acceptance_rate != null ? `${m.acceptance_rate}%` : '—'}
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:compColor, fontWeight:600 }}>
                      {m.completion_rate != null ? `${m.completion_rate}%` : '—'}
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#7A7870' }}>
                      {m.avg_response_minutes != null
                        ? m.avg_response_minutes < 60
                          ? `${m.avg_response_minutes}m`
                          : `${Math.round(m.avg_response_minutes/60 * 10)/10}h`
                        : '—'}
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'center', color:'#C9A84C', fontWeight:600 }}>
                      {m.avg_rating ? `${m.avg_rating}★` : '—'}
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'center', fontWeight:600, color:'#0D1B3E' }}>
                      {m.total_signings || 0}
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'center' }}>
                      {m.ready_to_work ? (
                        <span title="Ready to Work" style={{ fontSize:16 }}>🟢</span>
                      ) : (
                        <span title="Not Available" style={{ fontSize:16, opacity:0.4 }}>⚪</span>
                      )}
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'center' }}>
                      {m.is_vetted ? (
                        <span style={{ background:'#0D1B3E', color:'#C9A84C', fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:10 }}>
                          ★ {m.vetted_method === 'auto' ? 'AUTO-VETTED' : 'VETTED'}
                        </span>
                      ) : (
                        <span style={{ background:'#E8E6DF', color:'#7A7870', fontSize:10, fontWeight:600, padding:'3px 8px', borderRadius:10 }}>
                          IN PROGRESS
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ fontSize:11, color:'#7A7870', textAlign:'center', marginTop:14, fontStyle:'italic' }}>
        💡 Click any column header to sort · Filter tabs above to focus on specific groups
      </div>
    </>
  )
}

function Clients() {
  const [clients,  setClients]  = useState([])
  const [loading,  setLoading]  = useState(true)
  const [saving,   setSaving]   = useState(null)
  const [expanded, setExpanded] = useState(null)
  const [jobs,     setJobs]     = useState({})

  useEffect(() => { loadClients() }, [])

  async function loadClients() {
    const { data } = await supabaseAdmin.from('profiles').select('*').eq('role','client').order('created_at', { ascending:false })
    setClients(data||[]); setLoading(false)
  }

  async function toggleExpand(clientId) {
    if (expanded === clientId) { setExpanded(null); return }
    setExpanded(clientId)
    if (!jobs[clientId]) {
      const { data } = await supabase.from('jobs').select('*').eq('client_id', clientId).order('created_at', { ascending:false }).limit(10)
      setJobs(j => ({ ...j, [clientId]: data || [] }))
    }
  }

  async function approveClient(c) {
    setSaving(c.id)
    try {
      await supabaseAdmin.from('profiles').update({ is_approved: true }).eq('id', c.id)
      const { data: updated } = await supabaseAdmin.from('profiles').select('client_number').eq('id', c.id).single()
      await sendClientWelcomeEmail({
        client_name:   c.company_name || c.full_name,
        client_email:  c.email,
        client_number: updated?.client_number || '—',
      })
      toast.success(`${c.company_name || c.full_name} approved — welcome email sent!!`)
      loadClients()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(null) }
  }

  async function rejectClient(c) {
    if (!window.confirm(`Reject and DELETE ${c.company_name || c.full_name}? This permanently removes their registration and login. This cannot be undone.`)) return
    setSaving(c.id)
    try {
      // Remove the login account first (cascades to the profile where the FK
      // is set); fall back to removing the profile row directly if auth
      // deletion is unavailable in this client.
      let authGone = false
      try {
        const { error: authErr } = await supabaseAdmin.auth.admin.deleteUser(c.id)
        if (!authErr) authGone = true
      } catch (e) { /* fall through to profile delete */ }
      await supabaseAdmin.from('profiles').delete().eq('id', c.id)
      toast.success(`${c.company_name || c.full_name} rejected and removed!!${authGone ? '' : ' (Login record may need cleanup in the Supabase dashboard.)'}`)
      loadClients()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(null) }
  }

  return (
    <>
      {/* 🎨 The .expand-btn styles live in VETTING_STYLES (Notaries page only) —
          the Clients page never loaded them, leaving Details as a raw default
          button. This block gives Clients its own copy of the branded style. */}
      <style>{`
        .expand-btn { background:#FDF6E3; border:1px solid #C9A84C; cursor:pointer; font-size:11px; font-weight:700; color:#0D1B3E; padding:4px 10px; border-radius:8px; transition:all 0.15s; box-shadow:0 1px 3px rgba(201,168,76,0.15); }
        .expand-btn:hover { background:linear-gradient(135deg,#0D1B3E,#1A3A5E); color:#C9A84C; border-color:#0D1B3E; box-shadow:0 2px 8px rgba(13,27,62,0.25); }
      `}</style>
<h1 className="page-title" tabIndex={-1}>Client Companies</h1>
      <div className="section-card" style={{ padding:0 }}>
        <table className="data-table">
          <thead><tr><th>Company</th><th>Contact</th><th>Email</th><th>Phone</th><th>Client #</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign:'center', padding:24 }}><div className="spinner" style={{ margin:'auto' }} /></td></tr>}
            {clients.map(c => {
              const isOpen = expanded === c.id
              const clientJobs = jobs[c.id] || []
              return (
                <React.Fragment key={c.id}>
                  <tr style={{ background: isOpen ? '#F8F6EF' : '#fff' }}>
                    <td>
                      <div style={{ fontWeight:500 }}>{c.company_name || '—'}</div>
                      {(c.business_address || c.business_city) && (
                        <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>
                          📍 {[c.business_address, c.business_city, c.business_state, c.business_zip].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </td>
                    <td>{c.full_name}</td>
                    <td><a href={`mailto:${c.email}`} style={{ color:'#9A7A30', textDecoration:'none' }}>{c.email}</a></td>
                    <td>{c.phone ? <a href={`tel:${c.phone}`} style={{ color:'#9A7A30', textDecoration:'none' }}>📞 {c.phone}</a> : '—'}</td>
                    <td style={{ fontFamily:'Cormorant Garamond,serif', fontWeight:600, color:'#9A7A30' }}>{c.client_number || '—'}</td>
                    <td>{c.is_approved ? <span className="badge badge-approved">Active</span> : <span className="badge badge-pending">Pending</span>}</td>
                    <td style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {!c.is_approved && (
                        <button onClick={() => approveClient(c)} disabled={saving === c.id}
                          style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#C9A84C', border:'none', borderRadius:10, padding:'7px 16px', fontWeight:700, fontSize:12, cursor:'pointer', boxShadow:'0 2px 8px rgba(13,27,62,0.25)', opacity: saving === c.id ? 0.6 : 1 }}>
                          {saving === c.id ? '...' : '✓ Approve'}
                        </button>
                      )}
                      {!c.is_approved && (
                        <button onClick={() => rejectClient(c)} disabled={saving === c.id}
                          style={{ background:'#fff', border:'1.5px solid #D9534F', color:'#B03A36', borderRadius:10, padding:'7px 16px', fontWeight:700, fontSize:12, cursor:'pointer', opacity: saving === c.id ? 0.6 : 1 }}>
                          {saving === c.id ? '...' : 'Reject'}
                        </button>
                      )}
                      {c.is_approved && <span style={{ fontSize:11, color:'#2D7A4F' }}>✓ Active</span>}
                      <button className="expand-btn" onClick={() => toggleExpand(c.id)}>
                        {isOpen ? '▲ Hide' : '▼ Details'}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={7} style={{ padding:0, background:'#F8F6EF' }}>
                        <div style={{ padding:16, borderTop:'1px solid #E8E6DF' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:16 }}>
                            {/* CLIENT DETAILS */}
                            <div>
                              <div style={{ fontSize:11, fontWeight:600, color:'#7A7870', letterSpacing:'0.06em', marginBottom:10 }}>CLIENT DETAILS</div>
                              <div style={{ fontSize:12, display:'flex', flexDirection:'column', gap:6 }}>
                                <div><span style={{ color:'#B8B5AB' }}>Company: </span><span style={{ fontWeight:500 }}>{c.company_name || '—'}</span></div>
                                <div><span style={{ color:'#B8B5AB' }}>Contact: </span><span>{c.full_name}</span></div>
                                <div><span style={{ color:'#B8B5AB' }}>Email: </span><a href={`mailto:${c.email}`} style={{ color:'#9A7A30', textDecoration:'none' }}>{c.email}</a></div>
                                {c.phone && <div><span style={{ color:'#B8B5AB' }}>Phone: </span><a href={`tel:${c.phone}`} style={{ color:'#9A7A30', textDecoration:'none' }}>📞 {c.phone}</a></div>}
                                {c.business_address && <div><span style={{ color:'#B8B5AB' }}>Address: </span><span>{[c.business_address, c.business_city, c.business_state, c.business_zip].filter(Boolean).join(', ')}</span></div>}
                                <div><span style={{ color:'#B8B5AB' }}>Client #: </span><span style={{ fontFamily:'Cormorant Garamond,serif', fontWeight:600, color:'#9A7A30' }}>{c.client_number || 'Pending'}</span></div>
                                <div><span style={{ color:'#B8B5AB' }}>Member since: </span><span>{new Date(c.created_at).toLocaleString()}</span></div>
                              </div>
                            </div>
                            {/* RECENT ORDERS */}
                            <div>
                              <div style={{ fontSize:11, fontWeight:600, color:'#7A7870', letterSpacing:'0.06em', marginBottom:10 }}>RECENT ORDERS</div>
                              {clientJobs.length === 0
                                ? <div style={{ fontSize:12, color:'#B8B5AB', fontStyle:'italic' }}>No orders yet</div>
                                : clientJobs.map(j => (
                                  <div key={j.id} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'4px 0', borderBottom:'1px solid #F0EEE8' }}>
                                    <div>
                                      <span style={{ fontWeight:500 }}>{j.job_number}</span>
                                      <span style={{ color:'#7A7870', marginLeft:8 }}>{j.signing_type}</span>
                                    </div>
                                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                                      <span style={{ color:'#2D7A4F', fontWeight:500 }}>${j.total_fee}</span>
                                      <span className={`badge badge-${j.status}`} style={{ fontSize:10 }}>{statusLabel(j.status)}</span>
                                    </div>
                                  </div>
                                ))
                              }
                            </div>
                          </div>

                          {/* CUSTOM DEFAULT FEES */}
                          <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #E8E6DF' }}>
                            <div style={{ fontSize:11, fontWeight:600, color:'#7A7870', letterSpacing:'0.06em', marginBottom:10 }}>💰 CUSTOM DEFAULT FEES</div>
                            <div style={{ fontSize:11, color:'#B8B5AB', marginBottom:10 }}>Set custom pricing for this client — overrides standard rates for their orders!!</div>
                            <ClientCustomFees clientId={c.id} currentOverrides={c.default_fee_override} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {!loading && clients.length === 0 && <tr><td colSpan={7} style={{ textAlign:'center', color:'#B8B5AB', padding:24 }}>No clients yet</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ── CLIENT CUSTOM FEES COMPONENT ─────────────────────────────
function ClientCustomFees({ clientId, currentOverrides }) {
  const COMMON_SERVICES = [
    'Buyer Closing Package', 'Refinance Package', 'HELOC Package',
    'Reverse Mortgage', 'Loan Modification', 'Seller Closing Package',
  ]
  const [overrides, setOverrides] = useState(currentOverrides || {})
  const [saving, setSaving]       = useState(false)
  const [saved,  setSaved]        = useState(false)

  async function saveOverrides() {
    setSaving(true)
    try {
      await supabaseAdmin.from('profiles').update({ default_fee_override: overrides }).eq('id', clientId)
      setSaved(true)
      toast.success('Custom fees saved!! New orders from this client will use these rates!!')
      setTimeout(() => setSaved(false), 3000)
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
        {COMMON_SERVICES.map(svc => (
          <div key={svc} style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ fontSize:11, color:'#0D1B3E', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{svc}</div>
            <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
              <span style={{ fontSize:11, color:'#7A7870' }}>$</span>
              <input
                type="number"
                value={overrides[svc] || ''}
                onChange={e => { setOverrides(o => ({ ...o, [svc]: e.target.value ? parseInt(e.target.value) : undefined })); setSaved(false) }}
                placeholder="Default"
                style={{ width:70, padding:'4px 8px', border:'1px solid #E0D9CC', borderRadius:6, fontSize:11, textAlign:'right' }}
              />
            </div>
          </div>
        ))}
      </div>
      <button onClick={saveOverrides} disabled={saving || saved} className="btn btn-primary btn-sm" style={{ fontSize:11 }}>
        {saving ? 'Saving...' : saved ? '✓ Saved!!' : '💾 Save Custom Fees'}
      </button>
      <div style={{ fontSize:10, color:'#B8B5AB', marginTop:6 }}>Leave blank to use standard pricing. Custom fees apply to new orders only!!</div>
    </div>
  )
}

// ── NOTARY RATINGS PANEL (shown in admin notary details) ──────
function NotaryRatingsPanel({ notaryId }) {
  const [ratings, setRatings] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from('jobs')
      .select('client_rating, client_rating_comment, client_rated_at, borrower_name, job_number')
      .eq('notary_id', notaryId)
      .not('client_rating', 'is', null)
      .order('client_rated_at', { ascending:false })
      .limit(5)
      .then(({ data }) => { setRatings(data||[]); setLoading(false) })
  }, [notaryId])

  if (loading) return <div style={{ fontSize:11, color:'#B8B5AB' }}>Loading reviews...</div>
  if (ratings.length === 0) return <div style={{ fontSize:11, color:'#B8B5AB', fontStyle:'italic' }}>No reviews yet</div>

  return (
    <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:8 }}>
      {ratings.map((r, i) => (
        <div key={i} style={{ background:'#F8F7F4', borderRadius:8, padding:'8px 12px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
            <span style={{ color:'#C9A84C', fontSize:14 }}>{'★'.repeat(r.client_rating)}{'☆'.repeat(5-r.client_rating)}</span>
            <span style={{ fontSize:10, color:'#B8B5AB' }}>{r.job_number} · {r.client_rated_at ? new Date(r.client_rated_at).toLocaleString() : ''}</span>
          </div>
          {r.client_rating_comment && <div style={{ fontSize:11, color:'#4A4840', fontStyle:'italic' }}>"{r.client_rating_comment}"</div>}
          <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>— {r.borrower_name}</div>
        </div>
      ))}
    </div>
  )
}

// ── PRIVATE CLIENT RATINGS — admin eyes only!! ─────────────────
function PrivateClientRatings({ notaryId }) {
  const [ratings, setRatings] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from('jobs')
      .select('notary_rating, notary_rating_comment, notary_rated_at, borrower_name, company_name, job_number')
      .eq('notary_id', notaryId)
      .not('notary_rating', 'is', null)
      .order('notary_rated_at', { ascending:false })
      .limit(10)
      .then(({ data }) => { setRatings(data||[]); setLoading(false) })
  }, [notaryId])

  if (loading) return <div style={{ fontSize:11, color:'#B8B5AB' }}>Loading...</div>
  if (ratings.length === 0) return <div style={{ fontSize:11, color:'#B8B5AB', fontStyle:'italic' }}>No client feedback yet</div>

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {ratings.map((r, i) => (
        <div key={i} style={{ background:'#FFF8F0', border:'1px solid #F0E8D8', borderRadius:8, padding:'8px 12px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
            <span style={{ color: r.notary_rating >= 4 ? '#2D7A4F' : r.notary_rating >= 3 ? '#C9A84C' : '#E24B4A', fontSize:14, fontWeight:600 }}>
              {'★'.repeat(r.notary_rating)}{'☆'.repeat(5-r.notary_rating)}
            </span>
            <span style={{ fontSize:10, color:'#B8B5AB' }}>{r.job_number} · {r.notary_rated_at ? new Date(r.notary_rated_at).toLocaleString() : ''}</span>
          </div>
          {r.notary_rating_comment && <div style={{ fontSize:11, color:'#4A4840', fontStyle:'italic' }}>"{r.notary_rating_comment}"</div>}
          <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>Client: {r.borrower_name || r.company_name || '—'}</div>
          {r.notary_rating <= 2 && (
            <div style={{ fontSize:10, color:'#E24B4A', fontWeight:600, marginTop:4 }}>⚠️ Low rating — follow up recommended!!</div>
          )}
        </div>
      ))}
    </div>
  )
}

// 📄 PAY STUB MODAL — branded notary payout statement + Download PDF + Print
function PayStubModal({ job, onClose }) {
  const docRef = useRef(null)
  const [busy, setBusy] = useState(false)

  const fmtDateTime = (v) => {
    if (!v) return '—'
    const d = new Date(v)
    if (isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })
      + ' · ' + d.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' })
  }
  const money = (n) => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD' })

  const notary  = job.profiles || {}
  const vendor  = notary.notaries?.vendor_number
  const paid    = !!job.notary_paid_at
  const signerLoc = [job.signing_city, job.signing_state].filter(Boolean).join(', ')
  const safeNo  = (job.job_number || 'paystub').replace(/[^a-z0-9]+/gi, '-')

  async function downloadPDF() {
    setBusy(true)
    try {
      await ensurePdfLibs()
      const canvas = await window.html2canvas(docRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' })
      const imgData = canvas.toDataURL('image/png')
      const { jsPDF } = window.jspdf
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
      const pageW = pdf.internal.pageSize.getWidth()
      const pageH = pdf.internal.pageSize.getHeight()
      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height)
      const w = canvas.width * ratio, h = canvas.height * ratio
      pdf.addImage(imgData, 'PNG', (pageW - w) / 2, 20, w, h)
      pdf.save(`LCA-PayStub-${safeNo}.pdf`)
    } catch (err) {
      toast.error('PDF generation failed: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  async function printRecord() {
    setBusy(true)
    try {
      await ensurePdfLibs()
      const canvas = await window.html2canvas(docRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' })
      const imgData = canvas.toDataURL('image/png')
      const win = window.open('', '_blank')
      if (!win) { toast.error('Please allow pop-ups to print.'); return }
      win.document.write(`<html><head><title>LCA Pay Stub ${job.job_number || ''}</title></head><body style="margin:0;text-align:center;"><img src="${imgData}" style="width:100%;max-width:760px;" onload="window.focus();window.print();" /></body></html>`)
      win.document.close()
    } catch (err) {
      toast.error('Print failed: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:3000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:20, overflowY:'auto' }}>
      <div style={{ display:'flex', gap:10, marginBottom:16, position:'sticky', top:0, zIndex:5 }}>
        <button onClick={downloadPDF} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'linear-gradient(135deg,#C9A84C,#E8C46A)', color:'#0D1B3E', border:'none', cursor: busy?'wait':'pointer', fontWeight:700, fontSize:13, opacity: busy?0.7:1 }}>
          {busy ? '⏳ Working…' : '⬇️ Download PDF'}
        </button>
        <button onClick={printRecord} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'#fff', color:'#0D1B3E', border:'1px solid #C9A84C', cursor: busy?'wait':'pointer', fontWeight:700, fontSize:13, opacity: busy?0.7:1 }}>
          🖨️ Print
        </button>
        <button onClick={onClose} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'rgba(255,255,255,0.1)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', cursor:'pointer', fontSize:13 }}>
          ✕ Close
        </button>
      </div>

      {/* PAY STUB DOCUMENT */}
      <div ref={docRef} style={{ width:720, maxWidth:'100%', background:'#fff', boxShadow:'0 18px 50px rgba(13,27,62,0.25)' }}>
        <div style={{ border:'12px solid #0D1B3E', position:'relative', padding:'38px 42px' }}>
          <div style={{ position:'absolute', inset:8, border:'3px solid #C9A84C', pointerEvents:'none' }} />

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:20 }}>
            <div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:13, fontWeight:600, color:'#C9A84C', letterSpacing:'0.18em', textTransform:'uppercase' }}>Loan Closing Agent, LLC. DBA</div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:21, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.03em' }}>Nationwide Notary Professionals Network</div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:30, fontWeight:700, color:'#0D1B3E', lineHeight:1 }}>PAY STUB</div>
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0 20px' }}>
            <div style={{ flex:1, height:2, background:'linear-gradient(90deg,#0D1B3E,#C9A84C)' }} />
            <div style={{ color:'#C9A84C', fontSize:13 }}>✦</div>
            <div style={{ flex:1, height:2, background:'linear-gradient(90deg,#C9A84C,#0D1B3E)' }} />
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', gap:24, marginBottom:24 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#C9A84C', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Pay To</div>
              <div style={{ fontSize:15, fontWeight:700, color:'#0D1B3E' }}>{notary.full_name || 'Notary'}</div>
              {vendor       && <div style={{ fontSize:13, color:'#2C2C2C' }}>Vendor #: {vendor}</div>}
              {notary.phone && <div style={{ fontSize:13, color:'#2C2C2C' }}>{notary.phone}</div>}
              {notary.email && <div style={{ fontSize:13, color:'#1F5C99' }}>{notary.email}</div>}
            </div>
            <div style={{ flex:1, textAlign:'right' }}>
              <table style={{ marginLeft:'auto', fontSize:12, color:'#2C2C2C', borderCollapse:'collapse' }}>
                <tbody>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Stub #</td><td style={{ padding:'2px 0', fontWeight:700, color:'#0D1B3E', fontFamily:'Courier New, monospace' }}>{job.job_number || '—'}</td></tr>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Payment Date</td><td style={{ padding:'2px 0', color:'#0D1B3E' }}>{paid ? fmtDateTime(job.notary_paid_at) : 'Pending'}</td></tr>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Service Date</td><td style={{ padding:'2px 0', color:'#0D1B3E' }}>{fmtDateTime(job.scheduled_at)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:6 }}>
            <thead>
              <tr style={{ background:'#0D1B3E' }}>
                <th style={{ textAlign:'left', color:'#fff', fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', padding:'9px 12px' }}>Description</th>
                <th style={{ textAlign:'right', color:'#fff', fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', padding:'9px 12px', width:120 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom:'1px solid #EDEAE2' }}>
                <td style={{ padding:'13px 12px', fontSize:13, color:'#2C2C2C', verticalAlign:'top' }}>
                  <div style={{ fontWeight:600, color:'#0D1B3E' }}>Notary Signing Service{job.signing_type ? ` — ${job.signing_type}` : ''}</div>
                  <div style={{ fontSize:11.5, color:'#7A7870', marginTop:3 }}>{job.borrower_name ? `Signer: ${job.borrower_name}` : ''}{job.borrower_name && signerLoc ? '  ·  ' : ''}{signerLoc}</div>
                  <div style={{ fontSize:11.5, color:'#7A7870', marginTop:2 }}>Job #: <span style={{ fontWeight:600, color:'#0D1B3E' }}>{job.job_number}</span></div>
                  {(job.file_number || job.client_po) && <div style={{ fontSize:11.5, color:'#7A7870', marginTop:2 }}>Client File #: <span style={{ fontWeight:600, color:'#0D1B3E' }}>{job.file_number || job.client_po}</span></div>}
                </td>
                <td style={{ padding:'13px 12px', fontSize:14, color:'#0D1B3E', textAlign:'right', fontWeight:600, verticalAlign:'top' }}>{money(job.notary_pay)}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:20, marginTop:14 }}>
            <div style={{ flex:1, fontSize:11, color:'#7A7870', fontStyle:'italic', lineHeight:1.5, maxWidth:320 }}>
              Payment for notary services rendered, paid via Direct Deposit. Thank you for being part of our network.
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:11, color:'#7A7870', letterSpacing:'0.08em', textTransform:'uppercase' }}>Total Paid</div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:30, fontWeight:700, color:'#0D1B3E', lineHeight:1.1 }}>{money(job.notary_pay)}</div>
              {paid
                ? <div style={{ display:'inline-block', marginTop:6, padding:'4px 14px', borderRadius:20, background:'#D7F0DE', color:'#2D7A4F', fontSize:11, fontWeight:700, letterSpacing:'0.05em' }}>PAID · {fmtDateTime(job.notary_paid_at)}</div>
                : <div style={{ display:'inline-block', marginTop:6, padding:'4px 14px', borderRadius:20, background:'#FCE9C8', color:'#9A7A30', fontSize:11, fontWeight:700, letterSpacing:'0.05em' }}>PAYMENT PENDING</div>}
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'24px 0 16px' }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,transparent,#C9A84C)' }} />
            <div style={{ color:'#C9A84C', fontSize:11 }}>✦ ✦ ✦</div>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,#C9A84C,transparent)' }} />
          </div>

          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:12, color:'#2C2C2C', marginBottom:4 }}>Issued by <span style={{ fontWeight:600, color:'#0D1B3E' }}>Loan Closing Agent, LLC.</span></div>
            <div style={{ fontSize:11, color:'#7A7870' }}>7946 Meadow Walk Ln.  ·  Jacksonville, FL 32256</div>
            <div style={{ fontSize:11, color:'#7A7870' }}>Questions? bookings@loanclosingagent.com  ·  904-865-0133</div>
          </div>
        </div>
      </div>
      <div style={{ height:30 }} />
    </div>
  )
}

function Payouts() {
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [filter,  setFilter]  = useState('unpaid')
  const [saving,  setSaving]  = useState(null)
  const [stubJob, setStubJob] = useState(null)

  useEffect(() => { loadJobs() }, [])

  async function loadJobs() {
    const { data } = await supabase
      .from('jobs')
      .select('*, profiles!jobs_notary_id_fkey(full_name, email, phone, notaries(vendor_number))')
      .in('status', ['completed', 'scanbacks_uploaded', 'client_approved', 'closed'])
      .not('notary_id','is',null)
      .order('created_at', { ascending:false })
    setJobs(safeParseJobs(data))
    setLoading(false)
  }

  async function markPaid(job) {
    // 🛡️ DATA INTEGRITY GATE — extra safety check!!
    if (!job.notary_id) {
      toast.error('🛡️ Cannot mark as paid — no notary is assigned to this job!!', { duration: 6000 })
      return
    }
    setSaving(job.id)
    try {
      await supabase.from('jobs').update({ notary_paid_at: new Date().toISOString() }).eq('id', job.id)
      await supabase.from('payouts').insert({ notary_id:job.notary_id, job_id:job.id, amount:job.notary_pay, method:'Direct Deposit', status:'sent', sent_at:new Date().toISOString() })
      if (job.profiles?.email) {
        await sendPaymentSent({ notary_name:job.profiles.full_name, notary_email:job.profiles.email, job_number:job.job_number, service_name:job.signing_type || 'Notary Signing', amount:job.notary_pay, payment_method:'Direct Deposit' })
      }
      toast.success(`Payment of $${job.notary_pay} sent to ${job.profiles?.full_name||'notary'} — they've been emailed!`)
      loadJobs()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(null)
    }
  }

  const filtered    = jobs.filter(j => filter==='all' ? true : filter==='unpaid' ? !j.notary_paid_at : !!j.notary_paid_at)
  const totalUnpaid = jobs.filter(j => !j.notary_paid_at).reduce((s,j) => s+(j.notary_pay||0), 0)
  const totalPaid   = jobs.filter(j => !!j.notary_paid_at).reduce((s,j) => s+(j.notary_pay||0), 0)
  const totalLCA    = jobs.filter(j => !!j.notary_paid_at).reduce((s,j) => s+(j.lca_cut||0), 0)

  return (
    <>
      <h1 className="page-title">Notary Payouts</h1>
      <p className="page-sub">Track and manage payments to your notary network</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        <div className="stat-card"><div className="stat-label">Unpaid Balance</div><div className="stat-value" style={{ color:'#E24B4A' }}>${totalUnpaid.toLocaleString()}</div><div className="stat-sub">{jobs.filter(j=>!j.notary_paid_at).length} jobs pending</div></div>
        <div className="stat-card"><div className="stat-label">Total Paid Out</div><div className="stat-value" style={{ color:'#2D7A4F' }}>${totalPaid.toLocaleString()}</div><div className="stat-sub">{jobs.filter(j=>!!j.notary_paid_at).length} jobs paid</div></div>
        <div className="stat-card"><div className="stat-label">LCA Revenue</div><div className="stat-value gold">${totalLCA.toLocaleString()}</div><div className="stat-sub">from completed jobs</div></div>
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {[{ key:'unpaid', label:'Needs Payment', color:'#E24B4A' }, { key:'paid', label:'Paid', color:'#2D7A4F' }, { key:'all', label:'All', color:'#7A7870' }].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ padding:'6px 16px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer', border:`1px solid ${filter===f.key?f.color:'#E8E6DF'}`, background:filter===f.key?f.color:'transparent', color:filter===f.key?'#fff':'#7A7870', transition:'all 0.15s' }}>{f.label}</button>
        ))}
      </div>
      <div className="section-card" style={{ padding:0 }}>
        <table className="data-table">
          <thead><tr><th>Job #</th><th>Notary</th><th>Service</th><th>Date</th><th>Client Paid</th><th>Notary Pay</th><th>LCA Cut</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ textAlign:'center', padding:24 }}><div className="spinner" style={{ margin:'auto' }} /></td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={9} style={{ textAlign:'center', color:'#B8B5AB', padding:24 }}>{filter==='unpaid'?'No pending payments — all notaries are paid up!':'No records found'}</td></tr>}
            {filtered.map(j => (
              <tr key={j.id} style={{ background:!j.notary_paid_at?'rgba(226,75,74,0.03)':'transparent' }}>
                <td style={{ fontWeight:500 }}>{j.job_number}</td>
                <td><div style={{ fontWeight:500 }}>{j.profiles?.full_name||'—'}</div><VendorBadge number={j.profiles?.notaries?.vendor_number} /><div style={{ fontSize:11, color:'#7A7870' }}>{j.profiles?.email||''}</div></td>
                <td style={{ fontSize:12 }}>{j.signing_type}</td>
                <td style={{ fontSize:12 }}>{j.scheduled_at?new Date(j.scheduled_at).toLocaleString():'—'}</td>
                <td style={{ color:'#0D1B3E', fontWeight:500 }}>${j.total_fee||0}</td>
                <td style={{ color:'#2D7A4F', fontWeight:600 }}>${j.notary_pay||0}</td>
                <td style={{ color:'#C9A84C', fontWeight:500 }}>${j.lca_cut||0}</td>
                <td>{!j.notary_paid_at?<span className="badge badge-pending">Unpaid</span>:<span className="badge badge-approved">Paid</span>}</td>
                <td>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                  <button onClick={() => setStubJob(j)} style={{ background:'#fff', color:'#9A7A30', border:'1px solid #C9A84C', borderRadius:6, padding:'5px 12px', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>📄 Pay Stub</button>
                  {!j.notary_paid_at && <button className="btn btn-primary btn-sm" onClick={() => markPaid(j)} disabled={saving===j.id}>{saving===j.id?'...':`Pay $${j.notary_pay}`}</button>}
                  {!!j.notary_paid_at && <span style={{ fontSize:11, color:'#2D7A4F' }}>✓ Sent</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {stubJob && <PayStubModal job={stubJob} onClose={() => setStubJob(null)} />}
    </>
  )
}

// ── ADMIN MFA SECTION ─────────────────────────────────────────
function AdminMFASection() {
  const [factors,    setFactors]    = useState([])
  const [loading,    setLoading]    = useState(true)
  const [enrolling,  setEnrolling]  = useState(false)
  const [qrCode,     setQrCode]     = useState(null)
  const [secret,     setSecret]     = useState(null)
  const [factorId,   setFactorId]   = useState(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [verifying,  setVerifying]  = useState(false)

  useEffect(() => { loadFactors() }, [])

  async function loadFactors() {
    try {
      const { data } = await supabase.auth.mfa.listFactors()
      setFactors(data?.totp || [])
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  async function startEnroll() {
    setEnrolling(true)
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType:'totp', issuer:'LCA Admin', friendlyName:'LCA Admin Authenticator' })
      if (error) throw error
      setQrCode(data.totp.qr_code)
      setSecret(data.totp.secret)
      setFactorId(data.id)
    } catch (err) { toast.error(err.message) }
    finally { setEnrolling(false) }
  }

  async function verifyEnroll() {
    setVerifying(true)
    try {
      const { data: challengeData } = await supabase.auth.mfa.challenge({ factorId })
      const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: challengeData.id, code: verifyCode.trim() })
      if (error) throw error
      toast.success('🔐 Admin 2FA enabled successfully!!')
      setQrCode(null); setSecret(null); setFactorId(null); setVerifyCode('')
      loadFactors()
    } catch (err) { toast.error('Invalid code — please try again!!') }
    finally { setVerifying(false) }
  }

  const isEnabled = factors.some(f => f.status === 'verified')

  return (
    <div className="section-card" style={{ marginTop:16 }}>
      <div className="section-header">
        <span className="section-title">🔐 Admin Two-Factor Authentication</span>
        {isEnabled && <span style={{ fontSize:11, background:'#EAF5EE', color:'#2D7A4F', padding:'3px 10px', borderRadius:10, fontWeight:600 }}>✓ Enabled</span>}
      </div>
      <div className="section-body">
        {loading && <div style={{ color:'#B8B5AB', fontSize:13 }}>Loading...</div>}
        {!loading && !isEnabled && !qrCode && (
          <div>
            <p style={{ fontSize:13, color:'#7A7870', marginBottom:16 }}>Protect the admin portal with Two-Factor Authentication!! Required for ALTA compliance!!</p>
            <button onClick={startEnroll} disabled={enrolling} className="btn btn-primary">{enrolling ? 'Setting up...' : '🔐 Enable 2FA'}</button>
          </div>
        )}
        {!loading && qrCode && (
          <div>
            <p style={{ fontSize:13, color:'#7A7870', marginBottom:12 }}>Scan with Google Authenticator or Authy!!</p>
            <img src={qrCode} alt="QR Code" style={{ width:180, height:180, border:'2px solid #E8E6DF', borderRadius:8, marginBottom:12 }} />
            <div style={{ fontSize:11, color:'#7A7870', marginBottom:12 }}>Manual code: <code style={{ background:'#F8F7F4', padding:'2px 6px', borderRadius:4 }}>{secret}</code></div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input type="text" inputMode="numeric" maxLength={6} value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g,''))} placeholder="000000" style={{ width:130, padding:'8px 12px', border:'2px solid #E0D9CC', borderRadius:8, fontSize:18, textAlign:'center', letterSpacing:'0.2em', fontFamily:'monospace' }} />
              <button onClick={verifyEnroll} disabled={verifying || verifyCode.length !== 6} className="btn btn-primary">{verifying ? 'Verifying...' : 'Verify'}</button>
              <button onClick={() => { setQrCode(null); setSecret(null); setFactorId(null) }} className="btn btn-outline">Cancel</button>
            </div>
          </div>
        )}
        {!loading && isEnabled && (
          <p style={{ fontSize:13, color:'#2D7A4F' }}>✅ Admin account protected with Two-Factor Authentication!!</p>
        )}
      </div>
    </div>
  )
}

function Settings() {
  const { profile } = useAuth()
  const [saving, setSaving] = useState(false)
  const [phone,  setPhone]  = useState('(904) 865-0133')
  const [email,  setEmail]  = useState('nancy@loanclosingagent.com')
  // ⚖️ Legal & Compliance fields (ALTA-ready!)
  const [privacyUrl,  setPrivacyUrl]  = useState('')
  const [termsUrl,    setTermsUrl]    = useState('')
  const [cookieUrl,   setCookieUrl]   = useState('')

  useEffect(() => {
    if (profile?.privacy_policy_url) setPrivacyUrl(profile.privacy_policy_url)
    if (profile?.terms_of_service_url) setTermsUrl(profile.terms_of_service_url)
    if (profile?.cookie_policy_url)   setCookieUrl(profile.cookie_policy_url)
  }, [profile])

  async function save(e) {
    e.preventDefault()
    setSaving(true)
    try {
      await supabase.from('profiles').update({
        phone,
        privacy_policy_url:   privacyUrl   || null,
        terms_of_service_url: termsUrl     || null,
        cookie_policy_url:    cookieUrl    || null,
      }).eq('id', profile?.id)
      toast.success('Settings saved!!')
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <h1 className="page-title">Settings</h1>
      <div className="section-card">
        <div className="section-header"><span className="section-title">Platform Settings</span></div>
        <div className="section-body">
          <form onSubmit={save} noValidate>
            <div className="form-grid">
              <div className="form-group full"><label className="form-label">Admin email</label><input value={profile?.email||''} readOnly style={{ color:'#B8B5AB' }} /></div>
              <div className="form-group"><label className="form-label">LCA flat fee per signing</label><input value="$25.00" readOnly style={{ color:'#9A7A30', fontWeight:500 }} /></div>
              <div className="form-group"><label className="form-label">Business phone</label><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(904) 865-0133" /></div>
              <div className="form-group"><label className="form-label">Business contact email</label><input value={email} onChange={e => setEmail(e.target.value)} placeholder="nancy@loanclosingagent.com" /></div>
              <div className="form-group full">
                <label className="form-label">Stripe payments</label>
                <input placeholder="Configure in Netlify Environment Variables" readOnly style={{ color:'#B8B5AB' }} />
                <p style={{ fontSize:11, color:'#B8B5AB', marginTop:4 }}>Stripe key is stored securely in Netlify — never paste keys here!!</p>
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ marginTop:16 }}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </form>
        </div>
      </div>

      {/* ⚖️ LEGAL & COMPLIANCE — ALTA-ready fields!! */}
      <div className="section-card" style={{ marginTop:24 }}>
        <div className="section-header" style={{ borderTop:'4px solid #C9A84C' }}>
          <span className="section-title">⚖️ Legal &amp; Compliance</span>
          <span style={{ fontSize:11, color:'#B8B5AB' }}>ALTA-ready · Required for vendor approvals</span>
        </div>
        <div className="section-body">
          <p style={{ fontSize:12, color:'#7A7870', marginBottom:16, lineHeight:1.6 }}>
            Title Companies and government agencies often require these documents for vendor onboarding.
            Paste full URLs (e.g. <span style={{ color:'#9A7A30' }}>https://loanclosingagent.com/privacy</span>).
            These links will appear in email footers and platform pages.
          </p>
          <form onSubmit={save} noValidate>
            <div className="form-grid">
              <div className="form-group full">
                <label className="form-label">Privacy Policy URL</label>
                <input value={privacyUrl} onChange={e => setPrivacyUrl(e.target.value)} placeholder="https://loanclosingagent.com/privacy" type="url" />
                <p style={{ fontSize:11, color:'#B8B5AB', marginTop:4 }}>How LCA collects, stores, and protects user data — required for ALTA compliance.</p>
              </div>
              <div className="form-group full">
                <label className="form-label">Terms of Service URL</label>
                <input value={termsUrl} onChange={e => setTermsUrl(e.target.value)} placeholder="https://loanclosingagent.com/terms" type="url" />
                <p style={{ fontSize:11, color:'#B8B5AB', marginTop:4 }}>Service rules, limitations of liability, and user agreements — required for client onboarding.</p>
              </div>
              <div className="form-group full">
                <label className="form-label">Cookie Policy URL <span style={{ fontSize:11, color:'#B8B5AB', fontWeight:400 }}>(optional)</span></label>
                <input value={cookieUrl} onChange={e => setCookieUrl(e.target.value)} placeholder="https://loanclosingagent.com/cookies" type="url" />
                <p style={{ fontSize:11, color:'#B8B5AB', marginTop:4 }}>Required if the EU/CA users access your platform (GDPR, CCPA compliance).</p>
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ marginTop:16 }}>
              {saving ? 'Saving...' : 'Save Legal Settings'}
            </button>
          </form>
        </div>
      </div>

      <AdminMFASection />
    </>
  )
}

// COVERAGE MAP — dual view (notaries + companies) on a real US map
const US_GEO = [
  {id:"AK",name:"Alaska",shape:"M158.07671,453.67502L157.75339,539.03215L159.36999,540.00211L162.44156,540.16377L163.8965,539.03215L166.48308,539.03215L166.64475,541.94205L173.59618,548.73182L174.08117,551.3184L177.47605,549.37846L178.1227,549.2168L178.44602,546.14524L179.90096,544.52863L181.0326,544.36697L182.97253,542.91201L186.04409,545.01361L186.69074,547.92352L188.63067,549.05514L189.7623,551.48006L193.64218,553.25833L197.03706,559.2398L199.78529,563.11966L202.04855,565.86791L203.50351,569.58611L208.515,571.36439L213.68817,573.46598L214.65813,577.83084L215.14311,580.9024L214.17315,584.29729L212.39487,586.56054L210.77826,585.75224L209.32331,582.68067L206.57507,581.22573L204.7968,580.09409L203.98849,580.9024L205.44344,583.65065L205.6051,587.36885L204.47347,587.85383L202.53354,585.9139L200.43195,584.62061L200.91693,586.23722L202.21021,588.0155L201.40191,588.8238C201.40191,588.8238,200.59361,588.50048,200.10863,587.85383C199.62363,587.20719,198.00703,584.45895,198.00703,584.45895L197.03706,582.19569C197.03706,582.19569,196.71374,583.48898,196.06709,583.16565C195.42044,582.84233,194.7738,581.71071,194.7738,581.71071L196.55207,579.77077L195.09712,578.31582L195.09712,573.30432L194.28882,573.30432L193.48052,576.6992L192.34888,577.1842L191.37892,573.46598L190.73227,569.74777L189.92396,569.26279L190.24729,574.92094L190.24729,576.05256L188.79233,574.75928L185.23579,568.77781L183.13419,568.29283L182.48755,564.57462L180.87094,561.66472L179.25432,560.53308L179.25432,558.26983L181.35592,556.97654L180.87094,556.65322L178.28436,557.29986L174.88947,554.87495L172.30289,551.96504L167.45306,549.37846L163.41152,546.79188L164.70482,543.55866L164.70482,541.94205L162.92654,543.55866L160.01664,544.69029L156.29843,543.55866L150.64028,541.13375L145.14381,541.13375L144.49717,541.61873L138.03072,537.73885L135.92912,537.41553L133.18088,531.59573L129.62433,531.91905L126.06778,533.374L126.55277,537.90052L127.68439,534.99062L128.65437,535.31394L127.19941,539.67879L130.43263,536.93055L131.07928,538.54716L127.19941,542.91201L125.90612,542.58869L125.42114,540.64875L124.12785,539.84045L122.83456,540.97208L120.08632,539.19381L117.01475,541.29541L115.23649,543.397L111.8416,545.4986L107.15342,545.33693L106.66844,543.23534L110.38664,542.58869L110.38664,541.29541L108.12338,540.64875L109.09336,538.22384L111.35661,534.34397L111.35661,532.5657L111.51827,531.75739L115.88313,529.49413L116.85309,530.78742L119.60134,530.78742L118.30805,528.20085L114.58983,527.87752L109.57834,530.62576L107.15342,534.02064L105.37515,536.60723L104.24352,538.87049L100.04033,540.32543L96.96876,542.91201L96.645439,544.52863L98.908696,545.4986L99.717009,547.60018L96.96876,550.83341L90.502321,555.03661L82.742574,559.2398L80.640977,560.37142L75.306159,561.50306L69.971333,563.76631L71.749608,565.0596L70.294654,566.51455L69.809672,567.64618L67.061434,566.67621L63.828214,566.83787L63.019902,569.10113L62.049939,569.10113L62.37326,566.67621L58.816709,567.96951L55.90681,568.93947L52.511924,567.64618L49.602023,569.58611L46.368799,569.58611L44.267202,570.87941L42.65059,571.68771L40.548995,571.36439L37.962415,570.23276L35.699158,570.87941L34.729191,571.84937L33.112578,570.71775L33.112578,568.77781L36.184142,567.48452L42.488929,568.13117L46.853782,566.51455L48.955378,564.41296L51.86528,563.76631L53.643553,562.958L56.391794,563.11966L58.008406,564.41296L58.978369,564.08964L61.241626,561.3414L64.313196,560.37142L67.708076,559.72478L69.00137,559.40146L69.648012,559.88644L70.456324,559.88644L71.749608,556.16823L75.791141,554.71329L77.731077,550.99508L79.994336,546.46856L81.610951,545.01361L81.934272,542.42703L80.317657,543.72032L76.922764,544.36697L76.276122,541.94205L74.982838,541.61873L74.012865,542.58869L73.851205,545.4986L72.39625,545.33693L70.941306,539.51713L69.648012,540.81041L68.516388,540.32543L68.193068,538.3855L64.151535,538.54716L62.049939,539.67879L59.463361,539.35547L60.918305,537.90052L61.403286,535.31394L60.756645,533.374L62.211599,532.40404L63.504883,532.24238L62.858241,530.4641L62.858241,526.09925L61.888278,525.12928L61.079966,526.58423L54.936843,526.58423L53.481892,525.29094L52.835247,521.41108L50.733651,517.85452L50.733651,516.88456L52.835247,516.07625L52.996908,513.97465L54.128536,512.84303L53.320231,512.35805L52.026941,512.84303L50.895313,510.09479L51.86528,505.08328L56.391794,501.85007L58.978369,500.23345L60.918305,496.51525L63.666554,495.22195L66.253132,496.35359L66.576453,498.77851L69.00137,498.45517L72.23459,496.03026L73.851205,496.67691L74.821167,497.32355L76.437782,497.32355L78.701041,496.03026L79.509354,491.6654C79.509354,491.6654,79.832675,488.75551,80.479317,488.27052C81.125959,487.78554,81.44928,487.30056,81.44928,487.30056L80.317657,485.36062L77.731077,486.16893L74.497847,486.97723L72.557911,486.49225L69.00137,484.71397L63.989875,484.55231L60.433324,480.83411L60.918305,476.95424L61.564957,474.52932L59.463361,472.75105L57.523423,469.03283L58.008406,468.22453L64.798177,467.73955L66.899773,467.73955L67.869736,468.70951L68.516388,468.70951L68.354728,467.0929L72.23459,466.44626L74.821167,466.76958L76.276122,467.90121L74.821167,470.00281L74.336186,471.45775L77.084435,473.07437L82.095932,474.85264L83.874208,473.88268L81.610951,469.51783L80.640977,466.2846L81.610951,465.47629L78.21606,463.53636L77.731077,462.40472L78.21606,460.78812L77.407756,456.90825L74.497847,452.22007L72.072929,448.01688L74.982838,446.07694L78.21606,446.07694L79.994336,446.72359L84.197528,446.56193L87.915733,443.00539L89.047366,439.93382L92.765578,437.5089L94.382182,438.47887L97.130421,437.83222L100.84863,435.73062L101.98027,435.56896L102.95023,436.37728L107.47674,436.21561L110.22498,433.14405L111.35661,433.14405L114.91316,435.56896L116.85309,437.67056L116.36811,438.80219L117.01475,439.93382L118.63137,438.31721L122.51124,438.64053L122.83456,442.35873L124.7745,443.81369L131.88759,444.46033L138.19238,448.66352L139.64732,447.69356L144.82049,450.28014L146.92208,449.6335L148.86202,448.82518L153.71185,450.76512L158.07671,453.67502ZM42.973913,482.61238L45.075509,487.9472L44.913847,488.91717L42.003945,488.59384L40.225672,484.55231L38.447399,483.09737L36.02248,483.09737L35.86082,480.51078L37.639093,478.08586L38.770722,480.51078L40.225672,481.96573L42.973913,482.61238ZM40.387333,516.07625L44.105542,516.88456L47.823749,517.85452L48.632056,518.8245L47.015444,522.5427L43.94388,522.38104L40.548995,518.8245L40.387333,516.07625ZM19.694697,502.01173L20.826327,504.5983L21.957955,506.21492L20.826327,507.02322L18.72473,503.95166L18.72473,502.01173L19.694697,502.01173ZM5.9534943,575.0826L9.3483796,572.81934L12.743265,571.84937L15.329845,572.17269L15.814828,573.7893L17.754763,574.27429L19.694697,572.33436L19.371375,570.71775L22.119616,570.0711L25.029518,572.65768L23.897889,574.43595L19.533037,575.56758L16.784795,575.0826L13.066588,573.95097L8.7017347,575.40592L7.0851227,575.72924L5.9534943,575.0826ZM54.936843,570.55609L56.553455,572.49602L58.655048,570.87941L57.2001,569.58611L54.936843,570.55609ZM57.846745,573.62764L58.978369,571.36439L61.079966,571.68771L60.271663,573.62764L57.846745,573.62764ZM81.44928,571.68771L82.904234,573.46598L83.874208,572.33436L83.065895,570.39442L81.44928,571.68771ZM90.17899,559.2398L91.310623,565.0596L94.220522,565.86791L99.232017,562.958L103.59687,560.37142L101.98027,557.94651L102.46525,555.52159L100.36365,556.81488L97.453752,556.00657L99.070357,554.87495L101.01029,555.68325L104.89016,553.90497L105.37515,552.45003L102.95023,551.64172L103.75853,549.70178L101.01029,551.64172L96.322118,555.19827L91.472284,558.10817L90.17899,559.2398ZM132.53423,539.35547L134.95915,537.90052L133.98918,536.12224L132.21091,537.09221L132.53423,539.35547Z"},
  {id:"AL",name:"Alabama",shape:"M631.30647,460.41572L629.81587,446.09422L627.06763,427.34158L627.22929,413.27709L628.03759,382.23824L627.87593,365.58718L628.04102,359.16812L672.5255,355.54867L672.3777,357.73109L672.53936,359.83269L673.18601,363.22756L676.58089,371.14893L679.00579,381.01024L680.46074,387.15335L682.07734,392.00317L683.5323,398.95458L685.63388,405.25934L688.22045,408.65423L688.70543,412.04909L690.64537,412.8574L690.80703,414.95899L689.02875,419.80881L688.54377,423.04203L688.38211,424.98195L689.99873,429.3468L690.32205,434.68159L689.51373,437.10651L690.16039,437.91481L691.61533,438.72311L691.94347,441.61193L686.34581,441.25838L679.55606,441.90503L654.01366,444.81491L643.6021,446.22168L643.38072,449.09908L645.15899,450.87735L647.74556,452.81727L648.32642,460.75271L642.78436,463.32561L640.03614,463.00229L642.78436,461.06236L642.78436,460.0924L639.71282,454.11096L637.44957,453.46432L635.99462,457.82915L634.70134,460.57738L634.0547,460.41572L631.30647,460.41572Z"},
  {id:"AR",name:"Arkansas",shape:"M593.82477,343.05296L589.84489,343.76966L584.73274,343.13563L585.15344,341.53356L588.13319,338.96687L589.07657,335.31062L587.24759,332.33852L508.83002,334.85337L510.43038,341.71206L510.43037,349.94248L511.80212,360.91647L512.03074,398.7534L514.31697,400.69669L517.28906,399.32496L520.03254,400.46807L520.71288,407.04137L576.33414,405.90077L577.47977,403.8104L577.19315,400.26089L575.36752,397.28879L576.96621,395.80358L575.36752,393.29208L576.05172,390.78225L577.42011,385.17682L579.9383,383.11419L579.25243,380.82963L582.9104,375.45784L585.65387,374.08945L585.54039,372.59587L585.19495,370.77023L588.0519,365.1715L590.45494,363.91491L590.83907,360.48728L592.60974,359.24558L589.46622,358.76131L588.12476,354.75087L590.92884,352.37416L591.4791,350.35496L592.75858,346.30835L593.82477,343.05296Z"},
  {id:"AZ",name:"Arizona",shape:"M144.9112,382.62909L142.28419,384.78742L141.96087,386.24237L142.44585,387.21233L161.36012,397.88192L173.48466,405.47996L188.19576,414.04797L205.00845,424.07092L217.29465,426.49583L242.24581,429.20074L259.50142,310.07367L175.76579,298.15642L172.6734,314.56888L171.06711,314.58419L169.35244,317.21335L166.83759,317.09903L165.58017,314.35556L162.8367,314.01263L161.9222,312.86952L161.00772,312.86952L160.09322,313.44108L158.14993,314.46988L158.03563,321.44286L157.80699,323.15753L157.23545,335.73177L155.7494,337.90368L155.17784,341.21871L157.92131,346.1341L159.17873,351.96398L159.97892,352.99278L161.00772,353.56434L160.8934,355.85056L159.29305,357.22229L155.86371,358.93696L153.92042,360.88026L152.43437,364.53821L151.86281,369.4536L149.00503,372.19707L146.94743,372.88294L147.08312,373.71282L146.62587,375.42749L147.08312,376.22767L150.74108,376.79921L150.16952,379.54269L148.68347,381.7146L144.9112,382.62909Z"},
  {id:"CA",name:"California",shape:"M144.69443,382.19813L148.63451,381.70951L150.12055,379.69807L150.66509,376.75698L147.11357,376.16686L146.5994,375.49864L147.0769,373.46633L146.91762,372.87666L148.84019,372.25707L151.88297,369.42439L152.46453,364.42929L153.84443,361.02718L155.78772,358.86092L159.30659,357.27125L160.96098,355.66642L161.02971,353.55758L160.03638,352.97757L159.01323,351.90484L157.85801,346.05639L155.17281,341.2263L155.73862,337.7213L153.31904,336.69199L84.257718,232.51359L103.15983,164.9121L36.079967,149.21414L34.573071,153.94738L34.41141,161.38376L29.238275,173.18497L26.166727,175.77154L25.843406,176.90316L24.06514,177.71147L22.610196,181.91464L21.801894,185.14785L24.550122,189.35102L26.166727,193.55419L27.29835,197.11072L26.975029,203.57714L25.196764,206.64869L24.550122,212.46847L23.580159,216.18666L25.358424,220.06651L28.106652,224.593L30.369899,229.44282L31.663182,233.48433L31.339862,236.71754L31.016541,237.20252L31.016541,239.3041L36.674657,245.60886L36.189676,248.03377L35.543034,250.29702L34.896392,252.23694L35.058052,260.48163L37.159638,264.19982L39.099564,266.78638L41.847792,267.27137L42.817755,270.01959L41.686132,273.57612L39.584545,275.19273L38.452922,275.19273L37.64462,279.07258L38.129601,281.98247L41.362811,286.3473L42.979415,291.6821L44.434359,296.37025L45.727643,299.4418L49.122513,305.26158L50.577457,307.84814L51.062439,310.75803L52.679043,311.72799L52.679043,314.1529L51.870741,316.09283L50.092476,323.20589L49.607494,325.14581L52.032402,327.89404L56.235574,328.37902L60.762067,330.15729L64.641918,332.25887L67.551807,332.25887L70.461695,335.33042L73.048262,340.18024L74.179886,342.44348L78.059737,344.54507L82.909551,345.35337L84.364495,347.45496L85.011137,350.68817L83.556193,351.33481L83.879514,352.30477L87.112725,353.11307L89.860953,353.27474L93.020842,351.58789L96.900696,355.79106L97.708998,358.05431L100.29557,362.25748L100.61889,365.49069L100.61889,374.867L101.10387,376.64526L111.12682,378.10021L130.84939,380.84843L144.69443,382.19813ZM56.559218,338.48145L57.852506,340.01723L57.690846,341.31052L54.457625,341.22969L53.891811,340.01723L53.245167,338.56228L56.559218,338.48145ZM58.49915,338.48145L59.711608,337.83481L63.268151,339.9364L66.339711,341.14885L65.450575,341.79551L60.924066,341.55301L59.307456,339.9364L58.49915,338.48145ZM79.191764,358.28493L80.970029,360.62901L81.778342,361.59898L83.314121,362.16479L83.879928,360.70984L82.909965,358.93157L80.242562,356.91081L79.191764,357.07247L79.191764,358.28493ZM77.736809,366.93379L79.515085,370.08618L80.727543,372.02612L79.272589,372.2686L77.979305,371.05615C77.979305,371.05615,77.251828,369.6012,77.251828,369.19704C77.251828,368.7929,77.251828,367.01462,77.251828,367.01462L77.736809,366.93379Z"},
  {id:"CO",name:"Colarado",shape:"M380.03242,320.96457L384.93566,234.63961L271.5471,221.99565L259.33328,309.93481L380.03242,320.96457Z"},
  {id:"CT",name:"Connecticut",shape:"M874.06831,178.86288L870.39088,163.98407L865.67206,164.90438L844.44328,169.64747L845.44347,172.87314L846.89842,180.14788L847.0752,189.1148L845.85518,191.28967L847.77597,193.22201L852.0475,189.31637L855.60403,186.08316L857.54395,183.98157L858.35226,184.62821L861.10048,183.17327L866.27362,182.04165L874.06831,178.86288Z"},
  {id:"DC",name:"Washington DC",shape:"M805.81945,250.84384L803.96117,249.01967L802.72854,248.33338L804.17155,246.31091L807.06064,248.25941L805.81945,250.84384Z"},
  {id:"DE",name:"Delaware",shape:"M825.6261,228.2791L825.99441,226.13221L826.36948,224.44116L824.74648,224.83892L823.13102,225.30648L820.92476,227.07078L822.64488,232.11366L824.90814,237.77178L827.00972,247.47143L828.62634,253.77621L833.63782,253.61455L839.77994,252.43387L837.51571,245.0476L836.54574,245.53258L832.98921,243.10768L831.21095,238.41952L829.27102,234.86299L826.1239,231.99268L825.25974,229.89456L825.6261,228.2791Z"},
  {id:"FL",name:"Florida",shape:"M759.8167,439.1428L762.08236,446.4614L765.81206,456.20366L771.14685,465.57996L774.86504,471.88472L779.71486,477.38118L783.75637,481.09937L785.37297,484.00926L784.24135,485.30254L783.43305,486.59582L786.34293,494.03221L789.25282,496.94209L791.83939,502.27689L795.39592,508.09667L799.92241,516.34135L801.2157,523.93939L801.70068,535.90227L802.34732,537.68053L802.024,541.0754L799.59909,542.36869L799.92241,544.30861L799.27577,546.24854L799.59909,548.67344L800.08407,550.61337L797.33585,553.84658L794.2643,555.30152L790.38445,555.46318L788.9295,557.07979L786.5046,558.04975L785.21131,557.56477L784.07969,556.59481L783.75637,553.68492L782.94806,550.29005L779.55319,545.11691L775.99666,542.85367L772.11681,542.53035L771.30851,543.82363L768.23696,539.4588L767.59032,535.90227L765.00375,531.86076L763.22549,530.72913L761.60888,532.83072L759.83062,532.5074L757.72903,527.49592L754.81914,523.61607L751.90925,518.28128L749.32269,515.20973L745.76616,511.49154L747.86774,509.06663L751.10095,503.57017L750.93929,501.95357L746.4128,500.98361L744.79619,501.63025L745.11952,502.27689L747.70608,503.24685L746.25114,507.77335L745.44284,508.25833L743.66457,504.21682L742.37129,499.367L742.04797,496.61877L743.50291,491.93062L743.50291,482.39265L740.43136,478.67446L739.13808,475.60291L733.96494,474.30963L732.02502,473.66299L730.40841,471.07642L727.01354,469.45981L725.88192,466.06494L723.13369,465.09498L720.70878,461.37679L716.50561,459.92185L713.59572,458.4669L711.00916,458.4669L706.96764,459.27521L706.80598,461.21513L707.61429,462.18509L707.1293,463.31672L704.05776,463.15506L700.33957,466.71159L696.78303,468.65151L692.90318,468.65151L689.66997,469.9448L689.34665,467.19657L687.73005,465.25664L684.82016,464.12502L683.20356,462.67007L675.12053,458.79022L667.52249,457.01196L663.15766,457.6586L657.17622,458.14358L651.19478,460.24517L647.71554,460.85813L647.47762,452.80838L644.89105,450.86846L643.11278,449.09019L643.4361,446.01863L653.62072,444.72535L679.16312,441.81546L685.95287,441.16882L691.38887,441.44909L693.97544,445.32895L695.43038,446.78389L703.52854,447.29911L714.34829,446.65247L735.86068,445.35918L741.3064,444.68481L746.41398,444.88932L746.84081,447.79921L749.07381,448.60751L749.30875,443.97751L747.78053,439.80456L749.08893,438.36473L754.64356,438.81948L759.8167,439.1428ZM772.36211,571.54788L774.78703,570.90124L776.08031,570.65875L777.53527,568.31466L779.87935,566.69805L781.17264,567.18304L782.87008,567.50636L783.27423,568.55715L779.79853,569.76961L775.59533,571.22456L773.25125,572.43702L772.36211,571.54788ZM785.86081,566.53639L787.07327,567.58719L789.82151,565.4856L795.15632,561.28241L798.87452,557.40254L801.38027,550.77444L802.35024,549.077L802.5119,545.68212L801.78442,546.1671L800.81446,548.99617L799.3595,553.6035L796.12628,558.8575L791.76144,563.06068L788.36656,565.00061L785.86081,566.53639Z"},
  {id:"GA",name:"Georgia",shape:"M672.29229,355.5518L672.29229,357.73422L672.45395,359.83582L673.10059,363.23069L676.49547,371.15206L678.92038,381.01337L680.37532,387.15648L681.99193,392.00629L683.44688,398.9577L685.54847,405.26247L688.13504,408.65735L688.62002,412.05222L690.55995,412.86052L690.72161,414.96212L688.94334,419.81193L688.45836,423.04515L688.2967,424.98508L689.91331,429.34992L690.23663,434.68472L689.42832,437.10963L690.07497,437.91794L691.52992,438.72624L691.73462,441.94433L693.96763,445.29386L696.21807,447.45591L704.13945,447.61757L714.9592,446.97093L736.47159,445.67765L741.91731,445.00328L746.49456,445.03101L746.65622,447.9409L749.24279,448.7492L749.56611,444.38436L747.9495,439.85786L749.08113,438.24126L754.90091,439.04956L759.87832,439.36734L759.1029,433.06855L761.36614,423.0456L762.82109,418.84242L762.3361,416.25586L765.67051,410.01156L765.16021,408.65988L763.2468,409.36446L760.66024,408.07116L760.01359,405.96957L758.72031,402.41304L756.45705,400.31145L753.87049,399.66481L752.25388,394.81499L749.32887,388.47999L745.1257,386.54006L743.0241,384.60013L741.73081,382.01356L739.62923,380.07363L737.36598,378.78034L735.10273,375.87045L732.03118,373.60721L727.50467,371.82893L727.01969,370.37399L724.59478,367.4641L724.1098,366.00915L720.71492,361.03867L717.19505,361.13784L713.44014,358.7817L712.02186,357.48842L711.69854,355.71015L712.56934,353.77023L714.79598,352.66009L714.16204,350.56287L672.29229,355.5518Z"},
  {id:"HI",name:"Hawaii",shape:"M233.08751,519.30948L235.02744,515.75293L237.2907,515.42961L237.61402,516.23791L235.51242,519.30948L233.08751,519.30948ZM243.27217,515.59127L249.4153,518.17784L251.51689,517.85452L253.1335,513.97465L252.48686,510.57977L248.28366,510.09479L244.24213,511.87306L243.27217,515.59127ZM273.9878,525.61427L277.706,531.11074L280.13092,530.78742L281.26255,530.30244L282.7175,531.59573L286.43571,531.43407L287.40568,529.97912L284.49577,528.20085L282.55584,524.48263L280.45424,520.92609L274.63444,523.83599L273.9878,525.61427ZM294.19545,534.50564L295.48874,532.5657L300.17691,533.53566L300.82356,533.05068L306.96668,533.69732L306.64336,534.99062L304.05678,536.44556L299.69193,536.12224L294.19545,534.50564ZM299.53027,539.67879L301.47021,543.55866L304.54176,542.42703L304.86509,540.81041L303.24848,538.70882L299.53027,538.3855L299.53027,539.67879ZM306.4817,538.54716L308.74496,535.63726L313.43313,538.06218L317.79798,539.19381L322.16284,541.94205L322.16284,543.88198L318.6063,545.66026L313.75645,546.63022L311.33154,545.17527L306.4817,538.54716ZM323.13281,554.06663L324.74942,552.77335L328.14431,554.38997L335.74238,557.94651L339.13727,560.0481L340.75387,562.47302L342.69381,566.83787L346.73534,569.42445L346.41202,570.71775L342.53215,573.95097L338.32896,575.40592L336.87401,574.75928L333.80244,576.53754L331.37753,579.77077L329.11427,582.68067L327.33599,582.51901L323.77945,579.93243L323.45613,575.40592L324.10277,572.981L322.48616,567.32286L320.38456,565.54458L320.2229,562.958L322.48616,561.98804L324.58776,558.91648L325.07274,557.94651L323.45613,556.16823L323.13281,554.06663Z"},
  {id:"IA",name:"Iowa",shape:"M569.19154,199.5843L569.45592,202.3705L571.67964,202.94776L572.63358,204.17309L573.13359,206.02845L576.92643,209.3871L577.6123,211.7786L576.93796,215.20307L575.35565,218.43505L574.55631,221.17684L572.38356,222.77888L570.66805,223.35128L565.08903,225.21148L563.69757,229.06017L564.42621,230.43191L566.26672,232.1145L565.98379,236.15079L564.22064,237.68865L563.44923,239.33179L563.57645,242.10811L561.69014,242.56535L560.06469,243.67026L559.7859,245.02289L560.06469,247.13781L558.51367,248.25388L556.04314,245.1206L554.78057,242.67073L489.04475,245.18558L488.12672,245.35102L486.07432,240.83506L485.8457,234.20499L484.24534,230.08978L483.55948,224.83147L481.27325,221.1735L480.35877,216.37243L477.61529,208.82788L476.47218,203.45524L475.10044,201.28333L473.50008,198.53987L475.45406,193.69604L476.8258,187.98047L474.08233,185.92286L473.62508,183.17939L474.53958,180.66454L476.25425,180.66454L558.90825,179.39506L559.74251,183.57818L561.99469,185.13915L562.2514,186.56224L560.22186,189.95155L560.41227,193.15707L562.92713,196.95527L565.45392,198.24889L568.5332,198.75194L569.19154,199.5843Z"},
  {id:"ID",name:"Idaho",shape:"M148.47881,176.48395L157.24968,141.26323L158.62142,137.03371L161.13626,131.08953L159.87884,128.8033L157.36398,128.91761L156.56381,127.88881L157.02106,126.7457L157.36398,123.65929L161.82213,118.17234L163.65111,117.7151L164.79422,116.57199L165.36578,113.37127L166.28026,112.68541L170.16685,106.85553L174.05344,102.5117L174.28206,98.739432L170.85272,96.110269L169.31717,91.709286L182.94208,28.367595L196.45967,30.895706L192.05159,52.278719L195.61194,59.764071L194.03083,64.424911L196.00068,69.066144L199.1389,70.321335L202.97424,79.877923L206.48693,84.315077L206.99418,85.458195L210.33513,86.601313L210.70398,88.698388L203.73297,106.07448L203.56779,108.64041L206.19891,111.96211L207.10399,111.91321L212.01528,108.88761L212.6927,107.79264L214.25501,108.4515L213.97657,113.80522L216.71582,126.38793L220.63365,129.56584L222.31483,131.73129L221.59822,135.81515L222.66444,138.62256L223.72607,139.71384L226.20536,137.36242L229.05352,137.41131L231.97277,138.74651L234.75279,138.06458L238.54705,137.9041L242.52595,139.50446L245.26943,139.2077L245.76617,136.17039L248.69876,135.40556L249.95893,136.92147L250.39986,139.86643L251.8242,141.07964L243.4382,194.6883C243.4382,194.6883,155.47221,177.98769,148.47881,176.48395Z"},
  {id:"IL",name:"Illinois",shape:"M619.54145,300.34244L619.5727,297.11273L620.14009,292.46677L622.47262,289.55091L624.33927,285.47515L626.57229,281.47982L626.20079,276.22742L624.19558,272.68485L624.0992,269.33817L624.79403,264.06866L623.96862,256.89029L622.90228,241.11284L621.609,226.0955L620.68672,214.4563L620.41421,213.53491L619.60591,210.94834L618.31263,207.23015L616.69602,205.45188L615.24108,202.86532L615.00751,197.37636L569.21108,199.97461L569.4397,202.34656L571.72593,203.03243L572.64041,204.17554L573.09766,206.00452L576.98424,209.43386L577.67012,211.72009L576.98424,215.14943L575.15526,218.80739L574.4694,221.32223L572.18317,223.15122L570.35419,223.83709L565.09587,225.20882L564.41,227.0378L563.72413,229.09541L564.41,230.46715L566.23898,232.06751L566.01036,236.18271L564.18137,237.78307L563.49551,239.38343L563.49551,242.1269L561.66653,242.58414L560.06617,243.72726L559.83755,245.099L560.06617,247.1566L558.3515,248.47117L557.3227,251.27181L557.77994,254.92976L560.06617,262.24569L567.3821,269.79024L572.86903,273.4482L572.64041,277.79203L573.55491,279.16377L579.95634,279.62101L582.69981,280.99275L582.01395,284.65071L579.72772,290.5949L579.04185,293.79562L581.32807,297.6822L587.72951,302.94052L592.30197,303.62639L594.35956,308.65609L596.41717,311.8568L595.50268,314.82889L597.10304,318.9441L598.93202,321.00171L600.34605,320.12102L601.25371,318.04623L603.46679,316.29903L605.59826,315.68463L608.20079,316.86443L611.82778,318.24013L613.01673,317.9419L613.2166,315.68345L611.9293,313.27166L612.23352,310.89494L614.07192,309.54749L617.09446,308.7372L618.35536,308.27868L617.74275,306.8918L616.95138,304.53743L618.38398,303.55647L619.54145,300.34244Z"},
  {id:"IN",name:"Indiana",shape:"M619.56954,299.97132L619.63482,297.11274L620.11981,292.58623L622.38305,289.67635L624.16133,285.79648L626.74789,281.59331L626.26291,275.77352L624.48465,273.02529L624.16133,269.79208L624.96963,264.29561L624.48465,257.3442L623.19135,241.33979L621.89807,225.98203L620.9276,214.26201L623.99866,215.15152L625.45361,216.12148L626.58523,215.79816L628.68682,213.85824L631.51639,212.24125L636.60919,212.07921L658.59506,209.81595L664.17079,209.28279L665.67393,225.239L669.92528,262.08055L670.52374,267.85215L670.15224,270.1154L671.38022,271.91077L671.47661,273.28332L668.95532,274.88283L665.41589,276.43414L662.21376,276.98442L661.6153,281.85135L657.04061,285.16382L654.24419,289.17426L654.56751,291.55099L653.98617,293.08519L650.6597,293.08519L649.07417,291.46859L646.58086,292.73079L643.8979,294.23393L644.05957,297.28838L642.86578,297.54641L642.3979,296.52827L640.23102,295.02513L636.9807,296.36661L635.42939,299.37286L633.99155,298.56456L632.5366,296.96505L628.07226,297.45004L622.47943,298.42L619.56954,299.97132Z"},
  {id:"KS",name:"Kansas",shape:"M507.88059,324.38028L495.26233,324.58471L449.17324,324.12748L404.61576,322.06985L379.98602,320.81244L383.87981,256.21747L405.96327,256.89264L446.2524,257.73404L490.55364,258.72162L495.64927,258.72162L497.83367,260.88402L499.85133,260.86264L501.49163,261.87511L501.42913,264.88434L499.60015,266.60971L499.2679,268.84188L501.11098,272.24421L504.06334,275.43927L506.39069,277.05373L507.69146,288.29455L507.88059,324.38028Z"},
  {id:"KY",name:"Kentucky",shape:"M725.9944,295.2707L723.70108,297.67238L720.12289,301.66642L715.19834,307.13109L713.98257,308.84686L713.92007,310.94844L709.54021,313.11253L703.88209,316.50741L696.65022,318.30626L644.78233,323.20512L629.02277,324.98338L624.40157,325.49609L620.53322,325.46837L620.30627,329.68865L612.12686,329.83321L605.17545,330.47985L597.18797,330.41963L598.39575,329.09955L600.89529,327.5587L601.12392,324.35797L602.03841,322.52899L600.43159,319.99009L601.23342,318.08328L603.49668,316.30502L605.59826,315.65837L608.34649,316.95166L611.90303,318.24494L613.03466,317.92162L613.19632,315.65837L611.90303,313.23346L612.22635,310.97021L614.16628,309.51527L616.75286,308.86862L618.36946,308.22198L617.56116,306.44371L616.91452,304.50378L618.42114,303.50798C618.42442,303.47086,619.6751,299.98569,619.65943,299.85017L622.71265,298.37149L628.03244,297.40153L632.52648,296.91655L633.91892,298.54398L635.44719,299.41478L637.03796,296.30657L640.22504,295.02395L642.43013,296.50798L642.84069,297.50702L644.01421,297.24301L643.85254,294.29008L646.98341,292.54089L649.1315,291.46741L650.66086,293.12822L653.97901,293.08402L654.56634,291.51277L654.19883,289.24953L656.79936,285.25103L661.57591,281.81313L662.28186,276.97727L665.20688,276.52136L668.99834,274.87568L671.44166,273.16744L671.24333,271.60251L670.10088,270.14757L670.6667,267.15266L674.85155,267.03516L677.15146,266.28936L680.49885,267.71846L682.55296,272.0833L687.68525,272.09412L689.73626,274.30231L691.35171,274.15461L693.9534,272.87644L699.19046,273.44981L701.76538,273.66732L703.45296,271.61108L706.07091,270.1852L707.95269,269.4781L708.59933,272.31473L710.64276,273.37307L713.28552,275.45556L713.40299,281.1288L714.21129,282.70121L716.80101,284.25749L717.57265,286.552L721.73254,289.98894L723.53785,293.61218L725.9944,295.2707Z"},
  {id:"LA",name:"Louisiana",shape:"M607.96706,459.16125L604.68245,455.99511L605.69236,450.49488L605.03101,449.6018L595.76934,450.60836L570.74102,451.06728L570.05683,448.6726L570.96964,440.2169L574.28552,434.27105L579.31688,425.58003L578.74281,423.18201L579.9994,422.50116L580.45833,420.54867L578.17209,418.49274L578.0603,416.55029L576.22964,412.20478L576.08259,405.86618L520.6088,406.79015L520.63737,416.36372L521.32324,425.73725L522.00911,429.62383L524.52396,433.73904L525.43845,438.76875L529.78228,444.25568L530.0109,447.4564L530.69677,448.14227L530.0109,456.60131L527.03881,461.631L528.63917,463.68861L527.95329,466.20345L527.26743,473.51938L525.89569,476.72009L526.01815,480.33654L530.70463,478.81639L542.81798,479.0234L553.16425,482.57993L559.63067,483.71156L563.34886,482.25661L566.58207,483.38824L569.81528,484.3582L570.62358,482.25661L567.39037,481.12499L564.8038,481.60997L562.05557,479.99337C562.05557,479.99337,562.21724,478.70008,562.86388,478.53842C563.51052,478.37676,565.93543,477.56846,565.93543,477.56846L567.71369,479.0234L569.49196,478.05344L572.72517,478.70008L574.18011,481.12499L574.50343,483.38824L579.02992,483.71156L580.80819,485.48982L579.99989,487.10643L578.7066,487.91473L580.32321,489.53133L588.72955,493.08786L592.28608,491.79458L593.25605,489.36967L595.84261,488.72303L597.62088,487.26809L598.91416,488.23805L599.72246,491.14794L597.45922,491.95624L598.10586,492.60288L601.50073,491.3096L603.76398,487.91473L604.57228,487.42975L602.47069,487.10643L603.27899,485.48982L603.11733,484.03488L605.21892,483.5499L606.35054,482.25661L606.99718,483.06491C606.99718,483.06491,606.83552,486.13646,607.64383,486.13646C608.45213,486.13646,611.847,486.78311,611.847,486.78311L615.88851,488.72303L616.85847,490.17798L619.76836,490.17798L620.89999,491.14794L623.16323,488.07639L623.16323,486.62144L621.86995,486.62144L618.47508,483.87322L612.6553,483.06491L609.42209,480.80167L610.55372,478.05344L612.81696,478.37676L612.97862,477.73012L611.20036,476.76016L611.20036,476.27517L614.43357,476.27517L616.21183,473.20363L614.91855,471.2637L614.59523,468.51547L613.14028,468.67713L611.20036,470.77872L610.55372,473.36529L607.48217,472.71864L606.5122,470.94038L608.29047,469.00045L610.1938,465.55485L609.1327,463.14258L607.96706,459.16125Z"},
  {id:"MA",name:"Massachusets",shape:"M899.62349,173.25394L901.79541,172.56806L902.25267,170.85339L903.28147,170.9677L904.31027,173.25394L903.05285,173.71118L899.16625,173.8255L899.62349,173.25394ZM890.24995,174.05412L892.53617,171.42495L894.13654,171.42495L895.96553,172.911L893.56499,173.9398L891.39307,174.9686L890.24995,174.05412ZM855.45082,152.06593L873.09769,147.42525L875.36095,146.77861L877.27503,143.9829L881.0118,142.31959L883.90104,146.73243L881.47613,151.90557L881.15281,153.36051L883.09274,155.94708L884.22436,155.13878L886.00263,155.13878L888.26587,157.72534L892.14573,163.70678L895.70226,164.19176L897.9655,163.2218L899.74377,161.44353L898.93546,158.69531L896.83388,157.0787L895.37893,157.887L894.40897,156.59372L894.89395,156.10874L896.99554,155.94708L898.7738,156.75538L900.71373,159.18029L901.68369,162.09018L902.00701,164.51508L897.80384,165.97003L893.92399,167.90995L890.04414,172.43645L888.10421,173.89139L888.10421,172.92143L890.52912,171.46648L891.0141,169.68822L890.2058,166.61667L887.29591,168.07161L886.48761,169.52656L886.97259,171.7898L884.90626,172.79023L882.15906,168.2631L878.76418,163.89826L876.69368,162.08579L870.16041,163.96199L865.06808,165.01278L844.39292,169.60499L843.72516,164.83714L844.3718,154.24837L848.66107,153.35923L855.45082,152.06593Z"},
  {id:"MD",name:"Maryland",shape:"M839.79175,252.41476L833.7832,253.6186L828.6403,253.73606L826.79674,246.81373L824.87193,237.64441L822.29931,231.45596L821.01093,227.05763L813.50491,228.67999L798.6287,231.50331L761.17727,239.05421L762.30857,244.06587L763.27853,249.72398L763.60185,249.40066L765.70345,246.97576L767.96669,244.3581L770.3916,243.74254L771.84656,242.28759L773.62482,239.70102L774.9181,240.34767L777.82799,240.02434L780.41457,237.92276L782.42146,236.46949L784.26669,235.98451L785.91104,237.11446L788.82093,238.5694L790.76085,240.34767L791.97331,241.88345L796.09566,243.58088L796.09566,246.49077L801.59212,247.78406L802.73656,248.32604L804.14846,246.29772L807.03043,248.26788L805.75226,250.74981L804.98699,254.73547L803.20873,257.32204L803.20873,259.42363L803.85537,261.2019L808.91932,262.55759L813.23042,262.49587L816.30196,263.46584L818.40355,263.78916L819.37351,261.68757L817.91857,259.58599L817.91857,257.80772L815.49366,255.70613L813.39208,250.20968L814.68536,244.87488L814.5237,242.7733L813.23042,241.48001C813.23042,241.48001,814.68536,239.86341,814.68536,239.21677C814.68536,238.57012,815.17034,237.11518,815.17034,237.11518L817.11027,235.8219L819.05019,234.20529L819.53517,235.17526L818.08023,236.79186L816.78695,240.51005L817.11027,241.64167L818.88853,241.96499L819.37351,247.46145L817.27193,248.43141L817.59525,251.98794L818.08023,251.82628L819.21185,249.88636L820.82846,251.66462L819.21185,252.95791L818.88853,256.35278L821.4751,259.74765L825.35495,260.23263L826.97156,259.42433L830.20811,263.60726L831.56646,264.14356L838.22013,261.34661L840.22771,257.32274L839.79175,252.41476ZM823.82217,261.44348L824.95379,263.94923L825.11545,265.7275L826.24708,267.5866C826.24708,267.5866,827.13622,266.69746,827.13622,266.37414C827.13622,266.05082,826.40875,263.30258,826.40875,263.30258L825.68127,260.95849L823.82217,261.44348Z"},
  {id:"ME",name:"Maine",shape:"M922.83976,78.830719L924.77969,80.932305L927.04294,84.650496L927.04294,86.590422L924.94135,91.278575L923.00142,91.925217L919.60655,94.996766L914.75674,100.49322C914.75674,100.49322,914.1101,100.49322,913.46346,100.49322C912.81682,100.49322,912.49349,98.391636,912.49349,98.391636L910.71523,98.553296L909.74527,100.00824L907.32036,101.46319L906.3504,102.91813L907.967,104.37307L907.48202,105.01972L906.99704,107.76794L905.05711,107.60628L905.05711,105.98968L904.73379,104.69639L903.27885,105.01972L901.50058,101.78651L899.399,103.07979L900.69228,104.53473L901.0156,105.66636L900.2073,106.95964L900.53062,110.03119L900.69228,111.64779L899.07568,114.23436L896.16579,114.71934L895.84247,117.62923L890.50767,120.70078L889.21439,121.18576L887.59778,119.73082L884.52623,123.28735L885.4962,126.52056L884.04125,127.81384L883.87959,132.17867L882.75631,138.43803L880.29406,137.28208L879.80907,134.21052L875.92922,133.07889L875.6059,130.33065L868.33115,106.88983L863.63257,92.250088L865.05311,92.131923L866.5669,92.541822L866.5669,89.955254L867.8752,85.458798L870.46177,80.770645L871.91672,76.729133L869.97679,74.304226L869.97679,68.322789L870.78509,67.352826L871.5934,64.604598L871.43174,63.149654L871.27007,58.29984L873.04834,53.450026L875.95823,44.5587L878.05981,40.355528L879.3531,40.355528L880.64638,40.517188L880.64638,41.648811L881.93967,43.912058L884.68789,44.5587L885.4962,43.750397L885.4962,42.780435L889.53771,39.870546L891.31597,38.092281L892.77092,38.253942L898.75235,40.678849L900.69228,41.648811L909.74527,71.555998L915.7267,71.555998L916.53501,73.495924L916.69667,78.345738L919.60655,80.608984L920.41486,80.608984L920.57652,80.124003L920.09154,78.99238L922.83976,78.830719ZM901.90801,108.97825L903.44379,107.44247L904.81791,108.49327L905.38372,110.91819L903.68628,111.80732L901.90801,108.97825ZM908.61694,103.07763L910.39521,104.93673C910.39521,104.93673,911.6885,105.01755,911.6885,104.69423C911.6885,104.37091,911.93099,102.67347,911.93099,102.67347L912.82013,101.86517L912.01182,100.08689L909.99106,100.81437L908.61694,103.07763Z"},
  {id:"MI",name:"Michigan",shape:"M581.61931,82.059006L583.4483,80.001402L585.62022,79.201221L590.99286,75.314624L593.27908,74.743065L593.73634,75.200319L588.59232,80.344339L585.27728,82.287628L583.21967,83.202124L581.61931,82.059006ZM667.79369,114.18719L668.44033,116.69293L671.67355,116.85459L672.96684,115.64213C672.96684,115.64213,672.88601,114.18719,672.56269,114.02552C672.23936,113.86386,670.94608,112.16642,670.94608,112.16642L668.76366,112.40891L667.14704,112.57057L666.82372,113.7022L667.79369,114.18719ZM567.49209,111.21318L568.20837,110.63278L570.9566,109.82447L574.51313,107.56123L574.51313,106.59126L575.15978,105.94462L581.14121,104.97466L583.56612,103.03473L587.93095,100.93315L588.09261,99.639864L590.03254,96.729975L591.8108,95.921673L593.10409,94.143408L595.36733,91.880161L599.73217,89.455254L604.42032,88.970273L605.55194,90.101896L605.22862,91.071859L601.51043,92.041822L600.05549,95.113371L597.79224,95.921673L597.30726,98.34658L594.88235,101.57979L594.55903,104.16636L595.36733,104.65134L596.3373,103.51972L599.89383,100.60983L601.18711,101.90311L603.45036,101.90311L606.68357,102.87307L608.13851,104.0047L609.59345,107.07625L612.34168,109.82447L616.22153,109.66281L617.67648,108.69285L619.29308,109.98613L620.90969,110.47112L622.20297,109.66281L623.33459,109.66281L624.9512,108.69285L628.99271,105.13632L632.38758,104.0047L639.01566,103.68138L643.54215,101.74145L646.12872,100.44817L647.58367,100.60983L647.58367,106.26794L648.06865,106.59126L650.97853,107.39957L652.91846,106.91458L659.06156,105.29798L660.19318,104.16636L661.64813,104.65134L661.64813,111.60274L664.88134,114.67429L666.17462,115.32093L667.4679,116.29089L666.17462,116.61421L665.36632,116.29089L661.64813,115.80591L659.54654,116.45255L657.28329,116.29089L654.05008,117.74584L652.27182,117.74584L646.45204,116.45255L641.27891,116.61421L639.33898,119.20078L632.38758,119.84742L629.96267,120.65572L628.83105,123.72727L627.53777,124.8589L627.05279,124.69724L625.59784,123.08063L621.07135,125.50554L620.42471,125.50554L619.29308,123.88893L618.48478,124.05059L616.54486,128.41543L615.57489,132.45694L612.39377,139.45774L611.21701,138.42347L609.84527,137.39215L607.90449,127.10413L604.36001,125.73408L602.30743,123.44785L590.18707,120.70437L587.3318,119.67473L579.10138,117.50199L571.21139,116.35887L567.49209,111.21318ZM697.8,177.2L694.6,168.9L692.3,159.9L689.9,156.7L687.3,154.9L685.7,156L681.8,157.8L679.9,162.8L677.1,166.5L676,167.2L674.5,166.5C674.5,166.5,671.9,165.1,672.1,164.4C672.3,163.8,672.6,159.4,672.6,159.4L676,158.1L676.8,154.7L677.4,152.1L679.9,150.5L679.5,140.5L677.9,138.2L676.6,137.4L675.8,135.3L676.6,134.5L678.2,134.8L678.4,133.2L676,131L674.7,128.4L672.1,128.4L667.6,126.9L662.1,123.5L659.3,123.5L658.7,124.2L657.7,123.7L654.6,121.4L651.7,123.2L648.8,125.5L649.2,129L650.1,129.3L652.2,129.8L652.7,130.6L650.1,131.4L647.5,131.8L646.1,133.5L645.8,135.6L646.1,137.3L646.4,142.8L642.8,144.9L642.2,144.7L642.2,140.5L643.5,138.1L644.1,135.6L643.3,134.8L641.4,135.6L640.4,139.8L637.7,141L635.9,142.9L635.7,143.9L636.4,144.7L635.7,147.3L633.5,147.8L633.5,148.9L634.3,151.3L633.1,157.5L631.5,161.5L632.2,166.2L632.7,167.3L631.9,169.8L631.5,170.6L631.2,173.3L634.8,179.3L637.7,185.8L639.1,190.6L638.3,195.3L637.3,201.3L634.9,206.4L634.6,209.2L631.3,212.3L635.8,212.1L657.2,209.9L664.4,208.9L664.5,210.5L671.4,209.3L681.7,207.8L685.5,207.4L685.7,206.8L685.8,205.3L687.9,201.6L689.9,199.9L689.7,194.8L691.3,193.2L692.4,192.9L692.6,189.3L694.2,186.3L695.2,186.9L695.4,187.5L696.2,187.7L698.1,186.7L697.8,177.2Z"},
  {id:"MN",name:"Minnesota",shape:"M475.23781,128.82439L474.78056,120.36535L472.95158,113.04943L471.1226,99.560705L470.66535,89.729927L468.83637,86.300584L467.23601,81.270889L467.23601,70.982869L467.92187,67.096282L466.10094,61.644615L496.23336,61.679886L496.55668,53.435202L497.20332,53.273541L499.46657,53.758523L501.40649,54.566825L502.21479,60.063281L503.66974,66.206379L505.28634,67.822984L510.13616,67.822984L510.45948,69.277928L516.76424,69.601249L516.76424,71.702835L521.61405,71.702835L521.93737,70.409551L523.06899,69.277928L525.33224,68.631286L526.62552,69.601249L529.53541,69.601249L533.41526,72.187816L538.75006,74.612723L541.17497,75.097705L541.65995,74.127742L543.11489,73.64276L543.59987,76.552649L546.18644,77.845933L546.67142,77.360951L547.96471,77.522612L547.96471,79.624198L550.55127,80.594161L553.62282,80.594161L555.23943,79.785858L558.47264,76.552649L561.0592,76.067668L561.86751,77.845933L562.35249,79.139216L563.32245,79.139216L564.29241,78.330914L573.18374,78.007593L574.962,81.079142L575.60865,81.079142L576.32226,79.994863L580.76217,79.624198L580.15007,81.903657L576.21135,83.740782L566.96557,87.80191L562.19083,89.808807L559.11928,92.395375L556.69437,95.951905L554.43113,99.831756L552.65286,100.64006L548.12637,105.65153L546.83308,105.81319L542.5053,108.57031L540.04242,111.77542L539.8138,114.96681L539.90816,123.01016L538.53212,124.69891L533.45058,128.45888L531.2205,134.44129L534.09225,136.675L534.77214,139.90198L532.9169,143.14091L533.08769,146.88893L533.45655,153.61933L536.4848,156.62132L539.8138,156.62132L541.70491,159.75392L545.08408,160.25719L548.94324,165.92866L556.03053,170.04541L558.17368,172.92053L558.84483,179.36004L477.63333,180.50483L477.29541,144.82798L476.83817,141.85589L472.72296,138.42655L471.57984,136.59757L471.57984,134.9972L473.63744,133.39685L475.00918,132.02511L475.23781,128.82439Z"},
  {id:"MO",name:"Missouri",shape:"M558.44022,248.11316L555.92035,245.02591L554.77723,242.73968L490.42,245.14022L488.13374,245.25453L489.39117,247.76938L489.16255,250.0556L491.67739,253.94219L494.76379,258.0574L497.8502,260.80087L500.01143,261.02949L501.50816,261.94399L501.50816,264.91608L499.67919,266.51644L499.22193,268.80266L501.27954,272.23201L503.7944,275.2041L506.30924,277.03308L507.68097,288.69283L507.99511,324.76504L508.22373,329.45179L508.68097,334.8353L531.11396,333.96848L554.31999,333.28261L575.12465,332.4816L586.77939,332.2513L588.94879,335.6773L588.2646,338.9848L585.17735,341.38784L584.60496,343.22518L589.98345,343.68244L593.87841,342.99656L595.59559,337.50293L596.24701,331.64614L598.34504,329.09098L600.94107,327.60409L600.9925,324.55385L602.00852,322.61737L600.31429,320.0736L598.98336,321.05786L596.99074,318.83062L595.70571,314.07162L596.50672,311.55342L594.56259,308.12576L592.73195,303.54996L587.93254,302.75062L580.96374,297.15187L579.24488,293.03834L580.04423,289.83762L582.1035,283.77995L582.56242,280.91632L580.61328,279.88501L573.75794,279.08734L572.72997,277.37518L572.61817,273.14482L567.13123,269.71381L560.15572,261.94231L557.8695,254.62638L557.63921,250.40106L558.44022,248.11316Z"},
  {id:"MS",name:"Mississippi",shape:"M631.55882,459.34458L631.30456,460.60073L626.13142,460.60073L624.67648,459.79243L622.57489,459.46911L615.78515,461.40903L614.00689,460.60073L611.42032,464.8039L610.31778,465.58192L609.19395,463.09394L608.05083,459.20735L604.6215,456.00664L605.7646,450.46209L605.07874,449.5476L603.24976,449.77622L595.33184,450.64959L570.78534,451.02296L570.0156,448.7976L570.88897,440.4208L574.00581,434.74799L579.23288,425.60309L578.78714,423.17049L580.024,422.51424L580.45987,420.59477L578.14239,418.51579L578.02727,416.37431L576.19155,412.25322L576.08255,406.29045L577.41008,403.80948L577.18678,400.39373L575.41729,397.31114L576.94371,395.82893L575.3731,393.32939L575.83035,391.67718L577.40775,385.15081L579.8937,383.11446L579.25203,380.74749L582.91,375.44496L585.74186,374.08854L585.52089,372.41338L585.23276,370.73228L588.10882,365.16461L590.45454,363.9331L590.60617,363.04009L627.94965,359.15892L628.13451,365.44225L628.29617,382.09331L627.48787,413.13216L627.32621,427.19665L630.07445,445.94929L631.55882,459.34458Z"},
  {id:"MT",name:"Montana",shape:"M369.20952,56.969133L338.5352,54.1613L309.27465,50.60477L280.01411,46.563258L247.68201,41.228463L229.25272,37.833593L196.52907,30.900857L192.05005,52.248389L195.47939,59.79293L194.10765,64.365382L195.93663,68.937833L199.13736,70.309572L203.75818,81.079025L206.45328,84.255548L206.91052,85.398666L210.33986,86.541784L210.79711,88.599377L203.70981,106.20333L203.70981,108.71818L206.22466,111.91889L207.13914,111.91889L211.94021,108.9468L212.62609,107.80368L214.22645,108.48955L213.99782,113.74787L216.7413,126.32212L219.71339,128.83696L220.62787,129.52283L222.45686,131.80905L221.99961,135.2384L222.68548,138.66773L223.8286,139.58223L226.11482,137.296L228.85829,137.296L232.05901,138.89636L234.57386,137.98187L238.68907,137.98187L242.34702,139.58223L245.0905,139.12498L245.54774,136.15288L248.51983,135.46702L249.89157,136.83876L250.34882,140.03947L251.77469,140.87411L253.66164,129.83937L360.40731,143.26829L369.20952,56.969133Z"},
  {id:"NC",name:"North Carolina",shape:"M834.98153,294.31554L837.06653,299.23289L840.62306,305.69931L843.04796,308.12422L843.6946,310.38747L841.2697,310.54913L842.078,311.19577L841.75468,315.39894L839.16811,316.69222L838.52147,318.79381L837.22819,321.7037L833.50999,323.3203L831.08509,322.99698L829.63014,322.83532L828.01354,321.54204L828.33686,322.83532L828.33686,323.80529L830.27679,323.80529L831.08509,325.09857L829.14516,331.40333L833.34833,331.40333L833.99498,333.01993L836.25822,330.75669L837.55151,330.2717L835.61158,333.82823L832.54003,338.67805L831.24675,338.67805L830.11512,338.19307L827.3669,338.83971L822.19376,341.26462L815.72734,346.59941L812.33247,351.28756L810.39255,357.75398L809.90757,360.17889L805.21941,360.66387L799.76628,362.00053L789.81987,353.798L777.21033,346.19995L774.30044,345.39164L761.69091,346.84659L757.41445,347.59674L755.79785,344.36352L752.82749,342.24682L736.3381,342.7318L729.06336,343.5401L720.01037,348.06661L713.86726,350.65317L692.68971,353.23975L693.1898,349.18542L694.96807,347.73048L697.71631,347.08383L698.36295,343.36563L702.56613,340.61741L706.44598,339.16245L710.64917,335.60592L715.014,333.50433L715.66064,330.43277L719.5405,326.55292L720.18714,326.39126C720.18714,326.39126,720.18714,327.52289,720.99545,327.52289C721.80375,327.52289,722.93538,327.84621,722.93538,327.84621L725.19863,324.28967L727.30022,323.64302L729.56346,323.96635L731.18008,320.40982L734.08997,317.82324L734.57495,315.72165L734.76245,312.07346L739.03895,312.05094L746.23754,311.19515L761.99477,308.94272L777.13081,306.85615L798.77129,302.1368L818.75461,297.87823L829.93155,295.47242L834.98153,294.31554ZM839.25199,327.52211L841.83857,325.01636L844.99095,322.42978L846.52673,321.78314L846.68839,319.76238L846.04175,313.61926L844.5868,311.27518L843.94015,309.41608L844.66763,309.17358L847.41587,314.67006L847.82002,319.11573L847.65836,322.51062L844.26348,324.04639L841.43441,326.47131L840.30279,327.68377L839.25199,327.52211Z"},
  {id:"ND",name:"North Dakota",shape:"M475.30528,128.91846L474.69037,120.48479L473.01342,113.66887L471.12193,100.64465L470.66469,89.657624L468.92523,86.580482L467.16862,81.386086L467.19987,70.941816L467.82323,67.117729L465.98913,61.649968L437.34688,61.085941L418.75593,60.439299L392.24361,59.146015L369.29727,57.012146L362.30403,124.18898L417.23627,127.53263L475.30528,128.91846Z"},
  {id:"NE",name:"Nebraska",shape:"M486.09787,240.70058L489.32848,247.72049L489.19985,250.02301L492.65907,255.51689L495.37836,258.66923L490.32888,258.66923L446.84632,257.73055L406.05946,256.84025L383.80724,256.05638L384.88001,234.72853L352.56177,231.80828L356.9056,187.79842L372.45193,188.82723L392.57072,189.97033L410.40329,191.11345L434.18005,192.25656L444.92531,191.79932L446.98291,194.08554L451.78399,197.05764L452.9271,197.97213L457.27093,196.60039L461.15752,196.14315L463.90099,195.91452L465.72997,197.28626L469.7874,198.88662L472.75949,200.48698L473.21674,202.08734L474.13123,204.14494L475.96021,204.14494L476.75819,204.19111L477.65242,208.87293L480.57268,217.34085L481.14521,221.09756L483.6687,224.87181L484.23829,229.98595L485.84553,234.22632L486.09787,240.70058Z"},
  {id:"NH",name:"New Hampshire",shape:"M880.79902,142.42476L881.66802,141.34826L882.75824,138.05724L880.21516,137.14377L879.73017,134.07221L875.85032,132.94059L875.527,130.19235L868.25225,106.75153L863.65083,92.208542L862.75375,92.203482L862.10711,93.820087L861.46047,93.335106L860.4905,92.365143L859.03556,94.305068L858.98709,99.337122L859.29874,105.00434L861.23866,107.75258L861.23866,111.7941L857.52046,116.85688L854.93389,117.98852L854.93389,119.12014L856.06552,120.89841L856.06552,129.46643L855.25721,138.6811L855.09555,143.53092L856.06552,144.82422L855.90386,149.35071L855.41887,151.12899L856.38768,151.83821L873.17535,147.41366L875.35022,146.81121L877.19379,144.03788L880.79902,142.42476Z"},
  {id:"NJ",name:"New Jersey",shape:"M829.67942,188.46016L827.35687,191.19443L827.35687,194.26599L825.41693,197.33754L825.25527,198.95416L826.54857,200.24744L826.38691,202.67236L824.12365,203.80398L824.93195,206.55221L825.09361,207.68384L827.84185,208.00716L828.81181,210.59373L832.36835,213.01865L834.79326,214.63525L834.79326,215.44356L831.81005,218.14012L830.19344,220.40336L828.73849,223.1516L826.47524,224.44488L826.01279,226.04736L825.77029,227.25982L825.16106,229.86656L826.25333,232.11075L829.48654,235.02064L834.33635,237.28389L838.37786,237.93053L838.53952,239.38547L837.73122,240.35543L838.05454,243.10366L838.86284,243.10366L840.96443,240.67876L841.77273,235.82894L844.52096,231.78743L847.59251,225.32101L848.72413,219.82456L848.07749,218.69293L847.91583,209.31662L846.29922,205.92176L845.1676,206.73006L842.41937,207.05338L841.93439,206.5684L843.06602,205.59843L845.1676,203.65851L845.23066,202.56468L844.84627,199.13084L845.41964,196.3826L845.30217,194.41359L842.49463,192.66324L837.40249,191.48748L833.26505,190.10585L829.67942,188.46016Z"},
  {id:"NM",name:"New Mexico",shape:"M288.15255,424.01315L287.37714,419.26505L296.02092,419.79045L326.19268,422.73635L353.46084,424.42624L355.67611,405.71877L359.53347,349.8428L361.27115,330.45357L362.84285,330.58213L363.66825,319.41874L259.6638,308.78279L242.16645,429.2176L257.62712,431.20675L258.9204,421.1838L288.15255,424.01315Z"},
  {id:"NV",name:"Nevada",shape:"M196.39273,185.57552L172.75382,314.39827L170.92158,314.74742L169.34882,317.1536L166.97588,317.16429L165.50393,314.42082L162.88546,314.0424L162.11454,312.93477L161.07671,312.88073L158.29834,314.52502L157.98808,321.3105L157.62599,327.08767L157.27742,335.68048L155.83032,337.76964L153.3914,336.69561L84.311514,232.49442L103.30063,164.90951L196.39273,185.57552Z"},
  {id:"NY",name:"New York",shape:"M830.37944,188.7456L829.24781,187.77564L826.66123,187.61398L824.39799,185.67406L822.76738,179.54493L819.30892,179.63547L816.86521,176.92727L797.47989,181.30921L754.47811,190.0389L746.94846,191.26689L746.2103,184.79855L747.6384,183.67317L748.93168,182.54155L749.90165,180.92494L751.67991,179.79332L753.61984,178.01505L754.10482,176.39845L756.2064,173.65022L757.33803,172.68026L757.17637,171.71029L755.88308,168.63875L754.10482,168.47709L752.16489,162.33399L755.07478,160.55572L759.43961,159.10078L763.48113,157.80749L766.71434,157.32251L773.01909,157.16085L774.95902,158.45414L776.57562,158.6158L778.67721,157.32251L781.26378,156.19089L786.43691,155.70591L788.5385,153.92764L790.31676,150.69443L791.93337,148.75451L794.03495,148.75451L795.97488,147.62288L796.13654,145.35964L794.6816,143.25805L794.35828,141.80311L795.4899,139.70152L795.4899,138.24658L793.71163,138.24658L791.93337,137.43828L791.12507,136.30665L790.96341,133.72008L796.78318,128.22363L797.42982,127.41533L798.88477,124.50544L801.79466,119.97894L804.54289,116.26075L806.64447,113.83585L809.05957,112.01024L812.14093,110.7643L817.63738,109.47101L820.87059,109.63267L825.39709,108.17773L832.96228,106.10656L833.48207,111.08623L835.90699,117.55267L836.71529,122.72582L835.74533,126.60568L838.3319,131.13218L839.1402,133.23377L838.3319,136.14367L841.2418,137.43695L841.88844,137.76027L844.96,148.75321L844.42371,153.81288L843.93873,164.64415L844.74703,170.14062L845.55533,173.69716L847.01028,180.9719L847.01028,189.05494L845.87865,191.31819L847.71798,193.31098L848.51453,194.9894L846.57461,196.76767L846.89793,198.06095L848.19121,197.73763L849.64616,196.44435L851.9094,193.85778L853.04103,193.21114L854.65763,193.85778L856.92088,194.01944L864.84224,190.13959L867.75213,187.39136L869.04541,185.93642L873.24858,187.55302L869.85371,191.10955L865.97386,194.01944L858.8608,199.35423L856.27424,200.3242L850.45446,202.26412L846.41295,203.39575L845.23821,202.86282L844.99419,199.17429L845.47917,196.42605L845.31751,194.32447L842.504,192.62547L837.9775,191.6555L834.09764,190.52388L830.37944,188.7456Z"},
  {id:"OH",name:"Ohio",shape:"M735.32497,193.32832L729.23143,197.38167L725.35158,199.64492L721.95671,203.36311L717.9152,207.24296L714.68199,208.05126L711.7721,208.53624L706.27564,211.12281L704.17406,211.28447L700.77919,208.21292L695.60605,208.85957L693.01949,207.40462L690.63842,206.05379L685.74585,206.7572L675.56123,208.37381L664.35436,210.55854L665.64765,225.18882L667.42592,238.92999L670.01248,262.37079L670.5783,267.20196L674.70065,267.07294L677.12556,266.26463L680.48936,267.76777L682.55985,272.1326L687.69879,272.1155L689.59053,274.2342L691.3517,274.1689L693.89009,272.82744L696.39426,273.19894L701.81554,273.68162L703.54251,271.54894L705.88816,270.25566L707.95865,269.57481L708.60529,272.32305L710.38357,273.29301L713.85926,275.63708L716.04168,275.55626L717.3748,275.06378L717.55951,272.30225L719.14487,270.84729L719.24403,266.05457C719.24403,266.05457,720.26799,261.94551,720.26799,261.94551L721.56726,261.34423L722.88861,262.49197L723.42676,264.18899L725.14589,263.15157L725.58487,261.69082L724.46818,259.78776L724.53447,257.47333L725.28347,256.40102L727.43623,253.09454L728.48645,251.5512L730.58804,252.03618L732.85129,250.41957L735.92284,247.0247L738.69433,242.94597L739.01466,237.89046L739.49964,232.87897L739.32286,227.57209L738.36802,224.67731L738.71926,223.48753L740.52365,221.73742L738.23486,212.69009L735.32497,193.32832Z"},
  {id:"OK",name:"Oklahoma",shape:"M380.34313,320.82146L363.65895,319.54815L362.77873,330.50058L383.24411,331.65746L415.29966,332.96106L412.96506,357.37971L412.50781,375.21228L412.73644,376.81264L417.08027,380.4706L419.13787,381.61371L419.82374,381.38509L420.50961,379.32748L421.88135,381.15647L423.93895,381.15647L423.93895,379.78473L426.68242,381.15647L426.22518,385.04305L430.34039,385.27167L432.85523,386.41479L436.97044,387.10066L439.48529,388.92964L441.77152,386.87204L445.20086,387.5579L447.71571,390.98724L448.63019,390.98724L448.63019,393.27347L450.91642,393.95933L453.20264,391.67311L455.03163,392.35897L457.54647,392.35897L458.46097,394.87383L464.76204,396.9528L466.13378,396.26694L467.96276,392.15173L469.10587,392.15173L470.24899,394.20933L474.3642,394.8952L478.02215,396.26694L480.99425,397.18143L482.82324,396.26694L483.5091,393.75209L487.85293,393.75209L489.91053,394.66658L492.654,392.60897L493.79712,392.60897L494.48299,394.20933L498.59819,394.20933L500.19855,392.15173L502.02754,392.60897L504.08514,395.12383L507.28585,396.9528L510.48658,397.8673L512.42766,398.98623L512.03856,361.76922L510.66681,350.79524L510.50635,341.9229L509.06646,335.38517L508.28826,328.20553L508.22012,324.38931L496.08328,324.70805L449.67324,324.25081L404.63433,322.19319L380.34313,320.82146Z"},
  {id:"OR",name:"Oregon",shape:"M148.72184,175.53153L157.57154,140.73002L158.62233,136.5005L160.9767,130.87727L160.36119,129.71439L157.84633,129.66821L156.56473,127.99751L157.02197,126.53344L157.52538,123.28656L161.98353,117.79961L163.81251,116.70046L164.95562,115.55735L166.44166,111.99172L170.48872,106.32232L174.05435,102.45992L174.28297,99.008606L171.01411,96.539924L169.2307,91.897299L156.56693,88.285329L141.47784,84.741679L126.04582,84.855985L125.58858,83.484256L120.10163,85.54186L115.64349,84.970301L113.24295,83.36994L111.98553,84.055815L107.29877,83.827183L105.5841,82.455454L100.32578,80.39785L99.525598,80.512166L95.181768,79.02611L93.238477,80.855093L87.065665,80.512166L81.121482,76.396957L81.807347,75.596777L82.035968,67.823604L79.749743,63.937027L75.634535,63.365468L74.94867,60.850621L72.594738,60.384056L66.796213,62.44284L64.532966,68.909258L61.299757,78.932207L58.066547,85.398626L53.055073,99.463087L46.588654,113.04256L38.505631,125.65208L36.565705,128.56197L35.757403,137.12997L36.143498,149.2102L148.72184,175.53153Z"},
  {id:"PA",name:"Pennsylvania",shape:"M825.1237,224.69205L826.43212,224.42105L828.76165,223.1678L829.97353,220.68473L831.59014,218.42148L834.82335,215.34992L834.82335,214.54162L832.39844,212.92502L828.8419,210.5001L827.87194,207.91353L825.1237,207.59021L824.96204,206.45858L824.15374,203.71035L826.417,202.57873L826.57866,200.15381L825.28536,198.86052L825.44702,197.24391L827.38696,194.17236L827.38696,191.1008L830.08459,188.45492L829.16431,187.77994L826.64023,187.58703L824.34574,185.64711L822.79582,179.53105L819.29124,179.63157L816.83601,176.92824L798.74502,181.12601L755.74324,189.8557L746.85189,191.31064L746.23122,184.78925L740.86869,189.8569L739.5754,190.34188L735.37311,193.35077L738.28387,212.48822L740.76553,222.21758L744.33733,241.47907L747.60664,240.84139L759.55022,239.33892L797.47685,231.67372L812.35306,228.8504L820.65341,227.22804L820.92052,226.98951L823.02212,225.37289L825.1237,224.69205Z"},
  {id:"RI",name:"Rhode Island",shape:"M874.07001,178.89536L870.37422,163.93937L876.6435,162.09423L878.83463,164.02135L882.14112,168.342L884.82902,172.74409L881.82968,174.36888L880.5364,174.20722L879.40478,175.98549L876.97987,177.92541L874.07001,178.89536Z"},
  {id:"SC",name:"South Carolina",shape:"M764.94328,408.16488L763.16622,409.13438L760.57965,407.84109L759.93301,405.7395L758.63973,402.18297L756.37647,400.08137L753.7899,399.43473L752.1733,394.58492L749.42506,388.60347L745.22189,386.66353L743.12029,384.72361L741.82701,382.13704L739.72542,380.1971L737.46217,378.90382L735.19892,375.99393L732.12737,373.73069L727.60086,371.95241L727.11588,370.49747L724.69098,367.58758L724.20599,366.13262L720.81111,360.95949L717.41624,361.12115L713.37472,358.69623L712.08144,357.40295L711.75812,355.62468L712.56642,353.68476L714.82967,352.71478L714.31885,350.4257L720.08695,348.08913L729.20245,343.50013L736.97718,342.69182L753.09158,342.26934L755.72983,344.14677L757.40893,347.50499L761.71128,346.89501L774.32081,345.44005L777.2307,346.24836L789.84024,353.84642L799.94832,361.9681L794.52715,367.42644L791.94058,373.56954L791.4556,379.8743L789.839,380.6826L788.70737,383.43083L786.28247,384.07747L784.18088,387.634L781.43265,390.38223L779.16941,393.7771L777.5528,394.5854L773.99627,397.98027L771.08638,398.14193L772.05635,401.37514L767.04487,406.8716L764.94328,408.16488Z"},
  {id:"SD",name:"South Dakota",shape:"M476.44687,204.02465L476.39942,203.44378L473.50371,198.59834L475.36394,193.88623L476.85667,187.99969L474.0748,185.91998L473.68964,183.17652L474.48204,180.62217L477.67055,180.63738L477.54747,175.63124L477.21417,145.45699L476.59644,141.68941L472.52412,138.35848L471.54149,136.68152L471.47899,135.0727L473.50111,133.5433L475.03333,131.87763L475.27829,129.22084L417.0212,127.62049L362.22199,124.1714L356.89672,187.86259L371.48699,188.76639L391.43684,189.972L409.17989,190.90059L432.95665,192.20417L444.93935,191.77953L446.90565,194.02471L452.10029,197.27806L452.86418,198.00081L457.40562,196.548L463.94616,195.93309L465.62146,197.26936L469.82597,198.86549L472.77103,200.50132L473.17001,201.98513L474.2095,204.22601L476.44687,204.02465Z"},
  {id:"TN",name:"Tennessee",shape:"M696.67788,318.25411L644.78479,323.2656L629.02523,325.04386L624.40403,325.55657L620.53568,325.52885L620.31471,329.62968L612.12933,329.89369L605.17792,330.54033L597.08709,330.41647L595.67331,337.48933L593.97708,342.96938L590.68391,345.72022L589.33517,350.10128L589.01185,352.68785L584.97033,354.95109L586.42527,358.50763L585.45531,362.87247L584.48693,363.66212L692.64548,353.25457L693.04875,349.29963L694.85948,347.80924L697.69363,347.05979L698.36556,343.34281L702.46416,340.63785L706.51109,339.14382L710.59467,335.57349L715.03076,333.54803L715.55202,330.48068L719.61662,326.49569L720.16742,326.38152C720.16742,326.38152,720.19867,327.51314,721.00697,327.51314C721.81527,327.51314,722.9469,327.86771,722.9469,327.86771L725.21015,324.27992L727.28049,323.63328L729.5556,323.92849L731.15391,320.39563L734.10916,317.75172L734.53084,315.81261L734.8398,312.10146L732.69325,311.90169L730.09157,313.93002L723.09826,313.95909L704.73897,316.34591L696.67788,318.25411Z"},
  {id:"TX",name:"Texas",shape:"M361.46423,330.57358L384.15502,331.65952L415.24771,332.80264L412.9131,356.25844L412.61634,374.41196L412.68448,376.49375L417.02831,380.31218L419.01496,381.75934L420.19917,381.19965L420.57254,379.38193L421.71286,381.18555L423.8245,381.22948L423.82183,379.78239L425.49177,380.74966L426.63047,381.15853L426.2712,385.12618L430.35939,385.21969L433.28471,386.41686L437.23945,386.94224L439.62083,389.02122L441.74493,386.94505L445.46987,387.55996L447.69078,390.7849L448.76574,391.10586L448.60527,393.07113L450.81888,393.86342L453.14903,391.80862L455.28205,392.42354L457.51143,392.45902L458.4445,394.89446L464.77259,397.00891L466.36564,396.24198L467.85511,392.06427L468.19583,392.06427L469.10232,392.14591L470.33137,394.21454L474.26125,394.87982L477.59825,396.0027L481.02388,397.19867L482.86446,396.22367L483.57822,393.70883L488.03144,393.75303L489.84018,394.68381L492.63943,392.5773L493.74307,392.6215L494.59411,394.22657L498.64883,394.22657L500.1677,392.19795L502.03507,392.60519L503.9811,395.00847L507.50167,397.05262L510.36043,397.86243L511.87405,398.66227L514.32075,400.65959L517.36379,399.3318L520.05488,400.47068L520.61869,406.57662L520.57893,416.27879L521.26479,425.8128L521.96697,429.41791L524.6423,433.83777L525.54048,438.7885L529.75643,444.32652L529.95245,447.47146L530.69882,448.2573L529.96875,456.63737L527.09665,461.64387L528.62962,463.79674L527.99954,466.13482L527.32997,473.53914L525.82565,476.87714L526.12053,480.37949L520.45565,481.96467L510.59436,486.49117L509.6244,488.43109L507.03783,490.37102L504.93625,491.82596L503.64296,492.63426L497.98485,497.96906L495.23662,500.07065L489.90182,503.30385L484.24371,505.72876L477.93895,509.12363L476.16069,510.57858L470.34091,514.13511L466.94604,514.78175L463.06619,520.2782L459.02468,520.60153L458.05471,522.54145L460.31796,524.48138L458.86301,529.97783L457.56973,534.50433L456.43811,538.38418L455.62981,542.91067L456.43811,545.33558L458.21637,552.28698L459.18634,558.43007L460.9646,561.1783L459.99464,562.63325L456.92309,564.57317L451.26497,560.69332L445.76852,559.5617L444.47523,560.04668L441.24202,559.40004L437.03885,556.32849L431.86572,555.19687L424.26767,551.802L422.16609,547.92214L420.8728,541.45573L417.6396,539.5158L416.99295,537.25255L417.6396,536.60591L417.96292,533.21104L416.66963,532.5644L416.02299,531.59444L417.31627,527.2296L415.69967,524.96636L412.46646,523.67307L409.07159,519.30824L405.51506,512.68016L401.31189,510.09359L401.47355,508.15367L396.13875,495.86747L395.33045,491.6643L393.55219,489.72438L393.39053,488.26943L387.40909,482.93464L384.82252,479.86309L384.82252,478.73146L382.23595,476.62988L375.44621,475.49825L368.00983,474.85161L364.93828,472.58837L360.41179,474.36663L356.85526,475.82158L354.59201,479.05478L353.62205,482.77298L349.25722,488.91607L346.83231,491.34098L344.24574,490.37102L342.46748,489.23939L340.52755,488.59275L336.6477,486.32951L336.6477,485.68286L334.86944,483.74294L329.6963,481.64135L322.25992,473.88165L319.99667,469.1935L319.99667,461.11047L316.76346,454.64405L316.27848,451.89583L314.66188,450.92586L313.53025,448.82428L308.51878,446.72269L307.2255,445.10609L300.11243,437.18472L298.81915,433.95151L294.13099,431.68826L292.67604,427.32339L290.08945,424.41352L288.14954,423.92856L287.50031,419.25092L295.50218,419.93681L324.53717,422.68026L353.57225,424.28062L355.80578,404.8188L359.69233,349.26378L361.29272,330.51646L362.66446,330.54504M461.69381,560.20778L461.128,553.0947L458.37976,545.90078L457.81394,538.86853L459.34972,530.62382L462.66378,523.75323L466.13948,518.33758L469.29188,514.78103L469.93852,515.02353L465.16952,521.65163L460.80468,528.19891L458.78391,534.827L458.46059,540.00016L459.34972,546.14328L461.9363,553.3372L462.42128,558.51034L462.58294,559.9653L461.69381,560.20778Z"},
  {id:"UT",name:"Utah",shape:"M259.49836,310.10509L175.74933,298.23284L196.33694,185.69149L243.11725,194.43663L241.63245,205.06705L239.32083,218.23971L247.12852,219.16808L263.53504,220.97287L271.74601,221.82851L259.49836,310.10509Z"},
  {id:"VA",name:"Virginia",shape:"M831.63885,266.06892L831.49494,264.12189L837.94837,261.57201L837.17796,264.78985L834.25801,268.56896L833.83992,273.15478L834.30167,276.54522L832.4737,281.52338L830.30943,283.43952L828.83909,278.79871L829.28498,273.3496L830.87198,269.16653L831.63885,266.06892ZM834.97904,294.37028L776.80486,306.94571L739.37789,312.22478L732.69956,311.8496L730.11431,313.77598L722.77518,313.99667L714.39307,314.97434L703.47811,316.58896L713.94754,310.97776L713.93442,308.90283L715.45447,306.7567L726.00825,295.25527L729.95497,299.73273L733.73798,300.69671L736.28144,299.55639L738.51866,298.24523L741.05527,299.58875L744.96944,298.16099L746.84617,293.60465L749.44709,294.14467L752.30233,292.01342L754.1016,292.50702L756.92881,288.83045L757.27706,286.74734L756.3134,285.47177L757.31617,283.60514L762.59044,271.32799L763.20721,265.59291L764.4361,265.06937L766.61463,267.51224L770.55049,267.21107L772.4797,259.63744L775.27369,259.07658L776.32344,256.33551L778.90326,253.98863L781.67509,248.29344L781.76002,243.22589L791.58153,247.04871C792.26238,247.38913,792.41441,241.99956,792.41441,241.99956L796.06697,243.59789L796.1353,246.53605L801.91955,247.83554L804.0525,249.01174L805.71242,251.06743L805.05787,254.7161L803.11043,257.30708L803.22028,259.36615L803.80924,261.21906L808.78799,262.48749L813.23926,262.52737L816.30809,263.48601L818.2516,263.79531L818.96641,266.88377L822.15685,267.2863L823.02492,268.48632L822.58543,273.1764L823.96016,274.27895L823.48121,276.20934L824.71062,276.99911L824.48882,278.38371L821.79483,278.28877L821.88379,279.90429L824.16478,281.44716L824.28632,282.85906L826.05943,284.64444L826.55122,287.16857L823.99818,288.54988L825.5704,290.04418L831.37142,288.35835L834.97904,294.37028Z"},
  {id:"VT",name:"Vermont",shape:"M844.48416,154.05791L844.80086,148.71228L841.91015,137.92811L841.26351,137.60479L838.35361,136.3115L839.16191,133.40161L838.35361,131.30002L835.65356,126.66004L836.62353,122.78018L835.81522,117.60703L833.39031,111.14059L832.58474,106.21808L859.0041,99.48626L859.3128,105.00847L861.22906,107.7507L861.22906,111.79222L857.52191,116.85021L854.93534,117.99288L854.92429,119.11345L856.23426,120.63257L855.92333,128.73054L855.3139,137.9894L855.08595,143.54634L856.05591,144.83963L855.89425,149.41032L855.40927,151.10021L856.42345,151.82737L848.9859,153.33408L844.48416,154.05791Z"},
  {id:"WA",name:"Washington",shape:"M102.07324,7.6117734L106.43807,9.0667177L116.1377,11.814946L124.7057,13.754871L144.7516,19.412988L167.70739,25.071104L182.93051,28.278277L169.29815,91.864088L156.85315,88.33877L141.34514,84.768091L126.11585,84.801329L125.66028,83.45663L120.06106,85.635923L115.46563,84.899179L113.31866,83.315125L112.00545,83.973101L107.26979,83.832858L105.57143,82.483225L100.30839,80.370922L99.573419,80.51784L95.184297,78.993392L93.290999,80.810771L87.025093,80.512038L81.099395,76.386336L81.878352,75.453573L81.999575,67.776121L79.717576,63.93642L75.602368,63.32938L74.924958,60.818764L72.649446,60.361832L69.094498,61.592408L66.831251,58.373161L67.154572,55.463272L69.9028,55.139951L71.519405,51.09844L68.932837,49.966816L69.094498,46.248625L73.459331,45.601984L70.711103,42.853756L69.256158,35.740695L69.9028,32.830807L69.9028,24.909444L68.124535,21.676234L70.387782,12.299927L72.489368,12.784908L74.914275,15.694797L77.662503,18.281364L80.895712,20.22129L85.422205,22.322876L88.493756,22.969518L91.403645,24.424462L94.798518,25.394425L97.061764,25.232765L97.061764,22.807857L98.355048,21.676234L100.45663,20.38295L100.77996,21.514574L101.10328,23.292839L98.840029,23.77782L98.516708,25.879406L100.29497,27.334351L101.4266,29.759258L102.07324,31.699183L103.52818,31.537523L103.68984,30.244239L102.71988,28.950955L102.2349,25.717746L103.0432,23.939481L102.39656,22.484537L102.39656,20.22129L104.17483,16.66476L103.0432,14.078192L100.61829,9.2283781L100.94162,8.4200758L102.07324,7.6117734ZM92.616548,13.590738L94.637312,13.429078L95.122294,14.803197L96.658073,13.186582L99.002155,13.186582L99.810458,14.722361L98.274678,16.419801L98.92133,17.228114L98.193853,19.248875L96.819734,19.653021C96.819734,19.653021,95.930596,19.733857,95.930596,19.410536C95.930596,19.087215,97.385551,16.823958,97.385551,16.823958L95.688111,16.258141L95.36479,17.713095L94.637312,18.359737L93.10153,16.09648L92.616548,13.590738Z"},
  {id:"WI",name:"Wisconsin",shape:"M615.06589,197.36866L614.99915,194.21124L613.82004,189.68474L613.1734,183.54165L612.04178,181.11674L613.01174,178.04519L613.82004,175.1353L615.27499,172.54874L614.62834,169.15387L613.9817,165.59734L614.46668,163.81907L616.40661,161.39416L616.56827,158.64593L615.75997,157.35265L616.40661,154.76608L615.95409,150.59537L618.70232,144.93726L621.61221,138.14752L621.77387,135.88427L621.45055,134.91431L620.64224,135.39929L616.43907,141.70405L613.69084,145.74556L611.75092,147.52383L610.94262,149.78707L608.98767,150.59537L607.85605,152.5353L606.4011,152.21198L606.23944,150.43371L607.53273,148.00881L609.63431,143.32065L611.41258,141.70405L612.40341,139.3462L609.84296,137.44486L607.86814,127.07787L604.32067,125.73589L602.37441,123.42756L590.2447,120.70592L587.36881,119.69387L579.15569,117.52658L571.23777,116.36783L567.47261,111.23716L566.72221,111.79117L565.5243,111.62951L564.87765,110.49789L563.54364,110.79444L562.41201,110.9561L560.63375,111.92606L559.66378,111.27942L560.31043,109.33949L562.25035,106.26794L563.38197,105.13632L561.44205,103.68138L559.34046,104.48968L556.43057,106.4296L548.99419,109.66281L546.0843,110.30945L543.17442,109.82447L542.19269,108.94622L540.07599,111.7814L539.84737,114.52487L539.84737,122.9839L538.70425,124.58427L533.44593,128.47084L531.15971,134.41503L531.61695,134.64365L534.1318,136.70126L534.81766,139.90198L532.98868,143.10269L532.98868,146.98928L533.44593,153.61933L536.41802,156.59143L539.84737,156.59143L541.67635,159.79215L545.10568,160.24939L548.99227,165.96496L556.07957,170.08017L558.13717,172.82364L559.05167,180.25388L559.73753,183.5689L562.02376,185.16926L562.25238,186.541L560.19478,189.97033L560.4234,193.17106L562.93825,197.05764L565.4531,198.20075L568.42519,198.65799L569.76753,200.03811L615.06589,197.36866Z"},
  {id:"WV",name:"West Virginia",shape:"M761.18551,238.96731L762.29752,243.91184L763.38096,249.94317L765.51125,247.36283L767.77449,244.29127L770.31287,243.67572L771.76782,242.22078L773.54609,239.63421L774.99107,240.28085L777.90096,239.95753L780.48754,237.85594L782.49443,236.40268L784.33966,235.91769L785.64358,236.93416L789.28683,238.75579L791.22676,240.53406L792.60088,241.82734L791.83916,247.38228L786.00425,244.84106L781.759,243.21904L781.65786,248.39747L778.91022,253.9342L776.38019,256.36086L775.1881,259.11025L772.54452,259.61035L771.64668,263.21223L770.60345,267.1619L766.63521,267.50264L764.31148,265.06376L763.24033,265.62317L762.60765,271.09287L761.25736,274.62737L756.29896,285.58234L757.19565,286.74304L756.98979,288.65158L754.1811,292.53605L752.3726,291.99176L749.40455,294.1515L746.86217,293.57929L744.86294,298.13486C744.86294,298.13486,741.60363,299.56508,740.94003,299.50258C740.77952,299.48746,738.47093,298.25348,738.47093,298.25348L736.13441,299.63285L733.72461,300.67725L729.97992,299.78813L728.85852,298.61985L726.6663,295.59649L723.52371,293.60837L721.81214,289.98513L717.52726,286.51694L716.88061,284.25369L714.29404,282.79874L713.48573,281.18214L713.24324,275.92816L715.42566,275.84733L717.3656,275.03903L717.52726,272.2908L719.14386,270.83585L719.30552,265.82437L720.27548,261.94451L721.56877,261.29787L722.86205,262.42949L723.34704,264.20776L725.12531,263.23779L725.61029,261.62119L724.47867,259.84292L724.47867,257.41801L725.44863,256.12472L727.71188,252.72985L729.00516,251.27491L731.10676,251.75989L733.37,250.14327L736.44155,246.7484L738.70481,242.86854L739.02813,237.21043L739.51311,232.19894L739.51311,227.51078L738.38149,224.43923L739.35145,222.98427L740.63493,221.69099L744.12618,241.51811L748.75719,240.76696L761.18551,238.96731Z"},
  {id:"WY",name:"Wyoming",shape:"M360.37668,143.27587L253.63408,129.81881L239.5506,218.27684L352.81521,231.86233L360.37668,143.27587Z"},
]

function Maps() {
  const [notaries, setNotaries] = useState([])
  const [clients,  setClients]  = useState([])
  const [jobs,     setJobs]     = useState([])
  const [loading,  setLoading]  = useState(true)
  const [hoverState, setHoverState] = useState(null)
  const [mode, setMode] = useState('notaries')

  useEffect(() => {
    Promise.all([
      supabaseAdmin.from('profiles').select('*, notaries(*)').eq('role','notary').eq('is_approved',true),
      supabaseAdmin.from('profiles').select('*').eq('role','client').eq('is_approved',true),
      supabase.from('jobs').select('signing_state, status, total_fee'),
    ]).then(([{ data: nots }, { data: cls }, { data: jbs }]) => {
      setNotaries(nots || [])
      setClients(cls || [])
      setJobs(jbs || [])
      setLoading(false)
    })
  }, [])

  const notaryByState = {}
  notaries.forEach(n => {
    const raw = n.notaries?.commission_state || n.state || ''
    const st = raw.toUpperCase().trim()
    if (!st) return
    if (!notaryByState[st]) notaryByState[st] = []
    notaryByState[st].push(n)
  })

  const companyByState = {}
  clients.forEach(c => {
    const st = (c.business_state || c.state || '').toUpperCase().trim()
    if (!st) return
    if (!companyByState[st]) companyByState[st] = []
    companyByState[st].push(c)
  })

  const jobsByState = {}
  jobs.forEach(j => {
    const st = (j.signing_state || '').toUpperCase().trim()
    if (!st) return
    if (!jobsByState[st]) jobsByState[st] = { count:0, revenue:0 }
    jobsByState[st].count++
    jobsByState[st].revenue += j.total_fee || 0
  })

  const isNotaries  = mode === 'notaries'
  const activeByState = isNotaries ? notaryByState : companyByState
  const unitLabel   = isNotaries ? 'notaries' : 'companies'
  const unitLabelS  = isNotaries ? 'notary'   : 'company'

  const NAME = {}
  US_GEO.forEach(s => { NAME[s.id] = s.name })

  function getColor(count) {
    if (count === 0) return '#F0EEE8'
    if (count >= 5)  return '#0D1B3E'
    if (count >= 3)  return '#1A4A6B'
    if (count >= 2)  return '#2D7AB5'
    return '#C9A84C'
  }

  const statesWithData = US_GEO.filter(s => s.id !== 'DC').map(s => ({
    code: s.id, name: s.name,
    unitCount:    (activeByState[s.id] || []).length,
    notaryCount:  (notaryByState[s.id]  || []).length,
    companyCount: (companyByState[s.id] || []).length,
    jobCount:     (jobsByState[s.id]?.count)   || 0,
    revenue:      (jobsByState[s.id]?.revenue) || 0,
  })).sort((a, b) => b.unitCount - a.unitCount)

  const stateCount = Object.keys(activeByState).length
  const totalNots  = notaries.length
  const totalCos   = clients.length
  const totalJobs  = jobs.length
  const totalRev   = jobs.reduce((s, j) => s + (j.total_fee || 0), 0)

  const gaps = isNotaries
    ? statesWithData.filter(s => s.notaryCount === 0 && s.jobCount > 0)
    : statesWithData.filter(s => s.notaryCount === 0 && s.companyCount > 0)

  const sw = { display:'inline-block', width:14, height:14, borderRadius:3, marginRight:5, verticalAlign:'middle' }

  return (
    <>
      <h1 className="page-title">Coverage Map</h1>
      <p className="page-sub">Nationwide visibility of your {isNotaries ? 'notary network and signing volume' : 'client companies and where they need coverage'}</p>

      <div style={{ display:'flex', gap:8, marginBottom:18, flexWrap:'wrap' }}>
        <button onClick={() => { setMode('notaries'); setHoverState(null) }}
          style={{ flex:'1 1 160px', padding:'12px 16px', borderRadius:12, cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700,
                   border: isNotaries ? '2px solid #0D1B3E' : '2px solid #E8E6DF',
                   background: isNotaries ? '#0D1B3E' : '#fff',
                   color: isNotaries ? '#fff' : '#7A7870', transition:'all 0.15s' }}>
          🔵 Your Notaries
        </button>
        <button onClick={() => { setMode('companies'); setHoverState(null) }}
          style={{ flex:'1 1 160px', padding:'12px 16px', borderRadius:12, cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700,
                   border: !isNotaries ? '2px solid #C9A84C' : '2px solid #E8E6DF',
                   background: !isNotaries ? '#C9A84C' : '#fff',
                   color: !isNotaries ? '#0D1B3E' : '#7A7870', transition:'all 0.15s' }}>
          🏢 Your Companies
        </button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        <div className="stat-card"><div className="stat-label">States Covered</div><div className="stat-value gold">{stateCount}</div><div className="stat-sub">of 50 states</div></div>
        <div className="stat-card"><div className="stat-label">{isNotaries ? 'Total Notaries' : 'Total Companies'}</div><div className="stat-value">{isNotaries ? totalNots : totalCos}</div><div className="stat-sub">approved</div></div>
        <div className="stat-card"><div className="stat-label">Signings Performed</div><div className="stat-value" style={{ color:'#2D7A4F' }}>{totalJobs}</div></div>
        <div className="stat-card"><div className="stat-label">Total Revenue</div><div className="stat-value" style={{ color:'#2D7A4F' }}>${totalRev.toLocaleString()}</div></div>
      </div>

      {loading && <div style={{ textAlign:'center', padding:48 }}><div className="spinner" style={{ margin:'auto' }} /></div>}

      {!loading && (
        <div className="section-card">
          <div className="section-header"><span className="section-title">State-by-State Coverage — {isNotaries ? 'Notaries' : 'Companies'}</span></div>
          <div className="section-body">
            <div style={{ fontSize:11, color:'#7A7870', marginBottom:12, display:'flex', gap:14, flexWrap:'wrap' }}>
              <span><span style={{ ...sw, background:'#0D1B3E' }} />5+ {unitLabel}</span>
              <span><span style={{ ...sw, background:'#1A4A6B' }} />3-4 {unitLabel}</span>
              <span><span style={{ ...sw, background:'#2D7AB5' }} />2 {unitLabel}</span>
              <span><span style={{ ...sw, background:'#C9A84C' }} />1 {unitLabelS}</span>
              <span><span style={{ ...sw, background:'#F0EEE8', border:'1px solid #E8E6DF' }} />None yet</span>
            </div>

            <div style={{ width:'100%', overflowX:'auto', marginBottom:16 }}>
              <svg viewBox="0 0 960 600" style={{ width:'100%', maxWidth:760, height:'auto', margin:'0 auto', display:'block' }}>
                {US_GEO.map(s => {
                  const count   = (activeByState[s.id] || []).length
                  const isHover = hoverState === s.id
                  return (
                    <path key={s.id} d={s.shape}
                      onMouseEnter={() => setHoverState(s.id)}
                      onMouseLeave={() => setHoverState(null)}
                      onClick={() => setHoverState(s.id)}
                      style={{
                        fill: isHover ? '#C9A84C' : getColor(count),
                        stroke: isHover ? '#9A7A30' : '#FFFFFF',
                        strokeWidth: isHover ? 1.6 : 0.8,
                        cursor: 'pointer',
                        transition: 'fill 0.15s',
                      }}>
                      <title>{s.name}</title>
                    </path>
                  )
                })}
              </svg>
            </div>

            {hoverState && (
              <div style={{ background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:10, padding:14, marginBottom:16 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'#0D1B3E' }}>{NAME[hoverState] || hoverState}</div>
                <div style={{ fontSize:11, color:'#7A7870', marginTop:2 }}>
                  {(notaryByState[hoverState] || []).length} notaries · {(companyByState[hoverState] || []).length} companies · {(jobsByState[hoverState]?.count || 0)} signings · ${(jobsByState[hoverState]?.revenue || 0).toLocaleString()} revenue
                </div>
              </div>
            )}

            <div style={{ marginTop:24 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#7A7870', letterSpacing:'0.06em', marginBottom:10 }}>TOP STATES BY COVERAGE</div>
              <table className="data-table">
                <thead>
                  <tr><th>State</th><th>Notaries</th><th>Companies</th><th>Signings</th><th>Revenue</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {statesWithData.filter(s => s.notaryCount > 0 || s.companyCount > 0 || s.jobCount > 0).slice(0, 15).map(s => (
                    <tr key={s.code}>
                      <td style={{ fontWeight:500 }}>{s.code} — {s.name}</td>
                      <td>{s.notaryCount > 0 ? <span style={{ fontWeight:500, color:'#0D1B3E' }}>{s.notaryCount}</span> : <span style={{ color:'#E24B4A', fontStyle:'italic' }}>None!!</span>}</td>
                      <td>{s.companyCount > 0 ? <span style={{ fontWeight:500, color:'#9A7A30' }}>{s.companyCount}</span> : <span style={{ color:'#B8B5AB' }}>—</span>}</td>
                      <td>{s.jobCount}</td>
                      <td style={{ color:'#2D7A4F', fontWeight:500 }}>${s.revenue.toLocaleString()}</td>
                      <td>
                        {isNotaries
                          ? (s.notaryCount === 0 && s.jobCount > 0
                              ? <span className="badge badge-pending">Coverage Gap!!</span>
                              : s.notaryCount >= 3 ? <span className="badge badge-approved">Strong Coverage</span>
                              : s.notaryCount >= 1 ? <span className="badge" style={{ background:'#FDF6E3', color:'#9A7A30' }}>Light Coverage</span>
                              : <span style={{ fontSize:11, color:'#B8B5AB' }}>—</span>)
                          : (s.companyCount > 0 && s.notaryCount === 0
                              ? <span className="badge badge-pending">Needs a Notary!!</span>
                              : s.companyCount > 0 && s.notaryCount >= 1 ? <span className="badge badge-approved">Served</span>
                              : <span style={{ fontSize:11, color:'#B8B5AB' }}>—</span>)
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {gaps.length > 0 && (
              <div style={{ background:'#FDF2F2', border:'1px solid rgba(226,75,74,0.3)', borderRadius:10, padding:14, marginTop:16 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#C0392B', marginBottom:6 }}>⚠️ Coverage Gaps Detected!!</div>
                <div style={{ fontSize:11, color:'#7A2D2D' }}>
                  {isNotaries
                    ? <>These states had signings but no notaries: {gaps.map(s => s.code).join(', ')}</>
                    : <>You have client companies in these states but no notary to serve them: {gaps.map(s => s.code).join(', ')}</>}
                </div>
                <div style={{ fontSize:11, color:'#7A2D2D', marginTop:4 }}>Recruit notaries in these states to grow coverage!!</div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

// ── SERVICES MASTER LIST + FEE CALCULATOR (MERGED) ────────────
const DEFAULT_SERVICES_DATA = [
    { name:'Affidavit (General)',               category:'affidavits',    client_price:35,  notary_pay:20  },
    { name:'Acknowledgement',                   category:'affidavits',    client_price:35,  notary_pay:20  },
    { name:'Jurat / Oath',                      category:'affidavits',    client_price:35,  notary_pay:20  },
    { name:'Name Change Affidavit',             category:'affidavits',    client_price:35,  notary_pay:20  },
    { name:'Residency Affidavit',               category:'affidavits',    client_price:45,  notary_pay:25  },
    { name:'Identity Affidavit',                category:'affidavits',    client_price:45,  notary_pay:25  },
    { name:'Lost Documents Affidavit',          category:'affidavits',    client_price:55,  notary_pay:30  },
    { name:'Financial Affidavit',               category:'affidavits',    client_price:75,  notary_pay:49  },
    { name:'Heirship Affidavit',                category:'affidavits',    client_price:175, notary_pay:114 },
    { name:'Estate Affidavit',                  category:'affidavits',    client_price:175, notary_pay:114 },
    { name:'Certified Identity Statement',      category:'affidavits',    client_price:35,  notary_pay:20  },
    { name:'Certified Residency Statement',     category:'affidavits',    client_price:35,  notary_pay:20  },
    { name:'Parental Consent Form',             category:'family',        client_price:35,  notary_pay:20  },
    { name:'Tattoo Parental Consent (Minor)',   category:'family',        client_price:35,  notary_pay:20  },
    { name:'Travel Consent Letter for Minors',  category:'family',        client_price:100, notary_pay:65  },
    { name:'Adoption Documents',                category:'family',        client_price:175, notary_pay:114 },
    { name:'Guardianship / Custodian Affidavit',category:'family',        client_price:150, notary_pay:98  },
    { name:'School Documents',                  category:'family',        client_price:75,  notary_pay:49  },
    { name:'Proof of Life / Life Certificate',  category:'family',        client_price:75,  notary_pay:49  },
    { name:'Power of Attorney',                 category:'legal',         client_price:150, notary_pay:98  },
    { name:'Advance Health Directive',          category:'legal',         client_price:150, notary_pay:98  },
    { name:'Living Will',                       category:'legal',         client_price:150, notary_pay:98  },
    { name:'Court Documents',                   category:'legal',         client_price:125, notary_pay:81  },
    { name:'Hold Harmless Agreement',           category:'legal',         client_price:100, notary_pay:65  },
    { name:'Consent & Release Form',            category:'legal',         client_price:75,  notary_pay:49  },
    { name:'Private Party Agreement',           category:'legal',         client_price:100, notary_pay:65  },
    { name:'Medical Authorization Form',        category:'medical',       client_price:75,  notary_pay:49  },
    { name:'Insurance Claims Form',             category:'medical',       client_price:75,  notary_pay:49  },
    { name:'Hospital / Nursing Home Signing',   category:'medical',       client_price:175, notary_pay:123 },
    { name:'Banking Forms',                     category:'financial',     client_price:75,  notary_pay:49  },
    { name:'Pension / Retirement Forms',        category:'financial',     client_price:125, notary_pay:81  },
    { name:'Promissory Note (Non-Real Estate)', category:'financial',     client_price:125, notary_pay:81  },
    { name:'Bank Safe Deposit Box Inventory',   category:'financial',     client_price:250, notary_pay:163 },
    { name:'Lease Agreement',                   category:'realestate',    client_price:75,  notary_pay:49  },
    { name:'Rental Verification Form',          category:'realestate',    client_price:75,  notary_pay:49  },
    { name:'Bill of Sale',                      category:'realestate',    client_price:75,  notary_pay:49  },
    { name:'Employment Verification Form',      category:'government',    client_price:75,  notary_pay:49  },
    { name:'I-9 Form',                          category:'government',    client_price:75,  notary_pay:49  },
    { name:'DMV Forms',                         category:'government',    client_price:75,  notary_pay:49  },
    { name:'Passport Forms',                    category:'government',    client_price:75,  notary_pay:49  },
    { name:'VIN / Vehicle Identification Statement', category:'government', client_price:75, notary_pay:49 },
    { name:'Vehicle Purchase Contract',         category:'dealership',    client_price:100, notary_pay:55  },
    { name:'Title Transfer',                    category:'dealership',    client_price:100, notary_pay:55  },
    { name:'Lease Agreement (Auto)',            category:'dealership',    client_price:100, notary_pay:55  },
    { name:'Finance Documents Package',         category:'dealership',    client_price:150, notary_pay:83  },
    { name:'Power of Attorney (Vehicle)',       category:'dealership',    client_price:125, notary_pay:69  },
    { name:'Odometer Disclosure Statement',     category:'dealership',    client_price:75,  notary_pay:41  },
    { name:'Bill of Sale (Vehicle)',            category:'dealership',    client_price:75,  notary_pay:41  },
    { name:'Dealer Reassignment Form',          category:'dealership',    client_price:100, notary_pay:55  },
    { name:'Out-of-State Title Transfer',       category:'dealership',    client_price:75,  notary_pay:41  },
    { name:'Fleet / Commercial Packet',         category:'dealership',    client_price:125, notary_pay:69  },
    { name:'Dealership Bulk Visit',             category:'dealership',    client_price:100, notary_pay:55  },
    { name:'Refinance Package',                 category:'loans',         client_price:175, notary_pay:123 },
    { name:'Buyer Closing Package',             category:'loans',         client_price:225, notary_pay:169 },
    { name:'Seller Closing Package',            category:'loans',         client_price:150, notary_pay:105 },
    { name:'HELOC Package',                     category:'loans',         client_price:150, notary_pay:105 },
    { name:'Reverse Mortgage (Final)',          category:'loans',         client_price:275, notary_pay:215 },
    { name:'Loan Modification',                 category:'loans',         client_price:125, notary_pay:88  },
    { name:'Scanbacks',                         category:'loans',         client_price:35,  notary_pay:20  },
    { name:'Apostille',                         category:'international', client_price:200, notary_pay:130 },
    { name:'International Document Statement',  category:'international', client_price:150, notary_pay:98  },
    { name:'Certified Copies',                  category:'international', client_price:100, notary_pay:65  },
    { name:'Signature Witnessing',              category:'signature',     client_price:75,  notary_pay:49  },
    { name:'Prenuptial / Postnuptial Agreement',category:'signature',     client_price:250, notary_pay:163 },
    { name:'Wedding Ceremony Officiation',      category:'wedding',       client_price:200, notary_pay:120 },
].map((s,i) => ({ ...s, id:`default_${i}` }))
function ServicesMaster() {
  const [services,   setServices]   = useState(DEFAULT_SERVICES_DATA)
  const [loading,    setLoading]    = useState(true)
  const [filterCat,  setFilterCat]  = useState('all')
  const [editingId,  setEditingId]  = useState(null)
  const [editForm,   setEditForm]   = useState({})
  const [adding,     setAdding]     = useState(false)
  const [addForm,    setAddForm]    = useState({ name:'', category:'affidavits', client_price:'', individual_price:'', notary_pay:'', show_to_individuals:false })
  const [saving,     setSaving]     = useState(false)
  const [isVet,      setIsVet]      = useState(false)
  const [addingCategory,   setAddingCategory]   = useState(false)
  const [catForm,          setCatForm]          = useState({ icon:'📋', name_en:'', name_es:'', desc_en:'', desc_es:'' })
  const [savingCat,        setSavingCat]        = useState(false)

  const CATEGORIES = [
    { key:'affidavits',    label:'Affidavits'              },
    { key:'family',        label:'Family'                  },
    { key:'legal',         label:'Legal'                   },
    { key:'medical',       label:'Medical'                 },
    { key:'financial',     label:'Financial'               },
    { key:'realestate',    label:'Real Estate & Property'  },
    { key:'government',    label:'Employment & Government' },
    { key:'dealership',    label:'Auto / Dealership'       },
    { key:'loans',         label:'Loan Signings'           },
    { key:'international', label:'International'           },
    { key:'signature',     label:'Signature'               },
    { key:'wedding',       label:'Wedding'                 },
  ]

  useEffect(() => { loadServices() }, [])

  async function loadServices() {
    setLoading(true)
    try {
      const { data, error } = await supabase.from('services').select('*').order('category').order('name')
      if (!error && data && data.length > 0) { setServices(data) }
      else if (!error && (!data || data.length === 0)) {
        await supabase.from('services').insert(DEFAULT_SERVICES_DATA.map(({id,...s})=>s))
        const { data: seeded } = await supabase.from('services').select('*').order('category').order('name')
        setServices(seeded && seeded.length > 0 ? seeded : DEFAULT_SERVICES_DATA)
      } else { setServices(DEFAULT_SERVICES_DATA) }
    } catch(err) { setServices(DEFAULT_SERVICES_DATA) }
    setLoading(false)
  }

  async function saveEdit(id) {
    setSaving(true)
    try {
      await supabase.from('services').update({ name:editForm.name, category:editForm.category, client_price:Number(editForm.client_price), individual_price:Number(editForm.individual_price)||0, notary_pay:Number(editForm.notary_pay), show_to_individuals:!!editForm.show_to_individuals }).eq('id', id)
      toast.success('Service updated!!'); setEditingId(null); loadServices()
    } catch(err) { toast.error(err.message) } finally { setSaving(false) }
  }

  async function addService() {
    if (!addForm.name.trim()) { toast.error('Please enter a service name!!'); return }
    setSaving(true)
    try {
      await supabase.from('services').insert({ name:addForm.name.trim(), category:addForm.category, client_price:Number(addForm.client_price)||0, individual_price:Number(addForm.individual_price)||0, notary_pay:Number(addForm.notary_pay)||0, show_to_individuals:!!addForm.show_to_individuals })
      toast.success('Service added!!'); setAdding(false); setAddForm({ name:'', category:'affidavits', client_price:'', individual_price:'', notary_pay:'', show_to_individuals:false }); loadServices()
    } catch(err) { toast.error(err.message) } finally { setSaving(false) }
  }

  async function deleteService(id, name) {
    if (!window.confirm('Delete "' + name + '"? This cannot be undone!!')) return
    await supabase.from('services').delete().eq('id', id)
    toast.success('Deleted!!'); loadServices()
  }

  async function saveCategory() {
    if (!catForm.name_en.trim()) { toast.error('English category name is required!!'); return }
    setSavingCat(true)
    try {
      await supabase.from('service_categories').insert({
        icon:       catForm.icon,
        name_en:    catForm.name_en.trim(),
        name_es:    catForm.name_es.trim() || catForm.name_en.trim(),
        desc_en:    catForm.desc_en.trim(),
        desc_es:    catForm.desc_es.trim() || catForm.desc_en.trim(),
        sort_order: 99,
      })
      toast.success('Category added!! It will appear on the public Services page!!')
      setAddingCategory(false)
      setCatForm({ icon:'📋', name_en:'', name_es:'', desc_en:'', desc_es:'' })
    } catch(err) { toast.error(err.message) } finally { setSavingCat(false) }
  }

  const displayed = filterCat === 'all'
    ? services
    : services.filter(s => (s.category || '').toLowerCase().trim() === filterCat.toLowerCase().trim())

  // Count per category — case insensitive
  function catCount(key) {
    return services.filter(s => (s.category||'').toLowerCase().trim() === key.toLowerCase().trim()).length
  }
  const INPUT     = { padding:'6px 10px', border:'1.5px solid #E0D9CC', borderRadius:6, fontSize:11, fontFamily:'inherit', background:'#FAFAF8' }

  if (loading) return <div style={{ textAlign:'center', padding:48 }}><div className="spinner" style={{ margin:'auto' }} /></div>

  return (
    <>
      <h1 className="page-title">Services & Pricing</h1>
      <p className="page-sub">{services.length} services across {CATEGORIES.length} categories</p>

      {/* Toolbar */}
      <div style={{ display:'flex', gap:12, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>

        {/* Veteran discount toggle */}
        <div style={{ display:'flex', alignItems:'center', gap:10, background: isVet ? '#FDF6E3' : '#FAFAF8', border: `1.5px solid ${isVet ? '#C9A84C' : '#E0D9CC'}`, borderRadius:10, padding:'8px 14px' }}>
          <button
            onClick={() => setIsVet(v => !v)}
            style={{ width:36, height:20, borderRadius:10, border:'none', cursor:'pointer', position:'relative', background:isVet?'#C9A84C':'#E0D9CC', flexShrink:0, transition:'background 0.2s' }}>
            <div style={{ width:16, height:16, borderRadius:'50%', background:'#fff', position:'absolute', top:2, left:isVet?18:2, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
          </button>
          <span style={{ fontSize:12, color:isVet?'#8A5C00':'#7A7870', fontWeight:isVet?600:400 }}>
            {isVet ? '🎖️ Veteran 10% discount ON' : '🎖️ Veteran discount OFF'}
          </span>
        </div>

        {/* Category filter */}
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          style={{ ...INPUT, flex:1, maxWidth:280, padding:'8px 12px', fontSize:12 }}>
          <option value="all">All Categories ({services.length} services)</option>
          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label} ({catCount(c.key)})</option>)}
        </select>

        {/* Add button */}
        <button onClick={() => setAdding(a=>!a)}
          style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', border:'none', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#C9A84C', fontFamily:'inherit' }}>
          {adding ? '✕ Cancel' : '＋ Add Service'}
        </button>
        <button onClick={() => setAddingCategory(a=>!a)}
          style={{ background:'linear-gradient(135deg,#2D7A4F,#3A9A60)', border:'none', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#fff', fontFamily:'inherit' }}>
          {addingCategory ? '✕ Cancel' : '🗂️ Add Category'}
        </button>
      </div>

      {/* Add service form */}
      {adding && (
        <div style={{ background:'#fff', border:'1.5px solid #C9A84C', borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:15, fontWeight:600, color:'#0D1B3E', marginBottom:10 }}>➕ New Service</div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1.3fr 0.8fr 0.8fr 0.8fr 0.9fr auto', gap:10, alignItems:'end' }}>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>SERVICE NAME *</div>
              <input value={addForm.name} onChange={e=>setAddForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Apostille Expedited" style={{ ...INPUT, width:'100%' }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>CATEGORY</div>
              <select value={addForm.category} onChange={e=>setAddForm(f=>({...f,category:e.target.value}))} style={{ ...INPUT, width:'100%' }}>
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>CLIENT ($)</div>
              <input type="number" value={addForm.client_price} onChange={e=>setAddForm(f=>({...f,client_price:e.target.value}))} placeholder="0" style={{ ...INPUT, width:'100%' }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#6A3A9A', fontWeight:600, marginBottom:3 }}>INDIVIDUAL ($)</div>
              <input type="number" value={addForm.individual_price} onChange={e=>setAddForm(f=>({...f,individual_price:e.target.value}))} placeholder="0" style={{ ...INPUT, width:'100%' }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>NOTARY ($)</div>
              <input type="number" value={addForm.notary_pay} onChange={e=>setAddForm(f=>({...f,notary_pay:e.target.value}))} placeholder="0" style={{ ...INPUT, width:'100%' }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>👤 SHOW?</div>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', height:34 }}>
                <input type="checkbox" checked={!!addForm.show_to_individuals} onChange={e=>setAddForm(f=>({...f,show_to_individuals:e.target.checked}))} style={{ width:18, height:18, cursor:'pointer' }} />
                <span style={{ fontSize:11, color:'#7A7870' }}>Public</span>
              </label>
            </div>
            <button onClick={addService} disabled={saving}
              style={{ background:'linear-gradient(135deg,#2D7A4F,#3A9A60)', border:'none', borderRadius:8, padding:'8px 16px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#fff', fontFamily:'inherit', whiteSpace:'nowrap' }}>
              {saving ? '...' : '💾 Save'}
            </button>
          </div>
        </div>
      )}

      {/* Add category form */}
      {addingCategory && (
        <div style={{ background:'#fff', border:'1.5px solid #2D7A4F', borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:15, fontWeight:600, color:'#0D1B3E', marginBottom:10 }}>🗂️ New Service Category</div>
          <div style={{ display:'grid', gridTemplateColumns:'auto 1fr 1fr', gap:10, marginBottom:10, alignItems:'end' }}>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>ICON</div>
              <select value={catForm.icon} onChange={e=>setCatForm(f=>({...f,icon:e.target.value}))}
                style={{ ...INPUT, width:60, fontSize:18 }}>
                {['📋','⚖️','👨‍👩‍👧','🏥','💰','🏠','🚗','🌍','💍','🏛️','✍️','🏦','📄','🔏','🎖️','✈️','🏡','💼'].map(ic=><option key={ic} value={ic}>{ic}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>CATEGORY NAME (ENGLISH) *</div>
              <input value={catForm.name_en} onChange={e=>setCatForm(f=>({...f,name_en:e.target.value}))}
                placeholder="e.g. Real Estate Documents" style={{ ...INPUT, width:'100%' }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>CATEGORY NAME (SPANISH)</div>
              <input value={catForm.name_es} onChange={e=>setCatForm(f=>({...f,name_es:e.target.value}))}
                placeholder="e.g. Documentos de Bienes Raíces" style={{ ...INPUT, width:'100%' }} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>DESCRIPTION (EN)</div>
              <input value={catForm.desc_en} onChange={e=>setCatForm(f=>({...f,desc_en:e.target.value}))}
                placeholder="Short description of this category" style={{ ...INPUT, width:'100%' }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>DESCRIPTION (ES)</div>
              <input value={catForm.desc_es} onChange={e=>setCatForm(f=>({...f,desc_es:e.target.value}))}
                placeholder="Descripción corta de esta categoría" style={{ ...INPUT, width:'100%' }} />
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <button onClick={saveCategory} disabled={savingCat}
              style={{ background:'linear-gradient(135deg,#2D7A4F,#3A9A60)', border:'none', borderRadius:8, padding:'9px 20px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#fff', fontFamily:'inherit' }}>
              {savingCat ? 'Saving...' : '💾 Save Category'}
            </button>
          </div>
        </div>
      )}

      {/* Services table */}
      <div className="section-card" style={{ padding:0 }}>
        <table className="data-table" style={{ margin:0 }}>
          <thead>
            <tr>
              <th>Service Name</th>
              <th>Category</th>
              <th>Client Price{isVet ? ' (10% off)' : ''}</th>
              <th>Individual Price</th>
              <th>Notary Pay</th>
              <th>LCA Margin</th>
              <th>👤 Show to Public</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign:'center', color:'#B8B5AB', padding:24, fontSize:12 }}>No services found</td></tr>
            )}
            {displayed.map(s => {
              const isEditing  = editingId === s.id
              const baseClient = Number(s.client_price) || 0
              const baseNotary = Number(s.notary_pay)   || 0
              const dispClient = isVet ? Math.round(baseClient * 0.9) : baseClient
              const mgn        = dispClient - baseNotary
              const pct        = dispClient > 0 ? Math.round((mgn / dispClient) * 100) : 0
              const catLabel   = CATEGORIES.find(c=>c.key===(s.category||'').toLowerCase().trim())?.label || s.category
              return (
                <tr key={s.id} style={{ background:isEditing?'#FFFBF0':undefined }}>
                  <td>
                    {isEditing
                      ? <input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} style={{ ...INPUT, width:'100%' }} />
                      : <span style={{ fontWeight:500 }}>{s.name}</span>}
                  </td>
                  <td>
                    {isEditing
                      ? <select value={editForm.category} onChange={e=>setEditForm(f=>({...f,category:e.target.value}))} style={{ ...INPUT, width:'100%' }}>
                          {CATEGORIES.map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                      : <span style={{ fontSize:11, color:'#7A7870' }}>{catLabel}</span>}
                  </td>
                  <td>
                    {isEditing
                      ? <input type="number" value={editForm.client_price} onChange={e=>setEditForm(f=>({...f,client_price:e.target.value}))} style={{ ...INPUT, width:70 }} />
                      : <div>
                          <span style={{ color:'#0D1B3E', fontWeight:600 }}>${dispClient}</span>
                          {isVet && baseClient !== dispClient && (
                            <span style={{ fontSize:10, color:'#B8B5AB', marginLeft:4, textDecoration:'line-through' }}>${baseClient}</span>
                          )}
                        </div>}
                  </td>
                  <td>
                    {isEditing
                      ? <input type="number" value={editForm.individual_price} onChange={e=>setEditForm(f=>({...f,individual_price:e.target.value}))} style={{ ...INPUT, width:70 }} />
                      : <span style={{ color:'#6A3A9A', fontWeight:600 }}>${Number(s.individual_price)||0}</span>}
                  </td>
                  <td>
                    {isEditing
                      ? <input type="number" value={editForm.notary_pay} onChange={e=>setEditForm(f=>({...f,notary_pay:e.target.value}))} style={{ ...INPUT, width:70 }} />
                      : <span style={{ color:'#2D7A4F' }}>${baseNotary}</span>}
                  </td>
                  <td>
                    <span style={{ background:pct>=30?'#EAF5EE':'#FDF6E3', color:pct>=30?'#2D7A4F':'#9A7A30', fontWeight:700, fontSize:11, padding:'2px 8px', borderRadius:20 }}>
                      ${mgn} ({pct}%)
                    </span>
                  </td>
                  <td>
                    {isEditing
                      ? <label style={{ display:'inline-flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                          <input type="checkbox" checked={!!editForm.show_to_individuals} onChange={e=>setEditForm(f=>({...f,show_to_individuals:e.target.checked}))} style={{ width:16, height:16, cursor:'pointer' }} />
                          <span style={{ fontSize:11, color:'#7A7870' }}>Public</span>
                        </label>
                      : (s.show_to_individuals
                          ? <span style={{ background:'#F0EBF8', color:'#6A3A9A', fontWeight:700, fontSize:11, padding:'2px 10px', borderRadius:20 }}>👤 Public</span>
                          : <span style={{ fontSize:11, color:'#B8B5AB' }}>Hidden</span>)}
                  </td>
                  <td>
                    {isEditing ? (
                      <div style={{ display:'flex', gap:4 }}>
                        <button onClick={() => saveEdit(s.id)} disabled={saving}
                          style={{ background:'#2D7A4F', border:'none', borderRadius:6, padding:'5px 10px', cursor:'pointer', fontSize:11, color:'#fff', fontFamily:'inherit' }}>
                          {saving?'...':'💾'}
                        </button>
                        <button onClick={() => setEditingId(null)}
                          style={{ background:'none', border:'1px solid #E0D9CC', borderRadius:6, padding:'5px 8px', cursor:'pointer', fontSize:11, color:'#7A7870', fontFamily:'inherit' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display:'flex', gap:4 }}>
                        <button onClick={() => { setEditingId(s.id); setEditForm({ name:s.name, category:s.category, client_price:s.client_price, individual_price:s.individual_price, notary_pay:s.notary_pay, show_to_individuals:s.show_to_individuals }) }}
                          style={{ background:'none', border:'1px solid #E0D9CC', borderRadius:6, padding:'5px 10px', cursor:'pointer', fontSize:11, color:'#7A7870', fontFamily:'inherit' }}>✏️</button>
                        <button onClick={() => deleteService(s.id, s.name)}
                          style={{ background:'none', border:'1px solid rgba(226,75,74,0.3)', borderRadius:6, padding:'5px 8px', cursor:'pointer', fontSize:11, color:'#E24B4A', fontFamily:'inherit' }}>🗑️</button>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}


// ── SCAN-BACKS QC ─────────────────────────────────────────────
function ScanBacksQC() {
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from('jobs')
      .select('*, profiles!jobs_notary_id_fkey(full_name, notaries(vendor_number))')
      .in('status', ['completed', 'scanbacks_uploaded', 'client_approved', 'closed'])
      .order('created_at', { ascending:false })
      .limit(50)
      .then(({ data }) => { setJobs(safeParseJobs(data).filter(j => j.job_type !== 'individual' && j.scan_backs_required !== false)); setLoading(false) })
  }, [])

  return (
    <>
      <h1 className="page-title">Scan-Backs QC</h1>
      <p className="page-sub">Review scanned documents from completed signings</p>
      {loading && <div style={{ textAlign:'center', padding:48 }}><div className="spinner" style={{ margin:'auto' }} /></div>}
      {!loading && jobs.length === 0 && (
        <div style={{ background:'#F8F7F4', borderRadius:14, padding:40, textAlign:'center' }}>
          <div style={{ fontSize:32, marginBottom:12 }}>📁</div>
          <div style={{ fontSize:14, color:'#7A7870' }}>No completed signings to QC yet!!</div>
        </div>
      )}
      {!loading && jobs.length > 0 && (
        <div className="section-card" style={{ padding:0 }}>
          <table className="data-table">
            <thead><tr><th>Job #</th><th>Notary</th><th>Service</th><th>Borrower</th><th>Date</th><th>Status</th></tr></thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id}>
                  <td style={{ fontWeight:500 }}>{j.job_number}</td>
                  <td>
                    <div style={{ fontSize:12, fontWeight:500 }}>{j.profiles?.full_name || '—'}</div>
                    <VendorBadge number={j.profiles?.notaries?.vendor_number} />
                  </td>
                  <td style={{ fontSize:12 }}>{j.signing_type}</td>
                  <td>{j.borrower_name}</td>
                  <td>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</td>
                  <td><StatusBadge status={j.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ── INVOICES ─────────────────────────────────────────────────
function Invoices() {
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [filter,  setFilter]  = useState('all')
  const [saving,  setSaving]  = useState(null)
  const [invoiceJob, setInvoiceJob] = useState(null)

  useEffect(() => { load() }, [])
  async function load() {
    const { data } = await supabase.from('jobs')
      .select('*, profiles!jobs_client_id_fkey(full_name, company_name, email, client_number)')
      .order('created_at', { ascending:false })
    setJobs(safeParseJobs(data)); setLoading(false)
  }

  // 💵 Record a client payment (net 30/45/60) — independent of notary pay!!
  async function markClientPaid(job) {
    setSaving(job.id)
    try {
      await supabase.from('jobs').update({ client_paid_at: new Date().toISOString() }).eq('id', job.id)
      toast.success(`💵 Client payment recorded for ${job.job_number}!!`)
      load()
    } catch (err) { toast.error(err.message) } finally { setSaving(null) }
  }

  const filtered = jobs.filter(j => {
    if (filter === 'all') return true
    if (filter === 'unpaid') return j.invoice_sent_at && !j.client_paid_at
    if (filter === 'paid') return !!j.client_paid_at
    return true
  })

  const totalUnpaid = jobs.filter(j => j.invoice_sent_at && !j.client_paid_at).reduce((s, j) => s + (j.total_fee || 0), 0)
  const totalPaid   = jobs.filter(j => !!j.client_paid_at).reduce((s, j) => s + (j.total_fee || 0), 0)

  return (
    <>
      <h1 className="page-title">Invoices</h1>
      <p className="page-sub">Client invoices for all signings</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        <div className="stat-card"><div className="stat-label">Total Invoiced</div><div className="stat-value">{jobs.length}</div></div>
        <div className="stat-card"><div className="stat-label">Outstanding</div><div className="stat-value" style={{ color:'#E24B4A' }}>${totalUnpaid.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Collected</div><div className="stat-value" style={{ color:'#2D7A4F' }}>${totalPaid.toLocaleString()}</div></div>
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {['all', 'unpaid', 'paid'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`btn ${filter===f ? 'btn-primary' : 'btn-outline'} btn-sm`} style={{ textTransform:'capitalize' }}>{f}</button>
        ))}
      </div>
      {loading && <div style={{ textAlign:'center', padding:48 }}><div className="spinner" style={{ margin:'auto' }} /></div>}
      {!loading && (
        <div className="section-card" style={{ padding:0 }}>
          <table className="data-table">
            <thead><tr><th>Job #</th><th>Client</th><th>Service</th><th>Date</th><th>Amount</th><th>Invoice</th><th>Payment</th></tr></thead>
            <tbody>
              {filtered.map(j => (
                <tr key={j.id}>
                  <td style={{ fontWeight:500 }}>
                    {j.job_number}
                    <div><button onClick={() => setInvoiceJob(j)} style={{ marginTop:6, background:'#fff', color:'#9A7A30', border:'1px solid #C9A84C', borderRadius:6, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>🧾 Invoice</button></div>
                  </td>
                  <td>
                    <div style={{ fontSize:12, fontWeight:500 }}>{j.profiles?.company_name || j.profiles?.full_name || '—'}</div>
                    <div style={{ fontSize:10, color:'#9A7A30' }}>{j.profiles?.client_number || ''}</div>
                  </td>
                  <td style={{ fontSize:12 }}>{j.signing_type}</td>
                  <td>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</td>
                  <td style={{ color:'#2D7A4F', fontWeight:500 }}>${j.total_fee || 0}</td>
                  <td>{j.invoice_sent_at
                    ? <span className="badge badge-approved" style={{ fontSize:10 }}>Sent {new Date(j.invoice_sent_at).toLocaleString()}</span>
                    : <span style={{ fontSize:11, color:'#B8B5AB' }}>Not sent</span>}</td>
                  <td>{j.client_paid_at
                    ? <span style={{ fontSize:11, color:'#2D7A4F' }}>✓ Paid {new Date(j.client_paid_at).toLocaleString()}</span>
                    : <button className="btn btn-primary btn-sm" onClick={() => markClientPaid(j)} disabled={saving===j.id}>{saving===j.id?'...':'Mark Client Paid'}</button>}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} style={{ textAlign:'center', color:'#B8B5AB', padding:24 }}>No invoices in this view</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {invoiceJob && <InvoiceModal job={invoiceJob} onClose={() => setInvoiceJob(null)} />}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════
// MAIN ADMIN PORTAL EXPORT
// ═══════════════════════════════════════════════════════════════════
// ── 🥜 NUTTY NOTARIES BLACKLIST ───────────────────────────────
// Nancy's private do-not-assign list
// Saves to Supabase profiles.is_blacklisted + blacklist_reason
function NuttyNotaries() {
  const [notaries,   setNotaries]   = useState([])
  const [blacklist,  setBlacklist]  = useState([])
  const [loading,    setLoading]    = useState(true)
  const [search,     setSearch]     = useState('')
  const [adding,     setAdding]     = useState(false)
  const [form,       setForm]       = useState({ name:'', reason:'', date:'' })
  const [saving,     setSaving]     = useState(false)

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    const { data: nots } = await supabaseAdmin
      .from('profiles')
      .select('id, full_name, email, state, notaries(vendor_number, is_blacklisted, blacklist_reason, blacklisted_at)')
      .eq('role', 'notary')
    setNotaries(nots || [])
    setBlacklist((nots || []).filter(n => n.notaries?.is_blacklisted))
    setLoading(false)
  }

  async function addToBlacklist(notaryId, reason) {
    setSaving(true)
    try {
      await supabase.from('notaries').update({
        is_blacklisted:    true,
        blacklist_reason:  reason,
        blacklisted_at:    new Date().toISOString(),
      }).eq('id', notaryId)
      toast.success('🥜 Added to Nutty Notaries list!!')
      loadData()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function removeFromBlacklist(notaryId) {
    if (!window.confirm('Remove this notary from the blacklist?')) return
    await supabase.from('notaries').update({
      is_blacklisted:   false,
      blacklist_reason: null,
      blacklisted_at:   null,
    }).eq('id', notaryId)
    toast.success('Notary removed from blacklist!!')
    loadData()
  }

  const filteredNotaries = notaries.filter(n =>
    !n.notaries?.is_blacklisted &&
    (n.full_name?.toLowerCase().includes(search.toLowerCase()) ||
     n.email?.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <main tabIndex={-1} style={{ maxWidth:780, margin:'0 auto' }}>
      {/* Hero */}
      <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:14, padding:'20px 24px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:24, color:'#C9A84C', fontWeight:600, marginBottom:4 }}>
              🥜 Nutty Notaries
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)' }}>
              Nancy's private do-not-assign list. These notaries will not appear in job assignments.
            </div>
          </div>
          <div style={{ background:'rgba(226,75,74,0.2)', border:'1px solid rgba(226,75,74,0.4)', borderRadius:10, padding:'8px 16px', textAlign:'center' }}>
            <div style={{ fontSize:24, fontWeight:700, color:'#E24B4A', fontFamily:'Cormorant Garamond,serif' }}>{blacklist.length}</div>
            <div style={{ fontSize:9, color:'rgba(255,255,255,0.4)', letterSpacing:'0.08em' }}>BLACKLISTED</div>
          </div>
        </div>
      </div>

      {/* Current Blacklist */}
      {blacklist.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#E24B4A', letterSpacing:'0.08em', marginBottom:10 }}>
            🚫 DO NOT ASSIGN ({blacklist.length})
          </div>
          {blacklist.map(n => (
            <div key={n.id} style={{ background:'#FDF2F2', border:'1px solid rgba(226,75,74,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:8, display:'flex', alignItems:'flex-start', gap:12 }}>
              <div style={{ width:36, height:36, borderRadius:'50%', background:'#E24B4A', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>🥜</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#0D1B3E' }}>{n.full_name}</div>
                <div style={{ fontSize:11, color:'#7A7870' }}><a href={`mailto:${n.email}`} style={{ color:'inherit', textDecoration:'none' }}>{n.email}</a> {n.state && `· ${n.state}`}</div>
                {n.notaries?.blacklist_reason && (
                  <div style={{ fontSize:11, color:'#E24B4A', marginTop:4, fontStyle:'italic' }}>
                    Reason: {n.notaries.blacklist_reason}
                  </div>
                )}
                {n.notaries?.blacklisted_at && (
                  <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>
                    Added: {new Date(n.notaries.blacklisted_at).toLocaleString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit' })}
                  </div>
                )}
              </div>
              <button
                onClick={() => removeFromBlacklist(n.id)}
                style={{ background:'none', border:'1px solid rgba(226,75,74,0.4)', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:11, color:'#E24B4A', fontFamily:'inherit', flexShrink:0 }}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add from network */}
      <div style={{ background:'#fff', border:'1px solid #E8E6DF', borderRadius:12, overflow:'hidden' }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid #F0EEE8', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:16, fontWeight:600, color:'#0D1B3E' }}>
            Add a Notary to the List
          </div>
        </div>
        <div style={{ padding:'14px 16px' }}>
          <div style={{ position:'relative', marginBottom:12 }}>
            <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#B8B5AB' }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search notary by name or email..."
              style={{ width:'100%', padding:'10px 14px 10px 36px', border:'1.5px solid #E0D9CC', borderRadius:10, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
          </div>

          {search.length > 1 && (
            <div style={{ maxHeight:280, overflowY:'auto' }}>
              {filteredNotaries.length === 0 && (
                <div style={{ textAlign:'center', padding:'20px', color:'#B8B5AB', fontSize:12 }}>No notaries found</div>
              )}
              {filteredNotaries.map(n => (
                <div key={n.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:8, marginBottom:4, background:'#FAFAF8', border:'1px solid #F0EEE8' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:500, color:'#0D1B3E' }}>{n.full_name}</div>
                    <div style={{ fontSize:11, color:'#7A7870' }}><a href={`mailto:${n.email}`} style={{ color:'inherit', textDecoration:'none' }}>{n.email}</a> {n.state && `· ${n.state}`}</div>
                  </div>
                  <button
                    onClick={() => {
                      const reason = window.prompt(`Reason for blacklisting ${n.full_name}? (required)`)
                      if (reason && reason.trim()) addToBlacklist(n.id, reason.trim())
                    }}
                    disabled={saving}
                    style={{ background:'linear-gradient(135deg,#E24B4A,#C43B3A)', border:'none', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:11, fontWeight:600, color:'#fff', fontFamily:'inherit', flexShrink:0 }}>
                    🥜 Add
                  </button>
                </div>
              ))}
            </div>
          )}

          {search.length <= 1 && blacklist.length === 0 && (
            <div style={{ textAlign:'center', padding:'24px', color:'#B8B5AB', fontSize:12 }}>
              🥜 The list is empty — all your notaries are behaving!! For now... 😄
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop:12, padding:'10px 14px', background:'#FAFAF8', border:'1px solid #E8E6DF', borderRadius:8, fontSize:11, color:'#7A7870', textAlign:'center' }}>
        🔒 This list is private — only visible to Nancy. Blacklisted notaries cannot be assigned to jobs.
      </div>
    </main>
  )
}

// ── 🌟 INDIVIDUAL BOOKING SERVICES MANAGER ───────────────────
function IndividualServicesAdmin() {
  const [services, setServices] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [adding,   setAdding]   = useState(false)
  const [editingId,setEditingId]= useState(null)
  const [form,     setForm]     = useState({ name:'', description:'', icon:'📋', price:'', active:true, sort_order:0 })
  const [saving,   setSaving]   = useState(false)

  const ICONS = ['📋','✍️','⚖️','🏥','💰','🏠','🚗','🌍','💍','👨‍👩‍👧','🏛️','📄','🔏','📝']

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data } = await supabase.from('individual_services').select('*').order('sort_order')
    setServices(data || [])
    setLoading(false)
  }

  async function save() {
    if (!form.name.trim() || !form.price) { toast.error('Name and price are required!!'); return }
    setSaving(true)
    try {
      if (editingId) {
        await supabase.from('individual_services').update({ ...form, price:Number(form.price) }).eq('id', editingId)
        toast.success('Service updated!!')
      } else {
        await supabase.from('individual_services').insert({ ...form, price:Number(form.price), sort_order: services.length })
        toast.success('Service added!!')
      }
      setAdding(false); setEditingId(null); setForm({ name:'', description:'', icon:'📋', price:'', active:true, sort_order:0 }); load()
    } catch(err) { toast.error(err.message) } finally { setSaving(false) }
  }

  async function toggleActive(svc) {
    await supabase.from('individual_services').update({ active: !svc.active }).eq('id', svc.id)
    load()
  }

  async function deleteSvc(id, name) {
    if (!window.confirm(`Delete "${name}"?`)) return
    await supabase.from('individual_services').delete().eq('id', id)
    toast.success('Deleted!!'); load()
  }

  function startEdit(svc) {
    setForm({ name:svc.name, description:svc.description||'', icon:svc.icon||'📋', price:svc.price, active:svc.active, sort_order:svc.sort_order||0 })
    setEditingId(svc.id); setAdding(true)
  }

  const INPUT = { padding:'9px 12px', border:'1.5px solid #E0D9CC', borderRadius:8, fontSize:12, fontFamily:'inherit', background:'#FAFAF8', width:'100%', boxSizing:'border-box' }

  return (
    <main tabIndex={-1} style={{ maxWidth:720, margin:'0 auto' }}>
      <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:14, padding:'18px 22px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, color:'#C9A84C', fontWeight:600 }}>🌟 Individual Booking Services</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginTop:2 }}>These appear on the public booking page at /book</div>
          </div>
          <button onClick={() => { setAdding(a=>!a); setEditingId(null); setForm({ name:'', description:'', icon:'📋', price:'', active:true, sort_order:services.length }) }}
            style={{ background:'rgba(201,168,76,0.2)', border:'1px solid rgba(201,168,76,0.4)', borderRadius:8, padding:'8px 16px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#C9A84C', fontFamily:'inherit' }}>
            {adding ? '✕ Cancel' : '＋ Add Service'}
          </button>
        </div>
      </div>

      {/* Add/Edit form */}
      {adding && (
        <div style={{ background:'#fff', border:'1.5px solid #C9A84C', borderRadius:12, padding:'16px', marginBottom:16 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:16, fontWeight:600, color:'#0D1B3E', marginBottom:12 }}>
            {editingId ? '✏️ Edit Service' : '✏️ New Service'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'auto 1fr 1fr', gap:12, marginBottom:12, alignItems:'end' }}>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>ICON</div>
              <select value={form.icon} onChange={e=>setForm(f=>({...f,icon:e.target.value}))} style={{ ...INPUT, width:70, fontSize:18, textAlign:'center' }}>
                {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>SERVICE NAME *</div>
              <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Power of Attorney" style={INPUT} />
            </div>
            <div>
              <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>PRICE ($) *</div>
              <input type="number" value={form.price} onChange={e=>setForm(f=>({...f,price:e.target.value}))} placeholder="150" style={INPUT} />
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:'#7A7870', fontWeight:600, marginBottom:3 }}>PLAIN ENGLISH DESCRIPTION</div>
            <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}
              placeholder="e.g. Authorize someone to make decisions on your behalf legally"
              rows={2} style={{ ...INPUT, resize:'vertical' }} />
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color:'#0D1B3E' }}>
              <input type="checkbox" checked={form.active} onChange={e=>setForm(f=>({...f,active:e.target.checked}))} style={{ width:'auto' }} />
              Show on booking page
            </label>
            <button onClick={save} disabled={saving}
              style={{ background:'linear-gradient(135deg,#2D7A4F,#3A9A60)', border:'none', borderRadius:8, padding:'9px 20px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#fff', fontFamily:'inherit' }}>
              {saving ? 'Saving...' : editingId ? '💾 Update' : '💾 Add Service'}
            </button>
          </div>
        </div>
      )}

      {/* Services list */}
      {loading ? <div style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'auto' }} /></div> : (
        <div className="section-card" style={{ padding:0 }}>
          {services.length === 0 && (
            <div style={{ textAlign:'center', padding:40, color:'#B8B5AB', fontSize:13 }}>
              No services yet!! Add your first one above!! 🌟
            </div>
          )}
          {services.map(svc => (
            <div key={svc.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderBottom:'1px solid #F0EEE8' }}>
              <div style={{ width:40, height:40, borderRadius:8, background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                {svc.icon || '📋'}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#0D1B3E' }}>{svc.name}</div>
                {svc.description && <div style={{ fontSize:11, color:'#7A7870', marginTop:1 }}>{svc.description}</div>}
              </div>
              <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:700, color:'#0D1B3E', flexShrink:0 }}>${svc.price}</div>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                <button onClick={() => toggleActive(svc)}
                  style={{ fontSize:10, fontWeight:600, background: svc.active?'#EAF5EE':'#F8F7F4', color: svc.active?'#2D7A4F':'#B8B5AB', border:`1px solid ${svc.active?'rgba(45,122,79,0.3)':'#E0D9CC'}`, borderRadius:20, padding:'3px 10px', cursor:'pointer', fontFamily:'inherit' }}>
                  {svc.active ? '✅ Live' : '⏳ Hidden'}
                </button>
                <button onClick={() => startEdit(svc)} style={{ background:'none', border:'1px solid #E0D9CC', borderRadius:6, padding:'5px 8px', cursor:'pointer', fontSize:11, color:'#7A7870', fontFamily:'inherit' }}>✏️</button>
                <button onClick={() => deleteSvc(svc.id, svc.name)} style={{ background:'none', border:'1px solid rgba(226,75,74,0.3)', borderRadius:6, padding:'5px 8px', cursor:'pointer', fontSize:11, color:'#E24B4A', fontFamily:'inherit' }}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop:12, padding:'10px 14px', background:'#EBF4FF', border:'1px solid rgba(24,95,165,0.2)', borderRadius:8, fontSize:11, color:'#185FA5', textAlign:'center' }}>
        🌐 Live preview: <a href="/book" target="_blank" rel="noreferrer" style={{ color:'#185FA5', fontWeight:600 }}>loanclosingagent.com/book</a>
      </div>
    </main>
  )
}

// ── 📅 BOOKINGS ADMIN ─────────────────────────────────────────
function BookingsAdmin() {
  const [bookings, setBookings] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [filter,   setFilter]   = useState('all')

  useEffect(() => {
    load()
    // Real-time — new individual bookings
    const ch = supabase.channel('admin-bookings-realtime')
      .on('postgres_changes', { event:'*', schema:'public', table:'individual_bookings' }, payload => {
        if (payload.eventType === 'INSERT') {
          setBookings(prev => [payload.new, ...prev])
          toast.success('🔔 New individual booking!!', { duration:6000 })
        } else if (payload.eventType === 'UPDATE') {
          setBookings(prev => prev.map(b => b.id === payload.new.id ? { ...b, ...payload.new } : b))
        }
      })
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [])

  async function load() {
    setLoading(true)
    const { data } = await supabase.from('individual_bookings').select('*').order('created_at', { ascending:false })
    setBookings(data || [])
    setLoading(false)
  }

  async function updateStatus(id, status) {
    await supabase.from('individual_bookings').update({ status }).eq('id', id)
    toast.success('Status updated!!')
    load()
  }

  const STATUS_COLORS = {
    pending_payment: { bg:'#FDF2F2', color:'#E24B4A', label:'Pending Payment' },
    paid:            { bg:'#EAF5EE', color:'#2D7A4F', label:'Paid'            },
    assigned:        { bg:'#EBF4FF', color:'#185FA5', label:'📲 Awaiting Response'        },
    completed:       { bg:'#F0EBF8', color:'#6A3A9A', label:'Completed'       },
    cancelled:       { bg:'#F8F7F4', color:'#B8B5AB', label:'Cancelled'       },
  }

  const filtered = filter === 'all' ? bookings : bookings.filter(b => b.status === filter)

  return (
    <main tabIndex={-1} style={{ maxWidth:900, margin:'0 auto' }}>
      <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:14, padding:'18px 22px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, color:'#C9A84C', fontWeight:600 }}>📅 Individual Bookings</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginTop:2 }}>Guest bookings from loanclosingagent.com/book</div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {Object.entries(STATUS_COLORS).map(([key, s]) => (
              <button key={key} onClick={() => setFilter(filter===key?'all':key)}
                style={{ fontSize:10, fontWeight:600, background: filter===key?s.bg:'rgba(255,255,255,0.08)', color: filter===key?s.color:'rgba(255,255,255,0.5)', border:`1px solid ${filter===key?s.color:'rgba(255,255,255,0.15)'}`, borderRadius:20, padding:'4px 10px', cursor:'pointer', fontFamily:'inherit' }}>
                {s.label} ({bookings.filter(b=>b.status===key).length})
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? <div style={{ textAlign:'center', padding:48 }}><div className="spinner" style={{ margin:'auto' }} /></div> : (
        <div className="section-card" style={{ padding:0 }}>
          {filtered.length === 0 && (
            <div style={{ textAlign:'center', padding:40, color:'#B8B5AB', fontSize:13 }}>
              {filter === 'all' ? 'No individual bookings yet!! Share your /book link to get started!! 🌟' : `No ${STATUS_COLORS[filter]?.label} bookings`}
            </div>
          )}
          <table className="data-table" style={{ margin:0 }}>
            {filtered.length > 0 && (
              <thead><tr><th>Name</th><th>Service</th><th>Date/Time</th><th>Location</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
            )}
            <tbody>
              {filtered.map(b => {
                const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending_payment
                return (
                  <tr key={b.id}>
                    <td>
                      <div style={{ fontWeight:600, fontSize:12 }}>{b.first_name} {b.last_name}</div>
                      <div style={{ fontSize:11, color:'#7A7870' }}>{b.email}</div>
                      <a href={`tel:${b.phone}`} style={{ fontSize:11, color:'#9A7A30', textDecoration:'none' }}>📞 {b.phone}</a>
                    </td>
                    <td style={{ fontSize:12 }}>{b.service_name}</td>
                    <td>
                      <div style={{ fontSize:12 }}>{b.preferred_date ? new Date(b.preferred_date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : '—'}</div>
                      <div style={{ fontSize:11, color:'#7A7870' }}>{b.preferred_time}</div>
                    </td>
                    <td style={{ fontSize:11 }}>{b.city}, {b.state}</td>
                    <td style={{ fontWeight:700, color:'#0D1B3E' }}>${b.service_price}</td>
                    <td>
                      <span style={{ background:sc.bg, color:sc.color, fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20 }}>{sc.label}</span>
                    </td>
                    <td>
                      <select value={b.status} onChange={e=>updateStatus(b.id, e.target.value)}
                        style={{ fontSize:11, border:'1px solid #E0D9CC', borderRadius:6, padding:'4px 8px', fontFamily:'inherit', cursor:'pointer' }}>
                        {Object.entries(STATUS_COLORS).map(([key,s]) => <option key={key} value={key}>{s.label}</option>)}
                      </select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}

// ── 🗂️ SERVICE CATEGORIES ADMIN ──────────────────────────────
// Nancy can add, edit, reorder, and delete service categories
// These feed directly into ServicesPage.js (public) and ServicesMaster (admin)
function ServiceCategoriesAdmin() {
  const [categories, setCategories] = useState([])
  const [loading,    setLoading]    = useState(true)
  const [adding,     setAdding]     = useState(false)
  const [editingId,  setEditingId]  = useState(null)
  const [saving,     setSaving]     = useState(false)
  const BLANK = { icon:'📋', name_en:'', name_es:'', desc_en:'', desc_es:'',
                  services_en:'', services_es:'', plains_en:'', plains_es:'', sort_order:0 }
  const [form, setForm] = useState(BLANK)

  const ICONS = ['📋','⚖️','👨‍👩‍👧','🏥','💰','🏠','🚗','🌍','💍','🏛️','✍️','🏦','📄','🔏','🎖️','✈️','🏡','💼','🌐','🏢']

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('service_categories').select('*').order('sort_order')
    if (!error && data) setCategories(data)
    setLoading(false)
  }

  async function save() {
    if (!form.name_en.trim()) { toast.error('English name is required!!'); return }
    setSaving(true)
    try {
      const payload = {
        icon:        form.icon,
        name_en:     form.name_en.trim(),
        name_es:     form.name_es.trim() || form.name_en.trim(),
        desc_en:     form.desc_en.trim(),
        desc_es:     form.desc_es.trim() || form.desc_en.trim(),
        services_en: form.services_en ? form.services_en.split('\n').map(s=>s.trim()).filter(Boolean) : [],
        services_es: form.services_es ? form.services_es.split('\n').map(s=>s.trim()).filter(Boolean) : [],
        plains_en:   form.plains_en   ? form.plains_en.split('\n').map(s=>s.trim()).filter(Boolean)   : [],
        plains_es:   form.plains_es   ? form.plains_es.split('\n').map(s=>s.trim()).filter(Boolean)   : [],
        sort_order:  Number(form.sort_order) || categories.length,
      }
      if (editingId) {
        await supabase.from('service_categories').update(payload).eq('id', editingId)
        toast.success('Category updated!!')
      } else {
        await supabase.from('service_categories').insert(payload)
        toast.success('Category added!!')
      }
      setAdding(false); setEditingId(null); setForm(BLANK); load()
    } catch(err) { toast.error(err.message) } finally { setSaving(false) }
  }

  async function deleteCat(id, name) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone!!`)) return
    await supabase.from('service_categories').delete().eq('id', id)
    toast.success('Category deleted!!'); load()
  }

  function startEdit(cat) {
    setForm({
      icon:        cat.icon || '📋',
      name_en:     cat.name_en || '',
      name_es:     cat.name_es || '',
      desc_en:     cat.desc_en || '',
      desc_es:     cat.desc_es || '',
      services_en: Array.isArray(cat.services_en) ? cat.services_en.join('\n') : '',
      services_es: Array.isArray(cat.services_es) ? cat.services_es.join('\n') : '',
      plains_en:   Array.isArray(cat.plains_en)   ? cat.plains_en.join('\n')   : '',
      plains_es:   Array.isArray(cat.plains_es)   ? cat.plains_es.join('\n')   : '',
      sort_order:  cat.sort_order || 0,
    })
    setEditingId(cat.id); setAdding(true)
  }

  const INPUT = { padding:'8px 10px', border:'1.5px solid #E0D9CC', borderRadius:8, fontSize:12, fontFamily:'inherit', background:'#FAFAF8', width:'100%', boxSizing:'border-box' }
  const LABEL = { fontSize:10, fontWeight:600, color:'#7A7870', letterSpacing:'0.08em', marginBottom:4, display:'block' }
  const TA    = { ...INPUT, resize:'vertical', lineHeight:1.5 }

  return (
    <main tabIndex={-1} style={{ maxWidth:800, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:14, padding:'18px 22px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, color:'#C9A84C', fontWeight:600 }}>🗂️ Service Categories</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginTop:2 }}>
              Manage the categories shown on the public Services page — bilingual (EN + ES)!!
            </div>
          </div>
          <button onClick={() => { setAdding(a=>!a); setEditingId(null); setForm(BLANK) }}
            style={{ background:'rgba(201,168,76,0.2)', border:'1px solid rgba(201,168,76,0.4)', borderRadius:8, padding:'8px 16px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#C9A84C', fontFamily:'inherit' }}>
            {adding ? '✕ Cancel' : '＋ Add Category'}
          </button>
        </div>
      </div>

      {/* Add / Edit Form */}
      {adding && (
        <div style={{ background:'#fff', border:'1.5px solid #C9A84C', borderRadius:12, padding:'18px', marginBottom:16 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:16, fontWeight:600, color:'#0D1B3E', marginBottom:14 }}>
            {editingId ? '✏️ Edit Category' : '➕ New Category'}
          </div>
          {/* Row 1: icon + names */}
          <div style={{ display:'grid', gridTemplateColumns:'auto 1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={LABEL}>ICON</label>
              <select value={form.icon} onChange={e=>setForm(f=>({...f,icon:e.target.value}))} style={{ ...INPUT, width:60, fontSize:18, textAlign:'center' }}>
                {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </div>
            <div>
              <label style={LABEL}>NAME (ENGLISH) *</label>
              <input value={form.name_en} onChange={e=>setForm(f=>({...f,name_en:e.target.value}))} placeholder="e.g. Affidavits & Sworn Statements" style={INPUT} />
            </div>
            <div>
              <label style={LABEL}>NAME (SPANISH)</label>
              <input value={form.name_es} onChange={e=>setForm(f=>({...f,name_es:e.target.value}))} placeholder="e.g. Declaraciones Juradas" style={INPUT} />
            </div>
          </div>
          {/* Row 2: descriptions */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={LABEL}>DESCRIPTION (EN)</label>
              <input value={form.desc_en} onChange={e=>setForm(f=>({...f,desc_en:e.target.value}))} placeholder="Short description..." style={INPUT} />
            </div>
            <div>
              <label style={LABEL}>DESCRIPTION (ES)</label>
              <input value={form.desc_es} onChange={e=>setForm(f=>({...f,desc_es:e.target.value}))} placeholder="Descripción corta..." style={INPUT} />
            </div>
          </div>
          {/* Row 3: services — one per line */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={LABEL}>SERVICES (EN) — one per line</label>
              <textarea value={form.services_en} onChange={e=>setForm(f=>({...f,services_en:e.target.value}))}
                placeholder="Power of Attorney&#10;Living Will&#10;Court Documents" rows={6} style={TA} />
            </div>
            <div>
              <label style={LABEL}>SERVICES (ES) — one per line</label>
              <textarea value={form.services_es} onChange={e=>setForm(f=>({...f,services_es:e.target.value}))}
                placeholder="Poder Notarial&#10;Testamento Vital&#10;Documentos Judiciales" rows={6} style={TA} />
            </div>
          </div>
          {/* Row 4: plain descriptions — one per line */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div>
              <label style={LABEL}>PLAIN DESCRIPTIONS (EN) — one per line, matches services order</label>
              <textarea value={form.plains_en} onChange={e=>setForm(f=>({...f,plains_en:e.target.value}))}
                placeholder="Authorize someone to act on your behalf&#10;Written instructions for medical care" rows={6} style={TA} />
            </div>
            <div>
              <label style={LABEL}>PLAIN DESCRIPTIONS (ES) — one per line</label>
              <textarea value={form.plains_es} onChange={e=>setForm(f=>({...f,plains_es:e.target.value}))}
                placeholder="Autorizar a alguien a actuar en tu nombre&#10;Instrucciones escritas para atención médica" rows={6} style={TA} />
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <label style={{ ...LABEL, margin:0 }}>SORT ORDER</label>
              <input type="number" value={form.sort_order} onChange={e=>setForm(f=>({...f,sort_order:e.target.value}))}
                style={{ ...INPUT, width:60 }} />
              <span style={{ fontSize:11, color:'#B8B5AB' }}>(lower = appears first)</span>
            </div>
            <button onClick={save} disabled={saving}
              style={{ background:'linear-gradient(135deg,#2D7A4F,#3A9A60)', border:'none', borderRadius:8, padding:'9px 20px', cursor:'pointer', fontSize:12, fontWeight:600, color:'#fff', fontFamily:'inherit' }}>
              {saving ? 'Saving...' : editingId ? '💾 Update Category' : '💾 Add Category'}
            </button>
          </div>
        </div>
      )}

      {/* Info banner */}
      <div style={{ background:'#EBF4FF', border:'1px solid rgba(24,95,165,0.2)', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:11, color:'#185FA5' }}>
        💡 Changes here appear instantly on the public <strong>Services</strong> page at <a href="/services" target="_blank" rel="noreferrer" style={{ color:'#185FA5' }}>loanclosingagent.com/services</a>!!
        If no categories are saved here, the page uses the built-in default categories automatically!!
      </div>

      {/* Categories list */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'auto' }} /></div>
      ) : categories.length === 0 ? (
        <div style={{ background:'#fff', border:'1px solid #E8E6DF', borderRadius:12, padding:32, textAlign:'center', color:'#B8B5AB', fontSize:13 }}>
          No custom categories yet!! The public Services page is using the built-in defaults!!<br />
          Add a category above to start customizing!! 🗂️
        </div>
      ) : (
        <div className="section-card" style={{ padding:0 }}>
          {categories.map((cat, i) => (
            <div key={cat.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderBottom: i < categories.length-1 ? '1px solid #F0EEE8' : 'none' }}>
              <div style={{ width:42, height:42, borderRadius:10, background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                {cat.icon || '📋'}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:2 }}>
                  <span style={{ fontSize:14, fontWeight:600, color:'#0D1B3E' }}>{cat.name_en}</span>
                  {cat.name_es && <span style={{ fontSize:11, color:'#B8B5AB' }}>· {cat.name_es}</span>}
                </div>
                {cat.desc_en && <div style={{ fontSize:11, color:'#7A7870', marginBottom:3 }}>{cat.desc_en}</div>}
                <div style={{ fontSize:10, color:'#B8B5AB' }}>
                  {Array.isArray(cat.services_en) ? cat.services_en.length : 0} services · Sort: {cat.sort_order}
                </div>
              </div>
              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                <button onClick={() => startEdit(cat)}
                  style={{ background:'none', border:'1px solid #E0D9CC', borderRadius:6, padding:'5px 10px', cursor:'pointer', fontSize:11, color:'#7A7870', fontFamily:'inherit' }}>✏️ Edit</button>
                <button onClick={() => deleteCat(cat.id, cat.name_en)}
                  style={{ background:'none', border:'1px solid rgba(226,75,74,0.3)', borderRadius:6, padding:'5px 8px', cursor:'pointer', fontSize:11, color:'#E24B4A', fontFamily:'inherit' }}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  )
}

// ── TRAINING MANUAL CONTENT ──────────────────────────────────
const TRAINING_CONTENT = [
  { id:'l1_1', module:'Module 1 — LCA Standards', title:'Professionalism',  content:'As an LCA notary you are the face of Loan Closing Agent LLC at every signing table. Your professionalism reflects directly on our brand and on the trust our clients place in us.\n\nKey standards:\n• Always arrive on time — early is on time, on time is late\n• Dress business professional or business casual at minimum\n• Introduce yourself with confidence: "Good morning, I\'m [Name] with Loan Closing Agent LLC"\n• Maintain a calm, reassuring presence — many borrowers are signing the most important documents of their lives\n• Never discuss the terms of the loan — that is not your role' },
  { id:'l1_2', module:'Module 1 — LCA Standards', title:'Communication',    content:'Clear, timely communication is non-negotiable at LCA.\n\nWith borrowers:\n• Confirm appointments within 2 hours of assignment\n• Call 30 minutes before arrival (unless instructed otherwise)\n• Speak clearly and patiently — never rush a signer\n\nWith LCA:\n• Update the portal at every stage\n• Report issues immediately — never wait\n• If you cannot make an appointment, notify LCA as soon as possible so we can reassign\n\nRemember: Silence is never acceptable. When in doubt, communicate.' },
  { id:'l1_3', module:'Module 1 — LCA Standards', title:'Compliance',       content:'Notaries are officers of the state. Your compliance protects the signer, the lender, the title company, and LCA.\n\nCore compliance rules:\n• Never notarize a document you did not personally witness being signed\n• Never notarize for a family member (most states prohibit this)\n• Always verify signer identity with valid, unexpired government-issued ID\n• Never complete a certificate if any information is blank\n• Keep your commission current — expired commission = invalid notarization\n• Maintain your journal as required by your state' },
  { id:'l1_4', module:'Module 1 — LCA Standards', title:'Accuracy',         content:'A single error in a loan package can delay a closing by days and cost thousands of dollars.\n\nAccuracy checklist:\n• Read every instruction page carefully before the signing\n• Ensure borrower signs their name EXACTLY as printed\n• Watch for name variations — "Robert" vs "Bob" matters\n• Ensure your notarial stamp is clear, complete, and within its expiration\n• Count pages before and after — never leave the table with missing documents\n• When in doubt, call LCA immediately — do not guess' },
  { id:'l1_5', module:'Module 1 — LCA Standards', title:'Timeliness',       content:'In the loan signing world, time is money — literally.\n\nLCA timeliness standards:\n• Accept or decline jobs ASAP of notification\n• Confirm borrower appointment within 2 hours\n• Upload scanbacks within 2 hours of signing completion\n• Ship documents same day if possible — next day at the latest\n• Update your portal status in real time\n\nLenders have funding deadlines. A late package can cause a loan to miss its rate lock, costing the borrower thousands. Your timeliness protects everyone.' },
  { id:'l2_1', module:'Module 2 — The Signing Workflow', title:'Assignment',   content:'When LCA assigns you a job:\n\n1. You receive a notification via email and/or SMS\n2. Log into your portal immediately\n3. Review ALL job details carefully\n4. Accept or decline ASAP\n5. If accepted — the clock starts now\n\nNever accept a job you cannot complete. A last-minute cancellation is far more damaging than a declined assignment.' },
  { id:'l2_2', module:'Module 2 — The Signing Workflow', title:'Confirmation', content:'After accepting:\n\n1. Contact the borrower within 2 hours\n2. Confirm date, time, full address, all signers present, valid ID\n3. Provide your contact number\n4. Update portal status to "Confirmed with Borrower"\n\nPro tip: Always get a cell phone number for the borrower — not just the office number.' },
  { id:'l2_5', module:'Module 2 — The Signing Workflow', title:'Scanbacks',    content:'Scanbacks are required on most loan signings.\n\nLCA scanback standards:\n• Upload within 2 hours of signing completion\n• Ensure all pages are clear and legible\n• Upload in the correct order\n• Update portal status after uploading\n\nNever ship originals before scanback approval — unless specifically instructed.' },
  { id:'l3_1', module:'Module 3 — Pre-Appointment Checklist', title:'Why the Checklist Matters', content:'The Pre-Appointment Checklist is your guarantee of a smooth signing.\n\nEvery missed item is a potential error. The checklist has 4 sections:\n1. Immediately after accepting\n2. Document preparation\n3. Prep your notary bag\n4. Final check before leaving home\n\nPro tip: Notaries who use the checklist consistently have fewer errors and higher client ratings.' },
  { id:'l3_2', module:'Module 3 — Pre-Appointment Checklist', title:'Section 1 — After Accepting', content:'Immediately after accepting:\n\n✅ Confirm borrower name(s)\n✅ Confirm signing type and appointment\n✅ Confirm signing location\n✅ Download all documents\n✅ Call borrower to confirm\n✅ Confirm all signers will be present\n✅ Provide your contact number\n\nComplete within 2 hours of accepting!!' },
  { id:'l3_3', module:'Module 3 — Pre-Appointment Checklist', title:'Section 2 — Document Preparation', content:'Preparing your documents:\n\n✅ Print on correct paper size\n✅ No pages cut off\n✅ Barcodes are clear\n✅ Signature lines are visible\n✅ Notarial certificates are readable\n✅ Print borrower copy if required\n✅ Pre-tab signature pages and initial pages\n\nPrinting errors are one of the most common causes of loan rejection.' },
  { id:'l3_4', module:'Module 3 — Pre-Appointment Checklist', title:'Section 3 — Notary Bag', content:'Your notary bag is your toolkit. Never leave home without:\n\n✅ Notary stamp + backup stamp\n✅ Journal\n✅ Blue pens (multiple)\n✅ Sticky notes and highlighters\n✅ Binder clips and paper clips\n✅ Phone fully charged and portable charger\n✅ Hotspot ready and portal login tested' },
  { id:'l3_5', module:'Module 3 — Pre-Appointment Checklist', title:'Section 4 — Final Check', content:'Before you walk out the door:\n\n✅ All documents printed and accounted for\n✅ Shipping label printed\n✅ Travel time and parking confirmed\n✅ Traffic and weather checked\n\nIf you can check every item — you are ready. Go get it!!' },
  { id:'l4_2', module:'Module 4 — Signing Day Protocol',        title:'ID Verification', content:'ID verification is one of your most important responsibilities.\n\nAccepted IDs (in most states):\n• Driver\'s license (valid, not expired)\n• State-issued ID card\n• Passport\n• Military ID\n• Permanent Resident Card\n\nIf ID is expired or questionable — stop. Call LCA. Do not proceed without guidance.' },
  { id:'l5_1', module:'Module 5 — Post-Signing Requirements', title:'Scanbacks Upload', content:'Scanbacks are your first post-signing responsibility.\n\nWhat to scan: All notarized pages, all signature pages, any pages requested by the lender.\n\nHow to upload:\n1. Scan clearly — no shadows, no cut-off pages\n2. Upload to the portal\n3. Update status to Scanbacks Uploaded\n4. Wait for LCA approval before shipping originals\n\nUpload within 2 hours of signing completion.' },
  { id:'l5_2', module:'Module 5 — Post-Signing Requirements', title:'Shipping', content:'After scanback approval:\n\n1. Package documents securely\n2. Use the pre-printed shipping label if provided\n3. Ship SAME DAY if at all possible\n4. Enter tracking number in the portal immediately\n5. Keep your receipt\n\nStandards: FedEx or UPS overnight is standard. Never use regular USPS First Class for original loan documents.' },
  { id:'l5_3', module:'Module 5 — Post-Signing Requirements', title:'Portal Updates', content:'Every action must be reflected in the portal.\n\n☐ Status updated to Completed by Notary\n☐ Scanbacks uploaded\n☐ Tracking number entered\n☐ Notes or issues documented\n\nLCA cannot process your payment until these steps are complete.' },
  { id:'l6_1', module:'Module 6 — Payment & Compliance', title:'Payment Schedule', content:'LCA payment standards:\n\n• Payment is processed after the job is fully closed in the system\n• Typical payment cycle: within 30 days of job completion\n• Payment is processed via the method established in your contractor agreement\n\nFor payment inquiries: nancy@loanclosingagent.com' },
  { id:'l7_1', module:'Module 7 — Professional Standards', title:'Dress Code', content:'Your appearance communicates professionalism before you say a word.\n\nLCA standard:\n• Business professional or business casual at all times\n• Clean, pressed clothing\n• Closed-toe shoes recommended\n• No strong perfume or cologne\n\nWhat NOT to wear: Athletic wear, revealing clothing, or flip flops. Dress like the professional you are.' },
  { id:'l7_2', module:'Module 7 — Professional Standards', title:'Communication Tone', content:'Your words carry weight at the signing table.\n\nAlways:\n• Speak clearly and confidently\n• Use please and thank you\n• Keep your tone warm but professional\n\nNever:\n• Use slang or profanity\n• Complain about other signings\n• Express opinions about the loan or lender\n\nEvery word matters.' },
  { id:'l7_4', module:'Module 7 — Professional Standards', title:'Handling Issues', content:'How you handle problems defines your reputation.\n\nThe LCA standard:\n1. Stay calm\n2. Assess the problem\n3. Call LCA immediately\n4. Do not improvise\n5. Document everything in the portal\n\nNancy is always available for urgent issues. You are never alone at the table.' },
]

// ── RESOURCES CMS ─────────────────────────────────────────────
const STATE_RESOURCES_ADMIN = [
  { state:'Florida',       abbr:'FL', active:true,  commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://dos.fl.gov/notaries/',            notes:'Notary commission through Department of State. RON platform registration required.' },
  { state:'Georgia',       abbr:'GA', active:true,  commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.ga.gov/page/notaries',        notes:'Commission through Secretary of State. No journal required but strongly recommended.' },
  { state:'Texas',         abbr:'TX', active:true,  commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://www.sos.state.tx.us/statdoc/notaries.shtml', notes:'Strict state — platform and notary must both be registered for RON.' },
  { state:'Indiana',       abbr:'IN', active:true,  commission:'8 years',  journal:'Required',  ron:'Yes',     link:'https://www.in.gov/sos/business/notary-division/', notes:'Electronic journal accepted. RON permitted with approved platform.' },
  { state:'Pennsylvania',  abbr:'PA', active:true,  commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://www.dos.pa.gov/BusinessCharities/Notaries/Pages/default.aspx', notes:'Journal must be kept for 10 years. RON allowed since 2020.' },
  { state:'Tennessee',     abbr:'TN', active:true,  commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.tn.gov/notary', notes:'No journal required. RON permitted.' },
  { state:'Washington',    abbr:'WA', active:true,  commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://www.dol.wa.gov/business-services/notary-public', notes:'Electronic notarization permitted. RON allowed.' },
  { state:'Oklahoma',      abbr:'OK', active:true,  commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://www.sos.ok.gov/business-filing/notary-public.aspx', notes:'Journal required. RON permitted since 2019.' },
  { state:'Massachusetts', abbr:'MA', active:true,  commission:'7 years',  journal:'Required',  ron:'Limited', link:'https://www.mass.gov/how-to/become-a-notary-public', notes:'Long commission term. Journal is REQUIRED. RON laws evolving — verify current status.' },
  { state:'Alabama',       abbr:'AL', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.alabama.gov/alabama-votes/notary-public', notes:'' },
  { state:'Alaska',        abbr:'AK', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.commerce.alaska.gov/web/cbpl/ProfessionalLicensing/NotaryPublic.aspx', notes:'' },
  { state:'Arizona',       abbr:'AZ', active:false, commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://azsos.gov/business-services/notary', notes:'' },
  { state:'Arkansas',      abbr:'AR', active:false, commission:'10 years', journal:'Optional',  ron:'Yes',     link:'https://www.sos.arkansas.gov/business-commercial-services-bcs/notary', notes:'' },
  { state:'California',    abbr:'CA', active:false, commission:'4 years',  journal:'Required',  ron:'Limited', link:'https://notary.sos.ca.gov/', notes:'Strict state. RON limited — verify current status carefully.' },
  { state:'Colorado',      abbr:'CO', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.state.co.us/pubs/notary/home.html', notes:'' },
  { state:'Connecticut',   abbr:'CT', active:false, commission:'5 years',  journal:'Optional',  ron:'Yes',     link:'https://portal.ct.gov/SOTS/Business-Services/Notary-Public/Notary-Public', notes:'' },
  { state:'Delaware',      abbr:'DE', active:false, commission:'2 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.delaware.gov/notary-public/', notes:'' },
  { state:'Hawaii',        abbr:'HI', active:false, commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://cca.hawaii.gov/ocp/notaries/', notes:'' },
  { state:'Idaho',         abbr:'ID', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.idaho.gov/notary/', notes:'' },
  { state:'Illinois',      abbr:'IL', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.ilsos.gov/departments/index/notary/home.html', notes:'' },
  { state:'Iowa',          abbr:'IA', active:false, commission:'3 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.iowa.gov/elections/notary/', notes:'' },
  { state:'Kansas',        abbr:'KS', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.ks.gov/other/notary.html', notes:'' },
  { state:'Kentucky',      abbr:'KY', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.ky.gov/bus/notary/Pages/default.aspx', notes:'' },
  { state:'Louisiana',     abbr:'LA', active:false, commission:'Lifetime', journal:'Required',  ron:'Yes',     link:'https://www.sos.la.gov/BusinessServices/FileBusinessDocuments/NotaryServices/Pages/default.aspx', notes:'Louisiana is a civil law state — notary laws differ significantly.' },
  { state:'Maine',         abbr:'ME', active:false, commission:'7 years',  journal:'Optional',  ron:'Yes',     link:'https://www.maine.gov/sos/cec/notary/', notes:'' },
  { state:'Maryland',      abbr:'MD', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.maryland.gov/Notary/Pages/default.aspx', notes:'' },
  { state:'Michigan',      abbr:'MI', active:false, commission:'6 years',  journal:'Optional',  ron:'Yes',     link:'https://www.michigan.gov/sos/resources/notary', notes:'' },
  { state:'Minnesota',     abbr:'MN', active:false, commission:'5 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.state.mn.us/business-liens/notaries-authentications/', notes:'' },
  { state:'Mississippi',   abbr:'MS', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.ms.gov/business-services/notary-public', notes:'' },
  { state:'Missouri',      abbr:'MO', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.mo.gov/business/notary', notes:'Education required before commission.' },
  { state:'Montana',       abbr:'MT', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sosmt.gov/notary/', notes:'' },
  { state:'Nebraska',      abbr:'NE', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.nebraska.gov/business-services/notary-public', notes:'' },
  { state:'Nevada',        abbr:'NV', active:false, commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://www.nvsos.gov/sos/businesses/notary', notes:'Strict RON requirements — platform must be approved.' },
  { state:'New Hampshire', abbr:'NH', active:false, commission:'5 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.nh.gov/legal/notary/', notes:'' },
  { state:'New Jersey',    abbr:'NJ', active:false, commission:'5 years',  journal:'Optional',  ron:'Yes',     link:'https://www.njconsumeraffairs.gov/not/Pages/default.aspx', notes:'' },
  { state:'New Mexico',    abbr:'NM', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.nm.gov/business-services/notary-public/', notes:'' },
  { state:'New York',      abbr:'NY', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.dos.ny.gov/licensing-services/notary-public', notes:'County-level registration also required.' },
  { state:'North Carolina',abbr:'NC', active:false, commission:'5 years',  journal:'Required',  ron:'Yes',     link:'https://www.sosnc.gov/online_services/notary', notes:'Education required. Strong RON framework.' },
  { state:'North Dakota',  abbr:'ND', active:false, commission:'6 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.nd.gov/notary-public', notes:'' },
  { state:'Ohio',          abbr:'OH', active:false, commission:'5 years',  journal:'Optional',  ron:'Yes',     link:'https://www.ohiosos.gov/businesses/notary-resources/', notes:'' },
  { state:'Oregon',        abbr:'OR', active:false, commission:'4 years',  journal:'Required',  ron:'Yes',     link:'https://sos.oregon.gov/notary/Pages/default.aspx', notes:'' },
  { state:'Rhode Island',  abbr:'RI', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.ri.gov/divisions/notary-public', notes:'' },
  { state:'South Carolina',abbr:'SC', active:false, commission:'10 years', journal:'Optional',  ron:'Yes',     link:'https://sos.sc.gov/Notary-Public', notes:'' },
  { state:'South Dakota',  abbr:'SD', active:false, commission:'6 years',  journal:'Optional',  ron:'Yes',     link:'https://sdsos.gov/general-services/notary-public/default.aspx', notes:'' },
  { state:'Utah',          abbr:'UT', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://notary.utah.gov/', notes:'' },
  { state:'Vermont',       abbr:'VT', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.vermont.gov/notary-public/', notes:'' },
  { state:'Virginia',      abbr:'VA', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.sos.virginia.gov/notary/', notes:'' },
  { state:'West Virginia', abbr:'WV', active:false, commission:'10 years', journal:'Optional',  ron:'Yes',     link:'https://sos.wv.gov/business-licensing/notary/Pages/default.aspx', notes:'' },
  { state:'Wisconsin',     abbr:'WI', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://www.wdfi.org/notaries/', notes:'' },
  { state:'Wyoming',       abbr:'WY', active:false, commission:'4 years',  journal:'Optional',  ron:'Yes',     link:'https://sos.wyo.gov/business-division/notary-public.aspx', notes:'' },
]


// ═══════════════════════════════════════════════════════════════
// 📈 REPORTS CENTER — branded PDF reports for operations & accounting
// ═══════════════════════════════════════════════════════════════
const REPORT_DEFS = {
  roster:     { icon:'📋', title:'Notary Roster by State',    desc:'Every notary with city, state, and contact details.' },
  clients:    { icon:'🏢', title:'Client Directory by State', desc:'All approved clients with contact info and join date.' },
  compliance: { icon:'🛡️', title:'Compliance Watchlist',      desc:'Credentials expiring soon or needing attention.' },
  financial:  { icon:'💰', title:'Financial Summary',          desc:'Money in, notary pay out, and LCA net for a date range. Accountant-ready.' },
  activity:   { icon:'📅', title:'Signings Activity',          desc:'All signings in a date range, filterable by state.' },
}

function fmtDT(v) {
  if (!v) return '—'
  try { return new Date(v).toLocaleString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit' }) } catch (e) { return String(v) }
}
function fmtMoney(n) { return '$' + (Number(n) || 0).toFixed(2) }

function ReportsCenter() {
  const [reportKey,  setReportKey]  = useState('roster')
  const [stateFilter, setStateFilter] = useState('')
  const [dateFrom,   setDateFrom]   = useState(() => { const d = new Date(); d.setMonth(0, 1); return d.toISOString().slice(0, 10) })
  const [dateTo,     setDateTo]     = useState(() => new Date().toISOString().slice(0, 10))
  const [horizon,    setHorizon]    = useState(90)
  const [states,     setStates]     = useState([])
  const [generating, setGenerating] = useState(false)
  const [preview,    setPreview]    = useState(null)

  useEffect(() => { loadStates() }, [])

  async function loadStates() {
    const found = new Set()
    const { data: nots } = await supabaseAdmin.from('notaries').select('state')
    ;(nots || []).forEach(n => { const s = (n.state || '').toUpperCase().trim(); if (s) found.add(s) })
    const { data: jobs } = await supabaseAdmin.from('jobs').select('signing_state')
    ;(jobs || []).forEach(j => { const s = (j.signing_state || '').toUpperCase().trim(); if (s) found.add(s) })
    const { data: clis } = await supabaseAdmin.from('profiles').select('state').eq('role', 'client')
    ;(clis || []).forEach(c => { const s = (c.state || '').toUpperCase().trim(); if (s) found.add(s) })
    setStates([...found].sort())
  }

  // ── Branded PDF table renderer (navy/gold, page numbers, timestamped) ──
  async function renderReportPdf({ key, title, filterNote, columns, rows, totalsRow }) {
    await ensurePdfLibs()
    const { jsPDF } = window.jspdf
    const pdf = new jsPDF({ orientation:'portrait', unit:'pt', format:'letter' })
    const pageW = pdf.internal.pageSize.getWidth()
    const pageH = pdf.internal.pageSize.getHeight()
    const margin = 40
    const tableW = pageW - margin * 2
    const totalUnits = columns.reduce((s, c) => s + c.w, 0)
    const colWidths  = columns.map(c => (c.w / totalUnits) * tableW)

    function drawPageHeader() {
      pdf.setFillColor(13, 27, 62)
      pdf.rect(0, 0, pageW, 64, 'F')
      pdf.setTextColor(201, 168, 76)
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16)
      pdf.text(title, margin, 30)
      pdf.setTextColor(253, 246, 227)
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5)
      pdf.text(`Generated ${new Date().toLocaleString('en-US')}${filterNote ? '  •  ' + filterNote : ''}`, margin, 48)
    }
    function drawColumnHeaders(y) {
      pdf.setFillColor(13, 27, 62)
      pdf.rect(margin, y, tableW, 20, 'F')
      pdf.setTextColor(201, 168, 76)
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5)
      let x = margin
      columns.forEach((c, i) => { pdf.text(c.label, x + 4, y + 13); x += colWidths[i] })
      return y + 20
    }

    drawPageHeader()
    let y = drawColumnHeaders(80)
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5)

    const drawRow = (cells, { bold = false, fill = null } = {}) => {
      const wrapped = cells.map((cell, i) => pdf.splitTextToSize(String(cell ?? '—'), colWidths[i] - 8))
      const lines = Math.max(...wrapped.map(wl => wl.length))
      const rowH = lines * 10 + 7
      if (y + rowH > pageH - 50) {
        pdf.addPage()
        drawPageHeader()
        y = drawColumnHeaders(80)
      }
      if (fill) { pdf.setFillColor(...fill); pdf.rect(margin, y, tableW, rowH, 'F') }
      pdf.setTextColor(30, 42, 68)
      pdf.setFont('helvetica', bold ? 'bold' : 'normal')
      let x = margin
      wrapped.forEach((wl, i) => { pdf.text(wl, x + 4, y + 11); x += colWidths[i] })
      y += rowH
      pdf.setDrawColor(216, 212, 200); pdf.setLineWidth(0.4)
      pdf.line(margin, y, margin + tableW, y)
    }

    rows.forEach((r, idx) => drawRow(r, { fill: idx % 2 === 1 ? [248, 247, 244] : null }))
    if (totalsRow) drawRow(totalsRow, { bold: true, fill: [246, 237, 211] })
    if (rows.length === 0) {
      pdf.setTextColor(122, 120, 112)
      pdf.text('No records matched the selected filters.', margin, y + 20)
    }

    const pages = pdf.getNumberOfPages()
    for (let p = 1; p <= pages; p++) {
      pdf.setPage(p)
      pdf.setTextColor(122, 120, 112); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5)
      pdf.text('Loan Closing Agent, LLC. DBA Nationwide Notary Professionals Network', pageW / 2, pageH - 26, { align:'center' })
      pdf.text(`Page ${p} of ${pages}`, pageW / 2, pageH - 15, { align:'center' })
    }
    const stamp = new Date().toISOString().slice(0, 10)
    pdf.save(`LCA-Report-${key || reportKey}-${stamp}.pdf`)
  }

  async function generateReport() {
    setGenerating(true)
    try {
      let payload = null
      const stateNote = stateFilter ? `State: ${stateFilter}` : 'All states'

      if (reportKey === 'roster') {
        let q = supabaseAdmin.from('notaries').select('*').order('state', { ascending:true }).order('full_name', { ascending:true })
        const { data } = await q
        let rows = (data || [])
        if (stateFilter) rows = rows.filter(n => ((n.state || '') + '').toUpperCase().trim() === stateFilter)
        payload = ({
          key: 'roster',
          title: 'Notary Roster by State',
          filterNote: `${stateNote} • ${rows.length} notaries`,
          columns: [ { label:'Name', w:3 }, { label:'City', w:2 }, { label:'State', w:1 }, { label:'Phone', w:2 }, { label:'Email', w:3 } ],
          rows: rows.map(n => [n.full_name, n.city, ((n.state || '') + '').toUpperCase(), n.phone, n.email]),
        })

      } else if (reportKey === 'clients') {
        const { data } = await supabaseAdmin.from('profiles').select('*').eq('role','client').eq('is_approved', true).order('created_at', { ascending:false })
        let rows = (data || [])
        if (stateFilter) rows = rows.filter(c => ((c.state || '') + '').toUpperCase().trim() === stateFilter)
        payload = ({
          key: 'clients',
          title: 'Client Directory by State',
          filterNote: `${stateNote} • ${rows.length} clients`,
          columns: [ { label:'Company / Name', w:3 }, { label:'Contact', w:2 }, { label:'Email', w:3 }, { label:'State', w:1 }, { label:'Client Since', w:2 } ],
          rows: rows.map(c => [c.company_name || c.full_name, c.contact_name || c.full_name, c.email, ((c.state || '') + '').toUpperCase() || '—', fmtDT(c.created_at)]),
        })

      } else if (reportKey === 'compliance') {
        const { data } = await supabaseAdmin.from('notaries').select('*, notary_docs(*)')
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() + Number(horizon))
        const rows = []
        ;(data || []).forEach(n => {
          (n.notary_docs || []).forEach(d => {
            const expRaw = d.expiration_date || d.expires_at || d.expiry_date || d.expires || null
            const exp = expRaw ? new Date(expRaw) : null
            const expSoon = exp && exp <= cutoff
            const needsAttention = (d.status || '') !== 'approved'
            if (expSoon || needsAttention) {
              rows.push([
                n.full_name,
                (d.doc_type || 'document').replaceAll('_', ' '),
                (d.status || 'unknown').toUpperCase(),
                exp ? (exp < new Date() ? 'EXPIRED ' : '') + fmtDT(exp) : '—',
              ])
            }
          })
        })
        rows.sort((a, b) => String(a[3]).localeCompare(String(b[3])))
        payload = ({
          key: 'compliance',
          title: 'Compliance Watchlist',
          filterNote: `Expiring within ${horizon} days or needing review • ${rows.length} items`,
          columns: [ { label:'Notary', w:3 }, { label:'Document', w:2.5 }, { label:'Status', w:1.5 }, { label:'Expires', w:2.5 } ],
          rows,
        })

      } else if (reportKey === 'financial') {
        const { data } = await supabaseAdmin.from('jobs').select('*')
          .gte('created_at', dateFrom + 'T00:00:00')
          .lte('created_at', dateTo + 'T23:59:59')
          .order('created_at', { ascending:true })
        const rows = (data || []).filter(j => (j.status || '') !== 'cancelled')
        const tFee = rows.reduce((s, j) => s + (Number(j.total_fee) || 0), 0)
        const tPay = rows.reduce((s, j) => s + (Number(j.notary_pay) || 0), 0)
        const tLca = rows.reduce((s, j) => s + (Number(j.lca_cut) || 0), 0)
        payload = ({
          key: 'financial',
          title: 'Financial Summary',
          filterNote: `${dateFrom} to ${dateTo} • ${rows.length} orders (cancelled excluded)`,
          columns: [ { label:'Job #', w:1.6 }, { label:'Date & Time', w:2.2 }, { label:'Status', w:1.4 }, { label:'Money In', w:1.5 }, { label:'Notary Pay', w:1.5 }, { label:'LCA Net', w:1.5 } ],
          rows: rows.map(j => [j.job_number, fmtDT(j.created_at), j.status, fmtMoney(j.total_fee), fmtMoney(j.notary_pay), fmtMoney(j.lca_cut)]),
          totalsRow: ['TOTALS', `${rows.length} orders`, '', fmtMoney(tFee), fmtMoney(tPay), fmtMoney(tLca)],
        })

      } else if (reportKey === 'activity') {
        const { data } = await supabaseAdmin.from('jobs').select('*')
          .gte('created_at', dateFrom + 'T00:00:00')
          .lte('created_at', dateTo + 'T23:59:59')
          .order('created_at', { ascending:false })
        let rows = (data || [])
        if (stateFilter) rows = rows.filter(j => ((j.signing_state || '') + '').toUpperCase().trim() === stateFilter)
        payload = ({
          key: 'activity',
          title: 'Signings Activity',
          filterNote: `${dateFrom} to ${dateTo} • ${stateNote} • ${rows.length} signings`,
          columns: [ { label:'Job #', w:1.6 }, { label:'Date & Time', w:2.2 }, { label:'City', w:1.8 }, { label:'State', w:1 }, { label:'Type', w:1.4 }, { label:'Status', w:1.4 }, { label:'Fee', w:1.3 } ],
          rows: rows.map(j => [j.job_number, fmtDT(j.created_at), j.signing_city, ((j.signing_state || '') + '').toUpperCase(), j.job_type || 'client', j.status, fmtMoney(j.total_fee)]),
        })
      }
      if (payload) setPreview(payload)
    } catch (err) {
      console.error('Report error:', err)
      toast.error(err.message || 'Report generation hit a snag')
    } finally {
      setGenerating(false)
    }
  }

  async function downloadPreviewPdf() {
    if (!preview) return
    setGenerating(true)
    try {
      await renderReportPdf(preview)
      toast.success('🖨️ PDF downloaded — check your Downloads folder!!')
    } catch (err) {
      console.error('PDF error:', err)
      toast.error(err.message || 'PDF generation hit a snag')
    } finally {
      setGenerating(false)
    }
  }

  const needsState = ['roster', 'clients', 'activity'].includes(reportKey)
  const needsDates = ['financial', 'activity'].includes(reportKey)
  const needsHorizon = reportKey === 'compliance'

  return (
    <div style={{ padding:'24px' }}>
      <h1 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:28, color:'#0D1B3E', marginBottom:4 }}>📈 Reports Center</h1>
      <p style={{ fontSize:13, color:'#7A7870', marginBottom:22 }}>Pick a report, set your filters, and a branded PDF downloads straight to your computer.</p>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(230px, 1fr))', gap:12, marginBottom:22 }}>
        {Object.entries(REPORT_DEFS).map(([key, def]) => (
          <div key={key} onClick={() => setReportKey(key)}
            style={{ cursor:'pointer', borderRadius:12, padding:'16px 18px', border: reportKey === key ? '2px solid #C9A84C' : '1.5px solid #E8E6DF',
                     background: reportKey === key ? '#FDF6E3' : '#fff', transition:'all 0.15s' }}>
            <div style={{ fontSize:22, marginBottom:6 }}>{def.icon}</div>
            <div style={{ fontSize:14, fontWeight:700, color:'#0D1B3E', marginBottom:4 }}>{def.title}</div>
            <div style={{ fontSize:11.5, color:'#7A7870', lineHeight:1.5 }}>{def.desc}</div>
          </div>
        ))}
      </div>

      <div style={{ background:'#fff', border:'1.5px solid #E8E6DF', borderRadius:12, padding:'18px 20px', maxWidth:640 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#0D1B3E', marginBottom:12 }}>Filters — {REPORT_DEFS[reportKey].title}</div>
        <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'flex-end' }}>
          {needsState && (
            <div>
              <div style={{ fontSize:11, color:'#7A7870', marginBottom:4 }}>State</div>
              <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={{ padding:'8px 10px', borderRadius:8, border:'1.5px solid #E8E6DF', fontSize:13 }}>
                <option value="">All states</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          {needsDates && (
            <>
              <div>
                <div style={{ fontSize:11, color:'#7A7870', marginBottom:4 }}>From</div>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding:'8px 10px', borderRadius:8, border:'1.5px solid #E8E6DF', fontSize:13 }} />
              </div>
              <div>
                <div style={{ fontSize:11, color:'#7A7870', marginBottom:4 }}>To</div>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding:'8px 10px', borderRadius:8, border:'1.5px solid #E8E6DF', fontSize:13 }} />
              </div>
            </>
          )}
          {needsHorizon && (
            <div>
              <div style={{ fontSize:11, color:'#7A7870', marginBottom:4 }}>Expiring within</div>
              <select value={horizon} onChange={e => setHorizon(e.target.value)} style={{ padding:'8px 10px', borderRadius:8, border:'1.5px solid #E8E6DF', fontSize:13 }}>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
          )}
          <button className="btn btn-primary" onClick={generateReport} disabled={generating}
            style={{ padding:'10px 22px', fontSize:14, fontWeight:600 }}>
            {generating && !preview ? '⏳ Loading...' : '👁️ Preview Report'}
          </button>
        </div>
      </div>

      {/* 👁️ REPORT PREVIEW MODAL */}
      {preview && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setPreview(null)}>
          <div style={{ background:'#fff', borderRadius:16, maxWidth:900, width:'100%', maxHeight:'88vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.35)', overflow:'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ background:'#0D1B3E', padding:'16px 22px' }}>
              <div style={{ color:'#C9A84C', fontSize:17, fontWeight:700 }}>{preview.title}</div>
              <div style={{ color:'#FDF6E3', fontSize:11.5, marginTop:3 }}>{preview.filterNote} • Generated {new Date().toLocaleString('en-US')}</div>
            </div>
            <div style={{ overflowY:'auto', padding:'0 0 8px 0', flex:1 }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                <thead>
                  <tr>
                    {preview.columns.map((c, i) => (
                      <th key={i} style={{ position:'sticky', top:0, background:'#0D1B3E', color:'#C9A84C', textAlign:'left', padding:'9px 12px', fontSize:11.5, fontWeight:700 }}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, ri) => (
                    <tr key={ri} style={{ background: ri % 2 === 1 ? '#F8F7F4' : '#fff', borderBottom:'1px solid #EDEAE2' }}>
                      {r.map((cell, ci) => <td key={ci} style={{ padding:'8px 12px', color:'#1E2A44' }}>{String(cell ?? '—')}</td>)}
                    </tr>
                  ))}
                  {preview.totalsRow && (
                    <tr style={{ background:'#F6EDD3', fontWeight:700 }}>
                      {preview.totalsRow.map((cell, ci) => <td key={ci} style={{ padding:'10px 12px', color:'#0D1B3E' }}>{String(cell ?? '')}</td>)}
                    </tr>
                  )}
                  {preview.rows.length === 0 && (
                    <tr><td colSpan={preview.columns.length} style={{ padding:'22px', color:'#7A7870', textAlign:'center' }}>No records matched the selected filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display:'flex', gap:10, padding:'14px 22px', borderTop:'1px solid #E8E6DF', background:'#FDFCF9' }}>
              <div style={{ flex:1, alignSelf:'center', fontSize:12, color:'#7A7870' }}>{preview.rows.length} row{preview.rows.length === 1 ? '' : 's'} — exactly what the PDF will contain</div>
              <button className="btn btn-outline" onClick={() => setPreview(null)}>Close</button>
              <button className="btn btn-primary" onClick={downloadPreviewPdf} disabled={generating} style={{ fontWeight:600 }}>
                {generating ? '⏳ Rendering...' : '🖨️ Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


function ResourcesCMSLegacy() {
  const [activeTab,   setActiveTab]   = useState('states')
  const [states,      setStates]      = useState(STATE_RESOURCES_ADMIN)
  const [lessons,     setLessons]     = useState(TRAINING_CONTENT)
  const [editing,     setEditing]     = useState(null)
  const [editData,    setEditData]    = useState({})
  const [editLesson,  setEditLesson]  = useState(null)
  const [editContent, setEditContent] = useState('')
  const [search,      setSearch]      = useState('')
  const [filterActive,setFilterActive]= useState('all')
  const [saved,       setSaved]       = useState({})

  // ── STATE FUNCTIONS ────────────────────────────────────────
  const filtered = states.filter(s => {
    const matchSearch = !search || s.state.toLowerCase().includes(search.toLowerCase()) || s.abbr.toLowerCase().includes(search.toLowerCase())
    const matchFilter = filterActive === 'all' ? true : filterActive === 'active' ? s.active : !s.active
    return matchSearch && matchFilter
  })

  function startEdit(s) {
    setEditing(s.abbr)
    setEditData({ notes: s.notes, link: s.link, active: s.active, commission: s.commission, journal: s.journal, ron: s.ron })
  }

  function saveEdit(abbr) {
    setStates(prev => prev.map(s => s.abbr === abbr ? { ...s, ...editData } : s))
    setEditing(null)
    setSaved(prev => ({ ...prev, [abbr]: true }))
    toast.success(`✅ ${abbr} updated!! Changes are live!!`)
    setTimeout(() => setSaved(prev => ({ ...prev, [abbr]: false })), 3000)
  }

  // ── LESSON FUNCTIONS ───────────────────────────────────────
  function saveLesson(id) {
    setLessons(prev => prev.map(l => l.id === id ? { ...l, content: editContent } : l))
    setEditLesson(null)
    toast.success('✅ Lesson updated!! Changes are live for notaries!!')
  }

  const activeCount   = states.filter(s => s.active).length
  const inactiveCount = states.filter(s => !s.active).length

  // ── GROUP LESSONS BY MODULE ────────────────────────────────
  const moduleGroups = lessons.reduce((acc, l) => {
    if (!acc[l.module]) acc[l.module] = []
    acc[l.module].push(l)
    return acc
  }, {})

  return (
    <div style={{ padding:'24px', maxWidth:1000, margin:'0 auto' }}>
      {/* HEADER */}
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:28, color:'#0D1B3E', marginBottom:4 }}>📚 Resources CMS</h1>
        <p style={{ fontSize:13, color:'#7A7870' }}>Edit training content and state information visible to your notaries!!</p>
      </div>

      {/* TABS */}
      <div style={{ display:'flex', gap:8, marginBottom:24, borderBottom:'2px solid #E8E6DF', paddingBottom:0 }}>
        {[
          { id:'states',   label:'🗺️ State Rules'      },
          { id:'training', label:'📖 Training Manual'  },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding:'10px 20px', border:'none', background:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
              color:      activeTab===tab.id ? '#0D1B3E' : '#7A7870',
              borderBottom:`3px solid ${activeTab===tab.id ? '#C9A84C' : 'transparent'}`,
              marginBottom:-2 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── STATE RULES TAB ── */}
      {activeTab === 'states' && (
        <>
          {/* STATS */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
            {[
              { label:'Total States', value:states.length, color:'#0D1B3E', bg:'#F8F7F4' },
              { label:'🟢 LCA Active', value:activeCount,   color:'#2D7A4F', bg:'#EAF5EE' },
              { label:'⚪ Inactive',   value:inactiveCount, color:'#7A7870', bg:'#F8F7F4' },
            ].map(stat => (
              <div key={stat.label} style={{ background:stat.bg, border:'1px solid #E8E6DF', borderRadius:12, padding:'14px 16px', textAlign:'center' }}>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:28, color:stat.color, fontWeight:600 }}>{stat.value}</div>
                <div style={{ fontSize:11, color:'#7A7870', fontWeight:600, letterSpacing:'0.06em' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* FILTERS */}
          <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search state..."
              style={{ padding:'8px 14px', border:'1.5px solid #E0D9CC', borderRadius:10, fontSize:13, fontFamily:'inherit', width:200 }} />
            {['all','active','inactive'].map(f => (
              <button key={f} onClick={() => setFilterActive(f)}
                style={{ padding:'7px 16px', borderRadius:8, border:'1.5px solid', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
                  background: filterActive===f ? '#0D1B3E' : '#fff',
                  color:      filterActive===f ? '#C9A84C'  : '#7A7870',
                  borderColor:filterActive===f ? '#0D1B3E'  : '#E8E6DF' }}>
                {f === 'all' ? 'All States' : f === 'active' ? '🟢 Active' : '⚪ Inactive'}
              </button>
            ))}
          </div>

          {/* STATES LIST */}
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {filtered.map(s => (
              <div key={s.abbr} style={{ background:'#fff', border:`1.5px solid ${s.active ? 'rgba(45,122,79,0.3)' : '#E8E6DF'}`, borderRadius:12, overflow:'hidden' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 18px', background: s.active ? 'rgba(45,122,79,0.04)' : '#F8F7F4' }}>
                  <div style={{ width:44, height:44, borderRadius:10, background: s.active ? '#EAF5EE' : '#F0EEE8', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <span style={{ fontSize:13, fontWeight:800, color: s.active ? '#2D7A4F' : '#B8B5AB' }}>{s.abbr}</span>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:'#0D1B3E', marginBottom:2 }}>
                      {s.state}
                      {saved[s.abbr] && <span style={{ fontSize:10, color:'#2D7A4F', marginLeft:8, fontWeight:700 }}>✅ Saved!!</span>}
                    </div>
                    <div style={{ fontSize:11, color:'#7A7870', display:'flex', gap:10, flexWrap:'wrap' }}>
                      <span>Commission: <strong>{s.commission}</strong></span>
                      <span>Journal: <strong>{s.journal}</strong></span>
                      <span>RON: <strong>{s.ron}</strong></span>
                      <span style={{ color: s.active ? '#2D7A4F' : '#B8B5AB', fontWeight:700 }}>
                        {s.active ? '🟢 LCA ACTIVE' : '⚪ Not yet active'}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => editing === s.abbr ? setEditing(null) : startEdit(s)}
                    style={{ background: editing===s.abbr ? '#E8E6DF' : '#0D1B3E', color: editing===s.abbr ? '#7A7870' : '#C9A84C', border:'none', borderRadius:8, padding:'8px 16px', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                    {editing === s.abbr ? '✕ Cancel' : '✏️ Edit'}
                  </button>
                </div>
                {editing === s.abbr && (
                  <div style={{ padding:'16px 18px', borderTop:'1px solid #E8E6DF', background:'#FAFAF8' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                      <div>
                        <div style={{ fontSize:10, fontWeight:700, color:'#B8B5AB', letterSpacing:'0.08em', marginBottom:4 }}>OFFICIAL LINK</div>
                        <input value={editData.link || ''} onChange={e => setEditData(d => ({ ...d, link:e.target.value }))}
                          style={{ width:'100%', padding:'8px 10px', border:'1.5px solid #C9A84C', borderRadius:8, fontSize:12, fontFamily:'inherit' }} />
                      </div>
                      <div>
                        <div style={{ fontSize:10, fontWeight:700, color:'#B8B5AB', letterSpacing:'0.08em', marginBottom:4 }}>STATUS</div>
                        <div style={{ display:'flex', gap:8 }}>
                          {[true, false].map(val => (
                            <button key={String(val)} type="button" onClick={() => setEditData(d => ({ ...d, active:val }))}
                              style={{ flex:1, padding:'8px', borderRadius:8, border:'1.5px solid', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'inherit',
                                background: editData.active===val ? (val ? '#EAF5EE' : '#F8F7F4') : '#fff',
                                color:      editData.active===val ? (val ? '#2D7A4F' : '#7A7870') : '#B8B5AB',
                                borderColor:editData.active===val ? (val ? '#2D7A4F' : '#B8B5AB') : '#E8E6DF' }}>
                              {val ? '🟢 Active' : '⚪ Inactive'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div style={{ marginBottom:12 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:'#B8B5AB', letterSpacing:'0.08em', marginBottom:4 }}>NOTES FOR NOTARIES</div>
                      <textarea value={editData.notes || ''} onChange={e => setEditData(d => ({ ...d, notes:e.target.value }))}
                        rows={3} placeholder="Add notes about this state's requirements..."
                        style={{ width:'100%', padding:'8px 10px', border:'1.5px solid #C9A84C', borderRadius:8, fontSize:12, fontFamily:'inherit', resize:'none' }} />
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => saveEdit(s.abbr)}
                        style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#C9A84C', border:'none', borderRadius:8, padding:'10px 24px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                        ✅ Save Changes
                      </button>
                      <a href={editData.link} target="_blank" rel="noreferrer"
                        style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, color:'#185FA5', padding:'10px 14px', border:'1px solid #E8E6DF', borderRadius:8, textDecoration:'none' }}>
                        🔗 Test Link
                      </a>
                    </div>
                  </div>
                )}
                {editing !== s.abbr && s.notes && (
                  <div style={{ padding:'8px 18px 12px', borderTop:'1px solid #F0EEE8' }}>
                    <div style={{ fontSize:11, color:'#7A7870', fontStyle:'italic' }}>{s.notes}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── TRAINING MANUAL TAB ── */}
      {activeTab === 'training' && (
        <>
          <div style={{ background:'#EBF4FF', border:'1px solid rgba(24,95,165,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:13, color:'#1A3A5E' }}>
            ℹ️ Click <strong>✏️ Edit</strong> on any lesson to update the content!! Changes are live immediately for all notaries!!
          </div>

          {Object.entries(moduleGroups).map(([moduleName, moduleLessons]) => (
            <div key={moduleName} style={{ marginBottom:20 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.06em', marginBottom:10, padding:'8px 14px', background:'#F8F7F4', borderRadius:8, border:'1px solid #E8E6DF' }}>
                📚 {moduleName}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {moduleLessons.map(lesson => (
                  <div key={lesson.id} style={{ background:'#fff', border:'1px solid #E8E6DF', borderRadius:12, overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#F8F7F4' }}>
                      <div style={{ flex:1, fontSize:13, fontWeight:600, color:'#0D1B3E' }}>{lesson.title}</div>
                      <button onClick={() => { setEditLesson(lesson.id); setEditContent(lesson.content) }}
                        style={{ background:'#0D1B3E', color:'#C9A84C', border:'none', borderRadius:8, padding:'6px 14px', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                        ✏️ Edit
                      </button>
                    </div>

                    {editLesson === lesson.id ? (
                      <div style={{ padding:'14px 16px' }}>
                        <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={12}
                          style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #C9A84C', borderRadius:8, fontSize:12, fontFamily:'inherit', lineHeight:1.8, resize:'vertical', marginBottom:10 }} />
                        <div style={{ display:'flex', gap:8 }}>
                          <button onClick={() => saveLesson(lesson.id)}
                            style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#C9A84C', border:'none', borderRadius:8, padding:'10px 24px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                            ✅ Save Changes
                          </button>
                          <button onClick={() => setEditLesson(null)}
                            style={{ background:'#fff', color:'#7A7870', border:'1px solid #E0D9CC', borderRadius:8, padding:'10px 20px', fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding:'10px 16px 14px' }}>
                        <div style={{ fontSize:12, color:'#7A7870', lineHeight:1.7, whiteSpace:'pre-line', maxHeight:80, overflow:'hidden', position:'relative' }}>
                          {lesson.content.substring(0, 200)}{lesson.content.length > 200 ? '...' : ''}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export default function AdminPortal() {
  return (
    <AdminPortalLayout>
      <Routes>
        <Route path="/"            element={<Dashboard />} />
        <Route path="/jobs"        element={<AllJobs />} />
        <Route path="/jobs/:jobId" element={<AdminJobDetail />} />
        <Route path="/assign"      element={<AssignNotaryPage />} />
        <Route path="/notaries"    element={<Notaries />} />
        <Route path="/performance" element={<PerformanceDashboard />} />
        <Route path="/clients"     element={<Clients />} />
        <Route path="/broadcast"   element={<Broadcast />} />
        <Route path="/payouts"     element={<Payouts />} />
        <Route path="/reports"     element={<ReportsCenter />} />
        <Route path="/invoices"    element={<Invoices />} />
        <Route path="/fees"        element={<FeeCalculator />} />
        <Route path="/services"    element={<ServicesMaster />} />
        <Route path="/credentials" element={<Credentials />} />
        <Route path="/scanbacks"   element={<ScanBacksQC />} />
        <Route path="/maps"        element={<Maps />} />
        <Route path="/settings"    element={<Settings />} />
        <Route path="/maintenance" element={<PlatformMaintenance />} />
        <Route path="/academy-cms" element={<AcademyCMS />} />
        <Route path="/resources-cms" element={<ResourcesCMS />} />
        <Route path="/education" element={<EducationDashboard />} />
        <Route path="/tribe-cms" element={<TribeCMS />} />
        <Route path="/podcast-cms" element={<PodcastCMS />} />
        <Route path="/blacklist"        element={<NuttyNotaries />} />
        <Route path="/booking-services"    element={<IndividualServicesAdmin />} />
        <Route path="/bookings"             element={<BookingsAdmin />} />
        <Route path="/service-categories"   element={<ServiceCategoriesAdmin />} />
        <Route path="schedule" element={<AdminSchedule />} />
      </Routes>
    </AdminPortalLayout>
  )
}
